/* eslint-disable */

import { createReader, createWriter } from "mavlink.ts";

const textDecoder = new TextDecoder();
const textEncoder = new TextEncoder();

const toMap = <K extends string, V extends number | bigint | string>(obj: Record<K, V>) => new Map<K, V>(Object.entries(obj) as [K, V][]);

const invertMap = <K, V extends number | bigint>(map: Map<K, V>) => new Map<number | bigint, K>([...map].map(([k, v]) => [v, k]));

const decodeBitmask = <T extends string, V extends number | bigint>(value: number | bigint, mapping: Map<V, T>): T[] => {
  const v = BigInt(value);
  const result: T[] = [];
  for (const [key, val] of mapping.entries()) {
    const k = BigInt(key);
    if (k !== 0n && (v & k) === k) {
      result.push(val);
    }
  }
  return result;
};

const encodeBitmask = <T extends string, V extends number | bigint>(value: T[] | V, mapping: Map<T, V>): V => {
  if (Array.isArray(value)) {
    let acc = 0n;
    for (const val of value) acc |= BigInt(mapping.get(val as T) ?? 0);
    const firstVal = mapping.values().next().value;
    return (typeof firstVal === "bigint" ? acc : Number(acc)) as V;
  }
  return value;
};

const lookup = <T extends string, V extends number | bigint>(value: T | V | number | bigint, mapping: Map<T, V>): V => {
  return (typeof value === "string" ? mapping.get(value) : value) as V;
};

export const mavAutopilotEnumMap = {
  GENERIC: 0,
  RESERVED: 1,
  SLUGS: 2,
  ARDUPILOTMEGA: 3,
  OPENPILOT: 4,
  GENERIC_WAYPOINTS_ONLY: 5,
  GENERIC_WAYPOINTS_AND_SIMPLE_NAVIGATION_ONLY: 6,
  GENERIC_MISSION_FULL: 7,
  INVALID: 8,
  PPZ: 9,
  UDB: 10,
  FP: 11,
  PX4: 12,
  SMACCMPILOT: 13,
  AUTOQUAD: 14,
  ARMAZILA: 15,
  AEROB: 16,
  ASLUAV: 17,
  SMARTAP: 18,
  AIRRAILS: 19,
  REFLEX: 20,
} as const;

export type MavAutopilot = keyof typeof mavAutopilotEnumMap;
export const mavAutopilotMap = toMap(mavAutopilotEnumMap);
export const mavAutopilotInverseMap = invertMap(mavAutopilotMap);

export const mavTypeEnumMap = {
  GENERIC: 0,
  FIXED_WING: 1,
  QUADROTOR: 2,
  COAXIAL: 3,
  HELICOPTER: 4,
  ANTENNA_TRACKER: 5,
  GCS: 6,
  AIRSHIP: 7,
  FREE_BALLOON: 8,
  ROCKET: 9,
  GROUND_ROVER: 10,
  SURFACE_BOAT: 11,
  SUBMARINE: 12,
  HEXAROTOR: 13,
  OCTOROTOR: 14,
  TRICOPTER: 15,
  FLAPPING_WING: 16,
  KITE: 17,
  ONBOARD_CONTROLLER: 18,
  VTOL_DUOROTOR: 19,
  VTOL_QUADROTOR: 20,
  VTOL_TILTROTOR: 21,
  VTOL_RESERVED2: 22,
  VTOL_RESERVED3: 23,
  VTOL_RESERVED4: 24,
  VTOL_RESERVED5: 25,
  GIMBAL: 26,
  ADSB: 27,
  PARAFOIL: 28,
  DODECAROTOR: 29,
  CAMERA: 30,
  CHARGING_STATION: 31,
  FLARM: 32,
  SERVO: 33,
  ODID: 34,
  DECAROTOR: 35,
  BATTERY: 36,
  PARACHUTE: 37,
  LOG: 38,
  OSD: 39,
  IMU: 40,
  GPS: 41,
  WINCH: 42,
} as const;

export type MavType = keyof typeof mavTypeEnumMap;
export const mavTypeMap = toMap(mavTypeEnumMap);
export const mavTypeInverseMap = invertMap(mavTypeMap);

export const mavModeFlagEnumMap = {
  SAFETY_ARMED: 128,
  MANUAL_INPUT_ENABLED: 64,
  HIL_ENABLED: 32,
  STABILIZE_ENABLED: 16,
  GUIDED_ENABLED: 8,
  AUTO_ENABLED: 4,
  TEST_ENABLED: 2,
  CUSTOM_MODE_ENABLED: 1,
} as const;

export type MavModeFlag = keyof typeof mavModeFlagEnumMap;
export const mavModeFlagMap = toMap(mavModeFlagEnumMap);
export const mavModeFlagInverseMap = invertMap(mavModeFlagMap);

export const mavModeFlagDecodePositionEnumMap = {
  SAFETY: 128,
  MANUAL: 64,
  HIL: 32,
  STABILIZE: 16,
  GUIDED: 8,
  AUTO: 4,
  TEST: 2,
  CUSTOM_MODE: 1,
} as const;

export type MavModeFlagDecodePosition = keyof typeof mavModeFlagDecodePositionEnumMap;
export const mavModeFlagDecodePositionMap = toMap(mavModeFlagDecodePositionEnumMap);
export const mavModeFlagDecodePositionInverseMap = invertMap(mavModeFlagDecodePositionMap);

export const mavStateEnumMap = {
  UNINIT: 0,
  BOOT: 1,
  CALIBRATING: 2,
  STANDBY: 3,
  ACTIVE: 4,
  CRITICAL: 5,
  EMERGENCY: 6,
  POWEROFF: 7,
  FLIGHT_TERMINATION: 8,
} as const;

export type MavState = keyof typeof mavStateEnumMap;
export const mavStateMap = toMap(mavStateEnumMap);
export const mavStateInverseMap = invertMap(mavStateMap);

export const mavComponentEnumMap = {
  MAV_COMP_ID_ALL: 0,
  MAV_COMP_ID_AUTOPILOT1: 1,
  MAV_COMP_ID_USER1: 25,
  MAV_COMP_ID_USER2: 26,
  MAV_COMP_ID_USER3: 27,
  MAV_COMP_ID_USER4: 28,
  MAV_COMP_ID_USER5: 29,
  MAV_COMP_ID_USER6: 30,
  MAV_COMP_ID_USER7: 31,
  MAV_COMP_ID_USER8: 32,
  MAV_COMP_ID_USER9: 33,
  MAV_COMP_ID_USER10: 34,
  MAV_COMP_ID_USER11: 35,
  MAV_COMP_ID_USER12: 36,
  MAV_COMP_ID_USER13: 37,
  MAV_COMP_ID_USER14: 38,
  MAV_COMP_ID_USER15: 39,
  MAV_COMP_ID_USER16: 40,
  MAV_COMP_ID_USER17: 41,
  MAV_COMP_ID_USER18: 42,
  MAV_COMP_ID_USER19: 43,
  MAV_COMP_ID_USER20: 44,
  MAV_COMP_ID_USER21: 45,
  MAV_COMP_ID_USER22: 46,
  MAV_COMP_ID_USER23: 47,
  MAV_COMP_ID_USER24: 48,
  MAV_COMP_ID_USER25: 49,
  MAV_COMP_ID_USER26: 50,
  MAV_COMP_ID_USER27: 51,
  MAV_COMP_ID_USER28: 52,
  MAV_COMP_ID_USER29: 53,
  MAV_COMP_ID_USER30: 54,
  MAV_COMP_ID_USER31: 55,
  MAV_COMP_ID_USER32: 56,
  MAV_COMP_ID_USER33: 57,
  MAV_COMP_ID_USER34: 58,
  MAV_COMP_ID_USER35: 59,
  MAV_COMP_ID_USER36: 60,
  MAV_COMP_ID_USER37: 61,
  MAV_COMP_ID_USER38: 62,
  MAV_COMP_ID_USER39: 63,
  MAV_COMP_ID_USER40: 64,
  MAV_COMP_ID_USER41: 65,
  MAV_COMP_ID_USER42: 66,
  MAV_COMP_ID_USER43: 67,
  MAV_COMP_ID_TELEMETRY_RADIO: 68,
  MAV_COMP_ID_USER45: 69,
  MAV_COMP_ID_USER46: 70,
  MAV_COMP_ID_USER47: 71,
  MAV_COMP_ID_USER48: 72,
  MAV_COMP_ID_USER49: 73,
  MAV_COMP_ID_USER50: 74,
  MAV_COMP_ID_USER51: 75,
  MAV_COMP_ID_USER52: 76,
  MAV_COMP_ID_USER53: 77,
  MAV_COMP_ID_USER54: 78,
  MAV_COMP_ID_USER55: 79,
  MAV_COMP_ID_USER56: 80,
  MAV_COMP_ID_USER57: 81,
  MAV_COMP_ID_USER58: 82,
  MAV_COMP_ID_USER59: 83,
  MAV_COMP_ID_USER60: 84,
  MAV_COMP_ID_USER61: 85,
  MAV_COMP_ID_USER62: 86,
  MAV_COMP_ID_USER63: 87,
  MAV_COMP_ID_USER64: 88,
  MAV_COMP_ID_USER65: 89,
  MAV_COMP_ID_USER66: 90,
  MAV_COMP_ID_USER67: 91,
  MAV_COMP_ID_USER68: 92,
  MAV_COMP_ID_USER69: 93,
  MAV_COMP_ID_USER70: 94,
  MAV_COMP_ID_USER71: 95,
  MAV_COMP_ID_USER72: 96,
  MAV_COMP_ID_USER73: 97,
  MAV_COMP_ID_USER74: 98,
  MAV_COMP_ID_USER75: 99,
  MAV_COMP_ID_CAMERA: 100,
  MAV_COMP_ID_CAMERA2: 101,
  MAV_COMP_ID_CAMERA3: 102,
  MAV_COMP_ID_CAMERA4: 103,
  MAV_COMP_ID_CAMERA5: 104,
  MAV_COMP_ID_CAMERA6: 105,
  MAV_COMP_ID_SERVO1: 140,
  MAV_COMP_ID_SERVO2: 141,
  MAV_COMP_ID_SERVO3: 142,
  MAV_COMP_ID_SERVO4: 143,
  MAV_COMP_ID_SERVO5: 144,
  MAV_COMP_ID_SERVO6: 145,
  MAV_COMP_ID_SERVO7: 146,
  MAV_COMP_ID_SERVO8: 147,
  MAV_COMP_ID_SERVO9: 148,
  MAV_COMP_ID_SERVO10: 149,
  MAV_COMP_ID_SERVO11: 150,
  MAV_COMP_ID_SERVO12: 151,
  MAV_COMP_ID_SERVO13: 152,
  MAV_COMP_ID_SERVO14: 153,
  MAV_COMP_ID_GIMBAL: 154,
  MAV_COMP_ID_LOG: 155,
  MAV_COMP_ID_ADSB: 156,
  MAV_COMP_ID_OSD: 157,
  MAV_COMP_ID_PERIPHERAL: 158,
  MAV_COMP_ID_QX1_GIMBAL: 159,
  MAV_COMP_ID_FLARM: 160,
  MAV_COMP_ID_PARACHUTE: 161,
  MAV_COMP_ID_GIMBAL2: 171,
  MAV_COMP_ID_GIMBAL3: 172,
  MAV_COMP_ID_GIMBAL4: 173,
  MAV_COMP_ID_GIMBAL5: 174,
  MAV_COMP_ID_GIMBAL6: 175,
  MAV_COMP_ID_BATTERY: 180,
  MAV_COMP_ID_BATTERY2: 181,
  MAV_COMP_ID_MAVCAN: 189,
  MAV_COMP_ID_MISSIONPLANNER: 190,
  MAV_COMP_ID_ONBOARD_COMPUTER: 191,
  MAV_COMP_ID_ONBOARD_COMPUTER2: 192,
  MAV_COMP_ID_ONBOARD_COMPUTER3: 193,
  MAV_COMP_ID_ONBOARD_COMPUTER4: 194,
  MAV_COMP_ID_PATHPLANNER: 195,
  MAV_COMP_ID_OBSTACLE_AVOIDANCE: 196,
  MAV_COMP_ID_VISUAL_INERTIAL_ODOMETRY: 197,
  MAV_COMP_ID_PAIRING_MANAGER: 198,
  MAV_COMP_ID_IMU: 200,
  MAV_COMP_ID_IMU_2: 201,
  MAV_COMP_ID_IMU_3: 202,
  MAV_COMP_ID_GPS: 220,
  MAV_COMP_ID_GPS2: 221,
  MAV_COMP_ID_ODID_TXRX_1: 236,
  MAV_COMP_ID_ODID_TXRX_2: 237,
  MAV_COMP_ID_ODID_TXRX_3: 238,
  MAV_COMP_ID_UDP_BRIDGE: 240,
  MAV_COMP_ID_UART_BRIDGE: 241,
  MAV_COMP_ID_TUNNEL_NODE: 242,
  MAV_COMP_ID_SYSTEM_CONTROL: 250,
} as const;

export type MavComponent = keyof typeof mavComponentEnumMap;
export const mavComponentMap = toMap(mavComponentEnumMap);
export const mavComponentInverseMap = invertMap(mavComponentMap);

export const firmwareVersionTypeEnumMap = {
  DEV: 0,
  ALPHA: 64,
  BETA: 128,
  RC: 192,
  OFFICIAL: 255,
} as const;

export type FirmwareVersionType = keyof typeof firmwareVersionTypeEnumMap;
export const firmwareVersionTypeMap = toMap(firmwareVersionTypeEnumMap);
export const firmwareVersionTypeInverseMap = invertMap(firmwareVersionTypeMap);

export const hlFailureFlagEnumMap = {
  GPS: 1,
  DIFFERENTIAL_PRESSURE: 2,
  ABSOLUTE_PRESSURE: 4,
  "3D_ACCEL": 8,
  "3D_GYRO": 16,
  "3D_MAG": 32,
  TERRAIN: 64,
  BATTERY: 128,
  RC_RECEIVER: 256,
  OFFBOARD_LINK: 512,
  ENGINE: 1024,
  GEOFENCE: 2048,
  ESTIMATOR: 4096,
  MISSION: 8192,
} as const;

export type HlFailureFlag = keyof typeof hlFailureFlagEnumMap;
export const hlFailureFlagMap = toMap(hlFailureFlagEnumMap);
export const hlFailureFlagInverseMap = invertMap(hlFailureFlagMap);

export const mavGotoEnumMap = {
  DO_HOLD: 0,
  DO_CONTINUE: 1,
  HOLD_AT_CURRENT_POSITION: 2,
  HOLD_AT_SPECIFIED_POSITION: 3,
} as const;

export type MavGoto = keyof typeof mavGotoEnumMap;
export const mavGotoMap = toMap(mavGotoEnumMap);
export const mavGotoInverseMap = invertMap(mavGotoMap);

export const mavModeEnumMap = {
  PREFLIGHT: 0,
  STABILIZE_DISARMED: 80,
  STABILIZE_ARMED: 208,
  MANUAL_DISARMED: 64,
  MANUAL_ARMED: 192,
  GUIDED_DISARMED: 88,
  GUIDED_ARMED: 216,
  AUTO_DISARMED: 92,
  AUTO_ARMED: 220,
  TEST_DISARMED: 66,
  TEST_ARMED: 194,
} as const;

export type MavMode = keyof typeof mavModeEnumMap;
export const mavModeMap = toMap(mavModeEnumMap);
export const mavModeInverseMap = invertMap(mavModeMap);

export const mavSysStatusSensorEnumMap = {
  "3D_GYRO": 1,
  "3D_ACCEL": 2,
  "3D_MAG": 4,
  ABSOLUTE_PRESSURE: 8,
  DIFFERENTIAL_PRESSURE: 16,
  GPS: 32,
  OPTICAL_FLOW: 64,
  VISION_POSITION: 128,
  LASER_POSITION: 256,
  EXTERNAL_GROUND_TRUTH: 512,
  ANGULAR_RATE_CONTROL: 1024,
  ATTITUDE_STABILIZATION: 2048,
  YAW_POSITION: 4096,
  Z_ALTITUDE_CONTROL: 8192,
  XY_POSITION_CONTROL: 16384,
  MOTOR_OUTPUTS: 32768,
  RC_RECEIVER: 65536,
  "3D_GYRO2": 131072,
  "3D_ACCEL2": 262144,
  "3D_MAG2": 524288,
  MAV_SYS_STATUS_GEOFENCE: 1048576,
  MAV_SYS_STATUS_AHRS: 2097152,
  MAV_SYS_STATUS_TERRAIN: 4194304,
  MAV_SYS_STATUS_REVERSE_MOTOR: 8388608,
  MAV_SYS_STATUS_LOGGING: 16777216,
  BATTERY: 33554432,
  PROXIMITY: 67108864,
  SATCOM: 134217728,
  MAV_SYS_STATUS_PREARM_CHECK: 268435456,
  MAV_SYS_STATUS_OBSTACLE_AVOIDANCE: 536870912,
  PROPULSION: 1073741824,
} as const;

export type MavSysStatusSensor = keyof typeof mavSysStatusSensorEnumMap;
export const mavSysStatusSensorMap = toMap(mavSysStatusSensorEnumMap);
export const mavSysStatusSensorInverseMap = invertMap(mavSysStatusSensorMap);

export const mavFrameEnumMap = {
  GLOBAL: 0,
  LOCAL_NED: 1,
  MISSION: 2,
  GLOBAL_RELATIVE_ALT: 3,
  LOCAL_ENU: 4,
  GLOBAL_INT: 5,
  GLOBAL_RELATIVE_ALT_INT: 6,
  LOCAL_OFFSET_NED: 7,
  BODY_NED: 8,
  BODY_OFFSET_NED: 9,
  GLOBAL_TERRAIN_ALT: 10,
  GLOBAL_TERRAIN_ALT_INT: 11,
  BODY_FRD: 12,
  RESERVED_13: 13,
  RESERVED_14: 14,
  RESERVED_15: 15,
  RESERVED_16: 16,
  RESERVED_17: 17,
  RESERVED_18: 18,
  RESERVED_19: 19,
  LOCAL_FRD: 20,
  LOCAL_FLU: 21,
} as const;

export type MavFrame = keyof typeof mavFrameEnumMap;
export const mavFrameMap = toMap(mavFrameEnumMap);
export const mavFrameInverseMap = invertMap(mavFrameMap);

export const mavlinkDataStreamTypeEnumMap = {
  MAVLINK_DATA_STREAM_IMG_JPEG: 0,
  MAVLINK_DATA_STREAM_IMG_BMP: 1,
  MAVLINK_DATA_STREAM_IMG_RAW8U: 2,
  MAVLINK_DATA_STREAM_IMG_RAW32U: 3,
  MAVLINK_DATA_STREAM_IMG_PGM: 4,
  MAVLINK_DATA_STREAM_IMG_PNG: 5,
} as const;

export type MavlinkDataStreamType = keyof typeof mavlinkDataStreamTypeEnumMap;
export const mavlinkDataStreamTypeMap = toMap(mavlinkDataStreamTypeEnumMap);
export const mavlinkDataStreamTypeInverseMap = invertMap(mavlinkDataStreamTypeMap);

export const fenceActionEnumMap = {
  NONE: 0,
  GUIDED: 1,
  REPORT: 2,
  GUIDED_THR_PASS: 3,
  RTL: 4,
} as const;

export type FenceAction = keyof typeof fenceActionEnumMap;
export const fenceActionMap = toMap(fenceActionEnumMap);
export const fenceActionInverseMap = invertMap(fenceActionMap);

export const fenceBreachEnumMap = {
  NONE: 0,
  MINALT: 1,
  MAXALT: 2,
  BOUNDARY: 3,
} as const;

export type FenceBreach = keyof typeof fenceBreachEnumMap;
export const fenceBreachMap = toMap(fenceBreachEnumMap);
export const fenceBreachInverseMap = invertMap(fenceBreachMap);

export const fenceMitigateEnumMap = {
  UNKNOWN: 0,
  NONE: 1,
  VEL_LIMIT: 2,
} as const;

export type FenceMitigate = keyof typeof fenceMitigateEnumMap;
export const fenceMitigateMap = toMap(fenceMitigateEnumMap);
export const fenceMitigateInverseMap = invertMap(fenceMitigateMap);

export const fenceTypeEnumMap = {
  ALL: 0,
  ALT_MAX: 1,
  CIRCLE: 2,
  POLYGON: 4,
  ALT_MIN: 8,
} as const;

export type FenceType = keyof typeof fenceTypeEnumMap;
export const fenceTypeMap = toMap(fenceTypeEnumMap);
export const fenceTypeInverseMap = invertMap(fenceTypeMap);

export const mavMountModeEnumMap = {
  RETRACT: 0,
  NEUTRAL: 1,
  MAVLINK_TARGETING: 2,
  RC_TARGETING: 3,
  GPS_POINT: 4,
  SYSID_TARGET: 5,
  HOME_LOCATION: 6,
} as const;

export type MavMountMode = keyof typeof mavMountModeEnumMap;
export const mavMountModeMap = toMap(mavMountModeEnumMap);
export const mavMountModeInverseMap = invertMap(mavMountModeMap);

export const gimbalDeviceCapFlagsEnumMap = {
  HAS_RETRACT: 1,
  HAS_NEUTRAL: 2,
  HAS_ROLL_AXIS: 4,
  HAS_ROLL_FOLLOW: 8,
  HAS_ROLL_LOCK: 16,
  HAS_PITCH_AXIS: 32,
  HAS_PITCH_FOLLOW: 64,
  HAS_PITCH_LOCK: 128,
  HAS_YAW_AXIS: 256,
  HAS_YAW_FOLLOW: 512,
  HAS_YAW_LOCK: 1024,
  SUPPORTS_INFINITE_YAW: 2048,
  SUPPORTS_YAW_IN_EARTH_FRAME: 4096,
  HAS_RC_INPUTS: 8192,
} as const;

export type GimbalDeviceCapFlags = keyof typeof gimbalDeviceCapFlagsEnumMap;
export const gimbalDeviceCapFlagsMap = toMap(gimbalDeviceCapFlagsEnumMap);
export const gimbalDeviceCapFlagsInverseMap = invertMap(gimbalDeviceCapFlagsMap);

export const gimbalManagerCapFlagsEnumMap = {
  HAS_RETRACT: 1,
  HAS_NEUTRAL: 2,
  HAS_ROLL_AXIS: 4,
  HAS_ROLL_FOLLOW: 8,
  HAS_ROLL_LOCK: 16,
  HAS_PITCH_AXIS: 32,
  HAS_PITCH_FOLLOW: 64,
  HAS_PITCH_LOCK: 128,
  HAS_YAW_AXIS: 256,
  HAS_YAW_FOLLOW: 512,
  HAS_YAW_LOCK: 1024,
  SUPPORTS_INFINITE_YAW: 2048,
  SUPPORTS_YAW_IN_EARTH_FRAME: 4096,
  HAS_RC_INPUTS: 8192,
  CAN_POINT_LOCATION_LOCAL: 65536,
  CAN_POINT_LOCATION_GLOBAL: 131072,
} as const;

export type GimbalManagerCapFlags = keyof typeof gimbalManagerCapFlagsEnumMap;
export const gimbalManagerCapFlagsMap = toMap(gimbalManagerCapFlagsEnumMap);
export const gimbalManagerCapFlagsInverseMap = invertMap(gimbalManagerCapFlagsMap);

export const gimbalDeviceFlagsEnumMap = {
  RETRACT: 1,
  NEUTRAL: 2,
  ROLL_LOCK: 4,
  PITCH_LOCK: 8,
  YAW_LOCK: 16,
  YAW_IN_VEHICLE_FRAME: 32,
  YAW_IN_EARTH_FRAME: 64,
  ACCEPTS_YAW_IN_EARTH_FRAME: 128,
  RC_EXCLUSIVE: 256,
  RC_MIXED: 512,
} as const;

export type GimbalDeviceFlags = keyof typeof gimbalDeviceFlagsEnumMap;
export const gimbalDeviceFlagsMap = toMap(gimbalDeviceFlagsEnumMap);
export const gimbalDeviceFlagsInverseMap = invertMap(gimbalDeviceFlagsMap);

export const gimbalManagerFlagsEnumMap = {
  RETRACT: 1,
  NEUTRAL: 2,
  ROLL_LOCK: 4,
  PITCH_LOCK: 8,
  YAW_LOCK: 16,
  YAW_IN_VEHICLE_FRAME: 32,
  YAW_IN_EARTH_FRAME: 64,
  ACCEPTS_YAW_IN_EARTH_FRAME: 128,
  RC_EXCLUSIVE: 256,
  RC_MIXED: 512,
} as const;

export type GimbalManagerFlags = keyof typeof gimbalManagerFlagsEnumMap;
export const gimbalManagerFlagsMap = toMap(gimbalManagerFlagsEnumMap);
export const gimbalManagerFlagsInverseMap = invertMap(gimbalManagerFlagsMap);

export const gimbalDeviceErrorFlagsEnumMap = {
  AT_ROLL_LIMIT: 1,
  AT_PITCH_LIMIT: 2,
  AT_YAW_LIMIT: 4,
  ENCODER_ERROR: 8,
  POWER_ERROR: 16,
  MOTOR_ERROR: 32,
  SOFTWARE_ERROR: 64,
  COMMS_ERROR: 128,
  CALIBRATION_RUNNING: 256,
  NO_MANAGER: 512,
} as const;

export type GimbalDeviceErrorFlags = keyof typeof gimbalDeviceErrorFlagsEnumMap;
export const gimbalDeviceErrorFlagsMap = toMap(gimbalDeviceErrorFlagsEnumMap);
export const gimbalDeviceErrorFlagsInverseMap = invertMap(gimbalDeviceErrorFlagsMap);

export const gripperActionsEnumMap = {
  GRIPPER_ACTION_RELEASE: 0,
  GRIPPER_ACTION_GRAB: 1,
} as const;

export type GripperActions = keyof typeof gripperActionsEnumMap;
export const gripperActionsMap = toMap(gripperActionsEnumMap);
export const gripperActionsInverseMap = invertMap(gripperActionsMap);

export const winchActionsEnumMap = {
  WINCH_RELAXED: 0,
  WINCH_RELATIVE_LENGTH_CONTROL: 1,
  WINCH_RATE_CONTROL: 2,
  WINCH_LOCK: 3,
  WINCH_DELIVER: 4,
  WINCH_HOLD: 5,
  WINCH_RETRACT: 6,
  WINCH_LOAD_LINE: 7,
  WINCH_ABANDON_LINE: 8,
} as const;

export type WinchActions = keyof typeof winchActionsEnumMap;
export const winchActionsMap = toMap(winchActionsEnumMap);
export const winchActionsInverseMap = invertMap(winchActionsMap);

export const uavcanNodeHealthEnumMap = {
  OK: 0,
  WARNING: 1,
  ERROR: 2,
  CRITICAL: 3,
} as const;

export type UavcanNodeHealth = keyof typeof uavcanNodeHealthEnumMap;
export const uavcanNodeHealthMap = toMap(uavcanNodeHealthEnumMap);
export const uavcanNodeHealthInverseMap = invertMap(uavcanNodeHealthMap);

export const uavcanNodeModeEnumMap = {
  OPERATIONAL: 0,
  INITIALIZATION: 1,
  MAINTENANCE: 2,
  SOFTWARE_UPDATE: 3,
  OFFLINE: 7,
} as const;

export type UavcanNodeMode = keyof typeof uavcanNodeModeEnumMap;
export const uavcanNodeModeMap = toMap(uavcanNodeModeEnumMap);
export const uavcanNodeModeInverseMap = invertMap(uavcanNodeModeMap);

export const storageStatusEnumMap = {
  EMPTY: 0,
  UNFORMATTED: 1,
  READY: 2,
  NOT_SUPPORTED: 3,
} as const;

export type StorageStatus = keyof typeof storageStatusEnumMap;
export const storageStatusMap = toMap(storageStatusEnumMap);
export const storageStatusInverseMap = invertMap(storageStatusMap);

export const storageTypeEnumMap = {
  UNKNOWN: 0,
  USB_STICK: 1,
  SD: 2,
  MICROSD: 3,
  CF: 4,
  CFE: 5,
  XQD: 6,
  HD: 7,
  OTHER: 254,
} as const;

export type StorageType = keyof typeof storageTypeEnumMap;
export const storageTypeMap = toMap(storageTypeEnumMap);
export const storageTypeInverseMap = invertMap(storageTypeMap);

export const storageUsageFlagEnumMap = {
  SET: 1,
  PHOTO: 2,
  VIDEO: 4,
  LOGS: 8,
} as const;

export type StorageUsageFlag = keyof typeof storageUsageFlagEnumMap;
export const storageUsageFlagMap = toMap(storageUsageFlagEnumMap);
export const storageUsageFlagInverseMap = invertMap(storageUsageFlagMap);

export const autotuneAxisEnumMap = {
  DEFAULT: 0,
  ROLL: 1,
  PITCH: 2,
  YAW: 4,
} as const;

export type AutotuneAxis = keyof typeof autotuneAxisEnumMap;
export const autotuneAxisMap = toMap(autotuneAxisEnumMap);
export const autotuneAxisInverseMap = invertMap(autotuneAxisMap);

export const mavCmdEnumMap = {
  NAV_WAYPOINT: 16,
  NAV_LOITER_UNLIM: 17,
  NAV_LOITER_TURNS: 18,
  NAV_LOITER_TIME: 19,
  NAV_RETURN_TO_LAUNCH: 20,
  NAV_LAND: 21,
  NAV_TAKEOFF: 22,
  NAV_LAND_LOCAL: 23,
  NAV_TAKEOFF_LOCAL: 24,
  NAV_FOLLOW: 25,
  NAV_CONTINUE_AND_CHANGE_ALT: 30,
  NAV_LOITER_TO_ALT: 31,
  DO_FOLLOW: 32,
  DO_FOLLOW_REPOSITION: 33,
  NAV_ROI: 80,
  NAV_PATHPLANNING: 81,
  NAV_SPLINE_WAYPOINT: 82,
  NAV_VTOL_TAKEOFF: 84,
  NAV_VTOL_LAND: 85,
  NAV_GUIDED_ENABLE: 92,
  NAV_DELAY: 93,
  NAV_PAYLOAD_PLACE: 94,
  NAV_LAST: 95,
  CONDITION_DELAY: 112,
  CONDITION_CHANGE_ALT: 113,
  CONDITION_DISTANCE: 114,
  CONDITION_YAW: 115,
  CONDITION_LAST: 159,
  DO_SET_MODE: 176,
  DO_JUMP: 177,
  DO_CHANGE_SPEED: 178,
  DO_SET_HOME: 179,
  DO_SET_PARAMETER: 180,
  DO_SET_RELAY: 181,
  DO_REPEAT_RELAY: 182,
  DO_SET_SERVO: 183,
  DO_REPEAT_SERVO: 184,
  DO_FLIGHTTERMINATION: 185,
  DO_CHANGE_ALTITUDE: 186,
  DO_LAND_START: 189,
  DO_RALLY_LAND: 190,
  DO_GO_AROUND: 191,
  DO_REPOSITION: 192,
  DO_PAUSE_CONTINUE: 193,
  DO_SET_REVERSE: 194,
  DO_SET_ROI_LOCATION: 195,
  DO_SET_ROI_WPNEXT_OFFSET: 196,
  DO_SET_ROI_NONE: 197,
  DO_SET_ROI_SYSID: 198,
  DO_CONTROL_VIDEO: 200,
  DO_SET_ROI: 201,
  DO_DIGICAM_CONFIGURE: 202,
  DO_DIGICAM_CONTROL: 203,
  DO_MOUNT_CONFIGURE: 204,
  DO_MOUNT_CONTROL: 205,
  DO_SET_CAM_TRIGG_DIST: 206,
  DO_FENCE_ENABLE: 207,
  DO_PARACHUTE: 208,
  DO_MOTOR_TEST: 209,
  DO_INVERTED_FLIGHT: 210,
  DO_GRIPPER: 211,
  DO_AUTOTUNE_ENABLE: 212,
  NAV_SET_YAW_SPEED: 213,
  DO_SET_CAM_TRIGG_INTERVAL: 214,
  DO_MOUNT_CONTROL_QUAT: 220,
  DO_GUIDED_MASTER: 221,
  DO_GUIDED_LIMITS: 222,
  DO_ENGINE_CONTROL: 223,
  DO_SET_MISSION_CURRENT: 224,
  DO_LAST: 240,
  PREFLIGHT_CALIBRATION: 241,
  PREFLIGHT_SET_SENSOR_OFFSETS: 242,
  PREFLIGHT_UAVCAN: 243,
  PREFLIGHT_STORAGE: 245,
  PREFLIGHT_REBOOT_SHUTDOWN: 246,
  OVERRIDE_GOTO: 252,
  OBLIQUE_SURVEY: 260,
  MISSION_START: 300,
  COMPONENT_ARM_DISARM: 400,
  RUN_PREARM_CHECKS: 401,
  GET_HOME_POSITION: 410,
  START_RX_PAIR: 500,
  GET_MESSAGE_INTERVAL: 510,
  SET_MESSAGE_INTERVAL: 511,
  REQUEST_MESSAGE: 512,
  REQUEST_PROTOCOL_VERSION: 519,
  REQUEST_AUTOPILOT_CAPABILITIES: 520,
  REQUEST_CAMERA_INFORMATION: 521,
  REQUEST_CAMERA_SETTINGS: 522,
  REQUEST_STORAGE_INFORMATION: 525,
  STORAGE_FORMAT: 526,
  REQUEST_CAMERA_CAPTURE_STATUS: 527,
  REQUEST_FLIGHT_INFORMATION: 528,
  RESET_CAMERA_SETTINGS: 529,
  SET_CAMERA_MODE: 530,
  SET_CAMERA_ZOOM: 531,
  SET_CAMERA_FOCUS: 532,
  SET_STORAGE_USAGE: 533,
  SET_CAMERA_SOURCE: 534,
  JUMP_TAG: 600,
  DO_JUMP_TAG: 601,
  DO_GIMBAL_MANAGER_PITCHYAW: 1000,
  DO_GIMBAL_MANAGER_CONFIGURE: 1001,
  IMAGE_START_CAPTURE: 2000,
  IMAGE_STOP_CAPTURE: 2001,
  DO_TRIGGER_CONTROL: 2003,
  CAMERA_TRACK_POINT: 2004,
  CAMERA_TRACK_RECTANGLE: 2005,
  CAMERA_STOP_TRACKING: 2010,
  VIDEO_START_CAPTURE: 2500,
  VIDEO_STOP_CAPTURE: 2501,
  VIDEO_START_STREAMING: 2502,
  VIDEO_STOP_STREAMING: 2503,
  REQUEST_VIDEO_STREAM_INFORMATION: 2504,
  REQUEST_VIDEO_STREAM_STATUS: 2505,
  LOGGING_START: 2510,
  LOGGING_STOP: 2511,
  AIRFRAME_CONFIGURATION: 2520,
  CONTROL_HIGH_LATENCY: 2600,
  PANORAMA_CREATE: 2800,
  DO_VTOL_TRANSITION: 3000,
  ARM_AUTHORIZATION_REQUEST: 3001,
  SET_GUIDED_SUBMODE_STANDARD: 4000,
  SET_GUIDED_SUBMODE_CIRCLE: 4001,
  NAV_FENCE_RETURN_POINT: 5000,
  NAV_FENCE_POLYGON_VERTEX_INCLUSION: 5001,
  NAV_FENCE_POLYGON_VERTEX_EXCLUSION: 5002,
  NAV_FENCE_CIRCLE_INCLUSION: 5003,
  NAV_FENCE_CIRCLE_EXCLUSION: 5004,
  NAV_RALLY_POINT: 5100,
  UAVCAN_GET_NODE_INFO: 5200,
  DO_SET_SAFETY_SWITCH_STATE: 5300,
  DO_ADSB_OUT_IDENT: 10001,
  PAYLOAD_PREPARE_DEPLOY: 30001,
  PAYLOAD_CONTROL_DEPLOY: 30002,
  FIXED_MAG_CAL_YAW: 42006,
  DO_WINCH: 42600,
  WAYPOINT_USER_1: 31000,
  WAYPOINT_USER_2: 31001,
  WAYPOINT_USER_3: 31002,
  WAYPOINT_USER_4: 31003,
  WAYPOINT_USER_5: 31004,
  SPATIAL_USER_1: 31005,
  SPATIAL_USER_2: 31006,
  SPATIAL_USER_3: 31007,
  SPATIAL_USER_4: 31008,
  SPATIAL_USER_5: 31009,
  USER_1: 31010,
  USER_2: 31011,
  USER_3: 31012,
  USER_4: 31013,
  USER_5: 31014,
  CAN_FORWARD: 32000,
  DO_SET_RESUME_REPEAT_DIST: 215,
  DO_SPRAYER: 216,
  DO_SEND_SCRIPT_MESSAGE: 217,
  DO_AUX_FUNCTION: 218,
  NAV_ALTITUDE_WAIT: 83,
  POWER_OFF_INITIATED: 42000,
  SOLO_BTN_FLY_CLICK: 42001,
  SOLO_BTN_FLY_HOLD: 42002,
  SOLO_BTN_PAUSE_CLICK: 42003,
  FIXED_MAG_CAL: 42004,
  FIXED_MAG_CAL_FIELD: 42005,
  SET_EKF_SOURCE_SET: 42007,
  DO_START_MAG_CAL: 42424,
  DO_ACCEPT_MAG_CAL: 42425,
  DO_CANCEL_MAG_CAL: 42426,
  ACCELCAL_VEHICLE_POS: 42429,
  DO_SEND_BANNER: 42428,
  SET_FACTORY_TEST_MODE: 42427,
  GIMBAL_RESET: 42501,
  GIMBAL_AXIS_CALIBRATION_STATUS: 42502,
  GIMBAL_REQUEST_AXIS_CALIBRATION: 42503,
  GIMBAL_FULL_RESET: 42505,
  FLASH_BOOTLOADER: 42650,
  BATTERY_RESET: 42651,
  DEBUG_TRAP: 42700,
  SCRIPTING: 42701,
  NAV_SCRIPT_TIME: 42702,
  NAV_ATTITUDE_TIME: 42703,
  GUIDED_CHANGE_SPEED: 43000,
  GUIDED_CHANGE_ALTITUDE: 43001,
  GUIDED_CHANGE_HEADING: 43002,
  EXTERNAL_POSITION_ESTIMATE: 43003,
} as const;

export type MavCmd = keyof typeof mavCmdEnumMap;
export const mavCmdMap = toMap(mavCmdEnumMap);
export const mavCmdInverseMap = invertMap(mavCmdMap);

export const mavDataStreamEnumMap = {
  ALL: 0,
  RAW_SENSORS: 1,
  EXTENDED_STATUS: 2,
  RC_CHANNELS: 3,
  RAW_CONTROLLER: 4,
  POSITION: 6,
  EXTRA1: 10,
  EXTRA2: 11,
  EXTRA3: 12,
} as const;

export type MavDataStream = keyof typeof mavDataStreamEnumMap;
export const mavDataStreamMap = toMap(mavDataStreamEnumMap);
export const mavDataStreamInverseMap = invertMap(mavDataStreamMap);

export const mavRoiEnumMap = {
  NONE: 0,
  WPNEXT: 1,
  WPINDEX: 2,
  LOCATION: 3,
  TARGET: 4,
} as const;

export type MavRoi = keyof typeof mavRoiEnumMap;
export const mavRoiMap = toMap(mavRoiEnumMap);
export const mavRoiInverseMap = invertMap(mavRoiMap);

export const mavParamTypeEnumMap = {
  UINT8: 1,
  INT8: 2,
  UINT16: 3,
  INT16: 4,
  UINT32: 5,
  INT32: 6,
  UINT64: 7,
  INT64: 8,
  REAL32: 9,
  REAL64: 10,
} as const;

export type MavParamType = keyof typeof mavParamTypeEnumMap;
export const mavParamTypeMap = toMap(mavParamTypeEnumMap);
export const mavParamTypeInverseMap = invertMap(mavParamTypeMap);

export const mavParamExtTypeEnumMap = {
  UINT8: 1,
  INT8: 2,
  UINT16: 3,
  INT16: 4,
  UINT32: 5,
  INT32: 6,
  UINT64: 7,
  INT64: 8,
  REAL32: 9,
  REAL64: 10,
  CUSTOM: 11,
} as const;

export type MavParamExtType = keyof typeof mavParamExtTypeEnumMap;
export const mavParamExtTypeMap = toMap(mavParamExtTypeEnumMap);
export const mavParamExtTypeInverseMap = invertMap(mavParamExtTypeMap);

export const mavResultEnumMap = {
  ACCEPTED: 0,
  TEMPORARILY_REJECTED: 1,
  DENIED: 2,
  UNSUPPORTED: 3,
  FAILED: 4,
  IN_PROGRESS: 5,
  COMMAND_LONG_ONLY: 7,
  COMMAND_INT_ONLY: 8,
} as const;

export type MavResult = keyof typeof mavResultEnumMap;
export const mavResultMap = toMap(mavResultEnumMap);
export const mavResultInverseMap = invertMap(mavResultMap);

export const mavMissionResultEnumMap = {
  MAV_MISSION_ACCEPTED: 0,
  MAV_MISSION_ERROR: 1,
  MAV_MISSION_UNSUPPORTED_FRAME: 2,
  MAV_MISSION_UNSUPPORTED: 3,
  MAV_MISSION_NO_SPACE: 4,
  MAV_MISSION_INVALID: 5,
  MAV_MISSION_INVALID_PARAM1: 6,
  MAV_MISSION_INVALID_PARAM2: 7,
  MAV_MISSION_INVALID_PARAM3: 8,
  MAV_MISSION_INVALID_PARAM4: 9,
  MAV_MISSION_INVALID_PARAM5_X: 10,
  MAV_MISSION_INVALID_PARAM6_Y: 11,
  MAV_MISSION_INVALID_PARAM7: 12,
  MAV_MISSION_INVALID_SEQUENCE: 13,
  MAV_MISSION_DENIED: 14,
  MAV_MISSION_OPERATION_CANCELLED: 15,
} as const;

export type MavMissionResult = keyof typeof mavMissionResultEnumMap;
export const mavMissionResultMap = toMap(mavMissionResultEnumMap);
export const mavMissionResultInverseMap = invertMap(mavMissionResultMap);

export const mavSeverityEnumMap = {
  EMERGENCY: 0,
  ALERT: 1,
  CRITICAL: 2,
  ERROR: 3,
  WARNING: 4,
  NOTICE: 5,
  INFO: 6,
  DEBUG: 7,
} as const;

export type MavSeverity = keyof typeof mavSeverityEnumMap;
export const mavSeverityMap = toMap(mavSeverityEnumMap);
export const mavSeverityInverseMap = invertMap(mavSeverityMap);

export const mavPowerStatusEnumMap = {
  BRICK_VALID: 1,
  SERVO_VALID: 2,
  USB_CONNECTED: 4,
  PERIPH_OVERCURRENT: 8,
  PERIPH_HIPOWER_OVERCURRENT: 16,
  CHANGED: 32,
} as const;

export type MavPowerStatus = keyof typeof mavPowerStatusEnumMap;
export const mavPowerStatusMap = toMap(mavPowerStatusEnumMap);
export const mavPowerStatusInverseMap = invertMap(mavPowerStatusMap);

export const serialControlDevEnumMap = {
  TELEM1: 0,
  TELEM2: 1,
  GPS1: 2,
  GPS2: 3,
  SHELL: 10,
  SERIAL_CONTROL_SERIAL0: 100,
  SERIAL_CONTROL_SERIAL1: 101,
  SERIAL_CONTROL_SERIAL2: 102,
  SERIAL_CONTROL_SERIAL3: 103,
  SERIAL_CONTROL_SERIAL4: 104,
  SERIAL_CONTROL_SERIAL5: 105,
  SERIAL_CONTROL_SERIAL6: 106,
  SERIAL_CONTROL_SERIAL7: 107,
  SERIAL_CONTROL_SERIAL8: 108,
  SERIAL_CONTROL_SERIAL9: 109,
} as const;

export type SerialControlDev = keyof typeof serialControlDevEnumMap;
export const serialControlDevMap = toMap(serialControlDevEnumMap);
export const serialControlDevInverseMap = invertMap(serialControlDevMap);

export const serialControlFlagEnumMap = {
  REPLY: 1,
  RESPOND: 2,
  EXCLUSIVE: 4,
  BLOCKING: 8,
  MULTI: 16,
} as const;

export type SerialControlFlag = keyof typeof serialControlFlagEnumMap;
export const serialControlFlagMap = toMap(serialControlFlagEnumMap);
export const serialControlFlagInverseMap = invertMap(serialControlFlagMap);

export const mavDistanceSensorEnumMap = {
  LASER: 0,
  ULTRASOUND: 1,
  INFRARED: 2,
  RADAR: 3,
  UNKNOWN: 4,
} as const;

export type MavDistanceSensor = keyof typeof mavDistanceSensorEnumMap;
export const mavDistanceSensorMap = toMap(mavDistanceSensorEnumMap);
export const mavDistanceSensorInverseMap = invertMap(mavDistanceSensorMap);

export const mavSensorOrientationEnumMap = {
  MAV_SENSOR_ROTATION_NONE: 0,
  MAV_SENSOR_ROTATION_YAW_45: 1,
  MAV_SENSOR_ROTATION_YAW_90: 2,
  MAV_SENSOR_ROTATION_YAW_135: 3,
  MAV_SENSOR_ROTATION_YAW_180: 4,
  MAV_SENSOR_ROTATION_YAW_225: 5,
  MAV_SENSOR_ROTATION_YAW_270: 6,
  MAV_SENSOR_ROTATION_YAW_315: 7,
  MAV_SENSOR_ROTATION_ROLL_180: 8,
  MAV_SENSOR_ROTATION_ROLL_180_YAW_45: 9,
  MAV_SENSOR_ROTATION_ROLL_180_YAW_90: 10,
  MAV_SENSOR_ROTATION_ROLL_180_YAW_135: 11,
  MAV_SENSOR_ROTATION_PITCH_180: 12,
  MAV_SENSOR_ROTATION_ROLL_180_YAW_225: 13,
  MAV_SENSOR_ROTATION_ROLL_180_YAW_270: 14,
  MAV_SENSOR_ROTATION_ROLL_180_YAW_315: 15,
  MAV_SENSOR_ROTATION_ROLL_90: 16,
  MAV_SENSOR_ROTATION_ROLL_90_YAW_45: 17,
  MAV_SENSOR_ROTATION_ROLL_90_YAW_90: 18,
  MAV_SENSOR_ROTATION_ROLL_90_YAW_135: 19,
  MAV_SENSOR_ROTATION_ROLL_270: 20,
  MAV_SENSOR_ROTATION_ROLL_270_YAW_45: 21,
  MAV_SENSOR_ROTATION_ROLL_270_YAW_90: 22,
  MAV_SENSOR_ROTATION_ROLL_270_YAW_135: 23,
  MAV_SENSOR_ROTATION_PITCH_90: 24,
  MAV_SENSOR_ROTATION_PITCH_270: 25,
  MAV_SENSOR_ROTATION_PITCH_180_YAW_90: 26,
  MAV_SENSOR_ROTATION_PITCH_180_YAW_270: 27,
  MAV_SENSOR_ROTATION_ROLL_90_PITCH_90: 28,
  MAV_SENSOR_ROTATION_ROLL_180_PITCH_90: 29,
  MAV_SENSOR_ROTATION_ROLL_270_PITCH_90: 30,
  MAV_SENSOR_ROTATION_ROLL_90_PITCH_180: 31,
  MAV_SENSOR_ROTATION_ROLL_270_PITCH_180: 32,
  MAV_SENSOR_ROTATION_ROLL_90_PITCH_270: 33,
  MAV_SENSOR_ROTATION_ROLL_180_PITCH_270: 34,
  MAV_SENSOR_ROTATION_ROLL_270_PITCH_270: 35,
  MAV_SENSOR_ROTATION_ROLL_90_PITCH_180_YAW_90: 36,
  MAV_SENSOR_ROTATION_ROLL_90_YAW_270: 37,
  MAV_SENSOR_ROTATION_ROLL_90_PITCH_68_YAW_293: 38,
  MAV_SENSOR_ROTATION_PITCH_315: 39,
  MAV_SENSOR_ROTATION_ROLL_90_PITCH_315: 40,
  MAV_SENSOR_ROTATION_CUSTOM: 100,
} as const;

export type MavSensorOrientation = keyof typeof mavSensorOrientationEnumMap;
export const mavSensorOrientationMap = toMap(mavSensorOrientationEnumMap);
export const mavSensorOrientationInverseMap = invertMap(mavSensorOrientationMap);

export const mavProtocolCapabilityEnumMap = {
  MISSION_FLOAT: 1n,
  PARAM_FLOAT: 2n,
  MISSION_INT: 4n,
  COMMAND_INT: 8n,
  PARAM_UNION: 16n,
  FTP: 32n,
  SET_ATTITUDE_TARGET: 64n,
  SET_POSITION_TARGET_LOCAL_NED: 128n,
  SET_POSITION_TARGET_GLOBAL_INT: 256n,
  TERRAIN: 512n,
  SET_ACTUATOR_TARGET: 1024n,
  FLIGHT_TERMINATION: 2048n,
  COMPASS_CALIBRATION: 4096n,
  MAVLINK2: 8192n,
  MISSION_FENCE: 16384n,
  MISSION_RALLY: 32768n,
  FLIGHT_INFORMATION: 65536n,
  SWARMING: 131072n,
} as const;

export type MavProtocolCapability = keyof typeof mavProtocolCapabilityEnumMap;
export const mavProtocolCapabilityMap = toMap(mavProtocolCapabilityEnumMap);
export const mavProtocolCapabilityInverseMap = invertMap(mavProtocolCapabilityMap);

export const mavMissionTypeEnumMap = {
  MISSION: 0,
  FENCE: 1,
  RALLY: 2,
  ALL: 255,
} as const;

export type MavMissionType = keyof typeof mavMissionTypeEnumMap;
export const mavMissionTypeMap = toMap(mavMissionTypeEnumMap);
export const mavMissionTypeInverseMap = invertMap(mavMissionTypeMap);

export const mavEstimatorTypeEnumMap = {
  UNKNOWN: 0,
  NAIVE: 1,
  VISION: 2,
  VIO: 3,
  GPS: 4,
  GPS_INS: 5,
  MOCAP: 6,
  LIDAR: 7,
  AUTOPILOT: 8,
} as const;

export type MavEstimatorType = keyof typeof mavEstimatorTypeEnumMap;
export const mavEstimatorTypeMap = toMap(mavEstimatorTypeEnumMap);
export const mavEstimatorTypeInverseMap = invertMap(mavEstimatorTypeMap);

export const mavBatteryTypeEnumMap = {
  UNKNOWN: 0,
  LIPO: 1,
  LIFE: 2,
  LION: 3,
  NIMH: 4,
} as const;

export type MavBatteryType = keyof typeof mavBatteryTypeEnumMap;
export const mavBatteryTypeMap = toMap(mavBatteryTypeEnumMap);
export const mavBatteryTypeInverseMap = invertMap(mavBatteryTypeMap);

export const mavBatteryFunctionEnumMap = {
  UNKNOWN: 0,
  ALL: 1,
  PROPULSION: 2,
  AVIONICS: 3,
  MAV_BATTERY_TYPE_PAYLOAD: 4,
} as const;

export type MavBatteryFunction = keyof typeof mavBatteryFunctionEnumMap;
export const mavBatteryFunctionMap = toMap(mavBatteryFunctionEnumMap);
export const mavBatteryFunctionInverseMap = invertMap(mavBatteryFunctionMap);

export const mavBatteryChargeStateEnumMap = {
  UNDEFINED: 0,
  OK: 1,
  LOW: 2,
  CRITICAL: 3,
  EMERGENCY: 4,
  FAILED: 5,
  UNHEALTHY: 6,
  CHARGING: 7,
} as const;

export type MavBatteryChargeState = keyof typeof mavBatteryChargeStateEnumMap;
export const mavBatteryChargeStateMap = toMap(mavBatteryChargeStateEnumMap);
export const mavBatteryChargeStateInverseMap = invertMap(mavBatteryChargeStateMap);

export const mavBatteryModeEnumMap = {
  UNKNOWN: 0,
  AUTO_DISCHARGING: 1,
  HOT_SWAP: 2,
} as const;

export type MavBatteryMode = keyof typeof mavBatteryModeEnumMap;
export const mavBatteryModeMap = toMap(mavBatteryModeEnumMap);
export const mavBatteryModeInverseMap = invertMap(mavBatteryModeMap);

export const mavBatteryFaultEnumMap = {
  DEEP_DISCHARGE: 1,
  SPIKES: 2,
  CELL_FAIL: 4,
  OVER_CURRENT: 8,
  OVER_TEMPERATURE: 16,
  UNDER_TEMPERATURE: 32,
  INCOMPATIBLE_VOLTAGE: 64,
  INCOMPATIBLE_FIRMWARE: 128,
  BATTERY_FAULT_INCOMPATIBLE_CELLS_CONFIGURATION: 256,
} as const;

export type MavBatteryFault = keyof typeof mavBatteryFaultEnumMap;
export const mavBatteryFaultMap = toMap(mavBatteryFaultEnumMap);
export const mavBatteryFaultInverseMap = invertMap(mavBatteryFaultMap);

export const mavGeneratorStatusFlagEnumMap = {
  OFF: 1n,
  READY: 2n,
  GENERATING: 4n,
  CHARGING: 8n,
  REDUCED_POWER: 16n,
  MAXPOWER: 32n,
  OVERTEMP_WARNING: 64n,
  OVERTEMP_FAULT: 128n,
  ELECTRONICS_OVERTEMP_WARNING: 256n,
  ELECTRONICS_OVERTEMP_FAULT: 512n,
  ELECTRONICS_FAULT: 1024n,
  POWERSOURCE_FAULT: 2048n,
  COMMUNICATION_WARNING: 4096n,
  COOLING_WARNING: 8192n,
  POWER_RAIL_FAULT: 16384n,
  OVERCURRENT_FAULT: 32768n,
  BATTERY_OVERCHARGE_CURRENT_FAULT: 65536n,
  OVERVOLTAGE_FAULT: 131072n,
  BATTERY_UNDERVOLT_FAULT: 262144n,
  START_INHIBITED: 524288n,
  MAINTENANCE_REQUIRED: 1048576n,
  WARMING_UP: 2097152n,
  IDLE: 4194304n,
} as const;

export type MavGeneratorStatusFlag = keyof typeof mavGeneratorStatusFlagEnumMap;
export const mavGeneratorStatusFlagMap = toMap(mavGeneratorStatusFlagEnumMap);
export const mavGeneratorStatusFlagInverseMap = invertMap(mavGeneratorStatusFlagMap);

export const mavVtolStateEnumMap = {
  UNDEFINED: 0,
  TRANSITION_TO_FW: 1,
  TRANSITION_TO_MC: 2,
  MC: 3,
  FW: 4,
} as const;

export type MavVtolState = keyof typeof mavVtolStateEnumMap;
export const mavVtolStateMap = toMap(mavVtolStateEnumMap);
export const mavVtolStateInverseMap = invertMap(mavVtolStateMap);

export const mavLandedStateEnumMap = {
  UNDEFINED: 0,
  ON_GROUND: 1,
  IN_AIR: 2,
  TAKEOFF: 3,
  LANDING: 4,
  GO_AROUND: 5,
} as const;

export type MavLandedState = keyof typeof mavLandedStateEnumMap;
export const mavLandedStateMap = toMap(mavLandedStateEnumMap);
export const mavLandedStateInverseMap = invertMap(mavLandedStateMap);

export const adsbAltitudeTypeEnumMap = {
  PRESSURE_QNH: 0,
  GEOMETRIC: 1,
} as const;

export type AdsbAltitudeType = keyof typeof adsbAltitudeTypeEnumMap;
export const adsbAltitudeTypeMap = toMap(adsbAltitudeTypeEnumMap);
export const adsbAltitudeTypeInverseMap = invertMap(adsbAltitudeTypeMap);

export const adsbEmitterTypeEnumMap = {
  NO_INFO: 0,
  LIGHT: 1,
  SMALL: 2,
  LARGE: 3,
  HIGH_VORTEX_LARGE: 4,
  HEAVY: 5,
  HIGHLY_MANUV: 6,
  ROTOCRAFT: 7,
  UNASSIGNED: 8,
  GLIDER: 9,
  LIGHTER_AIR: 10,
  PARACHUTE: 11,
  ULTRA_LIGHT: 12,
  UNASSIGNED2: 13,
  UAV: 14,
  SPACE: 15,
  UNASSGINED3: 16,
  EMERGENCY_SURFACE: 17,
  SERVICE_SURFACE: 18,
  POINT_OBSTACLE: 19,
} as const;

export type AdsbEmitterType = keyof typeof adsbEmitterTypeEnumMap;
export const adsbEmitterTypeMap = toMap(adsbEmitterTypeEnumMap);
export const adsbEmitterTypeInverseMap = invertMap(adsbEmitterTypeMap);

export const adsbFlagsEnumMap = {
  VALID_COORDS: 1,
  VALID_ALTITUDE: 2,
  VALID_HEADING: 4,
  VALID_VELOCITY: 8,
  VALID_CALLSIGN: 16,
  VALID_SQUAWK: 32,
  SIMULATED: 64,
  VERTICAL_VELOCITY_VALID: 128,
  BARO_VALID: 256,
  SOURCE_UAT: 32768,
} as const;

export type AdsbFlags = keyof typeof adsbFlagsEnumMap;
export const adsbFlagsMap = toMap(adsbFlagsEnumMap);
export const adsbFlagsInverseMap = invertMap(adsbFlagsMap);

export const mavDoRepositionFlagsEnumMap = {
  CHANGE_MODE: 1,
} as const;

export type MavDoRepositionFlags = keyof typeof mavDoRepositionFlagsEnumMap;
export const mavDoRepositionFlagsMap = toMap(mavDoRepositionFlagsEnumMap);
export const mavDoRepositionFlagsInverseMap = invertMap(mavDoRepositionFlagsMap);

export const speedTypeEnumMap = {
  AIRSPEED: 0,
  GROUNDSPEED: 1,
  CLIMB_SPEED: 2,
  DESCENT_SPEED: 3,
} as const;

export type SpeedType = keyof typeof speedTypeEnumMap;
export const speedTypeMap = toMap(speedTypeEnumMap);
export const speedTypeInverseMap = invertMap(speedTypeMap);

export const estimatorStatusFlagsEnumMap = {
  ESTIMATOR_ATTITUDE: 1,
  ESTIMATOR_VELOCITY_HORIZ: 2,
  ESTIMATOR_VELOCITY_VERT: 4,
  ESTIMATOR_POS_HORIZ_REL: 8,
  ESTIMATOR_POS_HORIZ_ABS: 16,
  ESTIMATOR_POS_VERT_ABS: 32,
  ESTIMATOR_POS_VERT_AGL: 64,
  ESTIMATOR_CONST_POS_MODE: 128,
  ESTIMATOR_PRED_POS_HORIZ_REL: 256,
  ESTIMATOR_PRED_POS_HORIZ_ABS: 512,
  ESTIMATOR_GPS_GLITCH: 1024,
  ESTIMATOR_ACCEL_ERROR: 2048,
} as const;

export type EstimatorStatusFlags = keyof typeof estimatorStatusFlagsEnumMap;
export const estimatorStatusFlagsMap = toMap(estimatorStatusFlagsEnumMap);
export const estimatorStatusFlagsInverseMap = invertMap(estimatorStatusFlagsMap);

export const motorTestOrderEnumMap = {
  DEFAULT: 0,
  SEQUENCE: 1,
  BOARD: 2,
} as const;

export type MotorTestOrder = keyof typeof motorTestOrderEnumMap;
export const motorTestOrderMap = toMap(motorTestOrderEnumMap);
export const motorTestOrderInverseMap = invertMap(motorTestOrderMap);

export const motorTestThrottleTypeEnumMap = {
  MOTOR_TEST_THROTTLE_PERCENT: 0,
  MOTOR_TEST_THROTTLE_PWM: 1,
  MOTOR_TEST_THROTTLE_PILOT: 2,
  MOTOR_TEST_COMPASS_CAL: 3,
} as const;

export type MotorTestThrottleType = keyof typeof motorTestThrottleTypeEnumMap;
export const motorTestThrottleTypeMap = toMap(motorTestThrottleTypeEnumMap);
export const motorTestThrottleTypeInverseMap = invertMap(motorTestThrottleTypeMap);

export const gpsInputIgnoreFlagsEnumMap = {
  GPS_INPUT_IGNORE_FLAG_ALT: 1,
  GPS_INPUT_IGNORE_FLAG_HDOP: 2,
  GPS_INPUT_IGNORE_FLAG_VDOP: 4,
  GPS_INPUT_IGNORE_FLAG_VEL_HORIZ: 8,
  GPS_INPUT_IGNORE_FLAG_VEL_VERT: 16,
  GPS_INPUT_IGNORE_FLAG_SPEED_ACCURACY: 32,
  GPS_INPUT_IGNORE_FLAG_HORIZONTAL_ACCURACY: 64,
  GPS_INPUT_IGNORE_FLAG_VERTICAL_ACCURACY: 128,
} as const;

export type GpsInputIgnoreFlags = keyof typeof gpsInputIgnoreFlagsEnumMap;
export const gpsInputIgnoreFlagsMap = toMap(gpsInputIgnoreFlagsEnumMap);
export const gpsInputIgnoreFlagsInverseMap = invertMap(gpsInputIgnoreFlagsMap);

export const mavCollisionActionEnumMap = {
  NONE: 0,
  REPORT: 1,
  ASCEND_OR_DESCEND: 2,
  MOVE_HORIZONTALLY: 3,
  MOVE_PERPENDICULAR: 4,
  RTL: 5,
  HOVER: 6,
} as const;

export type MavCollisionAction = keyof typeof mavCollisionActionEnumMap;
export const mavCollisionActionMap = toMap(mavCollisionActionEnumMap);
export const mavCollisionActionInverseMap = invertMap(mavCollisionActionMap);

export const mavCollisionThreatLevelEnumMap = {
  NONE: 0,
  LOW: 1,
  HIGH: 2,
} as const;

export type MavCollisionThreatLevel = keyof typeof mavCollisionThreatLevelEnumMap;
export const mavCollisionThreatLevelMap = toMap(mavCollisionThreatLevelEnumMap);
export const mavCollisionThreatLevelInverseMap = invertMap(mavCollisionThreatLevelMap);

export const mavCollisionSrcEnumMap = {
  ADSB: 0,
  MAVLINK_GPS_GLOBAL_INT: 1,
} as const;

export type MavCollisionSrc = keyof typeof mavCollisionSrcEnumMap;
export const mavCollisionSrcMap = toMap(mavCollisionSrcEnumMap);
export const mavCollisionSrcInverseMap = invertMap(mavCollisionSrcMap);

export const gpsFixTypeEnumMap = {
  NO_GPS: 0,
  NO_FIX: 1,
  "2D_FIX": 2,
  "3D_FIX": 3,
  DGPS: 4,
  RTK_FLOAT: 5,
  RTK_FIXED: 6,
  STATIC: 7,
  PPP: 8,
} as const;

export type GpsFixType = keyof typeof gpsFixTypeEnumMap;
export const gpsFixTypeMap = toMap(gpsFixTypeEnumMap);
export const gpsFixTypeInverseMap = invertMap(gpsFixTypeMap);

export const rtkBaselineCoordinateSystemEnumMap = {
  ECEF: 0,
  NED: 1,
} as const;

export type RtkBaselineCoordinateSystem = keyof typeof rtkBaselineCoordinateSystemEnumMap;
export const rtkBaselineCoordinateSystemMap = toMap(rtkBaselineCoordinateSystemEnumMap);
export const rtkBaselineCoordinateSystemInverseMap = invertMap(rtkBaselineCoordinateSystemMap);

export const landingTargetTypeEnumMap = {
  LIGHT_BEACON: 0,
  RADIO_BEACON: 1,
  VISION_FIDUCIAL: 2,
  VISION_OTHER: 3,
} as const;

export type LandingTargetType = keyof typeof landingTargetTypeEnumMap;
export const landingTargetTypeMap = toMap(landingTargetTypeEnumMap);
export const landingTargetTypeInverseMap = invertMap(landingTargetTypeMap);

export const vtolTransitionHeadingEnumMap = {
  VEHICLE_DEFAULT: 0,
  NEXT_WAYPOINT: 1,
  TAKEOFF: 2,
  SPECIFIED: 3,
  ANY: 4,
} as const;

export type VtolTransitionHeading = keyof typeof vtolTransitionHeadingEnumMap;
export const vtolTransitionHeadingMap = toMap(vtolTransitionHeadingEnumMap);
export const vtolTransitionHeadingInverseMap = invertMap(vtolTransitionHeadingMap);

export const cameraCapFlagsEnumMap = {
  CAPTURE_VIDEO: 1,
  CAPTURE_IMAGE: 2,
  HAS_MODES: 4,
  CAN_CAPTURE_IMAGE_IN_VIDEO_MODE: 8,
  CAN_CAPTURE_VIDEO_IN_IMAGE_MODE: 16,
  HAS_IMAGE_SURVEY_MODE: 32,
  HAS_BASIC_ZOOM: 64,
  HAS_BASIC_FOCUS: 128,
  HAS_VIDEO_STREAM: 256,
  HAS_TRACKING_POINT: 512,
  HAS_TRACKING_RECTANGLE: 1024,
  HAS_TRACKING_GEO_STATUS: 2048,
} as const;

export type CameraCapFlags = keyof typeof cameraCapFlagsEnumMap;
export const cameraCapFlagsMap = toMap(cameraCapFlagsEnumMap);
export const cameraCapFlagsInverseMap = invertMap(cameraCapFlagsMap);

export const videoStreamStatusFlagsEnumMap = {
  RUNNING: 1,
  THERMAL: 2,
} as const;

export type VideoStreamStatusFlags = keyof typeof videoStreamStatusFlagsEnumMap;
export const videoStreamStatusFlagsMap = toMap(videoStreamStatusFlagsEnumMap);
export const videoStreamStatusFlagsInverseMap = invertMap(videoStreamStatusFlagsMap);

export const videoStreamTypeEnumMap = {
  RTSP: 0,
  RTPUDP: 1,
  TCP_MPEG: 2,
  MPEG_TS_H264: 3,
} as const;

export type VideoStreamType = keyof typeof videoStreamTypeEnumMap;
export const videoStreamTypeMap = toMap(videoStreamTypeEnumMap);
export const videoStreamTypeInverseMap = invertMap(videoStreamTypeMap);

export const cameraTrackingStatusFlagsEnumMap = {
  IDLE: 0,
  ACTIVE: 1,
  ERROR: 2,
} as const;

export type CameraTrackingStatusFlags = keyof typeof cameraTrackingStatusFlagsEnumMap;
export const cameraTrackingStatusFlagsMap = toMap(cameraTrackingStatusFlagsEnumMap);
export const cameraTrackingStatusFlagsInverseMap = invertMap(cameraTrackingStatusFlagsMap);

export const cameraTrackingModeEnumMap = {
  NONE: 0,
  POINT: 1,
  RECTANGLE: 2,
} as const;

export type CameraTrackingMode = keyof typeof cameraTrackingModeEnumMap;
export const cameraTrackingModeMap = toMap(cameraTrackingModeEnumMap);
export const cameraTrackingModeInverseMap = invertMap(cameraTrackingModeMap);

export const cameraTrackingTargetDataEnumMap = {
  NONE: 0,
  EMBEDDED: 1,
  RENDERED: 2,
  IN_STATUS: 4,
} as const;

export type CameraTrackingTargetData = keyof typeof cameraTrackingTargetDataEnumMap;
export const cameraTrackingTargetDataMap = toMap(cameraTrackingTargetDataEnumMap);
export const cameraTrackingTargetDataInverseMap = invertMap(cameraTrackingTargetDataMap);

export const cameraZoomTypeEnumMap = {
  ZOOM_TYPE_STEP: 0,
  ZOOM_TYPE_CONTINUOUS: 1,
  ZOOM_TYPE_RANGE: 2,
  ZOOM_TYPE_FOCAL_LENGTH: 3,
} as const;

export type CameraZoomType = keyof typeof cameraZoomTypeEnumMap;
export const cameraZoomTypeMap = toMap(cameraZoomTypeEnumMap);
export const cameraZoomTypeInverseMap = invertMap(cameraZoomTypeMap);

export const setFocusTypeEnumMap = {
  FOCUS_TYPE_STEP: 0,
  FOCUS_TYPE_CONTINUOUS: 1,
  FOCUS_TYPE_RANGE: 2,
  FOCUS_TYPE_METERS: 3,
  FOCUS_TYPE_AUTO: 4,
  FOCUS_TYPE_AUTO_SINGLE: 5,
  FOCUS_TYPE_AUTO_CONTINUOUS: 6,
} as const;

export type SetFocusType = keyof typeof setFocusTypeEnumMap;
export const setFocusTypeMap = toMap(setFocusTypeEnumMap);
export const setFocusTypeInverseMap = invertMap(setFocusTypeMap);

export const cameraSourceEnumMap = {
  DEFAULT: 0,
  RGB: 1,
  IR: 2,
  NDVI: 3,
} as const;

export type CameraSource = keyof typeof cameraSourceEnumMap;
export const cameraSourceMap = toMap(cameraSourceEnumMap);
export const cameraSourceInverseMap = invertMap(cameraSourceMap);

export const paramAckEnumMap = {
  ACCEPTED: 0,
  VALUE_UNSUPPORTED: 1,
  FAILED: 2,
  IN_PROGRESS: 3,
} as const;

export type ParamAck = keyof typeof paramAckEnumMap;
export const paramAckMap = toMap(paramAckEnumMap);
export const paramAckInverseMap = invertMap(paramAckMap);

export const cameraModeEnumMap = {
  IMAGE: 0,
  VIDEO: 1,
  IMAGE_SURVEY: 2,
} as const;

export type CameraMode = keyof typeof cameraModeEnumMap;
export const cameraModeMap = toMap(cameraModeEnumMap);
export const cameraModeInverseMap = invertMap(cameraModeMap);

export const mavArmAuthDeniedReasonEnumMap = {
  GENERIC: 0,
  NONE: 1,
  INVALID_WAYPOINT: 2,
  TIMEOUT: 3,
  AIRSPACE_IN_USE: 4,
  BAD_WEATHER: 5,
} as const;

export type MavArmAuthDeniedReason = keyof typeof mavArmAuthDeniedReasonEnumMap;
export const mavArmAuthDeniedReasonMap = toMap(mavArmAuthDeniedReasonEnumMap);
export const mavArmAuthDeniedReasonInverseMap = invertMap(mavArmAuthDeniedReasonMap);

export const rcTypeEnumMap = {
  SPEKTRUM_DSM2: 0,
  SPEKTRUM_DSMX: 1,
} as const;

export type RcType = keyof typeof rcTypeEnumMap;
export const rcTypeMap = toMap(rcTypeEnumMap);
export const rcTypeInverseMap = invertMap(rcTypeMap);

export const engineControlOptionsEnumMap = {
  ALLOW_START_WHILE_DISARMED: 1,
} as const;

export type EngineControlOptions = keyof typeof engineControlOptionsEnumMap;
export const engineControlOptionsMap = toMap(engineControlOptionsEnumMap);
export const engineControlOptionsInverseMap = invertMap(engineControlOptionsMap);

export const positionTargetTypemaskEnumMap = {
  X_IGNORE: 1,
  Y_IGNORE: 2,
  Z_IGNORE: 4,
  VX_IGNORE: 8,
  VY_IGNORE: 16,
  VZ_IGNORE: 32,
  AX_IGNORE: 64,
  AY_IGNORE: 128,
  AZ_IGNORE: 256,
  FORCE_SET: 512,
  YAW_IGNORE: 1024,
  YAW_RATE_IGNORE: 2048,
} as const;

export type PositionTargetTypemask = keyof typeof positionTargetTypemaskEnumMap;
export const positionTargetTypemaskMap = toMap(positionTargetTypemaskEnumMap);
export const positionTargetTypemaskInverseMap = invertMap(positionTargetTypemaskMap);

export const attitudeTargetTypemaskEnumMap = {
  BODY_ROLL_RATE_IGNORE: 1,
  BODY_PITCH_RATE_IGNORE: 2,
  BODY_YAW_RATE_IGNORE: 4,
  THROTTLE_IGNORE: 64,
  ATTITUDE_IGNORE: 128,
} as const;

export type AttitudeTargetTypemask = keyof typeof attitudeTargetTypemaskEnumMap;
export const attitudeTargetTypemaskMap = toMap(attitudeTargetTypemaskEnumMap);
export const attitudeTargetTypemaskInverseMap = invertMap(attitudeTargetTypemaskMap);

export const utmFlightStateEnumMap = {
  UNKNOWN: 1,
  GROUND: 2,
  AIRBORNE: 3,
  EMERGENCY: 16,
  NOCTRL: 32,
} as const;

export type UtmFlightState = keyof typeof utmFlightStateEnumMap;
export const utmFlightStateMap = toMap(utmFlightStateEnumMap);
export const utmFlightStateInverseMap = invertMap(utmFlightStateMap);

export const utmDataAvailFlagsEnumMap = {
  TIME_VALID: 1,
  UAS_ID_AVAILABLE: 2,
  POSITION_AVAILABLE: 4,
  ALTITUDE_AVAILABLE: 8,
  RELATIVE_ALTITUDE_AVAILABLE: 16,
  HORIZONTAL_VELO_AVAILABLE: 32,
  VERTICAL_VELO_AVAILABLE: 64,
  NEXT_WAYPOINT_AVAILABLE: 128,
} as const;

export type UtmDataAvailFlags = keyof typeof utmDataAvailFlagsEnumMap;
export const utmDataAvailFlagsMap = toMap(utmDataAvailFlagsEnumMap);
export const utmDataAvailFlagsInverseMap = invertMap(utmDataAvailFlagsMap);

export const precisionLandModeEnumMap = {
  DISABLED: 0,
  OPPORTUNISTIC: 1,
  REQUIRED: 2,
} as const;

export type PrecisionLandMode = keyof typeof precisionLandModeEnumMap;
export const precisionLandModeMap = toMap(precisionLandModeEnumMap);
export const precisionLandModeInverseMap = invertMap(precisionLandModeMap);

export const parachuteActionEnumMap = {
  PARACHUTE_DISABLE: 0,
  PARACHUTE_ENABLE: 1,
  PARACHUTE_RELEASE: 2,
} as const;

export type ParachuteAction = keyof typeof parachuteActionEnumMap;
export const parachuteActionMap = toMap(parachuteActionEnumMap);
export const parachuteActionInverseMap = invertMap(parachuteActionMap);

export const mavTunnelPayloadTypeEnumMap = {
  UNKNOWN: 0,
  STORM32_RESERVED0: 200,
  STORM32_RESERVED1: 201,
  STORM32_RESERVED2: 202,
  STORM32_RESERVED3: 203,
  STORM32_RESERVED4: 204,
  STORM32_RESERVED5: 205,
  STORM32_RESERVED6: 206,
  STORM32_RESERVED7: 207,
  STORM32_RESERVED8: 208,
  STORM32_RESERVED9: 209,
} as const;

export type MavTunnelPayloadType = keyof typeof mavTunnelPayloadTypeEnumMap;
export const mavTunnelPayloadTypeMap = toMap(mavTunnelPayloadTypeEnumMap);
export const mavTunnelPayloadTypeInverseMap = invertMap(mavTunnelPayloadTypeMap);

export const mavOdidIdTypeEnumMap = {
  NONE: 0,
  SERIAL_NUMBER: 1,
  CAA_REGISTRATION_ID: 2,
  UTM_ASSIGNED_UUID: 3,
  SPECIFIC_SESSION_ID: 4,
} as const;

export type MavOdidIdType = keyof typeof mavOdidIdTypeEnumMap;
export const mavOdidIdTypeMap = toMap(mavOdidIdTypeEnumMap);
export const mavOdidIdTypeInverseMap = invertMap(mavOdidIdTypeMap);

export const mavOdidUaTypeEnumMap = {
  NONE: 0,
  AEROPLANE: 1,
  HELICOPTER_OR_MULTIROTOR: 2,
  GYROPLANE: 3,
  HYBRID_LIFT: 4,
  ORNITHOPTER: 5,
  GLIDER: 6,
  KITE: 7,
  FREE_BALLOON: 8,
  CAPTIVE_BALLOON: 9,
  AIRSHIP: 10,
  FREE_FALL_PARACHUTE: 11,
  ROCKET: 12,
  TETHERED_POWERED_AIRCRAFT: 13,
  GROUND_OBSTACLE: 14,
  OTHER: 15,
} as const;

export type MavOdidUaType = keyof typeof mavOdidUaTypeEnumMap;
export const mavOdidUaTypeMap = toMap(mavOdidUaTypeEnumMap);
export const mavOdidUaTypeInverseMap = invertMap(mavOdidUaTypeMap);

export const mavOdidStatusEnumMap = {
  UNDECLARED: 0,
  GROUND: 1,
  AIRBORNE: 2,
  EMERGENCY: 3,
  REMOTE_ID_SYSTEM_FAILURE: 4,
} as const;

export type MavOdidStatus = keyof typeof mavOdidStatusEnumMap;
export const mavOdidStatusMap = toMap(mavOdidStatusEnumMap);
export const mavOdidStatusInverseMap = invertMap(mavOdidStatusMap);

export const mavOdidHeightRefEnumMap = {
  OVER_TAKEOFF: 0,
  OVER_GROUND: 1,
} as const;

export type MavOdidHeightRef = keyof typeof mavOdidHeightRefEnumMap;
export const mavOdidHeightRefMap = toMap(mavOdidHeightRefEnumMap);
export const mavOdidHeightRefInverseMap = invertMap(mavOdidHeightRefMap);

export const mavOdidHorAccEnumMap = {
  UNKNOWN: 0,
  "10NM": 1,
  "4NM": 2,
  "2NM": 3,
  "1NM": 4,
  "0_5NM": 5,
  "0_3NM": 6,
  "0_1NM": 7,
  "0_05NM": 8,
  "30_METER": 9,
  "10_METER": 10,
  "3_METER": 11,
  "1_METER": 12,
} as const;

export type MavOdidHorAcc = keyof typeof mavOdidHorAccEnumMap;
export const mavOdidHorAccMap = toMap(mavOdidHorAccEnumMap);
export const mavOdidHorAccInverseMap = invertMap(mavOdidHorAccMap);

export const mavOdidVerAccEnumMap = {
  UNKNOWN: 0,
  "150_METER": 1,
  "45_METER": 2,
  "25_METER": 3,
  "10_METER": 4,
  "3_METER": 5,
  "1_METER": 6,
} as const;

export type MavOdidVerAcc = keyof typeof mavOdidVerAccEnumMap;
export const mavOdidVerAccMap = toMap(mavOdidVerAccEnumMap);
export const mavOdidVerAccInverseMap = invertMap(mavOdidVerAccMap);

export const mavOdidSpeedAccEnumMap = {
  UNKNOWN: 0,
  "10_METERS_PER_SECOND": 1,
  "3_METERS_PER_SECOND": 2,
  "1_METERS_PER_SECOND": 3,
  "0_3_METERS_PER_SECOND": 4,
} as const;

export type MavOdidSpeedAcc = keyof typeof mavOdidSpeedAccEnumMap;
export const mavOdidSpeedAccMap = toMap(mavOdidSpeedAccEnumMap);
export const mavOdidSpeedAccInverseMap = invertMap(mavOdidSpeedAccMap);

export const mavOdidTimeAccEnumMap = {
  UNKNOWN: 0,
  "0_1_SECOND": 1,
  "0_2_SECOND": 2,
  "0_3_SECOND": 3,
  "0_4_SECOND": 4,
  "0_5_SECOND": 5,
  "0_6_SECOND": 6,
  "0_7_SECOND": 7,
  "0_8_SECOND": 8,
  "0_9_SECOND": 9,
  "1_0_SECOND": 10,
  "1_1_SECOND": 11,
  "1_2_SECOND": 12,
  "1_3_SECOND": 13,
  "1_4_SECOND": 14,
  "1_5_SECOND": 15,
} as const;

export type MavOdidTimeAcc = keyof typeof mavOdidTimeAccEnumMap;
export const mavOdidTimeAccMap = toMap(mavOdidTimeAccEnumMap);
export const mavOdidTimeAccInverseMap = invertMap(mavOdidTimeAccMap);

export const mavOdidAuthTypeEnumMap = {
  NONE: 0,
  UAS_ID_SIGNATURE: 1,
  OPERATOR_ID_SIGNATURE: 2,
  MESSAGE_SET_SIGNATURE: 3,
  NETWORK_REMOTE_ID: 4,
  SPECIFIC_AUTHENTICATION: 5,
} as const;

export type MavOdidAuthType = keyof typeof mavOdidAuthTypeEnumMap;
export const mavOdidAuthTypeMap = toMap(mavOdidAuthTypeEnumMap);
export const mavOdidAuthTypeInverseMap = invertMap(mavOdidAuthTypeMap);

export const mavOdidDescTypeEnumMap = {
  TEXT: 0,
  EMERGENCY: 1,
  EXTENDED_STATUS: 2,
} as const;

export type MavOdidDescType = keyof typeof mavOdidDescTypeEnumMap;
export const mavOdidDescTypeMap = toMap(mavOdidDescTypeEnumMap);
export const mavOdidDescTypeInverseMap = invertMap(mavOdidDescTypeMap);

export const mavOdidOperatorLocationTypeEnumMap = {
  TAKEOFF: 0,
  LIVE_GNSS: 1,
  FIXED: 2,
} as const;

export type MavOdidOperatorLocationType = keyof typeof mavOdidOperatorLocationTypeEnumMap;
export const mavOdidOperatorLocationTypeMap = toMap(mavOdidOperatorLocationTypeEnumMap);
export const mavOdidOperatorLocationTypeInverseMap = invertMap(mavOdidOperatorLocationTypeMap);

export const mavOdidClassificationTypeEnumMap = {
  UNDECLARED: 0,
  EU: 1,
} as const;

export type MavOdidClassificationType = keyof typeof mavOdidClassificationTypeEnumMap;
export const mavOdidClassificationTypeMap = toMap(mavOdidClassificationTypeEnumMap);
export const mavOdidClassificationTypeInverseMap = invertMap(mavOdidClassificationTypeMap);

export const mavOdidCategoryEuEnumMap = {
  UNDECLARED: 0,
  OPEN: 1,
  SPECIFIC: 2,
  CERTIFIED: 3,
} as const;

export type MavOdidCategoryEu = keyof typeof mavOdidCategoryEuEnumMap;
export const mavOdidCategoryEuMap = toMap(mavOdidCategoryEuEnumMap);
export const mavOdidCategoryEuInverseMap = invertMap(mavOdidCategoryEuMap);

export const mavOdidClassEuEnumMap = {
  UNDECLARED: 0,
  CLASS_0: 1,
  CLASS_1: 2,
  CLASS_2: 3,
  CLASS_3: 4,
  CLASS_4: 5,
  CLASS_5: 6,
  CLASS_6: 7,
} as const;

export type MavOdidClassEu = keyof typeof mavOdidClassEuEnumMap;
export const mavOdidClassEuMap = toMap(mavOdidClassEuEnumMap);
export const mavOdidClassEuInverseMap = invertMap(mavOdidClassEuMap);

export const mavOdidOperatorIdTypeEnumMap = {
  CAA: 0,
} as const;

export type MavOdidOperatorIdType = keyof typeof mavOdidOperatorIdTypeEnumMap;
export const mavOdidOperatorIdTypeMap = toMap(mavOdidOperatorIdTypeEnumMap);
export const mavOdidOperatorIdTypeInverseMap = invertMap(mavOdidOperatorIdTypeMap);

export const mavOdidArmStatusEnumMap = {
  GOOD_TO_ARM: 0,
  PRE_ARM_FAIL_GENERIC: 1,
} as const;

export type MavOdidArmStatus = keyof typeof mavOdidArmStatusEnumMap;
export const mavOdidArmStatusMap = toMap(mavOdidArmStatusEnumMap);
export const mavOdidArmStatusInverseMap = invertMap(mavOdidArmStatusMap);

export const aisTypeEnumMap = {
  UNKNOWN: 0,
  RESERVED_1: 1,
  RESERVED_2: 2,
  RESERVED_3: 3,
  RESERVED_4: 4,
  RESERVED_5: 5,
  RESERVED_6: 6,
  RESERVED_7: 7,
  RESERVED_8: 8,
  RESERVED_9: 9,
  RESERVED_10: 10,
  RESERVED_11: 11,
  RESERVED_12: 12,
  RESERVED_13: 13,
  RESERVED_14: 14,
  RESERVED_15: 15,
  RESERVED_16: 16,
  RESERVED_17: 17,
  RESERVED_18: 18,
  RESERVED_19: 19,
  WIG: 20,
  WIG_HAZARDOUS_A: 21,
  WIG_HAZARDOUS_B: 22,
  WIG_HAZARDOUS_C: 23,
  WIG_HAZARDOUS_D: 24,
  WIG_RESERVED_1: 25,
  WIG_RESERVED_2: 26,
  WIG_RESERVED_3: 27,
  WIG_RESERVED_4: 28,
  WIG_RESERVED_5: 29,
  FISHING: 30,
  TOWING: 31,
  TOWING_LARGE: 32,
  DREDGING: 33,
  DIVING: 34,
  MILITARY: 35,
  SAILING: 36,
  PLEASURE: 37,
  RESERVED_20: 38,
  RESERVED_21: 39,
  HSC: 40,
  HSC_HAZARDOUS_A: 41,
  HSC_HAZARDOUS_B: 42,
  HSC_HAZARDOUS_C: 43,
  HSC_HAZARDOUS_D: 44,
  HSC_RESERVED_1: 45,
  HSC_RESERVED_2: 46,
  HSC_RESERVED_3: 47,
  HSC_RESERVED_4: 48,
  HSC_UNKNOWN: 49,
  PILOT: 50,
  SAR: 51,
  TUG: 52,
  PORT_TENDER: 53,
  ANTI_POLLUTION: 54,
  LAW_ENFORCEMENT: 55,
  SPARE_LOCAL_1: 56,
  SPARE_LOCAL_2: 57,
  MEDICAL_TRANSPORT: 58,
  NONECOMBATANT: 59,
  PASSENGER: 60,
  PASSENGER_HAZARDOUS_A: 61,
  PASSENGER_HAZARDOUS_B: 62,
  AIS_TYPE_PASSENGER_HAZARDOUS_C: 63,
  PASSENGER_HAZARDOUS_D: 64,
  PASSENGER_RESERVED_1: 65,
  PASSENGER_RESERVED_2: 66,
  PASSENGER_RESERVED_3: 67,
  AIS_TYPE_PASSENGER_RESERVED_4: 68,
  PASSENGER_UNKNOWN: 69,
  CARGO: 70,
  CARGO_HAZARDOUS_A: 71,
  CARGO_HAZARDOUS_B: 72,
  CARGO_HAZARDOUS_C: 73,
  CARGO_HAZARDOUS_D: 74,
  CARGO_RESERVED_1: 75,
  CARGO_RESERVED_2: 76,
  CARGO_RESERVED_3: 77,
  CARGO_RESERVED_4: 78,
  CARGO_UNKNOWN: 79,
  TANKER: 80,
  TANKER_HAZARDOUS_A: 81,
  TANKER_HAZARDOUS_B: 82,
  TANKER_HAZARDOUS_C: 83,
  TANKER_HAZARDOUS_D: 84,
  TANKER_RESERVED_1: 85,
  TANKER_RESERVED_2: 86,
  TANKER_RESERVED_3: 87,
  TANKER_RESERVED_4: 88,
  TANKER_UNKNOWN: 89,
  OTHER: 90,
  OTHER_HAZARDOUS_A: 91,
  OTHER_HAZARDOUS_B: 92,
  OTHER_HAZARDOUS_C: 93,
  OTHER_HAZARDOUS_D: 94,
  OTHER_RESERVED_1: 95,
  OTHER_RESERVED_2: 96,
  OTHER_RESERVED_3: 97,
  OTHER_RESERVED_4: 98,
  OTHER_UNKNOWN: 99,
} as const;

export type AisType = keyof typeof aisTypeEnumMap;
export const aisTypeMap = toMap(aisTypeEnumMap);
export const aisTypeInverseMap = invertMap(aisTypeMap);

export const aisNavStatusEnumMap = {
  UNDER_WAY: 0,
  AIS_NAV_ANCHORED: 1,
  AIS_NAV_UN_COMMANDED: 2,
  AIS_NAV_RESTRICTED_MANOEUVERABILITY: 3,
  AIS_NAV_DRAUGHT_CONSTRAINED: 4,
  AIS_NAV_MOORED: 5,
  AIS_NAV_AGROUND: 6,
  AIS_NAV_FISHING: 7,
  AIS_NAV_SAILING: 8,
  AIS_NAV_RESERVED_HSC: 9,
  AIS_NAV_RESERVED_WIG: 10,
  AIS_NAV_RESERVED_1: 11,
  AIS_NAV_RESERVED_2: 12,
  AIS_NAV_RESERVED_3: 13,
  AIS_NAV_AIS_SART: 14,
  AIS_NAV_UNKNOWN: 15,
} as const;

export type AisNavStatus = keyof typeof aisNavStatusEnumMap;
export const aisNavStatusMap = toMap(aisNavStatusEnumMap);
export const aisNavStatusInverseMap = invertMap(aisNavStatusMap);

export const aisFlagsEnumMap = {
  POSITION_ACCURACY: 1,
  VALID_COG: 2,
  VALID_VELOCITY: 4,
  HIGH_VELOCITY: 8,
  VALID_TURN_RATE: 16,
  TURN_RATE_SIGN_ONLY: 32,
  VALID_DIMENSIONS: 64,
  LARGE_BOW_DIMENSION: 128,
  LARGE_STERN_DIMENSION: 256,
  LARGE_PORT_DIMENSION: 512,
  LARGE_STARBOARD_DIMENSION: 1024,
  VALID_CALLSIGN: 2048,
  VALID_NAME: 4096,
} as const;

export type AisFlags = keyof typeof aisFlagsEnumMap;
export const aisFlagsMap = toMap(aisFlagsEnumMap);
export const aisFlagsInverseMap = invertMap(aisFlagsMap);

export const mavWinchStatusFlagEnumMap = {
  MAV_WINCH_STATUS_HEALTHY: 1,
  MAV_WINCH_STATUS_FULLY_RETRACTED: 2,
  MAV_WINCH_STATUS_MOVING: 4,
  MAV_WINCH_STATUS_CLUTCH_ENGAGED: 8,
} as const;

export type MavWinchStatusFlag = keyof typeof mavWinchStatusFlagEnumMap;
export const mavWinchStatusFlagMap = toMap(mavWinchStatusFlagEnumMap);
export const mavWinchStatusFlagInverseMap = invertMap(mavWinchStatusFlagMap);

export const magCalStatusEnumMap = {
  MAG_CAL_NOT_STARTED: 0,
  MAG_CAL_WAITING_TO_START: 1,
  MAG_CAL_RUNNING_STEP_ONE: 2,
  MAG_CAL_RUNNING_STEP_TWO: 3,
  MAG_CAL_SUCCESS: 4,
  MAG_CAL_FAILED: 5,
  MAG_CAL_BAD_ORIENTATION: 6,
  MAG_CAL_BAD_RADIUS: 7,
} as const;

export type MagCalStatus = keyof typeof magCalStatusEnumMap;
export const magCalStatusMap = toMap(magCalStatusEnumMap);
export const magCalStatusInverseMap = invertMap(magCalStatusMap);

export const canFilterOpEnumMap = {
  CAN_FILTER_REPLACE: 0,
  CAN_FILTER_ADD: 1,
  CAN_FILTER_REMOVE: 2,
} as const;

export type CanFilterOp = keyof typeof canFilterOpEnumMap;
export const canFilterOpMap = toMap(canFilterOpEnumMap);
export const canFilterOpInverseMap = invertMap(canFilterOpMap);

export const navVtolLandOptionsEnumMap = {
  DEFAULT: 0,
  FW_SPIRAL_APPROACH: 1,
  FW_APPROACH: 2,
} as const;

export type NavVtolLandOptions = keyof typeof navVtolLandOptionsEnumMap;
export const navVtolLandOptionsMap = toMap(navVtolLandOptionsEnumMap);
export const navVtolLandOptionsInverseMap = invertMap(navVtolLandOptionsMap);

export const missionStateEnumMap = {
  UNKNOWN: 0,
  NO_MISSION: 1,
  NOT_STARTED: 2,
  ACTIVE: 3,
  PAUSED: 4,
  COMPLETE: 5,
} as const;

export type MissionState = keyof typeof missionStateEnumMap;
export const missionStateMap = toMap(missionStateEnumMap);
export const missionStateInverseMap = invertMap(missionStateMap);

export const safetySwitchStateEnumMap = {
  SAFE: 0,
  DANGEROUS: 1,
} as const;

export type SafetySwitchState = keyof typeof safetySwitchStateEnumMap;
export const safetySwitchStateMap = toMap(safetySwitchStateEnumMap);
export const safetySwitchStateInverseMap = invertMap(safetySwitchStateMap);

export const accelcalVehiclePosEnumMap = {
  LEVEL: 1,
  LEFT: 2,
  RIGHT: 3,
  NOSEDOWN: 4,
  NOSEUP: 5,
  BACK: 6,
  SUCCESS: 16777215,
  FAILED: 16777216,
} as const;

export type AccelcalVehiclePos = keyof typeof accelcalVehiclePosEnumMap;
export const accelcalVehiclePosMap = toMap(accelcalVehiclePosEnumMap);
export const accelcalVehiclePosInverseMap = invertMap(accelcalVehiclePosMap);

export const headingTypeEnumMap = {
  COURSE_OVER_GROUND: 0,
  HEADING: 1,
} as const;

export type HeadingType = keyof typeof headingTypeEnumMap;
export const headingTypeMap = toMap(headingTypeEnumMap);
export const headingTypeInverseMap = invertMap(headingTypeMap);

export const scriptingCmdEnumMap = {
  REPL_START: 0,
  REPL_STOP: 1,
  STOP: 2,
  STOP_AND_RESTART: 3,
} as const;

export type ScriptingCmd = keyof typeof scriptingCmdEnumMap;
export const scriptingCmdMap = toMap(scriptingCmdEnumMap);
export const scriptingCmdInverseMap = invertMap(scriptingCmdMap);

export const secureCommandOpEnumMap = {
  SECURE_COMMAND_GET_SESSION_KEY: 0,
  SECURE_COMMAND_GET_REMOTEID_SESSION_KEY: 1,
  SECURE_COMMAND_REMOVE_PUBLIC_KEYS: 2,
  SECURE_COMMAND_GET_PUBLIC_KEYS: 3,
  SECURE_COMMAND_SET_PUBLIC_KEYS: 4,
  SECURE_COMMAND_GET_REMOTEID_CONFIG: 5,
  SECURE_COMMAND_SET_REMOTEID_CONFIG: 6,
  SECURE_COMMAND_FLASH_BOOTLOADER: 7,
} as const;

export type SecureCommandOp = keyof typeof secureCommandOpEnumMap;
export const secureCommandOpMap = toMap(secureCommandOpEnumMap);
export const secureCommandOpInverseMap = invertMap(secureCommandOpMap);

export const limitsStateEnumMap = {
  LIMITS_INIT: 0,
  LIMITS_DISABLED: 1,
  LIMITS_ENABLED: 2,
  LIMITS_TRIGGERED: 3,
  LIMITS_RECOVERING: 4,
  LIMITS_RECOVERED: 5,
} as const;

export type LimitsState = keyof typeof limitsStateEnumMap;
export const limitsStateMap = toMap(limitsStateEnumMap);
export const limitsStateInverseMap = invertMap(limitsStateMap);

export const limitModuleEnumMap = {
  LIMIT_GPSLOCK: 1,
  LIMIT_GEOFENCE: 2,
  LIMIT_ALTITUDE: 4,
} as const;

export type LimitModule = keyof typeof limitModuleEnumMap;
export const limitModuleMap = toMap(limitModuleEnumMap);
export const limitModuleInverseMap = invertMap(limitModuleMap);

export const rallyFlagsEnumMap = {
  FAVORABLE_WIND: 1,
  LAND_IMMEDIATELY: 2,
  ALT_FRAME_VALID: 4,
  ALT_FRAME: 24,
} as const;

export type RallyFlags = keyof typeof rallyFlagsEnumMap;
export const rallyFlagsMap = toMap(rallyFlagsEnumMap);
export const rallyFlagsInverseMap = invertMap(rallyFlagsMap);

export const cameraStatusTypesEnumMap = {
  CAMERA_STATUS_TYPE_HEARTBEAT: 0,
  CAMERA_STATUS_TYPE_TRIGGER: 1,
  CAMERA_STATUS_TYPE_DISCONNECT: 2,
  CAMERA_STATUS_TYPE_ERROR: 3,
  CAMERA_STATUS_TYPE_LOWBATT: 4,
  CAMERA_STATUS_TYPE_LOWSTORE: 5,
  CAMERA_STATUS_TYPE_LOWSTOREV: 6,
} as const;

export type CameraStatusTypes = keyof typeof cameraStatusTypesEnumMap;
export const cameraStatusTypesMap = toMap(cameraStatusTypesEnumMap);
export const cameraStatusTypesInverseMap = invertMap(cameraStatusTypesMap);

export const cameraFeedbackFlagsEnumMap = {
  CAMERA_FEEDBACK_PHOTO: 0,
  CAMERA_FEEDBACK_VIDEO: 1,
  CAMERA_FEEDBACK_BADEXPOSURE: 2,
  CAMERA_FEEDBACK_CLOSEDLOOP: 3,
  CAMERA_FEEDBACK_OPENLOOP: 4,
} as const;

export type CameraFeedbackFlags = keyof typeof cameraFeedbackFlagsEnumMap;
export const cameraFeedbackFlagsMap = toMap(cameraFeedbackFlagsEnumMap);
export const cameraFeedbackFlagsInverseMap = invertMap(cameraFeedbackFlagsMap);

export const mavModeGimbalEnumMap = {
  UNINITIALIZED: 0,
  CALIBRATING_PITCH: 1,
  CALIBRATING_ROLL: 2,
  CALIBRATING_YAW: 3,
  INITIALIZED: 4,
  ACTIVE: 5,
  RATE_CMD_TIMEOUT: 6,
} as const;

export type MavModeGimbal = keyof typeof mavModeGimbalEnumMap;
export const mavModeGimbalMap = toMap(mavModeGimbalEnumMap);
export const mavModeGimbalInverseMap = invertMap(mavModeGimbalMap);

export const gimbalAxisEnumMap = {
  YAW: 0,
  PITCH: 1,
  ROLL: 2,
} as const;

export type GimbalAxis = keyof typeof gimbalAxisEnumMap;
export const gimbalAxisMap = toMap(gimbalAxisEnumMap);
export const gimbalAxisInverseMap = invertMap(gimbalAxisMap);

export const gimbalAxisCalibrationStatusEnumMap = {
  IN_PROGRESS: 0,
  SUCCEEDED: 1,
  FAILED: 2,
} as const;

export type GimbalAxisCalibrationStatus = keyof typeof gimbalAxisCalibrationStatusEnumMap;
export const gimbalAxisCalibrationStatusMap = toMap(gimbalAxisCalibrationStatusEnumMap);
export const gimbalAxisCalibrationStatusInverseMap = invertMap(gimbalAxisCalibrationStatusMap);

export const gimbalAxisCalibrationRequiredEnumMap = {
  UNKNOWN: 0,
  TRUE: 1,
  FALSE: 2,
} as const;

export type GimbalAxisCalibrationRequired = keyof typeof gimbalAxisCalibrationRequiredEnumMap;
export const gimbalAxisCalibrationRequiredMap = toMap(gimbalAxisCalibrationRequiredEnumMap);
export const gimbalAxisCalibrationRequiredInverseMap = invertMap(gimbalAxisCalibrationRequiredMap);

export const goproHeartbeatStatusEnumMap = {
  DISCONNECTED: 0,
  INCOMPATIBLE: 1,
  CONNECTED: 2,
  ERROR: 3,
} as const;

export type GoproHeartbeatStatus = keyof typeof goproHeartbeatStatusEnumMap;
export const goproHeartbeatStatusMap = toMap(goproHeartbeatStatusEnumMap);
export const goproHeartbeatStatusInverseMap = invertMap(goproHeartbeatStatusMap);

export const goproHeartbeatFlagsEnumMap = {
  GOPRO_FLAG_RECORDING: 1,
} as const;

export type GoproHeartbeatFlags = keyof typeof goproHeartbeatFlagsEnumMap;
export const goproHeartbeatFlagsMap = toMap(goproHeartbeatFlagsEnumMap);
export const goproHeartbeatFlagsInverseMap = invertMap(goproHeartbeatFlagsMap);

export const goproRequestStatusEnumMap = {
  GOPRO_REQUEST_SUCCESS: 0,
  GOPRO_REQUEST_FAILED: 1,
} as const;

export type GoproRequestStatus = keyof typeof goproRequestStatusEnumMap;
export const goproRequestStatusMap = toMap(goproRequestStatusEnumMap);
export const goproRequestStatusInverseMap = invertMap(goproRequestStatusMap);

export const goproCommandEnumMap = {
  POWER: 0,
  CAPTURE_MODE: 1,
  SHUTTER: 2,
  BATTERY: 3,
  MODEL: 4,
  VIDEO_SETTINGS: 5,
  LOW_LIGHT: 6,
  PHOTO_RESOLUTION: 7,
  PHOTO_BURST_RATE: 8,
  PROTUNE: 9,
  PROTUNE_WHITE_BALANCE: 10,
  PROTUNE_COLOUR: 11,
  PROTUNE_GAIN: 12,
  PROTUNE_SHARPNESS: 13,
  PROTUNE_EXPOSURE: 14,
  TIME: 15,
  CHARGING: 16,
} as const;

export type GoproCommand = keyof typeof goproCommandEnumMap;
export const goproCommandMap = toMap(goproCommandEnumMap);
export const goproCommandInverseMap = invertMap(goproCommandMap);

export const goproCaptureModeEnumMap = {
  VIDEO: 0,
  PHOTO: 1,
  BURST: 2,
  TIME_LAPSE: 3,
  MULTI_SHOT: 4,
  PLAYBACK: 5,
  SETUP: 6,
  UNKNOWN: 255,
} as const;

export type GoproCaptureMode = keyof typeof goproCaptureModeEnumMap;
export const goproCaptureModeMap = toMap(goproCaptureModeEnumMap);
export const goproCaptureModeInverseMap = invertMap(goproCaptureModeMap);

export const goproResolutionEnumMap = {
  "480p": 0,
  "720p": 1,
  "960p": 2,
  "1080p": 3,
  "1440p": 4,
  "2_7k_17_9": 5,
  "2_7k_16_9": 6,
  "2_7k_4_3": 7,
  "4k_16_9": 8,
  "4k_17_9": 9,
  "720p_SUPERVIEW": 10,
  "1080p_SUPERVIEW": 11,
  "2_7k_SUPERVIEW": 12,
  "4k_SUPERVIEW": 13,
} as const;

export type GoproResolution = keyof typeof goproResolutionEnumMap;
export const goproResolutionMap = toMap(goproResolutionEnumMap);
export const goproResolutionInverseMap = invertMap(goproResolutionMap);

export const goproFrameRateEnumMap = {
  "12": 0,
  "15": 1,
  "24": 2,
  "25": 3,
  "30": 4,
  "48": 5,
  "50": 6,
  "60": 7,
  "80": 8,
  "90": 9,
  "100": 10,
  "120": 11,
  "240": 12,
  "12_5": 13,
} as const;

export type GoproFrameRate = keyof typeof goproFrameRateEnumMap;
export const goproFrameRateMap = toMap(goproFrameRateEnumMap);
export const goproFrameRateInverseMap = invertMap(goproFrameRateMap);

export const goproFieldOfViewEnumMap = {
  WIDE: 0,
  MEDIUM: 1,
  NARROW: 2,
} as const;

export type GoproFieldOfView = keyof typeof goproFieldOfViewEnumMap;
export const goproFieldOfViewMap = toMap(goproFieldOfViewEnumMap);
export const goproFieldOfViewInverseMap = invertMap(goproFieldOfViewMap);

export const goproVideoSettingsFlagsEnumMap = {
  GOPRO_VIDEO_SETTINGS_TV_MODE: 1,
} as const;

export type GoproVideoSettingsFlags = keyof typeof goproVideoSettingsFlagsEnumMap;
export const goproVideoSettingsFlagsMap = toMap(goproVideoSettingsFlagsEnumMap);
export const goproVideoSettingsFlagsInverseMap = invertMap(goproVideoSettingsFlagsMap);

export const goproPhotoResolutionEnumMap = {
  "5MP_MEDIUM": 0,
  "7MP_MEDIUM": 1,
  "7MP_WIDE": 2,
  "10MP_WIDE": 3,
  "12MP_WIDE": 4,
} as const;

export type GoproPhotoResolution = keyof typeof goproPhotoResolutionEnumMap;
export const goproPhotoResolutionMap = toMap(goproPhotoResolutionEnumMap);
export const goproPhotoResolutionInverseMap = invertMap(goproPhotoResolutionMap);

export const goproProtuneWhiteBalanceEnumMap = {
  AUTO: 0,
  "3000K": 1,
  "5500K": 2,
  "6500K": 3,
  RAW: 4,
} as const;

export type GoproProtuneWhiteBalance = keyof typeof goproProtuneWhiteBalanceEnumMap;
export const goproProtuneWhiteBalanceMap = toMap(goproProtuneWhiteBalanceEnumMap);
export const goproProtuneWhiteBalanceInverseMap = invertMap(goproProtuneWhiteBalanceMap);

export const goproProtuneColourEnumMap = {
  STANDARD: 0,
  NEUTRAL: 1,
} as const;

export type GoproProtuneColour = keyof typeof goproProtuneColourEnumMap;
export const goproProtuneColourMap = toMap(goproProtuneColourEnumMap);
export const goproProtuneColourInverseMap = invertMap(goproProtuneColourMap);

export const goproProtuneGainEnumMap = {
  "400": 0,
  "800": 1,
  "1600": 2,
  "3200": 3,
  "6400": 4,
} as const;

export type GoproProtuneGain = keyof typeof goproProtuneGainEnumMap;
export const goproProtuneGainMap = toMap(goproProtuneGainEnumMap);
export const goproProtuneGainInverseMap = invertMap(goproProtuneGainMap);

export const goproProtuneSharpnessEnumMap = {
  LOW: 0,
  MEDIUM: 1,
  HIGH: 2,
} as const;

export type GoproProtuneSharpness = keyof typeof goproProtuneSharpnessEnumMap;
export const goproProtuneSharpnessMap = toMap(goproProtuneSharpnessEnumMap);
export const goproProtuneSharpnessInverseMap = invertMap(goproProtuneSharpnessMap);

export const goproProtuneExposureEnumMap = {
  NEG_5_0: 0,
  NEG_4_5: 1,
  NEG_4_0: 2,
  NEG_3_5: 3,
  NEG_3_0: 4,
  NEG_2_5: 5,
  NEG_2_0: 6,
  NEG_1_5: 7,
  NEG_1_0: 8,
  NEG_0_5: 9,
  ZERO: 10,
  POS_0_5: 11,
  POS_1_0: 12,
  POS_1_5: 13,
  POS_2_0: 14,
  POS_2_5: 15,
  POS_3_0: 16,
  POS_3_5: 17,
  POS_4_0: 18,
  POS_4_5: 19,
  POS_5_0: 20,
} as const;

export type GoproProtuneExposure = keyof typeof goproProtuneExposureEnumMap;
export const goproProtuneExposureMap = toMap(goproProtuneExposureEnumMap);
export const goproProtuneExposureInverseMap = invertMap(goproProtuneExposureMap);

export const goproChargingEnumMap = {
  DISABLED: 0,
  ENABLED: 1,
} as const;

export type GoproCharging = keyof typeof goproChargingEnumMap;
export const goproChargingMap = toMap(goproChargingEnumMap);
export const goproChargingInverseMap = invertMap(goproChargingMap);

export const goproModelEnumMap = {
  UNKNOWN: 0,
  HERO_3_PLUS_SILVER: 1,
  HERO_3_PLUS_BLACK: 2,
  HERO_4_SILVER: 3,
  HERO_4_BLACK: 4,
} as const;

export type GoproModel = keyof typeof goproModelEnumMap;
export const goproModelMap = toMap(goproModelEnumMap);
export const goproModelInverseMap = invertMap(goproModelMap);

export const goproBurstRateEnumMap = {
  "3_IN_1_SECOND": 0,
  "5_IN_1_SECOND": 1,
  "10_IN_1_SECOND": 2,
  "10_IN_2_SECOND": 3,
  "10_IN_3_SECOND": 4,
  "30_IN_1_SECOND": 5,
  "30_IN_2_SECOND": 6,
  "30_IN_3_SECOND": 7,
  "30_IN_6_SECOND": 8,
} as const;

export type GoproBurstRate = keyof typeof goproBurstRateEnumMap;
export const goproBurstRateMap = toMap(goproBurstRateEnumMap);
export const goproBurstRateInverseMap = invertMap(goproBurstRateMap);

export const mavCmdDoAuxFunctionSwitchLevelEnumMap = {
  LOW: 0,
  MIDDLE: 1,
  HIGH: 2,
} as const;

export type MavCmdDoAuxFunctionSwitchLevel = keyof typeof mavCmdDoAuxFunctionSwitchLevelEnumMap;
export const mavCmdDoAuxFunctionSwitchLevelMap = toMap(mavCmdDoAuxFunctionSwitchLevelEnumMap);
export const mavCmdDoAuxFunctionSwitchLevelInverseMap = invertMap(mavCmdDoAuxFunctionSwitchLevelMap);

export const ledControlPatternEnumMap = {
  OFF: 0,
  FIRMWAREUPDATE: 1,
  CUSTOM: 255,
} as const;

export type LedControlPattern = keyof typeof ledControlPatternEnumMap;
export const ledControlPatternMap = toMap(ledControlPatternEnumMap);
export const ledControlPatternInverseMap = invertMap(ledControlPatternMap);

export const ekfStatusFlagsEnumMap = {
  EKF_ATTITUDE: 1,
  EKF_VELOCITY_HORIZ: 2,
  EKF_VELOCITY_VERT: 4,
  EKF_POS_HORIZ_REL: 8,
  EKF_POS_HORIZ_ABS: 16,
  EKF_POS_VERT_ABS: 32,
  EKF_POS_VERT_AGL: 64,
  EKF_CONST_POS_MODE: 128,
  EKF_PRED_POS_HORIZ_REL: 256,
  EKF_PRED_POS_HORIZ_ABS: 512,
  EKF_UNINITIALIZED: 1024,
} as const;

export type EkfStatusFlags = keyof typeof ekfStatusFlagsEnumMap;
export const ekfStatusFlagsMap = toMap(ekfStatusFlagsEnumMap);
export const ekfStatusFlagsInverseMap = invertMap(ekfStatusFlagsMap);

export const pidTuningAxisEnumMap = {
  PID_TUNING_ROLL: 1,
  PID_TUNING_PITCH: 2,
  PID_TUNING_YAW: 3,
  PID_TUNING_ACCZ: 4,
  PID_TUNING_STEER: 5,
  PID_TUNING_LANDING: 6,
} as const;

export type PidTuningAxis = keyof typeof pidTuningAxisEnumMap;
export const pidTuningAxisMap = toMap(pidTuningAxisEnumMap);
export const pidTuningAxisInverseMap = invertMap(pidTuningAxisMap);

export const mavRemoteLogDataBlockCommandsEnumMap = {
  MAV_REMOTE_LOG_DATA_BLOCK_STOP: 2147483645,
  MAV_REMOTE_LOG_DATA_BLOCK_START: 2147483646,
} as const;

export type MavRemoteLogDataBlockCommands = keyof typeof mavRemoteLogDataBlockCommandsEnumMap;
export const mavRemoteLogDataBlockCommandsMap = toMap(mavRemoteLogDataBlockCommandsEnumMap);
export const mavRemoteLogDataBlockCommandsInverseMap = invertMap(mavRemoteLogDataBlockCommandsMap);

export const mavRemoteLogDataBlockStatusesEnumMap = {
  MAV_REMOTE_LOG_DATA_BLOCK_NACK: 0,
  MAV_REMOTE_LOG_DATA_BLOCK_ACK: 1,
} as const;

export type MavRemoteLogDataBlockStatuses = keyof typeof mavRemoteLogDataBlockStatusesEnumMap;
export const mavRemoteLogDataBlockStatusesMap = toMap(mavRemoteLogDataBlockStatusesEnumMap);
export const mavRemoteLogDataBlockStatusesInverseMap = invertMap(mavRemoteLogDataBlockStatusesMap);

export const deviceOpBustypeEnumMap = {
  I2C: 0,
  SPI: 1,
} as const;

export type DeviceOpBustype = keyof typeof deviceOpBustypeEnumMap;
export const deviceOpBustypeMap = toMap(deviceOpBustypeEnumMap);
export const deviceOpBustypeInverseMap = invertMap(deviceOpBustypeMap);

export const deepstallStageEnumMap = {
  FLY_TO_LANDING: 0,
  ESTIMATE_WIND: 1,
  WAIT_FOR_BREAKOUT: 2,
  FLY_TO_ARC: 3,
  ARC: 4,
  APPROACH: 5,
  LAND: 6,
} as const;

export type DeepstallStage = keyof typeof deepstallStageEnumMap;
export const deepstallStageMap = toMap(deepstallStageEnumMap);
export const deepstallStageInverseMap = invertMap(deepstallStageMap);

export const planeModeEnumMap = {
  MANUAL: 0,
  CIRCLE: 1,
  STABILIZE: 2,
  TRAINING: 3,
  ACRO: 4,
  FLY_BY_WIRE_A: 5,
  FLY_BY_WIRE_B: 6,
  CRUISE: 7,
  AUTOTUNE: 8,
  AUTO: 10,
  RTL: 11,
  LOITER: 12,
  TAKEOFF: 13,
  AVOID_ADSB: 14,
  GUIDED: 15,
  INITIALIZING: 16,
  QSTABILIZE: 17,
  QHOVER: 18,
  QLOITER: 19,
  QLAND: 20,
  QRTL: 21,
  QAUTOTUNE: 22,
  QACRO: 23,
  THERMAL: 24,
} as const;

export type PlaneMode = keyof typeof planeModeEnumMap;
export const planeModeMap = toMap(planeModeEnumMap);
export const planeModeInverseMap = invertMap(planeModeMap);

export const copterModeEnumMap = {
  STABILIZE: 0,
  ACRO: 1,
  ALT_HOLD: 2,
  AUTO: 3,
  GUIDED: 4,
  LOITER: 5,
  RTL: 6,
  CIRCLE: 7,
  LAND: 9,
  DRIFT: 11,
  SPORT: 13,
  FLIP: 14,
  AUTOTUNE: 15,
  POSHOLD: 16,
  BRAKE: 17,
  THROW: 18,
  AVOID_ADSB: 19,
  GUIDED_NOGPS: 20,
  SMART_RTL: 21,
  FLOWHOLD: 22,
  FOLLOW: 23,
  ZIGZAG: 24,
  SYSTEMID: 25,
  AUTOROTATE: 26,
  AUTO_RTL: 27,
} as const;

export type CopterMode = keyof typeof copterModeEnumMap;
export const copterModeMap = toMap(copterModeEnumMap);
export const copterModeInverseMap = invertMap(copterModeMap);

export const subModeEnumMap = {
  STABILIZE: 0,
  ACRO: 1,
  ALT_HOLD: 2,
  AUTO: 3,
  GUIDED: 4,
  CIRCLE: 7,
  SURFACE: 9,
  POSHOLD: 16,
  MANUAL: 19,
} as const;

export type SubMode = keyof typeof subModeEnumMap;
export const subModeMap = toMap(subModeEnumMap);
export const subModeInverseMap = invertMap(subModeMap);

export const roverModeEnumMap = {
  MANUAL: 0,
  ACRO: 1,
  STEERING: 3,
  HOLD: 4,
  LOITER: 5,
  FOLLOW: 6,
  SIMPLE: 7,
  AUTO: 10,
  RTL: 11,
  SMART_RTL: 12,
  GUIDED: 15,
  INITIALIZING: 16,
} as const;

export type RoverMode = keyof typeof roverModeEnumMap;
export const roverModeMap = toMap(roverModeEnumMap);
export const roverModeInverseMap = invertMap(roverModeMap);

export const trackerModeEnumMap = {
  MANUAL: 0,
  STOP: 1,
  SCAN: 2,
  SERVO_TEST: 3,
  AUTO: 10,
  INITIALIZING: 16,
} as const;

export type TrackerMode = keyof typeof trackerModeEnumMap;
export const trackerModeMap = toMap(trackerModeEnumMap);
export const trackerModeInverseMap = invertMap(trackerModeMap);

export const osdParamConfigTypeEnumMap = {
  OSD_PARAM_NONE: 0,
  OSD_PARAM_SERIAL_PROTOCOL: 1,
  OSD_PARAM_SERVO_FUNCTION: 2,
  OSD_PARAM_AUX_FUNCTION: 3,
  OSD_PARAM_FLIGHT_MODE: 4,
  OSD_PARAM_FAILSAFE_ACTION: 5,
  OSD_PARAM_FAILSAFE_ACTION_1: 6,
  OSD_PARAM_FAILSAFE_ACTION_2: 7,
  OSD_PARAM_NUM_TYPES: 8,
} as const;

export type OsdParamConfigType = keyof typeof osdParamConfigTypeEnumMap;
export const osdParamConfigTypeMap = toMap(osdParamConfigTypeEnumMap);
export const osdParamConfigTypeInverseMap = invertMap(osdParamConfigTypeMap);

export const osdParamConfigErrorEnumMap = {
  OSD_PARAM_SUCCESS: 0,
  OSD_PARAM_INVALID_SCREEN: 1,
  OSD_PARAM_INVALID_PARAMETER_INDEX: 2,
  OSD_PARAM_INVALID_PARAMETER: 3,
} as const;

export type OsdParamConfigError = keyof typeof osdParamConfigErrorEnumMap;
export const osdParamConfigErrorMap = toMap(osdParamConfigErrorEnumMap);
export const osdParamConfigErrorInverseMap = invertMap(osdParamConfigErrorMap);

export type Heartbeat = {
  type: MavType;
  autopilot: MavAutopilot;
  baseMode: MavModeFlag[];
  customMode: number;
  systemStatus: MavState;
  mavlinkVersion: number;
};

export type SysStatus = {
  onboardControlSensorsPresent: MavSysStatusSensor[];
  onboardControlSensorsEnabled: MavSysStatusSensor[];
  onboardControlSensorsHealth: MavSysStatusSensor[];
  load: number;
  voltageBattery: number;
  currentBattery: number;
  batteryRemaining: number;
  dropRateComm: number;
  errorsComm: number;
  errorsCount1: number;
  errorsCount2: number;
  errorsCount3: number;
  errorsCount4: number;
};

export type SystemTime = {
  timeUnixUsec: bigint;
  timeBootMs: number;
};

export type Ping = {
  timeUsec: bigint;
  seq: number;
  targetSystem: number;
  targetComponent: number;
};

export type ChangeOperatorControl = {
  targetSystem: number;
  controlRequest: number;
  version: number;
  passkey: string;
};

export type ChangeOperatorControlAck = {
  gcsSystemId: number;
  controlRequest: number;
  ack: number;
};

export type AuthKey = {
  key: string;
};

export type SetMode = {
  targetSystem: number;
  baseMode: MavMode;
  customMode: number;
};

export type ParamRequestRead = {
  targetSystem: number;
  targetComponent: number;
  paramId: string;
  paramIndex: number;
};

export type ParamRequestList = {
  targetSystem: number;
  targetComponent: number;
};

export type ParamValue = {
  paramId: string;
  paramValue: number;
  paramType: MavParamType;
  paramCount: number;
  paramIndex: number;
};

export type ParamSet = {
  targetSystem: number;
  targetComponent: number;
  paramId: string;
  paramValue: number;
  paramType: MavParamType;
};

export type GpsRawInt = {
  timeUsec: bigint;
  fixType: GpsFixType;
  lat: number;
  lon: number;
  alt: number;
  eph: number;
  epv: number;
  vel: number;
  cog: number;
  satellitesVisible: number;
  altEllipsoid: number;
  hAcc: number;
  vAcc: number;
  velAcc: number;
  hdgAcc: number;
  yaw: number;
};

export type GpsStatus = {
  satellitesVisible: number;
  satellitePrn: Uint8Array;
  satelliteUsed: Uint8Array;
  satelliteElevation: Uint8Array;
  satelliteAzimuth: Uint8Array;
  satelliteSnr: Uint8Array;
};

export type ScaledImu = {
  timeBootMs: number;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  xmag: number;
  ymag: number;
  zmag: number;
  temperature: number;
};

export type RawImu = {
  timeUsec: bigint;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  xmag: number;
  ymag: number;
  zmag: number;
  id: number;
  temperature: number;
};

export type RawPressure = {
  timeUsec: bigint;
  pressAbs: number;
  pressDiff1: number;
  pressDiff2: number;
  temperature: number;
};

export type ScaledPressure = {
  timeBootMs: number;
  pressAbs: number;
  pressDiff: number;
  temperature: number;
  temperaturePressDiff: number;
};

export type Attitude = {
  timeBootMs: number;
  roll: number;
  pitch: number;
  yaw: number;
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
};

export type AttitudeQuaternion = {
  timeBootMs: number;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
  reprOffsetQ: readonly [number, number, number, number];
};

export type LocalPositionNed = {
  timeBootMs: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
};

export type GlobalPositionInt = {
  timeBootMs: number;
  lat: number;
  lon: number;
  alt: number;
  relativeAlt: number;
  vx: number;
  vy: number;
  vz: number;
  hdg: number;
};

export type RcChannelsScaled = {
  timeBootMs: number;
  port: number;
  chan1Scaled: number;
  chan2Scaled: number;
  chan3Scaled: number;
  chan4Scaled: number;
  chan5Scaled: number;
  chan6Scaled: number;
  chan7Scaled: number;
  chan8Scaled: number;
  rssi: number;
};

export type RcChannelsRaw = {
  timeBootMs: number;
  port: number;
  chan1Raw: number;
  chan2Raw: number;
  chan3Raw: number;
  chan4Raw: number;
  chan5Raw: number;
  chan6Raw: number;
  chan7Raw: number;
  chan8Raw: number;
  rssi: number;
};

export type ServoOutputRaw = {
  timeUsec: number;
  port: number;
  servo1Raw: number;
  servo2Raw: number;
  servo3Raw: number;
  servo4Raw: number;
  servo5Raw: number;
  servo6Raw: number;
  servo7Raw: number;
  servo8Raw: number;
  servo9Raw: number;
  servo10Raw: number;
  servo11Raw: number;
  servo12Raw: number;
  servo13Raw: number;
  servo14Raw: number;
  servo15Raw: number;
  servo16Raw: number;
};

export type MissionRequestPartialList = {
  targetSystem: number;
  targetComponent: number;
  startIndex: number;
  endIndex: number;
  missionType: MavMissionType;
};

export type MissionWritePartialList = {
  targetSystem: number;
  targetComponent: number;
  startIndex: number;
  endIndex: number;
  missionType: MavMissionType;
};

export type MissionItem = {
  targetSystem: number;
  targetComponent: number;
  seq: number;
  frame: MavFrame;
  command: MavCmd;
  current: number;
  autocontinue: number;
  param1: number;
  param2: number;
  param3: number;
  param4: number;
  x: number;
  y: number;
  z: number;
  missionType: MavMissionType;
};

export type MissionRequest = {
  targetSystem: number;
  targetComponent: number;
  seq: number;
  missionType: MavMissionType;
};

export type MissionSetCurrent = {
  targetSystem: number;
  targetComponent: number;
  seq: number;
};

export type MissionCurrent = {
  seq: number;
  total: number;
  missionState: MissionState;
  missionMode: number;
};

export type MissionRequestList = {
  targetSystem: number;
  targetComponent: number;
  missionType: MavMissionType;
};

export type MissionCount = {
  targetSystem: number;
  targetComponent: number;
  count: number;
  missionType: MavMissionType;
};

export type MissionClearAll = {
  targetSystem: number;
  targetComponent: number;
  missionType: MavMissionType;
};

export type MissionItemReached = {
  seq: number;
};

export type MissionAck = {
  targetSystem: number;
  targetComponent: number;
  type: MavMissionResult;
  missionType: MavMissionType;
};

export type SetGpsGlobalOrigin = {
  targetSystem: number;
  latitude: number;
  longitude: number;
  altitude: number;
  timeUsec: bigint;
};

export type GpsGlobalOrigin = {
  latitude: number;
  longitude: number;
  altitude: number;
  timeUsec: bigint;
};

export type ParamMapRc = {
  targetSystem: number;
  targetComponent: number;
  paramId: string;
  paramIndex: number;
  parameterRcChannelIndex: number;
  paramValue0: number;
  scale: number;
  paramValueMin: number;
  paramValueMax: number;
};

export type MissionRequestInt = {
  targetSystem: number;
  targetComponent: number;
  seq: number;
  missionType: MavMissionType;
};

export type SafetySetAllowedArea = {
  targetSystem: number;
  targetComponent: number;
  frame: MavFrame;
  p1x: number;
  p1y: number;
  p1z: number;
  p2x: number;
  p2y: number;
  p2z: number;
};

export type SafetyAllowedArea = {
  frame: MavFrame;
  p1x: number;
  p1y: number;
  p1z: number;
  p2x: number;
  p2y: number;
  p2z: number;
};

export type AttitudeQuaternionCov = {
  timeUsec: bigint;
  q: readonly [number, number, number, number];
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number];
};

export type NavControllerOutput = {
  navRoll: number;
  navPitch: number;
  navBearing: number;
  targetBearing: number;
  wpDist: number;
  altError: number;
  aspdError: number;
  xtrackError: number;
};

export type GlobalPositionIntCov = {
  timeUsec: bigint;
  estimatorType: MavEstimatorType;
  lat: number;
  lon: number;
  alt: number;
  relativeAlt: number;
  vx: number;
  vy: number;
  vz: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type LocalPositionNedCov = {
  timeUsec: bigint;
  estimatorType: MavEstimatorType;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  ax: number;
  ay: number;
  az: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type RcChannels = {
  timeBootMs: number;
  chancount: number;
  chan1Raw: number;
  chan2Raw: number;
  chan3Raw: number;
  chan4Raw: number;
  chan5Raw: number;
  chan6Raw: number;
  chan7Raw: number;
  chan8Raw: number;
  chan9Raw: number;
  chan10Raw: number;
  chan11Raw: number;
  chan12Raw: number;
  chan13Raw: number;
  chan14Raw: number;
  chan15Raw: number;
  chan16Raw: number;
  chan17Raw: number;
  chan18Raw: number;
  rssi: number;
};

export type RequestDataStream = {
  targetSystem: number;
  targetComponent: number;
  reqStreamId: number;
  reqMessageRate: number;
  startStop: number;
};

export type DataStream = {
  streamId: number;
  messageRate: number;
  onOff: number;
};

export type ManualControl = {
  target: number;
  x: number;
  y: number;
  z: number;
  r: number;
  buttons: number;
  buttons2: number;
  enabledExtensions: number;
  s: number;
  t: number;
  aux1: number;
  aux2: number;
  aux3: number;
  aux4: number;
  aux5: number;
  aux6: number;
};

export type RcChannelsOverride = {
  targetSystem: number;
  targetComponent: number;
  chan1Raw: number;
  chan2Raw: number;
  chan3Raw: number;
  chan4Raw: number;
  chan5Raw: number;
  chan6Raw: number;
  chan7Raw: number;
  chan8Raw: number;
  chan9Raw: number;
  chan10Raw: number;
  chan11Raw: number;
  chan12Raw: number;
  chan13Raw: number;
  chan14Raw: number;
  chan15Raw: number;
  chan16Raw: number;
  chan17Raw: number;
  chan18Raw: number;
};

export type MissionItemInt = {
  targetSystem: number;
  targetComponent: number;
  seq: number;
  frame: MavFrame;
  command: MavCmd;
  current: number;
  autocontinue: number;
  param1: number;
  param2: number;
  param3: number;
  param4: number;
  x: number;
  y: number;
  z: number;
  missionType: MavMissionType;
};

export type VfrHud = {
  airspeed: number;
  groundspeed: number;
  heading: number;
  throttle: number;
  alt: number;
  climb: number;
};

export type CommandInt = {
  targetSystem: number;
  targetComponent: number;
  frame: MavFrame;
  command: MavCmd;
  current: number;
  autocontinue: number;
  param1: number;
  param2: number;
  param3: number;
  param4: number;
  x: number;
  y: number;
  z: number;
};

export type CommandLong = {
  targetSystem: number;
  targetComponent: number;
  command: MavCmd;
  confirmation: number;
  param1: number;
  param2: number;
  param3: number;
  param4: number;
  param5: number;
  param6: number;
  param7: number;
};

export type CommandAck = {
  command: MavCmd;
  result: MavResult;
  progress: number;
  resultParam2: number;
  targetSystem: number;
  targetComponent: number;
};

export type ManualSetpoint = {
  timeBootMs: number;
  roll: number;
  pitch: number;
  yaw: number;
  thrust: number;
  modeSwitch: number;
  manualOverrideSwitch: number;
};

export type SetAttitudeTarget = {
  timeBootMs: number;
  targetSystem: number;
  targetComponent: number;
  typeMask: AttitudeTargetTypemask[];
  q: readonly [number, number, number, number];
  bodyRollRate: number;
  bodyPitchRate: number;
  bodyYawRate: number;
  thrust: number;
};

export type AttitudeTarget = {
  timeBootMs: number;
  typeMask: AttitudeTargetTypemask[];
  q: readonly [number, number, number, number];
  bodyRollRate: number;
  bodyPitchRate: number;
  bodyYawRate: number;
  thrust: number;
};

export type SetPositionTargetLocalNed = {
  timeBootMs: number;
  targetSystem: number;
  targetComponent: number;
  coordinateFrame: MavFrame;
  typeMask: PositionTargetTypemask[];
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  afx: number;
  afy: number;
  afz: number;
  yaw: number;
  yawRate: number;
};

export type PositionTargetLocalNed = {
  timeBootMs: number;
  coordinateFrame: MavFrame;
  typeMask: PositionTargetTypemask[];
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  afx: number;
  afy: number;
  afz: number;
  yaw: number;
  yawRate: number;
};

export type SetPositionTargetGlobalInt = {
  timeBootMs: number;
  targetSystem: number;
  targetComponent: number;
  coordinateFrame: MavFrame;
  typeMask: PositionTargetTypemask[];
  latInt: number;
  lonInt: number;
  alt: number;
  vx: number;
  vy: number;
  vz: number;
  afx: number;
  afy: number;
  afz: number;
  yaw: number;
  yawRate: number;
};

export type PositionTargetGlobalInt = {
  timeBootMs: number;
  coordinateFrame: MavFrame;
  typeMask: PositionTargetTypemask[];
  latInt: number;
  lonInt: number;
  alt: number;
  vx: number;
  vy: number;
  vz: number;
  afx: number;
  afy: number;
  afz: number;
  yaw: number;
  yawRate: number;
};

export type LocalPositionNedSystemGlobalOffset = {
  timeBootMs: number;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
};

export type HilState = {
  timeUsec: bigint;
  roll: number;
  pitch: number;
  yaw: number;
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
  lat: number;
  lon: number;
  alt: number;
  vx: number;
  vy: number;
  vz: number;
  xacc: number;
  yacc: number;
  zacc: number;
};

export type HilControls = {
  timeUsec: bigint;
  rollAilerons: number;
  pitchElevator: number;
  yawRudder: number;
  throttle: number;
  aux1: number;
  aux2: number;
  aux3: number;
  aux4: number;
  mode: MavMode;
  navMode: number;
};

export type HilRcInputsRaw = {
  timeUsec: bigint;
  chan1Raw: number;
  chan2Raw: number;
  chan3Raw: number;
  chan4Raw: number;
  chan5Raw: number;
  chan6Raw: number;
  chan7Raw: number;
  chan8Raw: number;
  chan9Raw: number;
  chan10Raw: number;
  chan11Raw: number;
  chan12Raw: number;
  rssi: number;
};

export type HilActuatorControls = {
  timeUsec: bigint;
  controls: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
  mode: MavModeFlag[];
  flags: bigint;
};

export type OpticalFlow = {
  timeUsec: bigint;
  sensorId: number;
  flowX: number;
  flowY: number;
  flowCompMX: number;
  flowCompMY: number;
  quality: number;
  groundDistance: number;
  flowRateX: number;
  flowRateY: number;
};

export type GlobalVisionPositionEstimate = {
  usec: bigint;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
  resetCounter: number;
};

export type VisionPositionEstimate = {
  usec: bigint;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
  resetCounter: number;
};

export type VisionSpeedEstimate = {
  usec: bigint;
  x: number;
  y: number;
  z: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number];
  resetCounter: number;
};

export type ViconPositionEstimate = {
  usec: bigint;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type HighresImu = {
  timeUsec: bigint;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  xmag: number;
  ymag: number;
  zmag: number;
  absPressure: number;
  diffPressure: number;
  pressureAlt: number;
  temperature: number;
  fieldsUpdated: number;
  id: number;
};

export type OpticalFlowRad = {
  timeUsec: bigint;
  sensorId: number;
  integrationTimeUs: number;
  integratedX: number;
  integratedY: number;
  integratedXgyro: number;
  integratedYgyro: number;
  integratedZgyro: number;
  temperature: number;
  quality: number;
  timeDeltaDistanceUs: number;
  distance: number;
};

export type HilSensor = {
  timeUsec: bigint;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  xmag: number;
  ymag: number;
  zmag: number;
  absPressure: number;
  diffPressure: number;
  pressureAlt: number;
  temperature: number;
  fieldsUpdated: number;
  id: number;
};

export type SimState = {
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  roll: number;
  pitch: number;
  yaw: number;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  lat: number;
  lon: number;
  alt: number;
  stdDevHorz: number;
  stdDevVert: number;
  vn: number;
  ve: number;
  vd: number;
  latInt: number;
  lonInt: number;
};

export type RadioStatus = {
  rssi: number;
  remrssi: number;
  txbuf: number;
  noise: number;
  remnoise: number;
  rxerrors: number;
  fixed: number;
};

export type FileTransferProtocol = {
  targetNetwork: number;
  targetSystem: number;
  targetComponent: number;
  payload_: Uint8Array;
};

export type Timesync = {
  tc1: bigint;
  ts1: bigint;
};

export type CameraTrigger = {
  timeUsec: bigint;
  seq: number;
};

export type HilGps = {
  timeUsec: bigint;
  fixType: number;
  lat: number;
  lon: number;
  alt: number;
  eph: number;
  epv: number;
  vel: number;
  vn: number;
  ve: number;
  vd: number;
  cog: number;
  satellitesVisible: number;
  id: number;
  yaw: number;
};

export type HilOpticalFlow = {
  timeUsec: bigint;
  sensorId: number;
  integrationTimeUs: number;
  integratedX: number;
  integratedY: number;
  integratedXgyro: number;
  integratedYgyro: number;
  integratedZgyro: number;
  temperature: number;
  quality: number;
  timeDeltaDistanceUs: number;
  distance: number;
};

export type HilStateQuaternion = {
  timeUsec: bigint;
  attitudeQuaternion: readonly [number, number, number, number];
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
  lat: number;
  lon: number;
  alt: number;
  vx: number;
  vy: number;
  vz: number;
  indAirspeed: number;
  trueAirspeed: number;
  xacc: number;
  yacc: number;
  zacc: number;
};

export type ScaledImu2 = {
  timeBootMs: number;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  xmag: number;
  ymag: number;
  zmag: number;
  temperature: number;
};

export type LogRequestList = {
  targetSystem: number;
  targetComponent: number;
  start: number;
  end: number;
};

export type LogEntry = {
  id: number;
  numLogs: number;
  lastLogNum: number;
  timeUtc: number;
  size: number;
};

export type LogRequestData = {
  targetSystem: number;
  targetComponent: number;
  id: number;
  ofs: number;
  count: number;
};

export type LogData = {
  id: number;
  ofs: number;
  count: number;
  data: Uint8Array;
};

export type LogErase = {
  targetSystem: number;
  targetComponent: number;
};

export type LogRequestEnd = {
  targetSystem: number;
  targetComponent: number;
};

export type GpsInjectData = {
  targetSystem: number;
  targetComponent: number;
  len: number;
  data: Uint8Array;
};

export type Gps2Raw = {
  timeUsec: bigint;
  fixType: GpsFixType;
  lat: number;
  lon: number;
  alt: number;
  eph: number;
  epv: number;
  vel: number;
  cog: number;
  satellitesVisible: number;
  dgpsNumch: number;
  dgpsAge: number;
  yaw: number;
  altEllipsoid: number;
  hAcc: number;
  vAcc: number;
  velAcc: number;
  hdgAcc: number;
};

export type PowerStatus = {
  vcc: number;
  vservo: number;
  flags: MavPowerStatus[];
};

export type SerialControl = {
  device: SerialControlDev;
  flags: SerialControlFlag[];
  timeout: number;
  baudrate: number;
  count: number;
  data: Uint8Array;
};

export type GpsRtk = {
  timeLastBaselineMs: number;
  rtkReceiverId: number;
  wn: number;
  tow: number;
  rtkHealth: number;
  rtkRate: number;
  nsats: number;
  baselineCoordsType: RtkBaselineCoordinateSystem;
  baselineAMm: number;
  baselineBMm: number;
  baselineCMm: number;
  accuracy: number;
  iarNumHypotheses: number;
};

export type Gps2Rtk = {
  timeLastBaselineMs: number;
  rtkReceiverId: number;
  wn: number;
  tow: number;
  rtkHealth: number;
  rtkRate: number;
  nsats: number;
  baselineCoordsType: RtkBaselineCoordinateSystem;
  baselineAMm: number;
  baselineBMm: number;
  baselineCMm: number;
  accuracy: number;
  iarNumHypotheses: number;
};

export type ScaledImu3 = {
  timeBootMs: number;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  xmag: number;
  ymag: number;
  zmag: number;
  temperature: number;
};

export type DataTransmissionHandshake = {
  type: MavlinkDataStreamType;
  size: number;
  width: number;
  height: number;
  packets: number;
  payload_: number;
  jpgQuality: number;
};

export type EncapsulatedData = {
  seqnr: number;
  data: Uint8Array;
};

export type DistanceSensor = {
  timeBootMs: number;
  minDistance: number;
  maxDistance: number;
  currentDistance: number;
  type: MavDistanceSensor;
  id: number;
  orientation: MavSensorOrientation;
  covariance: number;
  horizontalFov: number;
  verticalFov: number;
  quaternion: readonly [number, number, number, number];
  signalQuality: number;
};

export type TerrainRequest = {
  lat: number;
  lon: number;
  gridSpacing: number;
  mask: bigint;
};

export type TerrainData = {
  lat: number;
  lon: number;
  gridSpacing: number;
  gridbit: number;
  data: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type TerrainCheck = {
  lat: number;
  lon: number;
};

export type TerrainReport = {
  lat: number;
  lon: number;
  spacing: number;
  terrainHeight: number;
  currentHeight: number;
  pending: number;
  loaded: number;
};

export type ScaledPressure2 = {
  timeBootMs: number;
  pressAbs: number;
  pressDiff: number;
  temperature: number;
  temperaturePressDiff: number;
};

export type AttPosMocap = {
  timeUsec: bigint;
  q: readonly [number, number, number, number];
  x: number;
  y: number;
  z: number;
  covariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type SetActuatorControlTarget = {
  timeUsec: bigint;
  groupMlx: number;
  targetSystem: number;
  targetComponent: number;
  controls: readonly [number, number, number, number, number, number, number, number];
};

export type ActuatorControlTarget = {
  timeUsec: bigint;
  groupMlx: number;
  controls: readonly [number, number, number, number, number, number, number, number];
};

export type Altitude = {
  timeUsec: bigint;
  altitudeMonotonic: number;
  altitudeAmsl: number;
  altitudeLocal: number;
  altitudeRelative: number;
  altitudeTerrain: number;
  bottomClearance: number;
};

export type ResourceRequest = {
  requestId: number;
  uriType: number;
  uri: Uint8Array;
  transferType: number;
  storage: Uint8Array;
};

export type ScaledPressure3 = {
  timeBootMs: number;
  pressAbs: number;
  pressDiff: number;
  temperature: number;
  temperaturePressDiff: number;
};

export type FollowTarget = {
  timestamp: bigint;
  estCapabilities: number;
  lat: number;
  lon: number;
  alt: number;
  vel: readonly [number, number, number];
  acc: readonly [number, number, number];
  attitudeQ: readonly [number, number, number, number];
  rates: readonly [number, number, number];
  positionCov: readonly [number, number, number];
  customState: bigint;
};

export type ControlSystemState = {
  timeUsec: bigint;
  xAcc: number;
  yAcc: number;
  zAcc: number;
  xVel: number;
  yVel: number;
  zVel: number;
  xPos: number;
  yPos: number;
  zPos: number;
  airspeed: number;
  velVariance: readonly [number, number, number];
  posVariance: readonly [number, number, number];
  q: readonly [number, number, number, number];
  rollRate: number;
  pitchRate: number;
  yawRate: number;
};

export type BatteryStatus = {
  id: number;
  batteryFunction: MavBatteryFunction;
  type: MavBatteryType;
  temperature: number;
  voltages: readonly [number, number, number, number, number, number, number, number, number, number];
  currentBattery: number;
  currentConsumed: number;
  energyConsumed: number;
  batteryRemaining: number;
  timeRemaining: number;
  chargeState: MavBatteryChargeState;
  voltagesExt: readonly [number, number, number, number];
  mode: MavBatteryMode;
  faultBitmask: MavBatteryFault[];
};

export type AutopilotVersion = {
  capabilities: MavProtocolCapability[];
  flightSwVersion: number;
  middlewareSwVersion: number;
  osSwVersion: number;
  boardVersion: number;
  flightCustomVersion: Uint8Array;
  middlewareCustomVersion: Uint8Array;
  osCustomVersion: Uint8Array;
  vendorId: number;
  productId: number;
  uid: bigint;
  uid2: Uint8Array;
};

export type LandingTarget = {
  timeUsec: bigint;
  targetNum: number;
  frame: MavFrame;
  angleX: number;
  angleY: number;
  distance: number;
  sizeX: number;
  sizeY: number;
  x: number;
  y: number;
  z: number;
  q: readonly [number, number, number, number];
  type: LandingTargetType;
  positionValid: number;
};

export type FenceStatus = {
  breachStatus: number;
  breachCount: number;
  breachType: FenceBreach;
  breachTime: number;
  breachMitigation: FenceMitigate;
};

export type MagCalReport = {
  compassId: number;
  calMask: number;
  calStatus: MagCalStatus;
  autosaved: number;
  fitness: number;
  ofsX: number;
  ofsY: number;
  ofsZ: number;
  diagX: number;
  diagY: number;
  diagZ: number;
  offdiagX: number;
  offdiagY: number;
  offdiagZ: number;
  orientationConfidence: number;
  oldOrientation: MavSensorOrientation;
  newOrientation: MavSensorOrientation;
  scaleFactor: number;
};

export type EfiStatus = {
  health: number;
  ecuIndex: number;
  rpm: number;
  fuelConsumed: number;
  fuelFlow: number;
  engineLoad: number;
  throttlePosition: number;
  sparkDwellTime: number;
  barometricPressure: number;
  intakeManifoldPressure: number;
  intakeManifoldTemperature: number;
  cylinderHeadTemperature: number;
  ignitionTiming: number;
  injectionTime: number;
  exhaustGasTemperature: number;
  throttleOut: number;
  ptCompensation: number;
  ignitionVoltage: number;
  fuelPressure: number;
};

export type EstimatorStatus = {
  timeUsec: bigint;
  flags: EstimatorStatusFlags[];
  velRatio: number;
  posHorizRatio: number;
  posVertRatio: number;
  magRatio: number;
  haglRatio: number;
  tasRatio: number;
  posHorizAccuracy: number;
  posVertAccuracy: number;
};

export type WindCov = {
  timeUsec: bigint;
  windX: number;
  windY: number;
  windZ: number;
  varHoriz: number;
  varVert: number;
  windAlt: number;
  horizAccuracy: number;
  vertAccuracy: number;
};

export type GpsInput = {
  timeUsec: bigint;
  gpsId: number;
  ignoreFlags: GpsInputIgnoreFlags[];
  timeWeekMs: number;
  timeWeek: number;
  fixType: number;
  lat: number;
  lon: number;
  alt: number;
  hdop: number;
  vdop: number;
  vn: number;
  ve: number;
  vd: number;
  speedAccuracy: number;
  horizAccuracy: number;
  vertAccuracy: number;
  satellitesVisible: number;
  yaw: number;
};

export type GpsRtcmData = {
  flags: number;
  len: number;
  data: Uint8Array;
};

export type HighLatency = {
  baseMode: MavModeFlag[];
  customMode: number;
  landedState: MavLandedState;
  roll: number;
  pitch: number;
  heading: number;
  throttle: number;
  headingSp: number;
  latitude: number;
  longitude: number;
  altitudeAmsl: number;
  altitudeSp: number;
  airspeed: number;
  airspeedSp: number;
  groundspeed: number;
  climbRate: number;
  gpsNsat: number;
  gpsFixType: GpsFixType;
  batteryRemaining: number;
  temperature: number;
  temperatureAir: number;
  failsafe: number;
  wpNum: number;
  wpDistance: number;
};

export type HighLatency2 = {
  timestamp: number;
  type: MavType;
  autopilot: MavAutopilot;
  customMode: number;
  latitude: number;
  longitude: number;
  altitude: number;
  targetAltitude: number;
  heading: number;
  targetHeading: number;
  targetDistance: number;
  throttle: number;
  airspeed: number;
  airspeedSp: number;
  groundspeed: number;
  windspeed: number;
  windHeading: number;
  eph: number;
  epv: number;
  temperatureAir: number;
  climbRate: number;
  battery: number;
  wpNum: number;
  failureFlags: HlFailureFlag[];
  custom0: number;
  custom1: number;
  custom2: number;
};

export type Vibration = {
  timeUsec: bigint;
  vibrationX: number;
  vibrationY: number;
  vibrationZ: number;
  clipping0: number;
  clipping1: number;
  clipping2: number;
};

export type HomePosition = {
  latitude: number;
  longitude: number;
  altitude: number;
  x: number;
  y: number;
  z: number;
  q: readonly [number, number, number, number];
  approachX: number;
  approachY: number;
  approachZ: number;
  timeUsec: bigint;
};

export type SetHomePosition = {
  targetSystem: number;
  latitude: number;
  longitude: number;
  altitude: number;
  x: number;
  y: number;
  z: number;
  q: readonly [number, number, number, number];
  approachX: number;
  approachY: number;
  approachZ: number;
  timeUsec: bigint;
};

export type MessageInterval = {
  messageId: number;
  intervalUs: number;
};

export type ExtendedSysState = {
  vtolState: MavVtolState;
  landedState: MavLandedState;
};

export type AdsbVehicle = {
  icaoAddress: number;
  lat: number;
  lon: number;
  altitudeType: AdsbAltitudeType;
  altitude: number;
  heading: number;
  horVelocity: number;
  verVelocity: number;
  callsign: string;
  emitterType: AdsbEmitterType;
  tslc: number;
  flags: AdsbFlags[];
  squawk: number;
};

export type Collision = {
  src: MavCollisionSrc;
  id: number;
  action: MavCollisionAction;
  threatLevel: MavCollisionThreatLevel;
  timeToMinimumDelta: number;
  altitudeMinimumDelta: number;
  horizontalMinimumDelta: number;
};

export type V2Extension = {
  targetNetwork: number;
  targetSystem: number;
  targetComponent: number;
  messageType: number;
  payload_: Uint8Array;
};

export type MemoryVect = {
  address: number;
  ver: number;
  type: number;
  value: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type DebugVect = {
  name: string;
  timeUsec: bigint;
  x: number;
  y: number;
  z: number;
};

export type NamedValueFloat = {
  timeBootMs: number;
  name: string;
  value: number;
};

export type NamedValueInt = {
  timeBootMs: number;
  name: string;
  value: number;
};

export type Statustext = {
  severity: MavSeverity;
  text: string;
  id: number;
  chunkSeq: number;
};

export type Debug = {
  timeBootMs: number;
  ind: number;
  value: number;
};

export type SetupSigning = {
  targetSystem: number;
  targetComponent: number;
  secretKey: Uint8Array;
  initialTimestamp: bigint;
};

export type ButtonChange = {
  timeBootMs: number;
  lastChangeMs: number;
  state: number;
};

export type PlayTune = {
  targetSystem: number;
  targetComponent: number;
  tune: string;
  tune2: string;
};

export type CameraInformation = {
  timeBootMs: number;
  vendorName: Uint8Array;
  modelName: Uint8Array;
  firmwareVersion: number;
  focalLength: number;
  sensorSizeH: number;
  sensorSizeV: number;
  resolutionH: number;
  resolutionV: number;
  lensId: number;
  flags: CameraCapFlags[];
  camDefinitionVersion: number;
  camDefinitionUri: string;
  gimbalDeviceId: number;
};

export type CameraSettings = {
  timeBootMs: number;
  modeId: CameraMode;
  zoomLevel: number;
  focusLevel: number;
};

export type StorageInformation = {
  timeBootMs: number;
  storageId: number;
  storageCount: number;
  status: StorageStatus;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  readSpeed: number;
  writeSpeed: number;
  type: StorageType;
  name: string;
};

export type CameraCaptureStatus = {
  timeBootMs: number;
  imageStatus: number;
  videoStatus: number;
  imageInterval: number;
  recordingTimeMs: number;
  availableCapacity: number;
  imageCount: number;
};

export type CameraImageCaptured = {
  timeBootMs: number;
  timeUtc: bigint;
  cameraId: number;
  lat: number;
  lon: number;
  alt: number;
  relativeAlt: number;
  q: readonly [number, number, number, number];
  imageIndex: number;
  captureResult: number;
  fileUrl: string;
};

export type FlightInformation = {
  timeBootMs: number;
  armingTimeUtc: bigint;
  takeoffTimeUtc: bigint;
  flightUuid: bigint;
};

export type MountOrientation = {
  timeBootMs: number;
  roll: number;
  pitch: number;
  yaw: number;
  yawAbsolute: number;
};

export type LoggingData = {
  targetSystem: number;
  targetComponent: number;
  sequence: number;
  length: number;
  firstMessageOffset: number;
  data: Uint8Array;
};

export type LoggingDataAcked = {
  targetSystem: number;
  targetComponent: number;
  sequence: number;
  length: number;
  firstMessageOffset: number;
  data: Uint8Array;
};

export type LoggingAck = {
  targetSystem: number;
  targetComponent: number;
  sequence: number;
};

export type VideoStreamInformation = {
  streamId: number;
  count: number;
  type: VideoStreamType;
  flags: VideoStreamStatusFlags[];
  framerate: number;
  resolutionH: number;
  resolutionV: number;
  bitrate: number;
  rotation: number;
  hfov: number;
  name: string;
  uri: string;
};

export type VideoStreamStatus = {
  streamId: number;
  flags: VideoStreamStatusFlags[];
  framerate: number;
  resolutionH: number;
  resolutionV: number;
  bitrate: number;
  rotation: number;
  hfov: number;
};

export type CameraFovStatus = {
  timeBootMs: number;
  latCamera: number;
  lonCamera: number;
  altCamera: number;
  latImage: number;
  lonImage: number;
  altImage: number;
  q: readonly [number, number, number, number];
  hfov: number;
  vfov: number;
};

export type CameraTrackingImageStatus = {
  trackingStatus: CameraTrackingStatusFlags;
  trackingMode: CameraTrackingMode;
  targetData: CameraTrackingTargetData[];
  pointX: number;
  pointY: number;
  radius: number;
  recTopX: number;
  recTopY: number;
  recBottomX: number;
  recBottomY: number;
};

export type CameraTrackingGeoStatus = {
  trackingStatus: CameraTrackingStatusFlags;
  lat: number;
  lon: number;
  alt: number;
  hAcc: number;
  vAcc: number;
  velN: number;
  velE: number;
  velD: number;
  velAcc: number;
  dist: number;
  hdg: number;
  hdgAcc: number;
};

export type GimbalManagerInformation = {
  timeBootMs: number;
  capFlags: GimbalManagerCapFlags[];
  gimbalDeviceId: number;
  rollMin: number;
  rollMax: number;
  pitchMin: number;
  pitchMax: number;
  yawMin: number;
  yawMax: number;
};

export type GimbalManagerStatus = {
  timeBootMs: number;
  flags: GimbalManagerFlags[];
  gimbalDeviceId: number;
  primaryControlSysid: number;
  primaryControlCompid: number;
  secondaryControlSysid: number;
  secondaryControlCompid: number;
};

export type GimbalManagerSetAttitude = {
  targetSystem: number;
  targetComponent: number;
  flags: GimbalManagerFlags[];
  gimbalDeviceId: number;
  q: readonly [number, number, number, number];
  angularVelocityX: number;
  angularVelocityY: number;
  angularVelocityZ: number;
};

export type GimbalDeviceInformation = {
  timeBootMs: number;
  vendorName: string;
  modelName: string;
  customName: string;
  firmwareVersion: number;
  hardwareVersion: number;
  uid: bigint;
  capFlags: GimbalDeviceCapFlags[];
  customCapFlags: number;
  rollMin: number;
  rollMax: number;
  pitchMin: number;
  pitchMax: number;
  yawMin: number;
  yawMax: number;
  gimbalDeviceId: number;
};

export type GimbalDeviceSetAttitude = {
  targetSystem: number;
  targetComponent: number;
  flags: GimbalDeviceFlags[];
  q: readonly [number, number, number, number];
  angularVelocityX: number;
  angularVelocityY: number;
  angularVelocityZ: number;
};

export type GimbalDeviceAttitudeStatus = {
  targetSystem: number;
  targetComponent: number;
  timeBootMs: number;
  flags: GimbalDeviceFlags[];
  q: readonly [number, number, number, number];
  angularVelocityX: number;
  angularVelocityY: number;
  angularVelocityZ: number;
  failureFlags: GimbalDeviceErrorFlags[];
  deltaYaw: number;
  deltaYawVelocity: number;
  gimbalDeviceId: number;
};

export type AutopilotStateForGimbalDevice = {
  targetSystem: number;
  targetComponent: number;
  timeBootUs: bigint;
  q: readonly [number, number, number, number];
  qEstimatedDelayUs: number;
  vx: number;
  vy: number;
  vz: number;
  vEstimatedDelayUs: number;
  feedForwardAngularVelocityZ: number;
  estimatorStatus: EstimatorStatusFlags[];
  landedState: MavLandedState;
  angularVelocityZ: number;
};

export type GimbalManagerSetPitchyaw = {
  targetSystem: number;
  targetComponent: number;
  flags: GimbalManagerFlags[];
  gimbalDeviceId: number;
  pitch: number;
  yaw: number;
  pitchRate: number;
  yawRate: number;
};

export type GimbalManagerSetManualControl = {
  targetSystem: number;
  targetComponent: number;
  flags: GimbalManagerFlags[];
  gimbalDeviceId: number;
  pitch: number;
  yaw: number;
  pitchRate: number;
  yawRate: number;
};

export type WifiConfigAp = {
  ssid: string;
  password: string;
};

export type AisVessel = {
  mmsi: number;
  lat: number;
  lon: number;
  cog: number;
  heading: number;
  velocity: number;
  turnRate: number;
  navigationalStatus: AisNavStatus;
  type: AisType;
  dimensionBow: number;
  dimensionStern: number;
  dimensionPort: number;
  dimensionStarboard: number;
  callsign: string;
  name: string;
  tslc: number;
  flags: AisFlags[];
};

export type UavcanNodeStatus = {
  timeUsec: bigint;
  uptimeSec: number;
  health: UavcanNodeHealth;
  mode: UavcanNodeMode;
  subMode: number;
  vendorSpecificStatusCode: number;
};

export type UavcanNodeInfo = {
  timeUsec: bigint;
  uptimeSec: number;
  name: string;
  hwVersionMajor: number;
  hwVersionMinor: number;
  hwUniqueId: Uint8Array;
  swVersionMajor: number;
  swVersionMinor: number;
  swVcsCommit: number;
};

export type ParamExtRequestRead = {
  targetSystem: number;
  targetComponent: number;
  paramId: string;
  paramIndex: number;
};

export type ParamExtRequestList = {
  targetSystem: number;
  targetComponent: number;
};

export type ParamExtValue = {
  paramId: string;
  paramValue: string;
  paramType: MavParamExtType;
  paramCount: number;
  paramIndex: number;
};

export type ParamExtSet = {
  targetSystem: number;
  targetComponent: number;
  paramId: string;
  paramValue: string;
  paramType: MavParamExtType;
};

export type ParamExtAck = {
  paramId: string;
  paramValue: string;
  paramType: MavParamExtType;
  paramResult: ParamAck;
};

export type ObstacleDistance = {
  timeUsec: bigint;
  sensorType: MavDistanceSensor;
  distances: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
  increment: number;
  minDistance: number;
  maxDistance: number;
  incrementF: number;
  angleOffset: number;
  frame: MavFrame;
};

export type Odometry = {
  timeUsec: bigint;
  frameId: MavFrame;
  childFrameId: MavFrame;
  x: number;
  y: number;
  z: number;
  q: readonly [number, number, number, number];
  vx: number;
  vy: number;
  vz: number;
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
  poseCovariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
  velocityCovariance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
  resetCounter: number;
  estimatorType: MavEstimatorType;
  quality: number;
};

export type IsbdLinkStatus = {
  timestamp: bigint;
  lastHeartbeat: bigint;
  failedSessions: number;
  successfulSessions: number;
  signalQuality: number;
  ringPending: number;
  txSessionPending: number;
  rxSessionPending: number;
};

export type RawRpm = {
  index: number;
  frequency: number;
};

export type UtmGlobalPosition = {
  time: bigint;
  uasId: Uint8Array;
  lat: number;
  lon: number;
  alt: number;
  relativeAlt: number;
  vx: number;
  vy: number;
  vz: number;
  hAcc: number;
  vAcc: number;
  velAcc: number;
  nextLat: number;
  nextLon: number;
  nextAlt: number;
  updateRate: number;
  flightState: UtmFlightState;
  flags: UtmDataAvailFlags[];
};

export type DebugFloatArray = {
  timeUsec: bigint;
  name: string;
  arrayId: number;
  data: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type SmartBatteryInfo = {
  id: number;
  batteryFunction: MavBatteryFunction;
  type: MavBatteryType;
  capacityFullSpecification: number;
  capacityFull: number;
  cycleCount: number;
  serialNumber: string;
  deviceName: string;
  weight: number;
  dischargeMinimumVoltage: number;
  chargingMinimumVoltage: number;
  restingMinimumVoltage: number;
  chargingMaximumVoltage: number;
  cellsInSeries: number;
  dischargeMaximumCurrent: number;
  dischargeMaximumBurstCurrent: number;
  manufactureDate: string;
};

export type GeneratorStatus = {
  status: MavGeneratorStatusFlag[];
  generatorSpeed: number;
  batteryCurrent: number;
  loadCurrent: number;
  powerGenerated: number;
  busVoltage: number;
  rectifierTemperature: number;
  batCurrentSetpoint: number;
  generatorTemperature: number;
  runtime: number;
  timeUntilMaintenance: number;
};

export type ActuatorOutputStatus = {
  timeUsec: bigint;
  active: number;
  actuator: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type RelayStatus = {
  timeBootMs: number;
  on: number;
  present: number;
};

export type Tunnel = {
  targetSystem: number;
  targetComponent: number;
  payloadType: MavTunnelPayloadType;
  payloadLength: number;
  payload_: Uint8Array;
};

export type CanFrame = {
  targetSystem: number;
  targetComponent: number;
  bus: number;
  len: number;
  id: number;
  data: Uint8Array;
};

export type CanfdFrame = {
  targetSystem: number;
  targetComponent: number;
  bus: number;
  len: number;
  id: number;
  data: Uint8Array;
};

export type CanFilterModify = {
  targetSystem: number;
  targetComponent: number;
  bus: number;
  operation: CanFilterOp;
  numIds: number;
  ids: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type WheelDistance = {
  timeUsec: bigint;
  count: number;
  distance: readonly [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number];
};

export type WinchStatus = {
  timeUsec: bigint;
  lineLength: number;
  speed: number;
  tension: number;
  voltage: number;
  current: number;
  temperature: number;
  status: MavWinchStatusFlag[];
};

export type OpenDroneIdBasicId = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  idType: MavOdidIdType;
  uaType: MavOdidUaType;
  uasId: Uint8Array;
};

export type OpenDroneIdLocation = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  status: MavOdidStatus;
  direction: number;
  speedHorizontal: number;
  speedVertical: number;
  latitude: number;
  longitude: number;
  altitudeBarometric: number;
  altitudeGeodetic: number;
  heightReference: MavOdidHeightRef;
  height: number;
  horizontalAccuracy: MavOdidHorAcc;
  verticalAccuracy: MavOdidVerAcc;
  barometerAccuracy: MavOdidVerAcc;
  speedAccuracy: MavOdidSpeedAcc;
  timestamp: number;
  timestampAccuracy: MavOdidTimeAcc;
};

export type OpenDroneIdAuthentication = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  authenticationType: MavOdidAuthType;
  dataPage: number;
  lastPageIndex: number;
  length: number;
  timestamp: number;
  authenticationData: Uint8Array;
};

export type OpenDroneIdSelfId = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  descriptionType: MavOdidDescType;
  description: string;
};

export type OpenDroneIdSystem = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  operatorLocationType: MavOdidOperatorLocationType;
  classificationType: MavOdidClassificationType;
  operatorLatitude: number;
  operatorLongitude: number;
  areaCount: number;
  areaRadius: number;
  areaCeiling: number;
  areaFloor: number;
  categoryEu: MavOdidCategoryEu;
  classEu: MavOdidClassEu;
  operatorAltitudeGeo: number;
  timestamp: number;
};

export type OpenDroneIdOperatorId = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  operatorIdType: MavOdidOperatorIdType;
  operatorId: string;
};

export type OpenDroneIdArmStatus = {
  status: MavOdidArmStatus;
  error: string;
};

export type OpenDroneIdMessagePack = {
  targetSystem: number;
  targetComponent: number;
  idOrMac: Uint8Array;
  singleMessageSize: number;
  msgPackSize: number;
  messages: Uint8Array;
};

export type OpenDroneIdSystemUpdate = {
  targetSystem: number;
  targetComponent: number;
  operatorLatitude: number;
  operatorLongitude: number;
  operatorAltitudeGeo: number;
  timestamp: number;
};

export type HygrometerSensor = {
  id: number;
  temperature: number;
  humidity: number;
};

export type SensorOffsets = {
  magOfsX: number;
  magOfsY: number;
  magOfsZ: number;
  magDeclination: number;
  rawPress: number;
  rawTemp: number;
  gyroCalX: number;
  gyroCalY: number;
  gyroCalZ: number;
  accelCalX: number;
  accelCalY: number;
  accelCalZ: number;
};

export type SetMagOffsets = {
  targetSystem: number;
  targetComponent: number;
  magOfsX: number;
  magOfsY: number;
  magOfsZ: number;
};

export type Meminfo = {
  brkval: number;
  freemem: number;
  freemem32: number;
};

export type ApAdc = {
  adc1: number;
  adc2: number;
  adc3: number;
  adc4: number;
  adc5: number;
  adc6: number;
};

export type DigicamConfigure = {
  targetSystem: number;
  targetComponent: number;
  mode: number;
  shutterSpeed: number;
  aperture: number;
  iso: number;
  exposureType: number;
  commandId: number;
  engineCutOff: number;
  extraParam: number;
  extraValue: number;
};

export type DigicamControl = {
  targetSystem: number;
  targetComponent: number;
  session: number;
  zoomPos: number;
  zoomStep: number;
  focusLock: number;
  shot: number;
  commandId: number;
  extraParam: number;
  extraValue: number;
};

export type MountConfigure = {
  targetSystem: number;
  targetComponent: number;
  mountMode: MavMountMode;
  stabRoll: number;
  stabPitch: number;
  stabYaw: number;
};

export type MountControl = {
  targetSystem: number;
  targetComponent: number;
  inputA: number;
  inputB: number;
  inputC: number;
  savePosition: number;
};

export type MountStatus = {
  targetSystem: number;
  targetComponent: number;
  pointingA: number;
  pointingB: number;
  pointingC: number;
  mountMode: MavMountMode;
};

export type FencePoint = {
  targetSystem: number;
  targetComponent: number;
  idx: number;
  count: number;
  lat: number;
  lng: number;
};

export type FenceFetchPoint = {
  targetSystem: number;
  targetComponent: number;
  idx: number;
};

export type Ahrs = {
  omegaIx: number;
  omegaIy: number;
  omegaIz: number;
  accelWeight: number;
  renormVal: number;
  errorRp: number;
  errorYaw: number;
};

export type Simstate = {
  roll: number;
  pitch: number;
  yaw: number;
  xacc: number;
  yacc: number;
  zacc: number;
  xgyro: number;
  ygyro: number;
  zgyro: number;
  lat: number;
  lng: number;
};

export type Hwstatus = {
  vcc: number;
  i2Cerr: number;
};

export type Radio = {
  rssi: number;
  remrssi: number;
  txbuf: number;
  noise: number;
  remnoise: number;
  rxerrors: number;
  fixed: number;
};

export type LimitsStatus = {
  limitsState: LimitsState;
  lastTrigger: number;
  lastAction: number;
  lastRecovery: number;
  lastClear: number;
  breachCount: number;
  modsEnabled: LimitModule[];
  modsRequired: LimitModule[];
  modsTriggered: LimitModule[];
};

export type Wind = {
  direction: number;
  speed: number;
  speedZ: number;
};

export type Data16 = {
  type: number;
  len: number;
  data: Uint8Array;
};

export type Data32 = {
  type: number;
  len: number;
  data: Uint8Array;
};

export type Data64 = {
  type: number;
  len: number;
  data: Uint8Array;
};

export type Data96 = {
  type: number;
  len: number;
  data: Uint8Array;
};

export type Rangefinder = {
  distance: number;
  voltage: number;
};

export type AirspeedAutocal = {
  vx: number;
  vy: number;
  vz: number;
  diffPressure: number;
  eas2tas: number;
  ratio: number;
  stateX: number;
  stateY: number;
  stateZ: number;
  pax: number;
  pby: number;
  pcz: number;
};

export type RallyPoint = {
  targetSystem: number;
  targetComponent: number;
  idx: number;
  count: number;
  lat: number;
  lng: number;
  alt: number;
  breakAlt: number;
  landDir: number;
  flags: RallyFlags[];
};

export type RallyFetchPoint = {
  targetSystem: number;
  targetComponent: number;
  idx: number;
};

export type CompassmotStatus = {
  throttle: number;
  current: number;
  interference: number;
  compensationX: number;
  compensationY: number;
  compensationZ: number;
};

export type Ahrs2 = {
  roll: number;
  pitch: number;
  yaw: number;
  altitude: number;
  lat: number;
  lng: number;
};

export type CameraStatus = {
  timeUsec: bigint;
  targetSystem: number;
  camIdx: number;
  imgIdx: number;
  eventId: CameraStatusTypes;
  p1: number;
  p2: number;
  p3: number;
  p4: number;
};

export type CameraFeedback = {
  timeUsec: bigint;
  targetSystem: number;
  camIdx: number;
  imgIdx: number;
  lat: number;
  lng: number;
  altMsl: number;
  altRel: number;
  roll: number;
  pitch: number;
  yaw: number;
  focLen: number;
  flags: CameraFeedbackFlags;
  completedCaptures: number;
};

export type Battery2 = {
  voltage: number;
  currentBattery: number;
};

export type Ahrs3 = {
  roll: number;
  pitch: number;
  yaw: number;
  altitude: number;
  lat: number;
  lng: number;
  v1: number;
  v2: number;
  v3: number;
  v4: number;
};

export type AutopilotVersionRequest = {
  targetSystem: number;
  targetComponent: number;
};

export type RemoteLogDataBlock = {
  targetSystem: number;
  targetComponent: number;
  seqno: MavRemoteLogDataBlockCommands;
  data: Uint8Array;
};

export type RemoteLogBlockStatus = {
  targetSystem: number;
  targetComponent: number;
  seqno: number;
  status: MavRemoteLogDataBlockStatuses;
};

export type LedControl = {
  targetSystem: number;
  targetComponent: number;
  instance: number;
  pattern: number;
  customLen: number;
  customBytes: Uint8Array;
};

export type MagCalProgress = {
  compassId: number;
  calMask: number;
  calStatus: MagCalStatus;
  attempt: number;
  completionPct: number;
  completionMask: Uint8Array;
  directionX: number;
  directionY: number;
  directionZ: number;
};

export type EkfStatusReport = {
  flags: EkfStatusFlags[];
  velocityVariance: number;
  posHorizVariance: number;
  posVertVariance: number;
  compassVariance: number;
  terrainAltVariance: number;
  airspeedVariance: number;
};

export type PidTuning = {
  axis: PidTuningAxis;
  desired: number;
  achieved: number;
  ff: number;
  p: number;
  i: number;
  d: number;
  sRate: number;
  pDmod: number;
};

export type Deepstall = {
  landingLat: number;
  landingLon: number;
  pathLat: number;
  pathLon: number;
  arcEntryLat: number;
  arcEntryLon: number;
  altitude: number;
  expectedTravelDistance: number;
  crossTrackError: number;
  stage: DeepstallStage;
};

export type GimbalReport = {
  targetSystem: number;
  targetComponent: number;
  deltaTime: number;
  deltaAngleX: number;
  deltaAngleY: number;
  deltaAngleZ: number;
  deltaVelocityX: number;
  deltaVelocityY: number;
  deltaVelocityZ: number;
  jointRoll: number;
  jointEl: number;
  jointAz: number;
};

export type GimbalControl = {
  targetSystem: number;
  targetComponent: number;
  demandedRateX: number;
  demandedRateY: number;
  demandedRateZ: number;
};

export type GimbalTorqueCmdReport = {
  targetSystem: number;
  targetComponent: number;
  rlTorqueCmd: number;
  elTorqueCmd: number;
  azTorqueCmd: number;
};

export type GoproHeartbeat = {
  status: GoproHeartbeatStatus;
  captureMode: GoproCaptureMode;
  flags: GoproHeartbeatFlags[];
};

export type GoproGetRequest = {
  targetSystem: number;
  targetComponent: number;
  cmdId: GoproCommand;
};

export type GoproGetResponse = {
  cmdId: GoproCommand;
  status: GoproRequestStatus;
  value: Uint8Array;
};

export type GoproSetRequest = {
  targetSystem: number;
  targetComponent: number;
  cmdId: GoproCommand;
  value: Uint8Array;
};

export type GoproSetResponse = {
  cmdId: GoproCommand;
  status: GoproRequestStatus;
};

export type Rpm = {
  rpm1: number;
  rpm2: number;
};

export type DeviceOpRead = {
  targetSystem: number;
  targetComponent: number;
  requestId: number;
  bustype: DeviceOpBustype;
  bus: number;
  address: number;
  busname: string;
  regstart: number;
  count: number;
  bank: number;
};

export type DeviceOpReadReply = {
  requestId: number;
  result: number;
  regstart: number;
  count: number;
  data: Uint8Array;
  bank: number;
};

export type DeviceOpWrite = {
  targetSystem: number;
  targetComponent: number;
  requestId: number;
  bustype: DeviceOpBustype;
  bus: number;
  address: number;
  busname: string;
  regstart: number;
  count: number;
  data: Uint8Array;
  bank: number;
};

export type DeviceOpWriteReply = {
  requestId: number;
  result: number;
};

export type SecureCommand = {
  targetSystem: number;
  targetComponent: number;
  sequence: number;
  operation: SecureCommandOp;
  dataLength: number;
  sigLength: number;
  data: Uint8Array;
};

export type SecureCommandReply = {
  sequence: number;
  operation: SecureCommandOp;
  result: MavResult;
  dataLength: number;
  data: Uint8Array;
};

export type AdapTuning = {
  axis: PidTuningAxis;
  desired: number;
  achieved: number;
  error: number;
  theta: number;
  omega: number;
  sigma: number;
  thetaDot: number;
  omegaDot: number;
  sigmaDot: number;
  f: number;
  fDot: number;
  u: number;
};

export type VisionPositionDelta = {
  timeUsec: bigint;
  timeDeltaUsec: bigint;
  angleDelta: readonly [number, number, number];
  positionDelta: readonly [number, number, number];
  confidence: number;
};

export type AoaSsa = {
  timeUsec: bigint;
  aoa: number;
  ssa: number;
};

export type EscTelemetry1To4 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type EscTelemetry5To8 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type EscTelemetry9To12 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type OsdParamConfig = {
  targetSystem: number;
  targetComponent: number;
  requestId: number;
  osdScreen: number;
  osdIndex: number;
  paramId: string;
  configType: OsdParamConfigType;
  minValue: number;
  maxValue: number;
  increment: number;
};

export type OsdParamConfigReply = {
  requestId: number;
  result: OsdParamConfigError;
};

export type OsdParamShowConfig = {
  targetSystem: number;
  targetComponent: number;
  requestId: number;
  osdScreen: number;
  osdIndex: number;
};

export type OsdParamShowConfigReply = {
  requestId: number;
  result: OsdParamConfigError;
  paramId: string;
  configType: OsdParamConfigType;
  minValue: number;
  maxValue: number;
  increment: number;
};

export type ObstacleDistance3d = {
  timeBootMs: number;
  sensorType: MavDistanceSensor;
  frame: MavFrame;
  obstacleId: number;
  x: number;
  y: number;
  z: number;
  minDistance: number;
  maxDistance: number;
};

export type WaterDepth = {
  timeBootMs: number;
  id: number;
  healthy: number;
  lat: number;
  lng: number;
  alt: number;
  roll: number;
  pitch: number;
  yaw: number;
  distance: number;
  temperature: number;
};

export type McuStatus = {
  id: number;
  mcuTemperature: number;
  mcuVoltage: number;
  mcuVoltageMin: number;
  mcuVoltageMax: number;
};

export type EscTelemetry13To16 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type EscTelemetry17To20 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type EscTelemetry21To24 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type EscTelemetry25To28 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export type EscTelemetry29To32 = {
  temperature: Uint8Array;
  voltage: readonly [number, number, number, number];
  current: readonly [number, number, number, number];
  totalcurrent: readonly [number, number, number, number];
  rpm: readonly [number, number, number, number];
  count: readonly [number, number, number, number];
};

export const schema = {
  HEARTBEAT: {
    id: 0,
    crcExtra: 50,
    decode(payload: Uint8Array): Heartbeat {
      const reader = createReader(payload, 9);
      const customMode = reader.getUint32();
      const type = reader.getUint8();
      const autopilot = reader.getUint8();
      const baseMode = reader.getUint8();
      const systemStatus = reader.getUint8();
      const mavlinkVersion = reader.getUint8();
      return {
        type: mavTypeInverseMap.get(type) as MavType,
        autopilot: mavAutopilotInverseMap.get(autopilot) as MavAutopilot,
        baseMode: decodeBitmask(baseMode, mavModeFlagInverseMap),
        customMode,
        systemStatus: mavStateInverseMap.get(systemStatus) as MavState,
        mavlinkVersion,
      };
    },
    encode(_: Heartbeat) {
      const writer = createWriter(9);
      writer.setUint32(_.customMode);
      writer.setUint8(lookup(_.type, mavTypeMap));
      writer.setUint8(lookup(_.autopilot, mavAutopilotMap));
      writer.setUint8(encodeBitmask(_.baseMode, mavModeFlagMap));
      writer.setUint8(lookup(_.systemStatus, mavStateMap));
      writer.setUint8(_.mavlinkVersion);
      return writer.finish();
    },
  },
  SYS_STATUS: {
    id: 1,
    crcExtra: 124,
    decode(payload: Uint8Array): SysStatus {
      const reader = createReader(payload, 31);
      const onboardControlSensorsPresent = reader.getUint32();
      const onboardControlSensorsEnabled = reader.getUint32();
      const onboardControlSensorsHealth = reader.getUint32();
      const load = reader.getUint16();
      const voltageBattery = reader.getUint16();
      const currentBattery = reader.getInt16();
      const dropRateComm = reader.getUint16();
      const errorsComm = reader.getUint16();
      const errorsCount1 = reader.getUint16();
      const errorsCount2 = reader.getUint16();
      const errorsCount3 = reader.getUint16();
      const errorsCount4 = reader.getUint16();
      const batteryRemaining = reader.getInt8();
      return {
        onboardControlSensorsPresent: decodeBitmask(onboardControlSensorsPresent, mavSysStatusSensorInverseMap),
        onboardControlSensorsEnabled: decodeBitmask(onboardControlSensorsEnabled, mavSysStatusSensorInverseMap),
        onboardControlSensorsHealth: decodeBitmask(onboardControlSensorsHealth, mavSysStatusSensorInverseMap),
        load,
        voltageBattery,
        currentBattery,
        batteryRemaining,
        dropRateComm,
        errorsComm,
        errorsCount1,
        errorsCount2,
        errorsCount3,
        errorsCount4,
      };
    },
    encode(_: SysStatus) {
      const writer = createWriter(31);
      writer.setUint32(encodeBitmask(_.onboardControlSensorsPresent, mavSysStatusSensorMap));
      writer.setUint32(encodeBitmask(_.onboardControlSensorsEnabled, mavSysStatusSensorMap));
      writer.setUint32(encodeBitmask(_.onboardControlSensorsHealth, mavSysStatusSensorMap));
      writer.setUint16(_.load);
      writer.setUint16(_.voltageBattery);
      writer.setInt16(_.currentBattery);
      writer.setUint16(_.dropRateComm);
      writer.setUint16(_.errorsComm);
      writer.setUint16(_.errorsCount1);
      writer.setUint16(_.errorsCount2);
      writer.setUint16(_.errorsCount3);
      writer.setUint16(_.errorsCount4);
      writer.setInt8(_.batteryRemaining);
      return writer.finish();
    },
  },
  SYSTEM_TIME: {
    id: 2,
    crcExtra: 137,
    decode(payload: Uint8Array): SystemTime {
      const reader = createReader(payload, 12);
      const timeUnixUsec = reader.getBigUint64();
      const timeBootMs = reader.getUint32();
      return {
        timeUnixUsec,
        timeBootMs,
      };
    },
    encode(_: SystemTime) {
      const writer = createWriter(12);
      writer.setBigUint64(_.timeUnixUsec);
      writer.setUint32(_.timeBootMs);
      return writer.finish();
    },
  },
  PING: {
    id: 4,
    crcExtra: 237,
    decode(payload: Uint8Array): Ping {
      const reader = createReader(payload, 14);
      const timeUsec = reader.getBigUint64();
      const seq = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        timeUsec,
        seq,
        targetSystem,
        targetComponent,
      };
    },
    encode(_: Ping) {
      const writer = createWriter(14);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.seq);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  CHANGE_OPERATOR_CONTROL: {
    id: 5,
    crcExtra: 217,
    decode(payload: Uint8Array): ChangeOperatorControl {
      const reader = createReader(payload, 28);
      const targetSystem = reader.getUint8();
      const controlRequest = reader.getUint8();
      const version = reader.getUint8();
      const passkey = textDecoder.decode(reader.getUint8Array(25)).replace(/\0+$/, "");
      return {
        targetSystem,
        controlRequest,
        version,
        passkey,
      };
    },
    encode(_: ChangeOperatorControl) {
      const writer = createWriter(28);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.controlRequest);
      writer.setUint8(_.version);
      const passkeyEncoded = textEncoder.encode(_.passkey);
      writer.setUint8Array(passkeyEncoded, 25);
      return writer.finish();
    },
  },
  CHANGE_OPERATOR_CONTROL_ACK: {
    id: 6,
    crcExtra: 104,
    decode(payload: Uint8Array): ChangeOperatorControlAck {
      const reader = createReader(payload, 3);
      const gcsSystemId = reader.getUint8();
      const controlRequest = reader.getUint8();
      const ack = reader.getUint8();
      return {
        gcsSystemId,
        controlRequest,
        ack,
      };
    },
    encode(_: ChangeOperatorControlAck) {
      const writer = createWriter(3);
      writer.setUint8(_.gcsSystemId);
      writer.setUint8(_.controlRequest);
      writer.setUint8(_.ack);
      return writer.finish();
    },
  },
  AUTH_KEY: {
    id: 7,
    crcExtra: 119,
    decode(payload: Uint8Array): AuthKey {
      const reader = createReader(payload, 32);
      const key = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      return {
        key,
      };
    },
    encode(_: AuthKey) {
      const writer = createWriter(32);
      const keyEncoded = textEncoder.encode(_.key);
      writer.setUint8Array(keyEncoded, 32);
      return writer.finish();
    },
  },
  SET_MODE: {
    id: 11,
    crcExtra: 89,
    decode(payload: Uint8Array): SetMode {
      const reader = createReader(payload, 6);
      const customMode = reader.getUint32();
      const targetSystem = reader.getUint8();
      const baseMode = reader.getUint8();
      return {
        targetSystem,
        baseMode: mavModeInverseMap.get(baseMode) as MavMode,
        customMode,
      };
    },
    encode(_: SetMode) {
      const writer = createWriter(6);
      writer.setUint32(_.customMode);
      writer.setUint8(_.targetSystem);
      writer.setUint8(lookup(_.baseMode, mavModeMap));
      return writer.finish();
    },
  },
  PARAM_REQUEST_READ: {
    id: 20,
    crcExtra: 214,
    decode(payload: Uint8Array): ParamRequestRead {
      const reader = createReader(payload, 20);
      const paramIndex = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      return {
        targetSystem,
        targetComponent,
        paramId,
        paramIndex,
      };
    },
    encode(_: ParamRequestRead) {
      const writer = createWriter(20);
      writer.setInt16(_.paramIndex);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      return writer.finish();
    },
  },
  PARAM_REQUEST_LIST: {
    id: 21,
    crcExtra: 159,
    decode(payload: Uint8Array): ParamRequestList {
      const reader = createReader(payload, 2);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
      };
    },
    encode(_: ParamRequestList) {
      const writer = createWriter(2);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  PARAM_VALUE: {
    id: 22,
    crcExtra: 220,
    decode(payload: Uint8Array): ParamValue {
      const reader = createReader(payload, 25);
      const paramValue = reader.getFloat32();
      const paramCount = reader.getUint16();
      const paramIndex = reader.getUint16();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const paramType = reader.getUint8();
      return {
        paramId,
        paramValue,
        paramType: mavParamTypeInverseMap.get(paramType) as MavParamType,
        paramCount,
        paramIndex,
      };
    },
    encode(_: ParamValue) {
      const writer = createWriter(25);
      writer.setFloat32(_.paramValue);
      writer.setUint16(_.paramCount);
      writer.setUint16(_.paramIndex);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      writer.setUint8(lookup(_.paramType, mavParamTypeMap));
      return writer.finish();
    },
  },
  PARAM_SET: {
    id: 23,
    crcExtra: 168,
    decode(payload: Uint8Array): ParamSet {
      const reader = createReader(payload, 23);
      const paramValue = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const paramType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        paramId,
        paramValue,
        paramType: mavParamTypeInverseMap.get(paramType) as MavParamType,
      };
    },
    encode(_: ParamSet) {
      const writer = createWriter(23);
      writer.setFloat32(_.paramValue);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      writer.setUint8(lookup(_.paramType, mavParamTypeMap));
      return writer.finish();
    },
  },
  GPS_RAW_INT: {
    id: 24,
    crcExtra: 24,
    decode(payload: Uint8Array): GpsRawInt {
      const reader = createReader(payload, 52);
      const timeUsec = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const eph = reader.getUint16();
      const epv = reader.getUint16();
      const vel = reader.getUint16();
      const cog = reader.getUint16();
      const fixType = reader.getUint8();
      const satellitesVisible = reader.getUint8();
      const altEllipsoid = reader.getInt32();
      const hAcc = reader.getUint32();
      const vAcc = reader.getUint32();
      const velAcc = reader.getUint32();
      const hdgAcc = reader.getUint32();
      const yaw = reader.getUint16();
      return {
        timeUsec,
        fixType: gpsFixTypeInverseMap.get(fixType) as GpsFixType,
        lat,
        lon,
        alt,
        eph,
        epv,
        vel,
        cog,
        satellitesVisible,
        altEllipsoid,
        hAcc,
        vAcc,
        velAcc,
        hdgAcc,
        yaw,
      };
    },
    encode(_: GpsRawInt) {
      const writer = createWriter(52);
      writer.setBigUint64(_.timeUsec);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setUint16(_.eph);
      writer.setUint16(_.epv);
      writer.setUint16(_.vel);
      writer.setUint16(_.cog);
      writer.setUint8(lookup(_.fixType, gpsFixTypeMap));
      writer.setUint8(_.satellitesVisible);
      writer.setInt32(_.altEllipsoid);
      writer.setUint32(_.hAcc);
      writer.setUint32(_.vAcc);
      writer.setUint32(_.velAcc);
      writer.setUint32(_.hdgAcc);
      writer.setUint16(_.yaw);
      return writer.finish();
    },
  },
  GPS_STATUS: {
    id: 25,
    crcExtra: 23,
    decode(payload: Uint8Array): GpsStatus {
      const reader = createReader(payload, 101);
      const satellitesVisible = reader.getUint8();
      const satellitePrn = reader.getUint8Array(20);
      const satelliteUsed = reader.getUint8Array(20);
      const satelliteElevation = reader.getUint8Array(20);
      const satelliteAzimuth = reader.getUint8Array(20);
      const satelliteSnr = reader.getUint8Array(20);
      return {
        satellitesVisible,
        satellitePrn,
        satelliteUsed,
        satelliteElevation,
        satelliteAzimuth,
        satelliteSnr,
      };
    },
    encode(_: GpsStatus) {
      const writer = createWriter(101);
      writer.setUint8(_.satellitesVisible);
      writer.setUint8Array(_.satellitePrn, 20);
      writer.setUint8Array(_.satelliteUsed, 20);
      writer.setUint8Array(_.satelliteElevation, 20);
      writer.setUint8Array(_.satelliteAzimuth, 20);
      writer.setUint8Array(_.satelliteSnr, 20);
      return writer.finish();
    },
  },
  SCALED_IMU: {
    id: 26,
    crcExtra: 170,
    decode(payload: Uint8Array): ScaledImu {
      const reader = createReader(payload, 24);
      const timeBootMs = reader.getUint32();
      const xacc = reader.getInt16();
      const yacc = reader.getInt16();
      const zacc = reader.getInt16();
      const xgyro = reader.getInt16();
      const ygyro = reader.getInt16();
      const zgyro = reader.getInt16();
      const xmag = reader.getInt16();
      const ymag = reader.getInt16();
      const zmag = reader.getInt16();
      const temperature = reader.getInt16();
      return {
        timeBootMs,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        xmag,
        ymag,
        zmag,
        temperature,
      };
    },
    encode(_: ScaledImu) {
      const writer = createWriter(24);
      writer.setUint32(_.timeBootMs);
      writer.setInt16(_.xacc);
      writer.setInt16(_.yacc);
      writer.setInt16(_.zacc);
      writer.setInt16(_.xgyro);
      writer.setInt16(_.ygyro);
      writer.setInt16(_.zgyro);
      writer.setInt16(_.xmag);
      writer.setInt16(_.ymag);
      writer.setInt16(_.zmag);
      writer.setInt16(_.temperature);
      return writer.finish();
    },
  },
  RAW_IMU: {
    id: 27,
    crcExtra: 144,
    decode(payload: Uint8Array): RawImu {
      const reader = createReader(payload, 29);
      const timeUsec = reader.getBigUint64();
      const xacc = reader.getInt16();
      const yacc = reader.getInt16();
      const zacc = reader.getInt16();
      const xgyro = reader.getInt16();
      const ygyro = reader.getInt16();
      const zgyro = reader.getInt16();
      const xmag = reader.getInt16();
      const ymag = reader.getInt16();
      const zmag = reader.getInt16();
      const id = reader.getUint8();
      const temperature = reader.getInt16();
      return {
        timeUsec,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        xmag,
        ymag,
        zmag,
        id,
        temperature,
      };
    },
    encode(_: RawImu) {
      const writer = createWriter(29);
      writer.setBigUint64(_.timeUsec);
      writer.setInt16(_.xacc);
      writer.setInt16(_.yacc);
      writer.setInt16(_.zacc);
      writer.setInt16(_.xgyro);
      writer.setInt16(_.ygyro);
      writer.setInt16(_.zgyro);
      writer.setInt16(_.xmag);
      writer.setInt16(_.ymag);
      writer.setInt16(_.zmag);
      writer.setUint8(_.id);
      writer.setInt16(_.temperature);
      return writer.finish();
    },
  },
  RAW_PRESSURE: {
    id: 28,
    crcExtra: 67,
    decode(payload: Uint8Array): RawPressure {
      const reader = createReader(payload, 16);
      const timeUsec = reader.getBigUint64();
      const pressAbs = reader.getInt16();
      const pressDiff1 = reader.getInt16();
      const pressDiff2 = reader.getInt16();
      const temperature = reader.getInt16();
      return {
        timeUsec,
        pressAbs,
        pressDiff1,
        pressDiff2,
        temperature,
      };
    },
    encode(_: RawPressure) {
      const writer = createWriter(16);
      writer.setBigUint64(_.timeUsec);
      writer.setInt16(_.pressAbs);
      writer.setInt16(_.pressDiff1);
      writer.setInt16(_.pressDiff2);
      writer.setInt16(_.temperature);
      return writer.finish();
    },
  },
  SCALED_PRESSURE: {
    id: 29,
    crcExtra: 115,
    decode(payload: Uint8Array): ScaledPressure {
      const reader = createReader(payload, 16);
      const timeBootMs = reader.getUint32();
      const pressAbs = reader.getFloat32();
      const pressDiff = reader.getFloat32();
      const temperature = reader.getInt16();
      const temperaturePressDiff = reader.getInt16();
      return {
        timeBootMs,
        pressAbs,
        pressDiff,
        temperature,
        temperaturePressDiff,
      };
    },
    encode(_: ScaledPressure) {
      const writer = createWriter(16);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.pressAbs);
      writer.setFloat32(_.pressDiff);
      writer.setInt16(_.temperature);
      writer.setInt16(_.temperaturePressDiff);
      return writer.finish();
    },
  },
  ATTITUDE: {
    id: 30,
    crcExtra: 39,
    decode(payload: Uint8Array): Attitude {
      const reader = createReader(payload, 28);
      const timeBootMs = reader.getUint32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const rollspeed = reader.getFloat32();
      const pitchspeed = reader.getFloat32();
      const yawspeed = reader.getFloat32();
      return {
        timeBootMs,
        roll,
        pitch,
        yaw,
        rollspeed,
        pitchspeed,
        yawspeed,
      };
    },
    encode(_: Attitude) {
      const writer = createWriter(28);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.rollspeed);
      writer.setFloat32(_.pitchspeed);
      writer.setFloat32(_.yawspeed);
      return writer.finish();
    },
  },
  ATTITUDE_QUATERNION: {
    id: 31,
    crcExtra: 246,
    decode(payload: Uint8Array): AttitudeQuaternion {
      const reader = createReader(payload, 48);
      const timeBootMs = reader.getUint32();
      const q1 = reader.getFloat32();
      const q2 = reader.getFloat32();
      const q3 = reader.getFloat32();
      const q4 = reader.getFloat32();
      const rollspeed = reader.getFloat32();
      const pitchspeed = reader.getFloat32();
      const yawspeed = reader.getFloat32();
      const reprOffsetQ = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      return {
        timeBootMs,
        q1,
        q2,
        q3,
        q4,
        rollspeed,
        pitchspeed,
        yawspeed,
        reprOffsetQ,
      };
    },
    encode(_: AttitudeQuaternion) {
      const writer = createWriter(48);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.q1);
      writer.setFloat32(_.q2);
      writer.setFloat32(_.q3);
      writer.setFloat32(_.q4);
      writer.setFloat32(_.rollspeed);
      writer.setFloat32(_.pitchspeed);
      writer.setFloat32(_.yawspeed);
      _.reprOffsetQ.forEach(writer.setFloat32);
      return writer.finish();
    },
  },
  LOCAL_POSITION_NED: {
    id: 32,
    crcExtra: 185,
    decode(payload: Uint8Array): LocalPositionNed {
      const reader = createReader(payload, 28);
      const timeBootMs = reader.getUint32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      return {
        timeBootMs,
        x,
        y,
        z,
        vx,
        vy,
        vz,
      };
    },
    encode(_: LocalPositionNed) {
      const writer = createWriter(28);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      return writer.finish();
    },
  },
  GLOBAL_POSITION_INT: {
    id: 33,
    crcExtra: 104,
    decode(payload: Uint8Array): GlobalPositionInt {
      const reader = createReader(payload, 28);
      const timeBootMs = reader.getUint32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const relativeAlt = reader.getInt32();
      const vx = reader.getInt16();
      const vy = reader.getInt16();
      const vz = reader.getInt16();
      const hdg = reader.getUint16();
      return {
        timeBootMs,
        lat,
        lon,
        alt,
        relativeAlt,
        vx,
        vy,
        vz,
        hdg,
      };
    },
    encode(_: GlobalPositionInt) {
      const writer = createWriter(28);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setInt32(_.relativeAlt);
      writer.setInt16(_.vx);
      writer.setInt16(_.vy);
      writer.setInt16(_.vz);
      writer.setUint16(_.hdg);
      return writer.finish();
    },
  },
  RC_CHANNELS_SCALED: {
    id: 34,
    crcExtra: 237,
    decode(payload: Uint8Array): RcChannelsScaled {
      const reader = createReader(payload, 22);
      const timeBootMs = reader.getUint32();
      const chan1Scaled = reader.getInt16();
      const chan2Scaled = reader.getInt16();
      const chan3Scaled = reader.getInt16();
      const chan4Scaled = reader.getInt16();
      const chan5Scaled = reader.getInt16();
      const chan6Scaled = reader.getInt16();
      const chan7Scaled = reader.getInt16();
      const chan8Scaled = reader.getInt16();
      const port = reader.getUint8();
      const rssi = reader.getUint8();
      return {
        timeBootMs,
        port,
        chan1Scaled,
        chan2Scaled,
        chan3Scaled,
        chan4Scaled,
        chan5Scaled,
        chan6Scaled,
        chan7Scaled,
        chan8Scaled,
        rssi,
      };
    },
    encode(_: RcChannelsScaled) {
      const writer = createWriter(22);
      writer.setUint32(_.timeBootMs);
      writer.setInt16(_.chan1Scaled);
      writer.setInt16(_.chan2Scaled);
      writer.setInt16(_.chan3Scaled);
      writer.setInt16(_.chan4Scaled);
      writer.setInt16(_.chan5Scaled);
      writer.setInt16(_.chan6Scaled);
      writer.setInt16(_.chan7Scaled);
      writer.setInt16(_.chan8Scaled);
      writer.setUint8(_.port);
      writer.setUint8(_.rssi);
      return writer.finish();
    },
  },
  RC_CHANNELS_RAW: {
    id: 35,
    crcExtra: 244,
    decode(payload: Uint8Array): RcChannelsRaw {
      const reader = createReader(payload, 22);
      const timeBootMs = reader.getUint32();
      const chan1Raw = reader.getUint16();
      const chan2Raw = reader.getUint16();
      const chan3Raw = reader.getUint16();
      const chan4Raw = reader.getUint16();
      const chan5Raw = reader.getUint16();
      const chan6Raw = reader.getUint16();
      const chan7Raw = reader.getUint16();
      const chan8Raw = reader.getUint16();
      const port = reader.getUint8();
      const rssi = reader.getUint8();
      return {
        timeBootMs,
        port,
        chan1Raw,
        chan2Raw,
        chan3Raw,
        chan4Raw,
        chan5Raw,
        chan6Raw,
        chan7Raw,
        chan8Raw,
        rssi,
      };
    },
    encode(_: RcChannelsRaw) {
      const writer = createWriter(22);
      writer.setUint32(_.timeBootMs);
      writer.setUint16(_.chan1Raw);
      writer.setUint16(_.chan2Raw);
      writer.setUint16(_.chan3Raw);
      writer.setUint16(_.chan4Raw);
      writer.setUint16(_.chan5Raw);
      writer.setUint16(_.chan6Raw);
      writer.setUint16(_.chan7Raw);
      writer.setUint16(_.chan8Raw);
      writer.setUint8(_.port);
      writer.setUint8(_.rssi);
      return writer.finish();
    },
  },
  SERVO_OUTPUT_RAW: {
    id: 36,
    crcExtra: 222,
    decode(payload: Uint8Array): ServoOutputRaw {
      const reader = createReader(payload, 37);
      const timeUsec = reader.getUint32();
      const servo1Raw = reader.getUint16();
      const servo2Raw = reader.getUint16();
      const servo3Raw = reader.getUint16();
      const servo4Raw = reader.getUint16();
      const servo5Raw = reader.getUint16();
      const servo6Raw = reader.getUint16();
      const servo7Raw = reader.getUint16();
      const servo8Raw = reader.getUint16();
      const port = reader.getUint8();
      const servo9Raw = reader.getUint16();
      const servo10Raw = reader.getUint16();
      const servo11Raw = reader.getUint16();
      const servo12Raw = reader.getUint16();
      const servo13Raw = reader.getUint16();
      const servo14Raw = reader.getUint16();
      const servo15Raw = reader.getUint16();
      const servo16Raw = reader.getUint16();
      return {
        timeUsec,
        port,
        servo1Raw,
        servo2Raw,
        servo3Raw,
        servo4Raw,
        servo5Raw,
        servo6Raw,
        servo7Raw,
        servo8Raw,
        servo9Raw,
        servo10Raw,
        servo11Raw,
        servo12Raw,
        servo13Raw,
        servo14Raw,
        servo15Raw,
        servo16Raw,
      };
    },
    encode(_: ServoOutputRaw) {
      const writer = createWriter(37);
      writer.setUint32(_.timeUsec);
      writer.setUint16(_.servo1Raw);
      writer.setUint16(_.servo2Raw);
      writer.setUint16(_.servo3Raw);
      writer.setUint16(_.servo4Raw);
      writer.setUint16(_.servo5Raw);
      writer.setUint16(_.servo6Raw);
      writer.setUint16(_.servo7Raw);
      writer.setUint16(_.servo8Raw);
      writer.setUint8(_.port);
      writer.setUint16(_.servo9Raw);
      writer.setUint16(_.servo10Raw);
      writer.setUint16(_.servo11Raw);
      writer.setUint16(_.servo12Raw);
      writer.setUint16(_.servo13Raw);
      writer.setUint16(_.servo14Raw);
      writer.setUint16(_.servo15Raw);
      writer.setUint16(_.servo16Raw);
      return writer.finish();
    },
  },
  MISSION_REQUEST_PARTIAL_LIST: {
    id: 37,
    crcExtra: 212,
    decode(payload: Uint8Array): MissionRequestPartialList {
      const reader = createReader(payload, 7);
      const startIndex = reader.getInt16();
      const endIndex = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        startIndex,
        endIndex,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionRequestPartialList) {
      const writer = createWriter(7);
      writer.setInt16(_.startIndex);
      writer.setInt16(_.endIndex);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_WRITE_PARTIAL_LIST: {
    id: 38,
    crcExtra: 9,
    decode(payload: Uint8Array): MissionWritePartialList {
      const reader = createReader(payload, 7);
      const startIndex = reader.getInt16();
      const endIndex = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        startIndex,
        endIndex,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionWritePartialList) {
      const writer = createWriter(7);
      writer.setInt16(_.startIndex);
      writer.setInt16(_.endIndex);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_ITEM: {
    id: 39,
    crcExtra: 254,
    decode(payload: Uint8Array): MissionItem {
      const reader = createReader(payload, 38);
      const param1 = reader.getFloat32();
      const param2 = reader.getFloat32();
      const param3 = reader.getFloat32();
      const param4 = reader.getFloat32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const seq = reader.getUint16();
      const command = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const frame = reader.getUint8();
      const current = reader.getUint8();
      const autocontinue = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        seq,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        command: mavCmdInverseMap.get(command) as MavCmd,
        current,
        autocontinue,
        param1,
        param2,
        param3,
        param4,
        x,
        y,
        z,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionItem) {
      const writer = createWriter(38);
      writer.setFloat32(_.param1);
      writer.setFloat32(_.param2);
      writer.setFloat32(_.param3);
      writer.setFloat32(_.param4);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setUint16(_.seq);
      writer.setUint16(lookup(_.command, mavCmdMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      writer.setUint8(_.current);
      writer.setUint8(_.autocontinue);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_REQUEST: {
    id: 40,
    crcExtra: 230,
    decode(payload: Uint8Array): MissionRequest {
      const reader = createReader(payload, 5);
      const seq = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        seq,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionRequest) {
      const writer = createWriter(5);
      writer.setUint16(_.seq);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_SET_CURRENT: {
    id: 41,
    crcExtra: 28,
    decode(payload: Uint8Array): MissionSetCurrent {
      const reader = createReader(payload, 4);
      const seq = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        seq,
      };
    },
    encode(_: MissionSetCurrent) {
      const writer = createWriter(4);
      writer.setUint16(_.seq);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  MISSION_CURRENT: {
    id: 42,
    crcExtra: 28,
    decode(payload: Uint8Array): MissionCurrent {
      const reader = createReader(payload, 6);
      const seq = reader.getUint16();
      const total = reader.getUint16();
      const missionState = reader.getUint8();
      const missionMode = reader.getUint8();
      return {
        seq,
        total,
        missionState: missionStateInverseMap.get(missionState) as MissionState,
        missionMode,
      };
    },
    encode(_: MissionCurrent) {
      const writer = createWriter(6);
      writer.setUint16(_.seq);
      writer.setUint16(_.total);
      writer.setUint8(lookup(_.missionState, missionStateMap));
      writer.setUint8(_.missionMode);
      return writer.finish();
    },
  },
  MISSION_REQUEST_LIST: {
    id: 43,
    crcExtra: 132,
    decode(payload: Uint8Array): MissionRequestList {
      const reader = createReader(payload, 3);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionRequestList) {
      const writer = createWriter(3);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_COUNT: {
    id: 44,
    crcExtra: 221,
    decode(payload: Uint8Array): MissionCount {
      const reader = createReader(payload, 5);
      const count = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        count,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionCount) {
      const writer = createWriter(5);
      writer.setUint16(_.count);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_CLEAR_ALL: {
    id: 45,
    crcExtra: 232,
    decode(payload: Uint8Array): MissionClearAll {
      const reader = createReader(payload, 3);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionClearAll) {
      const writer = createWriter(3);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  MISSION_ITEM_REACHED: {
    id: 46,
    crcExtra: 11,
    decode(payload: Uint8Array): MissionItemReached {
      const reader = createReader(payload, 2);
      const seq = reader.getUint16();
      return {
        seq,
      };
    },
    encode(_: MissionItemReached) {
      const writer = createWriter(2);
      writer.setUint16(_.seq);
      return writer.finish();
    },
  },
  MISSION_ACK: {
    id: 47,
    crcExtra: 153,
    decode(payload: Uint8Array): MissionAck {
      const reader = createReader(payload, 4);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const type = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        type: mavMissionResultInverseMap.get(type) as MavMissionResult,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionAck) {
      const writer = createWriter(4);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.type, mavMissionResultMap));
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  SET_GPS_GLOBAL_ORIGIN: {
    id: 48,
    crcExtra: 41,
    decode(payload: Uint8Array): SetGpsGlobalOrigin {
      const reader = createReader(payload, 21);
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const altitude = reader.getInt32();
      const targetSystem = reader.getUint8();
      const timeUsec = reader.getBigUint64();
      return {
        targetSystem,
        latitude,
        longitude,
        altitude,
        timeUsec,
      };
    },
    encode(_: SetGpsGlobalOrigin) {
      const writer = createWriter(21);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setInt32(_.altitude);
      writer.setUint8(_.targetSystem);
      writer.setBigUint64(_.timeUsec);
      return writer.finish();
    },
  },
  GPS_GLOBAL_ORIGIN: {
    id: 49,
    crcExtra: 39,
    decode(payload: Uint8Array): GpsGlobalOrigin {
      const reader = createReader(payload, 20);
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const altitude = reader.getInt32();
      const timeUsec = reader.getBigUint64();
      return {
        latitude,
        longitude,
        altitude,
        timeUsec,
      };
    },
    encode(_: GpsGlobalOrigin) {
      const writer = createWriter(20);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setInt32(_.altitude);
      writer.setBigUint64(_.timeUsec);
      return writer.finish();
    },
  },
  PARAM_MAP_RC: {
    id: 50,
    crcExtra: 78,
    decode(payload: Uint8Array): ParamMapRc {
      const reader = createReader(payload, 37);
      const paramValue0 = reader.getFloat32();
      const scale = reader.getFloat32();
      const paramValueMin = reader.getFloat32();
      const paramValueMax = reader.getFloat32();
      const paramIndex = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const parameterRcChannelIndex = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        paramId,
        paramIndex,
        parameterRcChannelIndex,
        paramValue0,
        scale,
        paramValueMin,
        paramValueMax,
      };
    },
    encode(_: ParamMapRc) {
      const writer = createWriter(37);
      writer.setFloat32(_.paramValue0);
      writer.setFloat32(_.scale);
      writer.setFloat32(_.paramValueMin);
      writer.setFloat32(_.paramValueMax);
      writer.setInt16(_.paramIndex);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      writer.setUint8(_.parameterRcChannelIndex);
      return writer.finish();
    },
  },
  MISSION_REQUEST_INT: {
    id: 51,
    crcExtra: 196,
    decode(payload: Uint8Array): MissionRequestInt {
      const reader = createReader(payload, 5);
      const seq = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        seq,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionRequestInt) {
      const writer = createWriter(5);
      writer.setUint16(_.seq);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  SAFETY_SET_ALLOWED_AREA: {
    id: 54,
    crcExtra: 15,
    decode(payload: Uint8Array): SafetySetAllowedArea {
      const reader = createReader(payload, 27);
      const p1x = reader.getFloat32();
      const p1y = reader.getFloat32();
      const p1z = reader.getFloat32();
      const p2x = reader.getFloat32();
      const p2y = reader.getFloat32();
      const p2z = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const frame = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        p1x,
        p1y,
        p1z,
        p2x,
        p2y,
        p2z,
      };
    },
    encode(_: SafetySetAllowedArea) {
      const writer = createWriter(27);
      writer.setFloat32(_.p1x);
      writer.setFloat32(_.p1y);
      writer.setFloat32(_.p1z);
      writer.setFloat32(_.p2x);
      writer.setFloat32(_.p2y);
      writer.setFloat32(_.p2z);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      return writer.finish();
    },
  },
  SAFETY_ALLOWED_AREA: {
    id: 55,
    crcExtra: 3,
    decode(payload: Uint8Array): SafetyAllowedArea {
      const reader = createReader(payload, 25);
      const p1x = reader.getFloat32();
      const p1y = reader.getFloat32();
      const p1z = reader.getFloat32();
      const p2x = reader.getFloat32();
      const p2y = reader.getFloat32();
      const p2z = reader.getFloat32();
      const frame = reader.getUint8();
      return {
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        p1x,
        p1y,
        p1z,
        p2x,
        p2y,
        p2z,
      };
    },
    encode(_: SafetyAllowedArea) {
      const writer = createWriter(25);
      writer.setFloat32(_.p1x);
      writer.setFloat32(_.p1y);
      writer.setFloat32(_.p1z);
      writer.setFloat32(_.p2x);
      writer.setFloat32(_.p2y);
      writer.setFloat32(_.p2z);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      return writer.finish();
    },
  },
  ATTITUDE_QUATERNION_COV: {
    id: 61,
    crcExtra: 167,
    decode(payload: Uint8Array): AttitudeQuaternionCov {
      const reader = createReader(payload, 72);
      const timeUsec = reader.getBigUint64();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const rollspeed = reader.getFloat32();
      const pitchspeed = reader.getFloat32();
      const yawspeed = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      return {
        timeUsec,
        q,
        rollspeed,
        pitchspeed,
        yawspeed,
        covariance,
      };
    },
    encode(_: AttitudeQuaternionCov) {
      const writer = createWriter(72);
      writer.setBigUint64(_.timeUsec);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.rollspeed);
      writer.setFloat32(_.pitchspeed);
      writer.setFloat32(_.yawspeed);
      _.covariance.forEach(writer.setFloat32);
      return writer.finish();
    },
  },
  NAV_CONTROLLER_OUTPUT: {
    id: 62,
    crcExtra: 183,
    decode(payload: Uint8Array): NavControllerOutput {
      const reader = createReader(payload, 26);
      const navRoll = reader.getFloat32();
      const navPitch = reader.getFloat32();
      const altError = reader.getFloat32();
      const aspdError = reader.getFloat32();
      const xtrackError = reader.getFloat32();
      const navBearing = reader.getInt16();
      const targetBearing = reader.getInt16();
      const wpDist = reader.getUint16();
      return {
        navRoll,
        navPitch,
        navBearing,
        targetBearing,
        wpDist,
        altError,
        aspdError,
        xtrackError,
      };
    },
    encode(_: NavControllerOutput) {
      const writer = createWriter(26);
      writer.setFloat32(_.navRoll);
      writer.setFloat32(_.navPitch);
      writer.setFloat32(_.altError);
      writer.setFloat32(_.aspdError);
      writer.setFloat32(_.xtrackError);
      writer.setInt16(_.navBearing);
      writer.setInt16(_.targetBearing);
      writer.setUint16(_.wpDist);
      return writer.finish();
    },
  },
  GLOBAL_POSITION_INT_COV: {
    id: 63,
    crcExtra: 119,
    decode(payload: Uint8Array): GlobalPositionIntCov {
      const reader = createReader(payload, 181);
      const timeUsec = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const relativeAlt = reader.getInt32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const estimatorType = reader.getUint8();
      return {
        timeUsec,
        estimatorType: mavEstimatorTypeInverseMap.get(estimatorType) as MavEstimatorType,
        lat,
        lon,
        alt,
        relativeAlt,
        vx,
        vy,
        vz,
        covariance,
      };
    },
    encode(_: GlobalPositionIntCov) {
      const writer = createWriter(181);
      writer.setBigUint64(_.timeUsec);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setInt32(_.relativeAlt);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      _.covariance.forEach(writer.setFloat32);
      writer.setUint8(lookup(_.estimatorType, mavEstimatorTypeMap));
      return writer.finish();
    },
  },
  LOCAL_POSITION_NED_COV: {
    id: 64,
    crcExtra: 191,
    decode(payload: Uint8Array): LocalPositionNedCov {
      const reader = createReader(payload, 225);
      const timeUsec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const ax = reader.getFloat32();
      const ay = reader.getFloat32();
      const az = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const estimatorType = reader.getUint8();
      return {
        timeUsec,
        estimatorType: mavEstimatorTypeInverseMap.get(estimatorType) as MavEstimatorType,
        x,
        y,
        z,
        vx,
        vy,
        vz,
        ax,
        ay,
        az,
        covariance,
      };
    },
    encode(_: LocalPositionNedCov) {
      const writer = createWriter(225);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.ax);
      writer.setFloat32(_.ay);
      writer.setFloat32(_.az);
      _.covariance.forEach(writer.setFloat32);
      writer.setUint8(lookup(_.estimatorType, mavEstimatorTypeMap));
      return writer.finish();
    },
  },
  RC_CHANNELS: {
    id: 65,
    crcExtra: 118,
    decode(payload: Uint8Array): RcChannels {
      const reader = createReader(payload, 42);
      const timeBootMs = reader.getUint32();
      const chan1Raw = reader.getUint16();
      const chan2Raw = reader.getUint16();
      const chan3Raw = reader.getUint16();
      const chan4Raw = reader.getUint16();
      const chan5Raw = reader.getUint16();
      const chan6Raw = reader.getUint16();
      const chan7Raw = reader.getUint16();
      const chan8Raw = reader.getUint16();
      const chan9Raw = reader.getUint16();
      const chan10Raw = reader.getUint16();
      const chan11Raw = reader.getUint16();
      const chan12Raw = reader.getUint16();
      const chan13Raw = reader.getUint16();
      const chan14Raw = reader.getUint16();
      const chan15Raw = reader.getUint16();
      const chan16Raw = reader.getUint16();
      const chan17Raw = reader.getUint16();
      const chan18Raw = reader.getUint16();
      const chancount = reader.getUint8();
      const rssi = reader.getUint8();
      return {
        timeBootMs,
        chancount,
        chan1Raw,
        chan2Raw,
        chan3Raw,
        chan4Raw,
        chan5Raw,
        chan6Raw,
        chan7Raw,
        chan8Raw,
        chan9Raw,
        chan10Raw,
        chan11Raw,
        chan12Raw,
        chan13Raw,
        chan14Raw,
        chan15Raw,
        chan16Raw,
        chan17Raw,
        chan18Raw,
        rssi,
      };
    },
    encode(_: RcChannels) {
      const writer = createWriter(42);
      writer.setUint32(_.timeBootMs);
      writer.setUint16(_.chan1Raw);
      writer.setUint16(_.chan2Raw);
      writer.setUint16(_.chan3Raw);
      writer.setUint16(_.chan4Raw);
      writer.setUint16(_.chan5Raw);
      writer.setUint16(_.chan6Raw);
      writer.setUint16(_.chan7Raw);
      writer.setUint16(_.chan8Raw);
      writer.setUint16(_.chan9Raw);
      writer.setUint16(_.chan10Raw);
      writer.setUint16(_.chan11Raw);
      writer.setUint16(_.chan12Raw);
      writer.setUint16(_.chan13Raw);
      writer.setUint16(_.chan14Raw);
      writer.setUint16(_.chan15Raw);
      writer.setUint16(_.chan16Raw);
      writer.setUint16(_.chan17Raw);
      writer.setUint16(_.chan18Raw);
      writer.setUint8(_.chancount);
      writer.setUint8(_.rssi);
      return writer.finish();
    },
  },
  REQUEST_DATA_STREAM: {
    id: 66,
    crcExtra: 148,
    decode(payload: Uint8Array): RequestDataStream {
      const reader = createReader(payload, 6);
      const reqMessageRate = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const reqStreamId = reader.getUint8();
      const startStop = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        reqStreamId,
        reqMessageRate,
        startStop,
      };
    },
    encode(_: RequestDataStream) {
      const writer = createWriter(6);
      writer.setUint16(_.reqMessageRate);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.reqStreamId);
      writer.setUint8(_.startStop);
      return writer.finish();
    },
  },
  DATA_STREAM: {
    id: 67,
    crcExtra: 21,
    decode(payload: Uint8Array): DataStream {
      const reader = createReader(payload, 4);
      const messageRate = reader.getUint16();
      const streamId = reader.getUint8();
      const onOff = reader.getUint8();
      return {
        streamId,
        messageRate,
        onOff,
      };
    },
    encode(_: DataStream) {
      const writer = createWriter(4);
      writer.setUint16(_.messageRate);
      writer.setUint8(_.streamId);
      writer.setUint8(_.onOff);
      return writer.finish();
    },
  },
  MANUAL_CONTROL: {
    id: 69,
    crcExtra: 243,
    decode(payload: Uint8Array): ManualControl {
      const reader = createReader(payload, 30);
      const x = reader.getInt16();
      const y = reader.getInt16();
      const z = reader.getInt16();
      const r = reader.getInt16();
      const buttons = reader.getUint16();
      const target = reader.getUint8();
      const buttons2 = reader.getUint16();
      const enabledExtensions = reader.getUint8();
      const s = reader.getInt16();
      const t = reader.getInt16();
      const aux1 = reader.getInt16();
      const aux2 = reader.getInt16();
      const aux3 = reader.getInt16();
      const aux4 = reader.getInt16();
      const aux5 = reader.getInt16();
      const aux6 = reader.getInt16();
      return {
        target,
        x,
        y,
        z,
        r,
        buttons,
        buttons2,
        enabledExtensions,
        s,
        t,
        aux1,
        aux2,
        aux3,
        aux4,
        aux5,
        aux6,
      };
    },
    encode(_: ManualControl) {
      const writer = createWriter(30);
      writer.setInt16(_.x);
      writer.setInt16(_.y);
      writer.setInt16(_.z);
      writer.setInt16(_.r);
      writer.setUint16(_.buttons);
      writer.setUint8(_.target);
      writer.setUint16(_.buttons2);
      writer.setUint8(_.enabledExtensions);
      writer.setInt16(_.s);
      writer.setInt16(_.t);
      writer.setInt16(_.aux1);
      writer.setInt16(_.aux2);
      writer.setInt16(_.aux3);
      writer.setInt16(_.aux4);
      writer.setInt16(_.aux5);
      writer.setInt16(_.aux6);
      return writer.finish();
    },
  },
  RC_CHANNELS_OVERRIDE: {
    id: 70,
    crcExtra: 124,
    decode(payload: Uint8Array): RcChannelsOverride {
      const reader = createReader(payload, 38);
      const chan1Raw = reader.getUint16();
      const chan2Raw = reader.getUint16();
      const chan3Raw = reader.getUint16();
      const chan4Raw = reader.getUint16();
      const chan5Raw = reader.getUint16();
      const chan6Raw = reader.getUint16();
      const chan7Raw = reader.getUint16();
      const chan8Raw = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const chan9Raw = reader.getUint16();
      const chan10Raw = reader.getUint16();
      const chan11Raw = reader.getUint16();
      const chan12Raw = reader.getUint16();
      const chan13Raw = reader.getUint16();
      const chan14Raw = reader.getUint16();
      const chan15Raw = reader.getUint16();
      const chan16Raw = reader.getUint16();
      const chan17Raw = reader.getUint16();
      const chan18Raw = reader.getUint16();
      return {
        targetSystem,
        targetComponent,
        chan1Raw,
        chan2Raw,
        chan3Raw,
        chan4Raw,
        chan5Raw,
        chan6Raw,
        chan7Raw,
        chan8Raw,
        chan9Raw,
        chan10Raw,
        chan11Raw,
        chan12Raw,
        chan13Raw,
        chan14Raw,
        chan15Raw,
        chan16Raw,
        chan17Raw,
        chan18Raw,
      };
    },
    encode(_: RcChannelsOverride) {
      const writer = createWriter(38);
      writer.setUint16(_.chan1Raw);
      writer.setUint16(_.chan2Raw);
      writer.setUint16(_.chan3Raw);
      writer.setUint16(_.chan4Raw);
      writer.setUint16(_.chan5Raw);
      writer.setUint16(_.chan6Raw);
      writer.setUint16(_.chan7Raw);
      writer.setUint16(_.chan8Raw);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint16(_.chan9Raw);
      writer.setUint16(_.chan10Raw);
      writer.setUint16(_.chan11Raw);
      writer.setUint16(_.chan12Raw);
      writer.setUint16(_.chan13Raw);
      writer.setUint16(_.chan14Raw);
      writer.setUint16(_.chan15Raw);
      writer.setUint16(_.chan16Raw);
      writer.setUint16(_.chan17Raw);
      writer.setUint16(_.chan18Raw);
      return writer.finish();
    },
  },
  MISSION_ITEM_INT: {
    id: 73,
    crcExtra: 38,
    decode(payload: Uint8Array): MissionItemInt {
      const reader = createReader(payload, 38);
      const param1 = reader.getFloat32();
      const param2 = reader.getFloat32();
      const param3 = reader.getFloat32();
      const param4 = reader.getFloat32();
      const x = reader.getInt32();
      const y = reader.getInt32();
      const z = reader.getFloat32();
      const seq = reader.getUint16();
      const command = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const frame = reader.getUint8();
      const current = reader.getUint8();
      const autocontinue = reader.getUint8();
      const missionType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        seq,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        command: mavCmdInverseMap.get(command) as MavCmd,
        current,
        autocontinue,
        param1,
        param2,
        param3,
        param4,
        x,
        y,
        z,
        missionType: mavMissionTypeInverseMap.get(missionType) as MavMissionType,
      };
    },
    encode(_: MissionItemInt) {
      const writer = createWriter(38);
      writer.setFloat32(_.param1);
      writer.setFloat32(_.param2);
      writer.setFloat32(_.param3);
      writer.setFloat32(_.param4);
      writer.setInt32(_.x);
      writer.setInt32(_.y);
      writer.setFloat32(_.z);
      writer.setUint16(_.seq);
      writer.setUint16(lookup(_.command, mavCmdMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      writer.setUint8(_.current);
      writer.setUint8(_.autocontinue);
      writer.setUint8(lookup(_.missionType, mavMissionTypeMap));
      return writer.finish();
    },
  },
  VFR_HUD: {
    id: 74,
    crcExtra: 20,
    decode(payload: Uint8Array): VfrHud {
      const reader = createReader(payload, 20);
      const airspeed = reader.getFloat32();
      const groundspeed = reader.getFloat32();
      const alt = reader.getFloat32();
      const climb = reader.getFloat32();
      const heading = reader.getInt16();
      const throttle = reader.getUint16();
      return {
        airspeed,
        groundspeed,
        heading,
        throttle,
        alt,
        climb,
      };
    },
    encode(_: VfrHud) {
      const writer = createWriter(20);
      writer.setFloat32(_.airspeed);
      writer.setFloat32(_.groundspeed);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.climb);
      writer.setInt16(_.heading);
      writer.setUint16(_.throttle);
      return writer.finish();
    },
  },
  COMMAND_INT: {
    id: 75,
    crcExtra: 158,
    decode(payload: Uint8Array): CommandInt {
      const reader = createReader(payload, 35);
      const param1 = reader.getFloat32();
      const param2 = reader.getFloat32();
      const param3 = reader.getFloat32();
      const param4 = reader.getFloat32();
      const x = reader.getInt32();
      const y = reader.getInt32();
      const z = reader.getFloat32();
      const command = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const frame = reader.getUint8();
      const current = reader.getUint8();
      const autocontinue = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        command: mavCmdInverseMap.get(command) as MavCmd,
        current,
        autocontinue,
        param1,
        param2,
        param3,
        param4,
        x,
        y,
        z,
      };
    },
    encode(_: CommandInt) {
      const writer = createWriter(35);
      writer.setFloat32(_.param1);
      writer.setFloat32(_.param2);
      writer.setFloat32(_.param3);
      writer.setFloat32(_.param4);
      writer.setInt32(_.x);
      writer.setInt32(_.y);
      writer.setFloat32(_.z);
      writer.setUint16(lookup(_.command, mavCmdMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      writer.setUint8(_.current);
      writer.setUint8(_.autocontinue);
      return writer.finish();
    },
  },
  COMMAND_LONG: {
    id: 76,
    crcExtra: 152,
    decode(payload: Uint8Array): CommandLong {
      const reader = createReader(payload, 33);
      const param1 = reader.getFloat32();
      const param2 = reader.getFloat32();
      const param3 = reader.getFloat32();
      const param4 = reader.getFloat32();
      const param5 = reader.getFloat32();
      const param6 = reader.getFloat32();
      const param7 = reader.getFloat32();
      const command = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const confirmation = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        command: mavCmdInverseMap.get(command) as MavCmd,
        confirmation,
        param1,
        param2,
        param3,
        param4,
        param5,
        param6,
        param7,
      };
    },
    encode(_: CommandLong) {
      const writer = createWriter(33);
      writer.setFloat32(_.param1);
      writer.setFloat32(_.param2);
      writer.setFloat32(_.param3);
      writer.setFloat32(_.param4);
      writer.setFloat32(_.param5);
      writer.setFloat32(_.param6);
      writer.setFloat32(_.param7);
      writer.setUint16(lookup(_.command, mavCmdMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.confirmation);
      return writer.finish();
    },
  },
  COMMAND_ACK: {
    id: 77,
    crcExtra: 143,
    decode(payload: Uint8Array): CommandAck {
      const reader = createReader(payload, 10);
      const command = reader.getUint16();
      const result = reader.getUint8();
      const progress = reader.getUint8();
      const resultParam2 = reader.getInt32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        command: mavCmdInverseMap.get(command) as MavCmd,
        result: mavResultInverseMap.get(result) as MavResult,
        progress,
        resultParam2,
        targetSystem,
        targetComponent,
      };
    },
    encode(_: CommandAck) {
      const writer = createWriter(10);
      writer.setUint16(lookup(_.command, mavCmdMap));
      writer.setUint8(lookup(_.result, mavResultMap));
      writer.setUint8(_.progress);
      writer.setInt32(_.resultParam2);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  MANUAL_SETPOINT: {
    id: 81,
    crcExtra: 106,
    decode(payload: Uint8Array): ManualSetpoint {
      const reader = createReader(payload, 22);
      const timeBootMs = reader.getUint32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const thrust = reader.getFloat32();
      const modeSwitch = reader.getUint8();
      const manualOverrideSwitch = reader.getUint8();
      return {
        timeBootMs,
        roll,
        pitch,
        yaw,
        thrust,
        modeSwitch,
        manualOverrideSwitch,
      };
    },
    encode(_: ManualSetpoint) {
      const writer = createWriter(22);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.thrust);
      writer.setUint8(_.modeSwitch);
      writer.setUint8(_.manualOverrideSwitch);
      return writer.finish();
    },
  },
  SET_ATTITUDE_TARGET: {
    id: 82,
    crcExtra: 49,
    decode(payload: Uint8Array): SetAttitudeTarget {
      const reader = createReader(payload, 39);
      const timeBootMs = reader.getUint32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const bodyRollRate = reader.getFloat32();
      const bodyPitchRate = reader.getFloat32();
      const bodyYawRate = reader.getFloat32();
      const thrust = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const typeMask = reader.getUint8();
      return {
        timeBootMs,
        targetSystem,
        targetComponent,
        typeMask: decodeBitmask(typeMask, attitudeTargetTypemaskInverseMap),
        q,
        bodyRollRate,
        bodyPitchRate,
        bodyYawRate,
        thrust,
      };
    },
    encode(_: SetAttitudeTarget) {
      const writer = createWriter(39);
      writer.setUint32(_.timeBootMs);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.bodyRollRate);
      writer.setFloat32(_.bodyPitchRate);
      writer.setFloat32(_.bodyYawRate);
      writer.setFloat32(_.thrust);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(encodeBitmask(_.typeMask, attitudeTargetTypemaskMap));
      return writer.finish();
    },
  },
  ATTITUDE_TARGET: {
    id: 83,
    crcExtra: 22,
    decode(payload: Uint8Array): AttitudeTarget {
      const reader = createReader(payload, 37);
      const timeBootMs = reader.getUint32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const bodyRollRate = reader.getFloat32();
      const bodyPitchRate = reader.getFloat32();
      const bodyYawRate = reader.getFloat32();
      const thrust = reader.getFloat32();
      const typeMask = reader.getUint8();
      return {
        timeBootMs,
        typeMask: decodeBitmask(typeMask, attitudeTargetTypemaskInverseMap),
        q,
        bodyRollRate,
        bodyPitchRate,
        bodyYawRate,
        thrust,
      };
    },
    encode(_: AttitudeTarget) {
      const writer = createWriter(37);
      writer.setUint32(_.timeBootMs);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.bodyRollRate);
      writer.setFloat32(_.bodyPitchRate);
      writer.setFloat32(_.bodyYawRate);
      writer.setFloat32(_.thrust);
      writer.setUint8(encodeBitmask(_.typeMask, attitudeTargetTypemaskMap));
      return writer.finish();
    },
  },
  SET_POSITION_TARGET_LOCAL_NED: {
    id: 84,
    crcExtra: 143,
    decode(payload: Uint8Array): SetPositionTargetLocalNed {
      const reader = createReader(payload, 53);
      const timeBootMs = reader.getUint32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const afx = reader.getFloat32();
      const afy = reader.getFloat32();
      const afz = reader.getFloat32();
      const yaw = reader.getFloat32();
      const yawRate = reader.getFloat32();
      const typeMask = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const coordinateFrame = reader.getUint8();
      return {
        timeBootMs,
        targetSystem,
        targetComponent,
        coordinateFrame: mavFrameInverseMap.get(coordinateFrame) as MavFrame,
        typeMask: decodeBitmask(typeMask, positionTargetTypemaskInverseMap),
        x,
        y,
        z,
        vx,
        vy,
        vz,
        afx,
        afy,
        afz,
        yaw,
        yawRate,
      };
    },
    encode(_: SetPositionTargetLocalNed) {
      const writer = createWriter(53);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.afx);
      writer.setFloat32(_.afy);
      writer.setFloat32(_.afz);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.yawRate);
      writer.setUint16(encodeBitmask(_.typeMask, positionTargetTypemaskMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.coordinateFrame, mavFrameMap));
      return writer.finish();
    },
  },
  POSITION_TARGET_LOCAL_NED: {
    id: 85,
    crcExtra: 140,
    decode(payload: Uint8Array): PositionTargetLocalNed {
      const reader = createReader(payload, 51);
      const timeBootMs = reader.getUint32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const afx = reader.getFloat32();
      const afy = reader.getFloat32();
      const afz = reader.getFloat32();
      const yaw = reader.getFloat32();
      const yawRate = reader.getFloat32();
      const typeMask = reader.getUint16();
      const coordinateFrame = reader.getUint8();
      return {
        timeBootMs,
        coordinateFrame: mavFrameInverseMap.get(coordinateFrame) as MavFrame,
        typeMask: decodeBitmask(typeMask, positionTargetTypemaskInverseMap),
        x,
        y,
        z,
        vx,
        vy,
        vz,
        afx,
        afy,
        afz,
        yaw,
        yawRate,
      };
    },
    encode(_: PositionTargetLocalNed) {
      const writer = createWriter(51);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.afx);
      writer.setFloat32(_.afy);
      writer.setFloat32(_.afz);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.yawRate);
      writer.setUint16(encodeBitmask(_.typeMask, positionTargetTypemaskMap));
      writer.setUint8(lookup(_.coordinateFrame, mavFrameMap));
      return writer.finish();
    },
  },
  SET_POSITION_TARGET_GLOBAL_INT: {
    id: 86,
    crcExtra: 5,
    decode(payload: Uint8Array): SetPositionTargetGlobalInt {
      const reader = createReader(payload, 53);
      const timeBootMs = reader.getUint32();
      const latInt = reader.getInt32();
      const lonInt = reader.getInt32();
      const alt = reader.getFloat32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const afx = reader.getFloat32();
      const afy = reader.getFloat32();
      const afz = reader.getFloat32();
      const yaw = reader.getFloat32();
      const yawRate = reader.getFloat32();
      const typeMask = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const coordinateFrame = reader.getUint8();
      return {
        timeBootMs,
        targetSystem,
        targetComponent,
        coordinateFrame: mavFrameInverseMap.get(coordinateFrame) as MavFrame,
        typeMask: decodeBitmask(typeMask, positionTargetTypemaskInverseMap),
        latInt,
        lonInt,
        alt,
        vx,
        vy,
        vz,
        afx,
        afy,
        afz,
        yaw,
        yawRate,
      };
    },
    encode(_: SetPositionTargetGlobalInt) {
      const writer = createWriter(53);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.latInt);
      writer.setInt32(_.lonInt);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.afx);
      writer.setFloat32(_.afy);
      writer.setFloat32(_.afz);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.yawRate);
      writer.setUint16(encodeBitmask(_.typeMask, positionTargetTypemaskMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.coordinateFrame, mavFrameMap));
      return writer.finish();
    },
  },
  POSITION_TARGET_GLOBAL_INT: {
    id: 87,
    crcExtra: 150,
    decode(payload: Uint8Array): PositionTargetGlobalInt {
      const reader = createReader(payload, 51);
      const timeBootMs = reader.getUint32();
      const latInt = reader.getInt32();
      const lonInt = reader.getInt32();
      const alt = reader.getFloat32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const afx = reader.getFloat32();
      const afy = reader.getFloat32();
      const afz = reader.getFloat32();
      const yaw = reader.getFloat32();
      const yawRate = reader.getFloat32();
      const typeMask = reader.getUint16();
      const coordinateFrame = reader.getUint8();
      return {
        timeBootMs,
        coordinateFrame: mavFrameInverseMap.get(coordinateFrame) as MavFrame,
        typeMask: decodeBitmask(typeMask, positionTargetTypemaskInverseMap),
        latInt,
        lonInt,
        alt,
        vx,
        vy,
        vz,
        afx,
        afy,
        afz,
        yaw,
        yawRate,
      };
    },
    encode(_: PositionTargetGlobalInt) {
      const writer = createWriter(51);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.latInt);
      writer.setInt32(_.lonInt);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.afx);
      writer.setFloat32(_.afy);
      writer.setFloat32(_.afz);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.yawRate);
      writer.setUint16(encodeBitmask(_.typeMask, positionTargetTypemaskMap));
      writer.setUint8(lookup(_.coordinateFrame, mavFrameMap));
      return writer.finish();
    },
  },
  LOCAL_POSITION_NED_SYSTEM_GLOBAL_OFFSET: {
    id: 89,
    crcExtra: 231,
    decode(payload: Uint8Array): LocalPositionNedSystemGlobalOffset {
      const reader = createReader(payload, 28);
      const timeBootMs = reader.getUint32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      return {
        timeBootMs,
        x,
        y,
        z,
        roll,
        pitch,
        yaw,
      };
    },
    encode(_: LocalPositionNedSystemGlobalOffset) {
      const writer = createWriter(28);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      return writer.finish();
    },
  },
  HIL_STATE: {
    id: 90,
    crcExtra: 183,
    decode(payload: Uint8Array): HilState {
      const reader = createReader(payload, 56);
      const timeUsec = reader.getBigUint64();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const rollspeed = reader.getFloat32();
      const pitchspeed = reader.getFloat32();
      const yawspeed = reader.getFloat32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const vx = reader.getInt16();
      const vy = reader.getInt16();
      const vz = reader.getInt16();
      const xacc = reader.getInt16();
      const yacc = reader.getInt16();
      const zacc = reader.getInt16();
      return {
        timeUsec,
        roll,
        pitch,
        yaw,
        rollspeed,
        pitchspeed,
        yawspeed,
        lat,
        lon,
        alt,
        vx,
        vy,
        vz,
        xacc,
        yacc,
        zacc,
      };
    },
    encode(_: HilState) {
      const writer = createWriter(56);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.rollspeed);
      writer.setFloat32(_.pitchspeed);
      writer.setFloat32(_.yawspeed);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setInt16(_.vx);
      writer.setInt16(_.vy);
      writer.setInt16(_.vz);
      writer.setInt16(_.xacc);
      writer.setInt16(_.yacc);
      writer.setInt16(_.zacc);
      return writer.finish();
    },
  },
  HIL_CONTROLS: {
    id: 91,
    crcExtra: 63,
    decode(payload: Uint8Array): HilControls {
      const reader = createReader(payload, 42);
      const timeUsec = reader.getBigUint64();
      const rollAilerons = reader.getFloat32();
      const pitchElevator = reader.getFloat32();
      const yawRudder = reader.getFloat32();
      const throttle = reader.getFloat32();
      const aux1 = reader.getFloat32();
      const aux2 = reader.getFloat32();
      const aux3 = reader.getFloat32();
      const aux4 = reader.getFloat32();
      const mode = reader.getUint8();
      const navMode = reader.getUint8();
      return {
        timeUsec,
        rollAilerons,
        pitchElevator,
        yawRudder,
        throttle,
        aux1,
        aux2,
        aux3,
        aux4,
        mode: mavModeInverseMap.get(mode) as MavMode,
        navMode,
      };
    },
    encode(_: HilControls) {
      const writer = createWriter(42);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.rollAilerons);
      writer.setFloat32(_.pitchElevator);
      writer.setFloat32(_.yawRudder);
      writer.setFloat32(_.throttle);
      writer.setFloat32(_.aux1);
      writer.setFloat32(_.aux2);
      writer.setFloat32(_.aux3);
      writer.setFloat32(_.aux4);
      writer.setUint8(lookup(_.mode, mavModeMap));
      writer.setUint8(_.navMode);
      return writer.finish();
    },
  },
  HIL_RC_INPUTS_RAW: {
    id: 92,
    crcExtra: 54,
    decode(payload: Uint8Array): HilRcInputsRaw {
      const reader = createReader(payload, 33);
      const timeUsec = reader.getBigUint64();
      const chan1Raw = reader.getUint16();
      const chan2Raw = reader.getUint16();
      const chan3Raw = reader.getUint16();
      const chan4Raw = reader.getUint16();
      const chan5Raw = reader.getUint16();
      const chan6Raw = reader.getUint16();
      const chan7Raw = reader.getUint16();
      const chan8Raw = reader.getUint16();
      const chan9Raw = reader.getUint16();
      const chan10Raw = reader.getUint16();
      const chan11Raw = reader.getUint16();
      const chan12Raw = reader.getUint16();
      const rssi = reader.getUint8();
      return {
        timeUsec,
        chan1Raw,
        chan2Raw,
        chan3Raw,
        chan4Raw,
        chan5Raw,
        chan6Raw,
        chan7Raw,
        chan8Raw,
        chan9Raw,
        chan10Raw,
        chan11Raw,
        chan12Raw,
        rssi,
      };
    },
    encode(_: HilRcInputsRaw) {
      const writer = createWriter(33);
      writer.setBigUint64(_.timeUsec);
      writer.setUint16(_.chan1Raw);
      writer.setUint16(_.chan2Raw);
      writer.setUint16(_.chan3Raw);
      writer.setUint16(_.chan4Raw);
      writer.setUint16(_.chan5Raw);
      writer.setUint16(_.chan6Raw);
      writer.setUint16(_.chan7Raw);
      writer.setUint16(_.chan8Raw);
      writer.setUint16(_.chan9Raw);
      writer.setUint16(_.chan10Raw);
      writer.setUint16(_.chan11Raw);
      writer.setUint16(_.chan12Raw);
      writer.setUint8(_.rssi);
      return writer.finish();
    },
  },
  HIL_ACTUATOR_CONTROLS: {
    id: 93,
    crcExtra: 47,
    decode(payload: Uint8Array): HilActuatorControls {
      const reader = createReader(payload, 81);
      const timeUsec = reader.getBigUint64();
      const flags = reader.getBigUint64();
      const controls = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const mode = reader.getUint8();
      return {
        timeUsec,
        controls,
        mode: decodeBitmask(mode, mavModeFlagInverseMap),
        flags,
      };
    },
    encode(_: HilActuatorControls) {
      const writer = createWriter(81);
      writer.setBigUint64(_.timeUsec);
      writer.setBigUint64(_.flags);
      _.controls.forEach(writer.setFloat32);
      writer.setUint8(encodeBitmask(_.mode, mavModeFlagMap));
      return writer.finish();
    },
  },
  OPTICAL_FLOW: {
    id: 100,
    crcExtra: 175,
    decode(payload: Uint8Array): OpticalFlow {
      const reader = createReader(payload, 34);
      const timeUsec = reader.getBigUint64();
      const flowCompMX = reader.getFloat32();
      const flowCompMY = reader.getFloat32();
      const groundDistance = reader.getFloat32();
      const flowX = reader.getInt16();
      const flowY = reader.getInt16();
      const sensorId = reader.getUint8();
      const quality = reader.getUint8();
      const flowRateX = reader.getFloat32();
      const flowRateY = reader.getFloat32();
      return {
        timeUsec,
        sensorId,
        flowX,
        flowY,
        flowCompMX,
        flowCompMY,
        quality,
        groundDistance,
        flowRateX,
        flowRateY,
      };
    },
    encode(_: OpticalFlow) {
      const writer = createWriter(34);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.flowCompMX);
      writer.setFloat32(_.flowCompMY);
      writer.setFloat32(_.groundDistance);
      writer.setInt16(_.flowX);
      writer.setInt16(_.flowY);
      writer.setUint8(_.sensorId);
      writer.setUint8(_.quality);
      writer.setFloat32(_.flowRateX);
      writer.setFloat32(_.flowRateY);
      return writer.finish();
    },
  },
  GLOBAL_VISION_POSITION_ESTIMATE: {
    id: 101,
    crcExtra: 102,
    decode(payload: Uint8Array): GlobalVisionPositionEstimate {
      const reader = createReader(payload, 117);
      const usec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const resetCounter = reader.getUint8();
      return {
        usec,
        x,
        y,
        z,
        roll,
        pitch,
        yaw,
        covariance,
        resetCounter,
      };
    },
    encode(_: GlobalVisionPositionEstimate) {
      const writer = createWriter(117);
      writer.setBigUint64(_.usec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      _.covariance.forEach(writer.setFloat32);
      writer.setUint8(_.resetCounter);
      return writer.finish();
    },
  },
  VISION_POSITION_ESTIMATE: {
    id: 102,
    crcExtra: 158,
    decode(payload: Uint8Array): VisionPositionEstimate {
      const reader = createReader(payload, 117);
      const usec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const resetCounter = reader.getUint8();
      return {
        usec,
        x,
        y,
        z,
        roll,
        pitch,
        yaw,
        covariance,
        resetCounter,
      };
    },
    encode(_: VisionPositionEstimate) {
      const writer = createWriter(117);
      writer.setBigUint64(_.usec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      _.covariance.forEach(writer.setFloat32);
      writer.setUint8(_.resetCounter);
      return writer.finish();
    },
  },
  VISION_SPEED_ESTIMATE: {
    id: 103,
    crcExtra: 208,
    decode(payload: Uint8Array): VisionSpeedEstimate {
      const reader = createReader(payload, 57);
      const usec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const resetCounter = reader.getUint8();
      return {
        usec,
        x,
        y,
        z,
        covariance,
        resetCounter,
      };
    },
    encode(_: VisionSpeedEstimate) {
      const writer = createWriter(57);
      writer.setBigUint64(_.usec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      _.covariance.forEach(writer.setFloat32);
      writer.setUint8(_.resetCounter);
      return writer.finish();
    },
  },
  VICON_POSITION_ESTIMATE: {
    id: 104,
    crcExtra: 56,
    decode(payload: Uint8Array): ViconPositionEstimate {
      const reader = createReader(payload, 116);
      const usec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      return {
        usec,
        x,
        y,
        z,
        roll,
        pitch,
        yaw,
        covariance,
      };
    },
    encode(_: ViconPositionEstimate) {
      const writer = createWriter(116);
      writer.setBigUint64(_.usec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      _.covariance.forEach(writer.setFloat32);
      return writer.finish();
    },
  },
  HIGHRES_IMU: {
    id: 105,
    crcExtra: 93,
    decode(payload: Uint8Array): HighresImu {
      const reader = createReader(payload, 63);
      const timeUsec = reader.getBigUint64();
      const xacc = reader.getFloat32();
      const yacc = reader.getFloat32();
      const zacc = reader.getFloat32();
      const xgyro = reader.getFloat32();
      const ygyro = reader.getFloat32();
      const zgyro = reader.getFloat32();
      const xmag = reader.getFloat32();
      const ymag = reader.getFloat32();
      const zmag = reader.getFloat32();
      const absPressure = reader.getFloat32();
      const diffPressure = reader.getFloat32();
      const pressureAlt = reader.getFloat32();
      const temperature = reader.getFloat32();
      const fieldsUpdated = reader.getUint16();
      const id = reader.getUint8();
      return {
        timeUsec,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        xmag,
        ymag,
        zmag,
        absPressure,
        diffPressure,
        pressureAlt,
        temperature,
        fieldsUpdated,
        id,
      };
    },
    encode(_: HighresImu) {
      const writer = createWriter(63);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.xacc);
      writer.setFloat32(_.yacc);
      writer.setFloat32(_.zacc);
      writer.setFloat32(_.xgyro);
      writer.setFloat32(_.ygyro);
      writer.setFloat32(_.zgyro);
      writer.setFloat32(_.xmag);
      writer.setFloat32(_.ymag);
      writer.setFloat32(_.zmag);
      writer.setFloat32(_.absPressure);
      writer.setFloat32(_.diffPressure);
      writer.setFloat32(_.pressureAlt);
      writer.setFloat32(_.temperature);
      writer.setUint16(_.fieldsUpdated);
      writer.setUint8(_.id);
      return writer.finish();
    },
  },
  OPTICAL_FLOW_RAD: {
    id: 106,
    crcExtra: 138,
    decode(payload: Uint8Array): OpticalFlowRad {
      const reader = createReader(payload, 44);
      const timeUsec = reader.getBigUint64();
      const integrationTimeUs = reader.getUint32();
      const integratedX = reader.getFloat32();
      const integratedY = reader.getFloat32();
      const integratedXgyro = reader.getFloat32();
      const integratedYgyro = reader.getFloat32();
      const integratedZgyro = reader.getFloat32();
      const timeDeltaDistanceUs = reader.getUint32();
      const distance = reader.getFloat32();
      const temperature = reader.getInt16();
      const sensorId = reader.getUint8();
      const quality = reader.getUint8();
      return {
        timeUsec,
        sensorId,
        integrationTimeUs,
        integratedX,
        integratedY,
        integratedXgyro,
        integratedYgyro,
        integratedZgyro,
        temperature,
        quality,
        timeDeltaDistanceUs,
        distance,
      };
    },
    encode(_: OpticalFlowRad) {
      const writer = createWriter(44);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.integrationTimeUs);
      writer.setFloat32(_.integratedX);
      writer.setFloat32(_.integratedY);
      writer.setFloat32(_.integratedXgyro);
      writer.setFloat32(_.integratedYgyro);
      writer.setFloat32(_.integratedZgyro);
      writer.setUint32(_.timeDeltaDistanceUs);
      writer.setFloat32(_.distance);
      writer.setInt16(_.temperature);
      writer.setUint8(_.sensorId);
      writer.setUint8(_.quality);
      return writer.finish();
    },
  },
  HIL_SENSOR: {
    id: 107,
    crcExtra: 108,
    decode(payload: Uint8Array): HilSensor {
      const reader = createReader(payload, 65);
      const timeUsec = reader.getBigUint64();
      const xacc = reader.getFloat32();
      const yacc = reader.getFloat32();
      const zacc = reader.getFloat32();
      const xgyro = reader.getFloat32();
      const ygyro = reader.getFloat32();
      const zgyro = reader.getFloat32();
      const xmag = reader.getFloat32();
      const ymag = reader.getFloat32();
      const zmag = reader.getFloat32();
      const absPressure = reader.getFloat32();
      const diffPressure = reader.getFloat32();
      const pressureAlt = reader.getFloat32();
      const temperature = reader.getFloat32();
      const fieldsUpdated = reader.getUint32();
      const id = reader.getUint8();
      return {
        timeUsec,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        xmag,
        ymag,
        zmag,
        absPressure,
        diffPressure,
        pressureAlt,
        temperature,
        fieldsUpdated,
        id,
      };
    },
    encode(_: HilSensor) {
      const writer = createWriter(65);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.xacc);
      writer.setFloat32(_.yacc);
      writer.setFloat32(_.zacc);
      writer.setFloat32(_.xgyro);
      writer.setFloat32(_.ygyro);
      writer.setFloat32(_.zgyro);
      writer.setFloat32(_.xmag);
      writer.setFloat32(_.ymag);
      writer.setFloat32(_.zmag);
      writer.setFloat32(_.absPressure);
      writer.setFloat32(_.diffPressure);
      writer.setFloat32(_.pressureAlt);
      writer.setFloat32(_.temperature);
      writer.setUint32(_.fieldsUpdated);
      writer.setUint8(_.id);
      return writer.finish();
    },
  },
  SIM_STATE: {
    id: 108,
    crcExtra: 32,
    decode(payload: Uint8Array): SimState {
      const reader = createReader(payload, 92);
      const q1 = reader.getFloat32();
      const q2 = reader.getFloat32();
      const q3 = reader.getFloat32();
      const q4 = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const xacc = reader.getFloat32();
      const yacc = reader.getFloat32();
      const zacc = reader.getFloat32();
      const xgyro = reader.getFloat32();
      const ygyro = reader.getFloat32();
      const zgyro = reader.getFloat32();
      const lat = reader.getFloat32();
      const lon = reader.getFloat32();
      const alt = reader.getFloat32();
      const stdDevHorz = reader.getFloat32();
      const stdDevVert = reader.getFloat32();
      const vn = reader.getFloat32();
      const ve = reader.getFloat32();
      const vd = reader.getFloat32();
      const latInt = reader.getInt32();
      const lonInt = reader.getInt32();
      return {
        q1,
        q2,
        q3,
        q4,
        roll,
        pitch,
        yaw,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        lat,
        lon,
        alt,
        stdDevHorz,
        stdDevVert,
        vn,
        ve,
        vd,
        latInt,
        lonInt,
      };
    },
    encode(_: SimState) {
      const writer = createWriter(92);
      writer.setFloat32(_.q1);
      writer.setFloat32(_.q2);
      writer.setFloat32(_.q3);
      writer.setFloat32(_.q4);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.xacc);
      writer.setFloat32(_.yacc);
      writer.setFloat32(_.zacc);
      writer.setFloat32(_.xgyro);
      writer.setFloat32(_.ygyro);
      writer.setFloat32(_.zgyro);
      writer.setFloat32(_.lat);
      writer.setFloat32(_.lon);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.stdDevHorz);
      writer.setFloat32(_.stdDevVert);
      writer.setFloat32(_.vn);
      writer.setFloat32(_.ve);
      writer.setFloat32(_.vd);
      writer.setInt32(_.latInt);
      writer.setInt32(_.lonInt);
      return writer.finish();
    },
  },
  RADIO_STATUS: {
    id: 109,
    crcExtra: 185,
    decode(payload: Uint8Array): RadioStatus {
      const reader = createReader(payload, 9);
      const rxerrors = reader.getUint16();
      const fixed = reader.getUint16();
      const rssi = reader.getUint8();
      const remrssi = reader.getUint8();
      const txbuf = reader.getUint8();
      const noise = reader.getUint8();
      const remnoise = reader.getUint8();
      return {
        rssi,
        remrssi,
        txbuf,
        noise,
        remnoise,
        rxerrors,
        fixed,
      };
    },
    encode(_: RadioStatus) {
      const writer = createWriter(9);
      writer.setUint16(_.rxerrors);
      writer.setUint16(_.fixed);
      writer.setUint8(_.rssi);
      writer.setUint8(_.remrssi);
      writer.setUint8(_.txbuf);
      writer.setUint8(_.noise);
      writer.setUint8(_.remnoise);
      return writer.finish();
    },
  },
  FILE_TRANSFER_PROTOCOL: {
    id: 110,
    crcExtra: 84,
    decode(payload: Uint8Array): FileTransferProtocol {
      const reader = createReader(payload, 254);
      const targetNetwork = reader.getUint8();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const payload_ = reader.getUint8Array(251);
      return {
        targetNetwork,
        targetSystem,
        targetComponent,
        payload_,
      };
    },
    encode(_: FileTransferProtocol) {
      const writer = createWriter(254);
      writer.setUint8(_.targetNetwork);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.payload_, 251);
      return writer.finish();
    },
  },
  TIMESYNC: {
    id: 111,
    crcExtra: 34,
    decode(payload: Uint8Array): Timesync {
      const reader = createReader(payload, 16);
      const tc1 = reader.getBigInt64();
      const ts1 = reader.getBigInt64();
      return {
        tc1,
        ts1,
      };
    },
    encode(_: Timesync) {
      const writer = createWriter(16);
      writer.setBigInt64(_.tc1);
      writer.setBigInt64(_.ts1);
      return writer.finish();
    },
  },
  CAMERA_TRIGGER: {
    id: 112,
    crcExtra: 174,
    decode(payload: Uint8Array): CameraTrigger {
      const reader = createReader(payload, 12);
      const timeUsec = reader.getBigUint64();
      const seq = reader.getUint32();
      return {
        timeUsec,
        seq,
      };
    },
    encode(_: CameraTrigger) {
      const writer = createWriter(12);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.seq);
      return writer.finish();
    },
  },
  HIL_GPS: {
    id: 113,
    crcExtra: 124,
    decode(payload: Uint8Array): HilGps {
      const reader = createReader(payload, 39);
      const timeUsec = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const eph = reader.getUint16();
      const epv = reader.getUint16();
      const vel = reader.getUint16();
      const vn = reader.getInt16();
      const ve = reader.getInt16();
      const vd = reader.getInt16();
      const cog = reader.getUint16();
      const fixType = reader.getUint8();
      const satellitesVisible = reader.getUint8();
      const id = reader.getUint8();
      const yaw = reader.getUint16();
      return {
        timeUsec,
        fixType,
        lat,
        lon,
        alt,
        eph,
        epv,
        vel,
        vn,
        ve,
        vd,
        cog,
        satellitesVisible,
        id,
        yaw,
      };
    },
    encode(_: HilGps) {
      const writer = createWriter(39);
      writer.setBigUint64(_.timeUsec);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setUint16(_.eph);
      writer.setUint16(_.epv);
      writer.setUint16(_.vel);
      writer.setInt16(_.vn);
      writer.setInt16(_.ve);
      writer.setInt16(_.vd);
      writer.setUint16(_.cog);
      writer.setUint8(_.fixType);
      writer.setUint8(_.satellitesVisible);
      writer.setUint8(_.id);
      writer.setUint16(_.yaw);
      return writer.finish();
    },
  },
  HIL_OPTICAL_FLOW: {
    id: 114,
    crcExtra: 237,
    decode(payload: Uint8Array): HilOpticalFlow {
      const reader = createReader(payload, 44);
      const timeUsec = reader.getBigUint64();
      const integrationTimeUs = reader.getUint32();
      const integratedX = reader.getFloat32();
      const integratedY = reader.getFloat32();
      const integratedXgyro = reader.getFloat32();
      const integratedYgyro = reader.getFloat32();
      const integratedZgyro = reader.getFloat32();
      const timeDeltaDistanceUs = reader.getUint32();
      const distance = reader.getFloat32();
      const temperature = reader.getInt16();
      const sensorId = reader.getUint8();
      const quality = reader.getUint8();
      return {
        timeUsec,
        sensorId,
        integrationTimeUs,
        integratedX,
        integratedY,
        integratedXgyro,
        integratedYgyro,
        integratedZgyro,
        temperature,
        quality,
        timeDeltaDistanceUs,
        distance,
      };
    },
    encode(_: HilOpticalFlow) {
      const writer = createWriter(44);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.integrationTimeUs);
      writer.setFloat32(_.integratedX);
      writer.setFloat32(_.integratedY);
      writer.setFloat32(_.integratedXgyro);
      writer.setFloat32(_.integratedYgyro);
      writer.setFloat32(_.integratedZgyro);
      writer.setUint32(_.timeDeltaDistanceUs);
      writer.setFloat32(_.distance);
      writer.setInt16(_.temperature);
      writer.setUint8(_.sensorId);
      writer.setUint8(_.quality);
      return writer.finish();
    },
  },
  HIL_STATE_QUATERNION: {
    id: 115,
    crcExtra: 4,
    decode(payload: Uint8Array): HilStateQuaternion {
      const reader = createReader(payload, 64);
      const timeUsec = reader.getBigUint64();
      const attitudeQuaternion = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const rollspeed = reader.getFloat32();
      const pitchspeed = reader.getFloat32();
      const yawspeed = reader.getFloat32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const vx = reader.getInt16();
      const vy = reader.getInt16();
      const vz = reader.getInt16();
      const indAirspeed = reader.getUint16();
      const trueAirspeed = reader.getUint16();
      const xacc = reader.getInt16();
      const yacc = reader.getInt16();
      const zacc = reader.getInt16();
      return {
        timeUsec,
        attitudeQuaternion,
        rollspeed,
        pitchspeed,
        yawspeed,
        lat,
        lon,
        alt,
        vx,
        vy,
        vz,
        indAirspeed,
        trueAirspeed,
        xacc,
        yacc,
        zacc,
      };
    },
    encode(_: HilStateQuaternion) {
      const writer = createWriter(64);
      writer.setBigUint64(_.timeUsec);
      _.attitudeQuaternion.forEach(writer.setFloat32);
      writer.setFloat32(_.rollspeed);
      writer.setFloat32(_.pitchspeed);
      writer.setFloat32(_.yawspeed);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setInt16(_.vx);
      writer.setInt16(_.vy);
      writer.setInt16(_.vz);
      writer.setUint16(_.indAirspeed);
      writer.setUint16(_.trueAirspeed);
      writer.setInt16(_.xacc);
      writer.setInt16(_.yacc);
      writer.setInt16(_.zacc);
      return writer.finish();
    },
  },
  SCALED_IMU2: {
    id: 116,
    crcExtra: 76,
    decode(payload: Uint8Array): ScaledImu2 {
      const reader = createReader(payload, 24);
      const timeBootMs = reader.getUint32();
      const xacc = reader.getInt16();
      const yacc = reader.getInt16();
      const zacc = reader.getInt16();
      const xgyro = reader.getInt16();
      const ygyro = reader.getInt16();
      const zgyro = reader.getInt16();
      const xmag = reader.getInt16();
      const ymag = reader.getInt16();
      const zmag = reader.getInt16();
      const temperature = reader.getInt16();
      return {
        timeBootMs,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        xmag,
        ymag,
        zmag,
        temperature,
      };
    },
    encode(_: ScaledImu2) {
      const writer = createWriter(24);
      writer.setUint32(_.timeBootMs);
      writer.setInt16(_.xacc);
      writer.setInt16(_.yacc);
      writer.setInt16(_.zacc);
      writer.setInt16(_.xgyro);
      writer.setInt16(_.ygyro);
      writer.setInt16(_.zgyro);
      writer.setInt16(_.xmag);
      writer.setInt16(_.ymag);
      writer.setInt16(_.zmag);
      writer.setInt16(_.temperature);
      return writer.finish();
    },
  },
  LOG_REQUEST_LIST: {
    id: 117,
    crcExtra: 128,
    decode(payload: Uint8Array): LogRequestList {
      const reader = createReader(payload, 6);
      const start = reader.getUint16();
      const end = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        start,
        end,
      };
    },
    encode(_: LogRequestList) {
      const writer = createWriter(6);
      writer.setUint16(_.start);
      writer.setUint16(_.end);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  LOG_ENTRY: {
    id: 118,
    crcExtra: 56,
    decode(payload: Uint8Array): LogEntry {
      const reader = createReader(payload, 14);
      const timeUtc = reader.getUint32();
      const size = reader.getUint32();
      const id = reader.getUint16();
      const numLogs = reader.getUint16();
      const lastLogNum = reader.getUint16();
      return {
        id,
        numLogs,
        lastLogNum,
        timeUtc,
        size,
      };
    },
    encode(_: LogEntry) {
      const writer = createWriter(14);
      writer.setUint32(_.timeUtc);
      writer.setUint32(_.size);
      writer.setUint16(_.id);
      writer.setUint16(_.numLogs);
      writer.setUint16(_.lastLogNum);
      return writer.finish();
    },
  },
  LOG_REQUEST_DATA: {
    id: 119,
    crcExtra: 116,
    decode(payload: Uint8Array): LogRequestData {
      const reader = createReader(payload, 12);
      const ofs = reader.getUint32();
      const count = reader.getUint32();
      const id = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        id,
        ofs,
        count,
      };
    },
    encode(_: LogRequestData) {
      const writer = createWriter(12);
      writer.setUint32(_.ofs);
      writer.setUint32(_.count);
      writer.setUint16(_.id);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  LOG_DATA: {
    id: 120,
    crcExtra: 134,
    decode(payload: Uint8Array): LogData {
      const reader = createReader(payload, 97);
      const ofs = reader.getUint32();
      const id = reader.getUint16();
      const count = reader.getUint8();
      const data = reader.getUint8Array(90);
      return {
        id,
        ofs,
        count,
        data,
      };
    },
    encode(_: LogData) {
      const writer = createWriter(97);
      writer.setUint32(_.ofs);
      writer.setUint16(_.id);
      writer.setUint8(_.count);
      writer.setUint8Array(_.data, 90);
      return writer.finish();
    },
  },
  LOG_ERASE: {
    id: 121,
    crcExtra: 237,
    decode(payload: Uint8Array): LogErase {
      const reader = createReader(payload, 2);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
      };
    },
    encode(_: LogErase) {
      const writer = createWriter(2);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  LOG_REQUEST_END: {
    id: 122,
    crcExtra: 203,
    decode(payload: Uint8Array): LogRequestEnd {
      const reader = createReader(payload, 2);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
      };
    },
    encode(_: LogRequestEnd) {
      const writer = createWriter(2);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  GPS_INJECT_DATA: {
    id: 123,
    crcExtra: 250,
    decode(payload: Uint8Array): GpsInjectData {
      const reader = createReader(payload, 113);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(110);
      return {
        targetSystem,
        targetComponent,
        len,
        data,
      };
    },
    encode(_: GpsInjectData) {
      const writer = createWriter(113);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 110);
      return writer.finish();
    },
  },
  GPS2_RAW: {
    id: 124,
    crcExtra: 87,
    decode(payload: Uint8Array): Gps2Raw {
      const reader = createReader(payload, 57);
      const timeUsec = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const dgpsAge = reader.getUint32();
      const eph = reader.getUint16();
      const epv = reader.getUint16();
      const vel = reader.getUint16();
      const cog = reader.getUint16();
      const fixType = reader.getUint8();
      const satellitesVisible = reader.getUint8();
      const dgpsNumch = reader.getUint8();
      const yaw = reader.getUint16();
      const altEllipsoid = reader.getInt32();
      const hAcc = reader.getUint32();
      const vAcc = reader.getUint32();
      const velAcc = reader.getUint32();
      const hdgAcc = reader.getUint32();
      return {
        timeUsec,
        fixType: gpsFixTypeInverseMap.get(fixType) as GpsFixType,
        lat,
        lon,
        alt,
        eph,
        epv,
        vel,
        cog,
        satellitesVisible,
        dgpsNumch,
        dgpsAge,
        yaw,
        altEllipsoid,
        hAcc,
        vAcc,
        velAcc,
        hdgAcc,
      };
    },
    encode(_: Gps2Raw) {
      const writer = createWriter(57);
      writer.setBigUint64(_.timeUsec);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setUint32(_.dgpsAge);
      writer.setUint16(_.eph);
      writer.setUint16(_.epv);
      writer.setUint16(_.vel);
      writer.setUint16(_.cog);
      writer.setUint8(lookup(_.fixType, gpsFixTypeMap));
      writer.setUint8(_.satellitesVisible);
      writer.setUint8(_.dgpsNumch);
      writer.setUint16(_.yaw);
      writer.setInt32(_.altEllipsoid);
      writer.setUint32(_.hAcc);
      writer.setUint32(_.vAcc);
      writer.setUint32(_.velAcc);
      writer.setUint32(_.hdgAcc);
      return writer.finish();
    },
  },
  POWER_STATUS: {
    id: 125,
    crcExtra: 203,
    decode(payload: Uint8Array): PowerStatus {
      const reader = createReader(payload, 6);
      const vcc = reader.getUint16();
      const vservo = reader.getUint16();
      const flags = reader.getUint16();
      return {
        vcc,
        vservo,
        flags: decodeBitmask(flags, mavPowerStatusInverseMap),
      };
    },
    encode(_: PowerStatus) {
      const writer = createWriter(6);
      writer.setUint16(_.vcc);
      writer.setUint16(_.vservo);
      writer.setUint16(encodeBitmask(_.flags, mavPowerStatusMap));
      return writer.finish();
    },
  },
  SERIAL_CONTROL: {
    id: 126,
    crcExtra: 220,
    decode(payload: Uint8Array): SerialControl {
      const reader = createReader(payload, 79);
      const baudrate = reader.getUint32();
      const timeout = reader.getUint16();
      const device = reader.getUint8();
      const flags = reader.getUint8();
      const count = reader.getUint8();
      const data = reader.getUint8Array(70);
      return {
        device: serialControlDevInverseMap.get(device) as SerialControlDev,
        flags: decodeBitmask(flags, serialControlFlagInverseMap),
        timeout,
        baudrate,
        count,
        data,
      };
    },
    encode(_: SerialControl) {
      const writer = createWriter(79);
      writer.setUint32(_.baudrate);
      writer.setUint16(_.timeout);
      writer.setUint8(lookup(_.device, serialControlDevMap));
      writer.setUint8(encodeBitmask(_.flags, serialControlFlagMap));
      writer.setUint8(_.count);
      writer.setUint8Array(_.data, 70);
      return writer.finish();
    },
  },
  GPS_RTK: {
    id: 127,
    crcExtra: 25,
    decode(payload: Uint8Array): GpsRtk {
      const reader = createReader(payload, 35);
      const timeLastBaselineMs = reader.getUint32();
      const tow = reader.getUint32();
      const baselineAMm = reader.getInt32();
      const baselineBMm = reader.getInt32();
      const baselineCMm = reader.getInt32();
      const accuracy = reader.getUint32();
      const iarNumHypotheses = reader.getInt32();
      const wn = reader.getUint16();
      const rtkReceiverId = reader.getUint8();
      const rtkHealth = reader.getUint8();
      const rtkRate = reader.getUint8();
      const nsats = reader.getUint8();
      const baselineCoordsType = reader.getUint8();
      return {
        timeLastBaselineMs,
        rtkReceiverId,
        wn,
        tow,
        rtkHealth,
        rtkRate,
        nsats,
        baselineCoordsType: rtkBaselineCoordinateSystemInverseMap.get(baselineCoordsType) as RtkBaselineCoordinateSystem,
        baselineAMm,
        baselineBMm,
        baselineCMm,
        accuracy,
        iarNumHypotheses,
      };
    },
    encode(_: GpsRtk) {
      const writer = createWriter(35);
      writer.setUint32(_.timeLastBaselineMs);
      writer.setUint32(_.tow);
      writer.setInt32(_.baselineAMm);
      writer.setInt32(_.baselineBMm);
      writer.setInt32(_.baselineCMm);
      writer.setUint32(_.accuracy);
      writer.setInt32(_.iarNumHypotheses);
      writer.setUint16(_.wn);
      writer.setUint8(_.rtkReceiverId);
      writer.setUint8(_.rtkHealth);
      writer.setUint8(_.rtkRate);
      writer.setUint8(_.nsats);
      writer.setUint8(lookup(_.baselineCoordsType, rtkBaselineCoordinateSystemMap));
      return writer.finish();
    },
  },
  GPS2_RTK: {
    id: 128,
    crcExtra: 226,
    decode(payload: Uint8Array): Gps2Rtk {
      const reader = createReader(payload, 35);
      const timeLastBaselineMs = reader.getUint32();
      const tow = reader.getUint32();
      const baselineAMm = reader.getInt32();
      const baselineBMm = reader.getInt32();
      const baselineCMm = reader.getInt32();
      const accuracy = reader.getUint32();
      const iarNumHypotheses = reader.getInt32();
      const wn = reader.getUint16();
      const rtkReceiverId = reader.getUint8();
      const rtkHealth = reader.getUint8();
      const rtkRate = reader.getUint8();
      const nsats = reader.getUint8();
      const baselineCoordsType = reader.getUint8();
      return {
        timeLastBaselineMs,
        rtkReceiverId,
        wn,
        tow,
        rtkHealth,
        rtkRate,
        nsats,
        baselineCoordsType: rtkBaselineCoordinateSystemInverseMap.get(baselineCoordsType) as RtkBaselineCoordinateSystem,
        baselineAMm,
        baselineBMm,
        baselineCMm,
        accuracy,
        iarNumHypotheses,
      };
    },
    encode(_: Gps2Rtk) {
      const writer = createWriter(35);
      writer.setUint32(_.timeLastBaselineMs);
      writer.setUint32(_.tow);
      writer.setInt32(_.baselineAMm);
      writer.setInt32(_.baselineBMm);
      writer.setInt32(_.baselineCMm);
      writer.setUint32(_.accuracy);
      writer.setInt32(_.iarNumHypotheses);
      writer.setUint16(_.wn);
      writer.setUint8(_.rtkReceiverId);
      writer.setUint8(_.rtkHealth);
      writer.setUint8(_.rtkRate);
      writer.setUint8(_.nsats);
      writer.setUint8(lookup(_.baselineCoordsType, rtkBaselineCoordinateSystemMap));
      return writer.finish();
    },
  },
  SCALED_IMU3: {
    id: 129,
    crcExtra: 46,
    decode(payload: Uint8Array): ScaledImu3 {
      const reader = createReader(payload, 24);
      const timeBootMs = reader.getUint32();
      const xacc = reader.getInt16();
      const yacc = reader.getInt16();
      const zacc = reader.getInt16();
      const xgyro = reader.getInt16();
      const ygyro = reader.getInt16();
      const zgyro = reader.getInt16();
      const xmag = reader.getInt16();
      const ymag = reader.getInt16();
      const zmag = reader.getInt16();
      const temperature = reader.getInt16();
      return {
        timeBootMs,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        xmag,
        ymag,
        zmag,
        temperature,
      };
    },
    encode(_: ScaledImu3) {
      const writer = createWriter(24);
      writer.setUint32(_.timeBootMs);
      writer.setInt16(_.xacc);
      writer.setInt16(_.yacc);
      writer.setInt16(_.zacc);
      writer.setInt16(_.xgyro);
      writer.setInt16(_.ygyro);
      writer.setInt16(_.zgyro);
      writer.setInt16(_.xmag);
      writer.setInt16(_.ymag);
      writer.setInt16(_.zmag);
      writer.setInt16(_.temperature);
      return writer.finish();
    },
  },
  DATA_TRANSMISSION_HANDSHAKE: {
    id: 130,
    crcExtra: 29,
    decode(payload: Uint8Array): DataTransmissionHandshake {
      const reader = createReader(payload, 13);
      const size = reader.getUint32();
      const width = reader.getUint16();
      const height = reader.getUint16();
      const packets = reader.getUint16();
      const type = reader.getUint8();
      const payload_ = reader.getUint8();
      const jpgQuality = reader.getUint8();
      return {
        type: mavlinkDataStreamTypeInverseMap.get(type) as MavlinkDataStreamType,
        size,
        width,
        height,
        packets,
        payload_,
        jpgQuality,
      };
    },
    encode(_: DataTransmissionHandshake) {
      const writer = createWriter(13);
      writer.setUint32(_.size);
      writer.setUint16(_.width);
      writer.setUint16(_.height);
      writer.setUint16(_.packets);
      writer.setUint8(lookup(_.type, mavlinkDataStreamTypeMap));
      writer.setUint8(_.payload_);
      writer.setUint8(_.jpgQuality);
      return writer.finish();
    },
  },
  ENCAPSULATED_DATA: {
    id: 131,
    crcExtra: 223,
    decode(payload: Uint8Array): EncapsulatedData {
      const reader = createReader(payload, 255);
      const seqnr = reader.getUint16();
      const data = reader.getUint8Array(253);
      return {
        seqnr,
        data,
      };
    },
    encode(_: EncapsulatedData) {
      const writer = createWriter(255);
      writer.setUint16(_.seqnr);
      writer.setUint8Array(_.data, 253);
      return writer.finish();
    },
  },
  DISTANCE_SENSOR: {
    id: 132,
    crcExtra: 85,
    decode(payload: Uint8Array): DistanceSensor {
      const reader = createReader(payload, 39);
      const timeBootMs = reader.getUint32();
      const minDistance = reader.getUint16();
      const maxDistance = reader.getUint16();
      const currentDistance = reader.getUint16();
      const type = reader.getUint8();
      const id = reader.getUint8();
      const orientation = reader.getUint8();
      const covariance = reader.getUint8();
      const horizontalFov = reader.getFloat32();
      const verticalFov = reader.getFloat32();
      const quaternion = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const signalQuality = reader.getUint8();
      return {
        timeBootMs,
        minDistance,
        maxDistance,
        currentDistance,
        type: mavDistanceSensorInverseMap.get(type) as MavDistanceSensor,
        id,
        orientation: mavSensorOrientationInverseMap.get(orientation) as MavSensorOrientation,
        covariance,
        horizontalFov,
        verticalFov,
        quaternion,
        signalQuality,
      };
    },
    encode(_: DistanceSensor) {
      const writer = createWriter(39);
      writer.setUint32(_.timeBootMs);
      writer.setUint16(_.minDistance);
      writer.setUint16(_.maxDistance);
      writer.setUint16(_.currentDistance);
      writer.setUint8(lookup(_.type, mavDistanceSensorMap));
      writer.setUint8(_.id);
      writer.setUint8(lookup(_.orientation, mavSensorOrientationMap));
      writer.setUint8(_.covariance);
      writer.setFloat32(_.horizontalFov);
      writer.setFloat32(_.verticalFov);
      _.quaternion.forEach(writer.setFloat32);
      writer.setUint8(_.signalQuality);
      return writer.finish();
    },
  },
  TERRAIN_REQUEST: {
    id: 133,
    crcExtra: 6,
    decode(payload: Uint8Array): TerrainRequest {
      const reader = createReader(payload, 18);
      const mask = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const gridSpacing = reader.getUint16();
      return {
        lat,
        lon,
        gridSpacing,
        mask,
      };
    },
    encode(_: TerrainRequest) {
      const writer = createWriter(18);
      writer.setBigUint64(_.mask);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setUint16(_.gridSpacing);
      return writer.finish();
    },
  },
  TERRAIN_DATA: {
    id: 134,
    crcExtra: 229,
    decode(payload: Uint8Array): TerrainData {
      const reader = createReader(payload, 43);
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const gridSpacing = reader.getUint16();
      const data = [reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16(), reader.getInt16()] as const;
      const gridbit = reader.getUint8();
      return {
        lat,
        lon,
        gridSpacing,
        gridbit,
        data,
      };
    },
    encode(_: TerrainData) {
      const writer = createWriter(43);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setUint16(_.gridSpacing);
      _.data.forEach(writer.setInt16);
      writer.setUint8(_.gridbit);
      return writer.finish();
    },
  },
  TERRAIN_CHECK: {
    id: 135,
    crcExtra: 203,
    decode(payload: Uint8Array): TerrainCheck {
      const reader = createReader(payload, 8);
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      return {
        lat,
        lon,
      };
    },
    encode(_: TerrainCheck) {
      const writer = createWriter(8);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      return writer.finish();
    },
  },
  TERRAIN_REPORT: {
    id: 136,
    crcExtra: 1,
    decode(payload: Uint8Array): TerrainReport {
      const reader = createReader(payload, 22);
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const terrainHeight = reader.getFloat32();
      const currentHeight = reader.getFloat32();
      const spacing = reader.getUint16();
      const pending = reader.getUint16();
      const loaded = reader.getUint16();
      return {
        lat,
        lon,
        spacing,
        terrainHeight,
        currentHeight,
        pending,
        loaded,
      };
    },
    encode(_: TerrainReport) {
      const writer = createWriter(22);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setFloat32(_.terrainHeight);
      writer.setFloat32(_.currentHeight);
      writer.setUint16(_.spacing);
      writer.setUint16(_.pending);
      writer.setUint16(_.loaded);
      return writer.finish();
    },
  },
  SCALED_PRESSURE2: {
    id: 137,
    crcExtra: 195,
    decode(payload: Uint8Array): ScaledPressure2 {
      const reader = createReader(payload, 16);
      const timeBootMs = reader.getUint32();
      const pressAbs = reader.getFloat32();
      const pressDiff = reader.getFloat32();
      const temperature = reader.getInt16();
      const temperaturePressDiff = reader.getInt16();
      return {
        timeBootMs,
        pressAbs,
        pressDiff,
        temperature,
        temperaturePressDiff,
      };
    },
    encode(_: ScaledPressure2) {
      const writer = createWriter(16);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.pressAbs);
      writer.setFloat32(_.pressDiff);
      writer.setInt16(_.temperature);
      writer.setInt16(_.temperaturePressDiff);
      return writer.finish();
    },
  },
  ATT_POS_MOCAP: {
    id: 138,
    crcExtra: 109,
    decode(payload: Uint8Array): AttPosMocap {
      const reader = createReader(payload, 120);
      const timeUsec = reader.getBigUint64();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const covariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      return {
        timeUsec,
        q,
        x,
        y,
        z,
        covariance,
      };
    },
    encode(_: AttPosMocap) {
      const writer = createWriter(120);
      writer.setBigUint64(_.timeUsec);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      _.covariance.forEach(writer.setFloat32);
      return writer.finish();
    },
  },
  SET_ACTUATOR_CONTROL_TARGET: {
    id: 139,
    crcExtra: 168,
    decode(payload: Uint8Array): SetActuatorControlTarget {
      const reader = createReader(payload, 43);
      const timeUsec = reader.getBigUint64();
      const controls = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const groupMlx = reader.getUint8();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        timeUsec,
        groupMlx,
        targetSystem,
        targetComponent,
        controls,
      };
    },
    encode(_: SetActuatorControlTarget) {
      const writer = createWriter(43);
      writer.setBigUint64(_.timeUsec);
      _.controls.forEach(writer.setFloat32);
      writer.setUint8(_.groupMlx);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  ACTUATOR_CONTROL_TARGET: {
    id: 140,
    crcExtra: 181,
    decode(payload: Uint8Array): ActuatorControlTarget {
      const reader = createReader(payload, 41);
      const timeUsec = reader.getBigUint64();
      const controls = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const groupMlx = reader.getUint8();
      return {
        timeUsec,
        groupMlx,
        controls,
      };
    },
    encode(_: ActuatorControlTarget) {
      const writer = createWriter(41);
      writer.setBigUint64(_.timeUsec);
      _.controls.forEach(writer.setFloat32);
      writer.setUint8(_.groupMlx);
      return writer.finish();
    },
  },
  ALTITUDE: {
    id: 141,
    crcExtra: 47,
    decode(payload: Uint8Array): Altitude {
      const reader = createReader(payload, 32);
      const timeUsec = reader.getBigUint64();
      const altitudeMonotonic = reader.getFloat32();
      const altitudeAmsl = reader.getFloat32();
      const altitudeLocal = reader.getFloat32();
      const altitudeRelative = reader.getFloat32();
      const altitudeTerrain = reader.getFloat32();
      const bottomClearance = reader.getFloat32();
      return {
        timeUsec,
        altitudeMonotonic,
        altitudeAmsl,
        altitudeLocal,
        altitudeRelative,
        altitudeTerrain,
        bottomClearance,
      };
    },
    encode(_: Altitude) {
      const writer = createWriter(32);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.altitudeMonotonic);
      writer.setFloat32(_.altitudeAmsl);
      writer.setFloat32(_.altitudeLocal);
      writer.setFloat32(_.altitudeRelative);
      writer.setFloat32(_.altitudeTerrain);
      writer.setFloat32(_.bottomClearance);
      return writer.finish();
    },
  },
  RESOURCE_REQUEST: {
    id: 142,
    crcExtra: 72,
    decode(payload: Uint8Array): ResourceRequest {
      const reader = createReader(payload, 243);
      const requestId = reader.getUint8();
      const uriType = reader.getUint8();
      const uri = reader.getUint8Array(120);
      const transferType = reader.getUint8();
      const storage = reader.getUint8Array(120);
      return {
        requestId,
        uriType,
        uri,
        transferType,
        storage,
      };
    },
    encode(_: ResourceRequest) {
      const writer = createWriter(243);
      writer.setUint8(_.requestId);
      writer.setUint8(_.uriType);
      writer.setUint8Array(_.uri, 120);
      writer.setUint8(_.transferType);
      writer.setUint8Array(_.storage, 120);
      return writer.finish();
    },
  },
  SCALED_PRESSURE3: {
    id: 143,
    crcExtra: 131,
    decode(payload: Uint8Array): ScaledPressure3 {
      const reader = createReader(payload, 16);
      const timeBootMs = reader.getUint32();
      const pressAbs = reader.getFloat32();
      const pressDiff = reader.getFloat32();
      const temperature = reader.getInt16();
      const temperaturePressDiff = reader.getInt16();
      return {
        timeBootMs,
        pressAbs,
        pressDiff,
        temperature,
        temperaturePressDiff,
      };
    },
    encode(_: ScaledPressure3) {
      const writer = createWriter(16);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.pressAbs);
      writer.setFloat32(_.pressDiff);
      writer.setInt16(_.temperature);
      writer.setInt16(_.temperaturePressDiff);
      return writer.finish();
    },
  },
  FOLLOW_TARGET: {
    id: 144,
    crcExtra: 127,
    decode(payload: Uint8Array): FollowTarget {
      const reader = createReader(payload, 93);
      const timestamp = reader.getBigUint64();
      const customState = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getFloat32();
      const vel = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const acc = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const attitudeQ = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const rates = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const positionCov = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const estCapabilities = reader.getUint8();
      return {
        timestamp,
        estCapabilities,
        lat,
        lon,
        alt,
        vel,
        acc,
        attitudeQ,
        rates,
        positionCov,
        customState,
      };
    },
    encode(_: FollowTarget) {
      const writer = createWriter(93);
      writer.setBigUint64(_.timestamp);
      writer.setBigUint64(_.customState);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setFloat32(_.alt);
      _.vel.forEach(writer.setFloat32);
      _.acc.forEach(writer.setFloat32);
      _.attitudeQ.forEach(writer.setFloat32);
      _.rates.forEach(writer.setFloat32);
      _.positionCov.forEach(writer.setFloat32);
      writer.setUint8(_.estCapabilities);
      return writer.finish();
    },
  },
  CONTROL_SYSTEM_STATE: {
    id: 146,
    crcExtra: 103,
    decode(payload: Uint8Array): ControlSystemState {
      const reader = createReader(payload, 100);
      const timeUsec = reader.getBigUint64();
      const xAcc = reader.getFloat32();
      const yAcc = reader.getFloat32();
      const zAcc = reader.getFloat32();
      const xVel = reader.getFloat32();
      const yVel = reader.getFloat32();
      const zVel = reader.getFloat32();
      const xPos = reader.getFloat32();
      const yPos = reader.getFloat32();
      const zPos = reader.getFloat32();
      const airspeed = reader.getFloat32();
      const velVariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const posVariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const rollRate = reader.getFloat32();
      const pitchRate = reader.getFloat32();
      const yawRate = reader.getFloat32();
      return {
        timeUsec,
        xAcc,
        yAcc,
        zAcc,
        xVel,
        yVel,
        zVel,
        xPos,
        yPos,
        zPos,
        airspeed,
        velVariance,
        posVariance,
        q,
        rollRate,
        pitchRate,
        yawRate,
      };
    },
    encode(_: ControlSystemState) {
      const writer = createWriter(100);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.xAcc);
      writer.setFloat32(_.yAcc);
      writer.setFloat32(_.zAcc);
      writer.setFloat32(_.xVel);
      writer.setFloat32(_.yVel);
      writer.setFloat32(_.zVel);
      writer.setFloat32(_.xPos);
      writer.setFloat32(_.yPos);
      writer.setFloat32(_.zPos);
      writer.setFloat32(_.airspeed);
      _.velVariance.forEach(writer.setFloat32);
      _.posVariance.forEach(writer.setFloat32);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.rollRate);
      writer.setFloat32(_.pitchRate);
      writer.setFloat32(_.yawRate);
      return writer.finish();
    },
  },
  BATTERY_STATUS: {
    id: 147,
    crcExtra: 154,
    decode(payload: Uint8Array): BatteryStatus {
      const reader = createReader(payload, 54);
      const currentConsumed = reader.getInt32();
      const energyConsumed = reader.getInt32();
      const temperature = reader.getInt16();
      const voltages = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const currentBattery = reader.getInt16();
      const id = reader.getUint8();
      const batteryFunction = reader.getUint8();
      const type = reader.getUint8();
      const batteryRemaining = reader.getInt8();
      const timeRemaining = reader.getInt32();
      const chargeState = reader.getUint8();
      const voltagesExt = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const mode = reader.getUint8();
      const faultBitmask = reader.getUint32();
      return {
        id,
        batteryFunction: mavBatteryFunctionInverseMap.get(batteryFunction) as MavBatteryFunction,
        type: mavBatteryTypeInverseMap.get(type) as MavBatteryType,
        temperature,
        voltages,
        currentBattery,
        currentConsumed,
        energyConsumed,
        batteryRemaining,
        timeRemaining,
        chargeState: mavBatteryChargeStateInverseMap.get(chargeState) as MavBatteryChargeState,
        voltagesExt,
        mode: mavBatteryModeInverseMap.get(mode) as MavBatteryMode,
        faultBitmask: decodeBitmask(faultBitmask, mavBatteryFaultInverseMap),
      };
    },
    encode(_: BatteryStatus) {
      const writer = createWriter(54);
      writer.setInt32(_.currentConsumed);
      writer.setInt32(_.energyConsumed);
      writer.setInt16(_.temperature);
      _.voltages.forEach(writer.setUint16);
      writer.setInt16(_.currentBattery);
      writer.setUint8(_.id);
      writer.setUint8(lookup(_.batteryFunction, mavBatteryFunctionMap));
      writer.setUint8(lookup(_.type, mavBatteryTypeMap));
      writer.setInt8(_.batteryRemaining);
      writer.setInt32(_.timeRemaining);
      writer.setUint8(lookup(_.chargeState, mavBatteryChargeStateMap));
      _.voltagesExt.forEach(writer.setUint16);
      writer.setUint8(lookup(_.mode, mavBatteryModeMap));
      writer.setUint32(encodeBitmask(_.faultBitmask, mavBatteryFaultMap));
      return writer.finish();
    },
  },
  AUTOPILOT_VERSION: {
    id: 148,
    crcExtra: 178,
    decode(payload: Uint8Array): AutopilotVersion {
      const reader = createReader(payload, 78);
      const capabilities = reader.getBigUint64();
      const uid = reader.getBigUint64();
      const flightSwVersion = reader.getUint32();
      const middlewareSwVersion = reader.getUint32();
      const osSwVersion = reader.getUint32();
      const boardVersion = reader.getUint32();
      const vendorId = reader.getUint16();
      const productId = reader.getUint16();
      const flightCustomVersion = reader.getUint8Array(8);
      const middlewareCustomVersion = reader.getUint8Array(8);
      const osCustomVersion = reader.getUint8Array(8);
      const uid2 = reader.getUint8Array(18);
      return {
        capabilities: decodeBitmask(capabilities, mavProtocolCapabilityInverseMap),
        flightSwVersion,
        middlewareSwVersion,
        osSwVersion,
        boardVersion,
        flightCustomVersion,
        middlewareCustomVersion,
        osCustomVersion,
        vendorId,
        productId,
        uid,
        uid2,
      };
    },
    encode(_: AutopilotVersion) {
      const writer = createWriter(78);
      writer.setBigUint64(encodeBitmask(_.capabilities, mavProtocolCapabilityMap));
      writer.setBigUint64(_.uid);
      writer.setUint32(_.flightSwVersion);
      writer.setUint32(_.middlewareSwVersion);
      writer.setUint32(_.osSwVersion);
      writer.setUint32(_.boardVersion);
      writer.setUint16(_.vendorId);
      writer.setUint16(_.productId);
      writer.setUint8Array(_.flightCustomVersion, 8);
      writer.setUint8Array(_.middlewareCustomVersion, 8);
      writer.setUint8Array(_.osCustomVersion, 8);
      writer.setUint8Array(_.uid2, 18);
      return writer.finish();
    },
  },
  LANDING_TARGET: {
    id: 149,
    crcExtra: 200,
    decode(payload: Uint8Array): LandingTarget {
      const reader = createReader(payload, 60);
      const timeUsec = reader.getBigUint64();
      const angleX = reader.getFloat32();
      const angleY = reader.getFloat32();
      const distance = reader.getFloat32();
      const sizeX = reader.getFloat32();
      const sizeY = reader.getFloat32();
      const targetNum = reader.getUint8();
      const frame = reader.getUint8();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const type = reader.getUint8();
      const positionValid = reader.getUint8();
      return {
        timeUsec,
        targetNum,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        angleX,
        angleY,
        distance,
        sizeX,
        sizeY,
        x,
        y,
        z,
        q,
        type: landingTargetTypeInverseMap.get(type) as LandingTargetType,
        positionValid,
      };
    },
    encode(_: LandingTarget) {
      const writer = createWriter(60);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.angleX);
      writer.setFloat32(_.angleY);
      writer.setFloat32(_.distance);
      writer.setFloat32(_.sizeX);
      writer.setFloat32(_.sizeY);
      writer.setUint8(_.targetNum);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      _.q.forEach(writer.setFloat32);
      writer.setUint8(lookup(_.type, landingTargetTypeMap));
      writer.setUint8(_.positionValid);
      return writer.finish();
    },
  },
  FENCE_STATUS: {
    id: 162,
    crcExtra: 189,
    decode(payload: Uint8Array): FenceStatus {
      const reader = createReader(payload, 9);
      const breachTime = reader.getUint32();
      const breachCount = reader.getUint16();
      const breachStatus = reader.getUint8();
      const breachType = reader.getUint8();
      const breachMitigation = reader.getUint8();
      return {
        breachStatus,
        breachCount,
        breachType: fenceBreachInverseMap.get(breachType) as FenceBreach,
        breachTime,
        breachMitigation: fenceMitigateInverseMap.get(breachMitigation) as FenceMitigate,
      };
    },
    encode(_: FenceStatus) {
      const writer = createWriter(9);
      writer.setUint32(_.breachTime);
      writer.setUint16(_.breachCount);
      writer.setUint8(_.breachStatus);
      writer.setUint8(lookup(_.breachType, fenceBreachMap));
      writer.setUint8(lookup(_.breachMitigation, fenceMitigateMap));
      return writer.finish();
    },
  },
  MAG_CAL_REPORT: {
    id: 192,
    crcExtra: 36,
    decode(payload: Uint8Array): MagCalReport {
      const reader = createReader(payload, 54);
      const fitness = reader.getFloat32();
      const ofsX = reader.getFloat32();
      const ofsY = reader.getFloat32();
      const ofsZ = reader.getFloat32();
      const diagX = reader.getFloat32();
      const diagY = reader.getFloat32();
      const diagZ = reader.getFloat32();
      const offdiagX = reader.getFloat32();
      const offdiagY = reader.getFloat32();
      const offdiagZ = reader.getFloat32();
      const compassId = reader.getUint8();
      const calMask = reader.getUint8();
      const calStatus = reader.getUint8();
      const autosaved = reader.getUint8();
      const orientationConfidence = reader.getFloat32();
      const oldOrientation = reader.getUint8();
      const newOrientation = reader.getUint8();
      const scaleFactor = reader.getFloat32();
      return {
        compassId,
        calMask,
        calStatus: magCalStatusInverseMap.get(calStatus) as MagCalStatus,
        autosaved,
        fitness,
        ofsX,
        ofsY,
        ofsZ,
        diagX,
        diagY,
        diagZ,
        offdiagX,
        offdiagY,
        offdiagZ,
        orientationConfidence,
        oldOrientation: mavSensorOrientationInverseMap.get(oldOrientation) as MavSensorOrientation,
        newOrientation: mavSensorOrientationInverseMap.get(newOrientation) as MavSensorOrientation,
        scaleFactor,
      };
    },
    encode(_: MagCalReport) {
      const writer = createWriter(54);
      writer.setFloat32(_.fitness);
      writer.setFloat32(_.ofsX);
      writer.setFloat32(_.ofsY);
      writer.setFloat32(_.ofsZ);
      writer.setFloat32(_.diagX);
      writer.setFloat32(_.diagY);
      writer.setFloat32(_.diagZ);
      writer.setFloat32(_.offdiagX);
      writer.setFloat32(_.offdiagY);
      writer.setFloat32(_.offdiagZ);
      writer.setUint8(_.compassId);
      writer.setUint8(_.calMask);
      writer.setUint8(lookup(_.calStatus, magCalStatusMap));
      writer.setUint8(_.autosaved);
      writer.setFloat32(_.orientationConfidence);
      writer.setUint8(lookup(_.oldOrientation, mavSensorOrientationMap));
      writer.setUint8(lookup(_.newOrientation, mavSensorOrientationMap));
      writer.setFloat32(_.scaleFactor);
      return writer.finish();
    },
  },
  EFI_STATUS: {
    id: 225,
    crcExtra: 208,
    decode(payload: Uint8Array): EfiStatus {
      const reader = createReader(payload, 73);
      const ecuIndex = reader.getFloat32();
      const rpm = reader.getFloat32();
      const fuelConsumed = reader.getFloat32();
      const fuelFlow = reader.getFloat32();
      const engineLoad = reader.getFloat32();
      const throttlePosition = reader.getFloat32();
      const sparkDwellTime = reader.getFloat32();
      const barometricPressure = reader.getFloat32();
      const intakeManifoldPressure = reader.getFloat32();
      const intakeManifoldTemperature = reader.getFloat32();
      const cylinderHeadTemperature = reader.getFloat32();
      const ignitionTiming = reader.getFloat32();
      const injectionTime = reader.getFloat32();
      const exhaustGasTemperature = reader.getFloat32();
      const throttleOut = reader.getFloat32();
      const ptCompensation = reader.getFloat32();
      const health = reader.getUint8();
      const ignitionVoltage = reader.getFloat32();
      const fuelPressure = reader.getFloat32();
      return {
        health,
        ecuIndex,
        rpm,
        fuelConsumed,
        fuelFlow,
        engineLoad,
        throttlePosition,
        sparkDwellTime,
        barometricPressure,
        intakeManifoldPressure,
        intakeManifoldTemperature,
        cylinderHeadTemperature,
        ignitionTiming,
        injectionTime,
        exhaustGasTemperature,
        throttleOut,
        ptCompensation,
        ignitionVoltage,
        fuelPressure,
      };
    },
    encode(_: EfiStatus) {
      const writer = createWriter(73);
      writer.setFloat32(_.ecuIndex);
      writer.setFloat32(_.rpm);
      writer.setFloat32(_.fuelConsumed);
      writer.setFloat32(_.fuelFlow);
      writer.setFloat32(_.engineLoad);
      writer.setFloat32(_.throttlePosition);
      writer.setFloat32(_.sparkDwellTime);
      writer.setFloat32(_.barometricPressure);
      writer.setFloat32(_.intakeManifoldPressure);
      writer.setFloat32(_.intakeManifoldTemperature);
      writer.setFloat32(_.cylinderHeadTemperature);
      writer.setFloat32(_.ignitionTiming);
      writer.setFloat32(_.injectionTime);
      writer.setFloat32(_.exhaustGasTemperature);
      writer.setFloat32(_.throttleOut);
      writer.setFloat32(_.ptCompensation);
      writer.setUint8(_.health);
      writer.setFloat32(_.ignitionVoltage);
      writer.setFloat32(_.fuelPressure);
      return writer.finish();
    },
  },
  ESTIMATOR_STATUS: {
    id: 230,
    crcExtra: 163,
    decode(payload: Uint8Array): EstimatorStatus {
      const reader = createReader(payload, 42);
      const timeUsec = reader.getBigUint64();
      const velRatio = reader.getFloat32();
      const posHorizRatio = reader.getFloat32();
      const posVertRatio = reader.getFloat32();
      const magRatio = reader.getFloat32();
      const haglRatio = reader.getFloat32();
      const tasRatio = reader.getFloat32();
      const posHorizAccuracy = reader.getFloat32();
      const posVertAccuracy = reader.getFloat32();
      const flags = reader.getUint16();
      return {
        timeUsec,
        flags: decodeBitmask(flags, estimatorStatusFlagsInverseMap),
        velRatio,
        posHorizRatio,
        posVertRatio,
        magRatio,
        haglRatio,
        tasRatio,
        posHorizAccuracy,
        posVertAccuracy,
      };
    },
    encode(_: EstimatorStatus) {
      const writer = createWriter(42);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.velRatio);
      writer.setFloat32(_.posHorizRatio);
      writer.setFloat32(_.posVertRatio);
      writer.setFloat32(_.magRatio);
      writer.setFloat32(_.haglRatio);
      writer.setFloat32(_.tasRatio);
      writer.setFloat32(_.posHorizAccuracy);
      writer.setFloat32(_.posVertAccuracy);
      writer.setUint16(encodeBitmask(_.flags, estimatorStatusFlagsMap));
      return writer.finish();
    },
  },
  WIND_COV: {
    id: 231,
    crcExtra: 105,
    decode(payload: Uint8Array): WindCov {
      const reader = createReader(payload, 40);
      const timeUsec = reader.getBigUint64();
      const windX = reader.getFloat32();
      const windY = reader.getFloat32();
      const windZ = reader.getFloat32();
      const varHoriz = reader.getFloat32();
      const varVert = reader.getFloat32();
      const windAlt = reader.getFloat32();
      const horizAccuracy = reader.getFloat32();
      const vertAccuracy = reader.getFloat32();
      return {
        timeUsec,
        windX,
        windY,
        windZ,
        varHoriz,
        varVert,
        windAlt,
        horizAccuracy,
        vertAccuracy,
      };
    },
    encode(_: WindCov) {
      const writer = createWriter(40);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.windX);
      writer.setFloat32(_.windY);
      writer.setFloat32(_.windZ);
      writer.setFloat32(_.varHoriz);
      writer.setFloat32(_.varVert);
      writer.setFloat32(_.windAlt);
      writer.setFloat32(_.horizAccuracy);
      writer.setFloat32(_.vertAccuracy);
      return writer.finish();
    },
  },
  GPS_INPUT: {
    id: 232,
    crcExtra: 151,
    decode(payload: Uint8Array): GpsInput {
      const reader = createReader(payload, 65);
      const timeUsec = reader.getBigUint64();
      const timeWeekMs = reader.getUint32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getFloat32();
      const hdop = reader.getFloat32();
      const vdop = reader.getFloat32();
      const vn = reader.getFloat32();
      const ve = reader.getFloat32();
      const vd = reader.getFloat32();
      const speedAccuracy = reader.getFloat32();
      const horizAccuracy = reader.getFloat32();
      const vertAccuracy = reader.getFloat32();
      const ignoreFlags = reader.getUint16();
      const timeWeek = reader.getUint16();
      const gpsId = reader.getUint8();
      const fixType = reader.getUint8();
      const satellitesVisible = reader.getUint8();
      const yaw = reader.getUint16();
      return {
        timeUsec,
        gpsId,
        ignoreFlags: decodeBitmask(ignoreFlags, gpsInputIgnoreFlagsInverseMap),
        timeWeekMs,
        timeWeek,
        fixType,
        lat,
        lon,
        alt,
        hdop,
        vdop,
        vn,
        ve,
        vd,
        speedAccuracy,
        horizAccuracy,
        vertAccuracy,
        satellitesVisible,
        yaw,
      };
    },
    encode(_: GpsInput) {
      const writer = createWriter(65);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.timeWeekMs);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.hdop);
      writer.setFloat32(_.vdop);
      writer.setFloat32(_.vn);
      writer.setFloat32(_.ve);
      writer.setFloat32(_.vd);
      writer.setFloat32(_.speedAccuracy);
      writer.setFloat32(_.horizAccuracy);
      writer.setFloat32(_.vertAccuracy);
      writer.setUint16(encodeBitmask(_.ignoreFlags, gpsInputIgnoreFlagsMap));
      writer.setUint16(_.timeWeek);
      writer.setUint8(_.gpsId);
      writer.setUint8(_.fixType);
      writer.setUint8(_.satellitesVisible);
      writer.setUint16(_.yaw);
      return writer.finish();
    },
  },
  GPS_RTCM_DATA: {
    id: 233,
    crcExtra: 35,
    decode(payload: Uint8Array): GpsRtcmData {
      const reader = createReader(payload, 182);
      const flags = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(180);
      return {
        flags,
        len,
        data,
      };
    },
    encode(_: GpsRtcmData) {
      const writer = createWriter(182);
      writer.setUint8(_.flags);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 180);
      return writer.finish();
    },
  },
  HIGH_LATENCY: {
    id: 234,
    crcExtra: 150,
    decode(payload: Uint8Array): HighLatency {
      const reader = createReader(payload, 40);
      const customMode = reader.getUint32();
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const roll = reader.getInt16();
      const pitch = reader.getInt16();
      const heading = reader.getUint16();
      const headingSp = reader.getInt16();
      const altitudeAmsl = reader.getInt16();
      const altitudeSp = reader.getInt16();
      const wpDistance = reader.getUint16();
      const baseMode = reader.getUint8();
      const landedState = reader.getUint8();
      const throttle = reader.getInt8();
      const airspeed = reader.getUint8();
      const airspeedSp = reader.getUint8();
      const groundspeed = reader.getUint8();
      const climbRate = reader.getInt8();
      const gpsNsat = reader.getUint8();
      const gpsFixType = reader.getUint8();
      const batteryRemaining = reader.getUint8();
      const temperature = reader.getInt8();
      const temperatureAir = reader.getInt8();
      const failsafe = reader.getUint8();
      const wpNum = reader.getUint8();
      return {
        baseMode: decodeBitmask(baseMode, mavModeFlagInverseMap),
        customMode,
        landedState: mavLandedStateInverseMap.get(landedState) as MavLandedState,
        roll,
        pitch,
        heading,
        throttle,
        headingSp,
        latitude,
        longitude,
        altitudeAmsl,
        altitudeSp,
        airspeed,
        airspeedSp,
        groundspeed,
        climbRate,
        gpsNsat,
        gpsFixType: gpsFixTypeInverseMap.get(gpsFixType) as GpsFixType,
        batteryRemaining,
        temperature,
        temperatureAir,
        failsafe,
        wpNum,
        wpDistance,
      };
    },
    encode(_: HighLatency) {
      const writer = createWriter(40);
      writer.setUint32(_.customMode);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setInt16(_.roll);
      writer.setInt16(_.pitch);
      writer.setUint16(_.heading);
      writer.setInt16(_.headingSp);
      writer.setInt16(_.altitudeAmsl);
      writer.setInt16(_.altitudeSp);
      writer.setUint16(_.wpDistance);
      writer.setUint8(encodeBitmask(_.baseMode, mavModeFlagMap));
      writer.setUint8(lookup(_.landedState, mavLandedStateMap));
      writer.setInt8(_.throttle);
      writer.setUint8(_.airspeed);
      writer.setUint8(_.airspeedSp);
      writer.setUint8(_.groundspeed);
      writer.setInt8(_.climbRate);
      writer.setUint8(_.gpsNsat);
      writer.setUint8(lookup(_.gpsFixType, gpsFixTypeMap));
      writer.setUint8(_.batteryRemaining);
      writer.setInt8(_.temperature);
      writer.setInt8(_.temperatureAir);
      writer.setUint8(_.failsafe);
      writer.setUint8(_.wpNum);
      return writer.finish();
    },
  },
  HIGH_LATENCY2: {
    id: 235,
    crcExtra: 179,
    decode(payload: Uint8Array): HighLatency2 {
      const reader = createReader(payload, 42);
      const timestamp = reader.getUint32();
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const customMode = reader.getUint16();
      const altitude = reader.getInt16();
      const targetAltitude = reader.getInt16();
      const targetDistance = reader.getUint16();
      const wpNum = reader.getUint16();
      const failureFlags = reader.getUint16();
      const type = reader.getUint8();
      const autopilot = reader.getUint8();
      const heading = reader.getUint8();
      const targetHeading = reader.getUint8();
      const throttle = reader.getUint8();
      const airspeed = reader.getUint8();
      const airspeedSp = reader.getUint8();
      const groundspeed = reader.getUint8();
      const windspeed = reader.getUint8();
      const windHeading = reader.getUint8();
      const eph = reader.getUint8();
      const epv = reader.getUint8();
      const temperatureAir = reader.getInt8();
      const climbRate = reader.getInt8();
      const battery = reader.getInt8();
      const custom0 = reader.getInt8();
      const custom1 = reader.getInt8();
      const custom2 = reader.getInt8();
      return {
        timestamp,
        type: mavTypeInverseMap.get(type) as MavType,
        autopilot: mavAutopilotInverseMap.get(autopilot) as MavAutopilot,
        customMode,
        latitude,
        longitude,
        altitude,
        targetAltitude,
        heading,
        targetHeading,
        targetDistance,
        throttle,
        airspeed,
        airspeedSp,
        groundspeed,
        windspeed,
        windHeading,
        eph,
        epv,
        temperatureAir,
        climbRate,
        battery,
        wpNum,
        failureFlags: decodeBitmask(failureFlags, hlFailureFlagInverseMap),
        custom0,
        custom1,
        custom2,
      };
    },
    encode(_: HighLatency2) {
      const writer = createWriter(42);
      writer.setUint32(_.timestamp);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setUint16(_.customMode);
      writer.setInt16(_.altitude);
      writer.setInt16(_.targetAltitude);
      writer.setUint16(_.targetDistance);
      writer.setUint16(_.wpNum);
      writer.setUint16(encodeBitmask(_.failureFlags, hlFailureFlagMap));
      writer.setUint8(lookup(_.type, mavTypeMap));
      writer.setUint8(lookup(_.autopilot, mavAutopilotMap));
      writer.setUint8(_.heading);
      writer.setUint8(_.targetHeading);
      writer.setUint8(_.throttle);
      writer.setUint8(_.airspeed);
      writer.setUint8(_.airspeedSp);
      writer.setUint8(_.groundspeed);
      writer.setUint8(_.windspeed);
      writer.setUint8(_.windHeading);
      writer.setUint8(_.eph);
      writer.setUint8(_.epv);
      writer.setInt8(_.temperatureAir);
      writer.setInt8(_.climbRate);
      writer.setInt8(_.battery);
      writer.setInt8(_.custom0);
      writer.setInt8(_.custom1);
      writer.setInt8(_.custom2);
      return writer.finish();
    },
  },
  VIBRATION: {
    id: 241,
    crcExtra: 90,
    decode(payload: Uint8Array): Vibration {
      const reader = createReader(payload, 32);
      const timeUsec = reader.getBigUint64();
      const vibrationX = reader.getFloat32();
      const vibrationY = reader.getFloat32();
      const vibrationZ = reader.getFloat32();
      const clipping0 = reader.getUint32();
      const clipping1 = reader.getUint32();
      const clipping2 = reader.getUint32();
      return {
        timeUsec,
        vibrationX,
        vibrationY,
        vibrationZ,
        clipping0,
        clipping1,
        clipping2,
      };
    },
    encode(_: Vibration) {
      const writer = createWriter(32);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.vibrationX);
      writer.setFloat32(_.vibrationY);
      writer.setFloat32(_.vibrationZ);
      writer.setUint32(_.clipping0);
      writer.setUint32(_.clipping1);
      writer.setUint32(_.clipping2);
      return writer.finish();
    },
  },
  HOME_POSITION: {
    id: 242,
    crcExtra: 104,
    decode(payload: Uint8Array): HomePosition {
      const reader = createReader(payload, 60);
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const altitude = reader.getInt32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const approachX = reader.getFloat32();
      const approachY = reader.getFloat32();
      const approachZ = reader.getFloat32();
      const timeUsec = reader.getBigUint64();
      return {
        latitude,
        longitude,
        altitude,
        x,
        y,
        z,
        q,
        approachX,
        approachY,
        approachZ,
        timeUsec,
      };
    },
    encode(_: HomePosition) {
      const writer = createWriter(60);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setInt32(_.altitude);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.approachX);
      writer.setFloat32(_.approachY);
      writer.setFloat32(_.approachZ);
      writer.setBigUint64(_.timeUsec);
      return writer.finish();
    },
  },
  SET_HOME_POSITION: {
    id: 243,
    crcExtra: 85,
    decode(payload: Uint8Array): SetHomePosition {
      const reader = createReader(payload, 61);
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const altitude = reader.getInt32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const approachX = reader.getFloat32();
      const approachY = reader.getFloat32();
      const approachZ = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const timeUsec = reader.getBigUint64();
      return {
        targetSystem,
        latitude,
        longitude,
        altitude,
        x,
        y,
        z,
        q,
        approachX,
        approachY,
        approachZ,
        timeUsec,
      };
    },
    encode(_: SetHomePosition) {
      const writer = createWriter(61);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setInt32(_.altitude);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.approachX);
      writer.setFloat32(_.approachY);
      writer.setFloat32(_.approachZ);
      writer.setUint8(_.targetSystem);
      writer.setBigUint64(_.timeUsec);
      return writer.finish();
    },
  },
  MESSAGE_INTERVAL: {
    id: 244,
    crcExtra: 95,
    decode(payload: Uint8Array): MessageInterval {
      const reader = createReader(payload, 6);
      const intervalUs = reader.getInt32();
      const messageId = reader.getUint16();
      return {
        messageId,
        intervalUs,
      };
    },
    encode(_: MessageInterval) {
      const writer = createWriter(6);
      writer.setInt32(_.intervalUs);
      writer.setUint16(_.messageId);
      return writer.finish();
    },
  },
  EXTENDED_SYS_STATE: {
    id: 245,
    crcExtra: 130,
    decode(payload: Uint8Array): ExtendedSysState {
      const reader = createReader(payload, 2);
      const vtolState = reader.getUint8();
      const landedState = reader.getUint8();
      return {
        vtolState: mavVtolStateInverseMap.get(vtolState) as MavVtolState,
        landedState: mavLandedStateInverseMap.get(landedState) as MavLandedState,
      };
    },
    encode(_: ExtendedSysState) {
      const writer = createWriter(2);
      writer.setUint8(lookup(_.vtolState, mavVtolStateMap));
      writer.setUint8(lookup(_.landedState, mavLandedStateMap));
      return writer.finish();
    },
  },
  ADSB_VEHICLE: {
    id: 246,
    crcExtra: 184,
    decode(payload: Uint8Array): AdsbVehicle {
      const reader = createReader(payload, 38);
      const icaoAddress = reader.getUint32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const altitude = reader.getInt32();
      const heading = reader.getUint16();
      const horVelocity = reader.getUint16();
      const verVelocity = reader.getInt16();
      const flags = reader.getUint16();
      const squawk = reader.getUint16();
      const altitudeType = reader.getUint8();
      const callsign = textDecoder.decode(reader.getUint8Array(9)).replace(/\0+$/, "");
      const emitterType = reader.getUint8();
      const tslc = reader.getUint8();
      return {
        icaoAddress,
        lat,
        lon,
        altitudeType: adsbAltitudeTypeInverseMap.get(altitudeType) as AdsbAltitudeType,
        altitude,
        heading,
        horVelocity,
        verVelocity,
        callsign,
        emitterType: adsbEmitterTypeInverseMap.get(emitterType) as AdsbEmitterType,
        tslc,
        flags: decodeBitmask(flags, adsbFlagsInverseMap),
        squawk,
      };
    },
    encode(_: AdsbVehicle) {
      const writer = createWriter(38);
      writer.setUint32(_.icaoAddress);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.altitude);
      writer.setUint16(_.heading);
      writer.setUint16(_.horVelocity);
      writer.setInt16(_.verVelocity);
      writer.setUint16(encodeBitmask(_.flags, adsbFlagsMap));
      writer.setUint16(_.squawk);
      writer.setUint8(lookup(_.altitudeType, adsbAltitudeTypeMap));
      const callsignEncoded = textEncoder.encode(_.callsign);
      writer.setUint8Array(callsignEncoded, 9);
      writer.setUint8(lookup(_.emitterType, adsbEmitterTypeMap));
      writer.setUint8(_.tslc);
      return writer.finish();
    },
  },
  COLLISION: {
    id: 247,
    crcExtra: 81,
    decode(payload: Uint8Array): Collision {
      const reader = createReader(payload, 19);
      const id = reader.getUint32();
      const timeToMinimumDelta = reader.getFloat32();
      const altitudeMinimumDelta = reader.getFloat32();
      const horizontalMinimumDelta = reader.getFloat32();
      const src = reader.getUint8();
      const action = reader.getUint8();
      const threatLevel = reader.getUint8();
      return {
        src: mavCollisionSrcInverseMap.get(src) as MavCollisionSrc,
        id,
        action: mavCollisionActionInverseMap.get(action) as MavCollisionAction,
        threatLevel: mavCollisionThreatLevelInverseMap.get(threatLevel) as MavCollisionThreatLevel,
        timeToMinimumDelta,
        altitudeMinimumDelta,
        horizontalMinimumDelta,
      };
    },
    encode(_: Collision) {
      const writer = createWriter(19);
      writer.setUint32(_.id);
      writer.setFloat32(_.timeToMinimumDelta);
      writer.setFloat32(_.altitudeMinimumDelta);
      writer.setFloat32(_.horizontalMinimumDelta);
      writer.setUint8(lookup(_.src, mavCollisionSrcMap));
      writer.setUint8(lookup(_.action, mavCollisionActionMap));
      writer.setUint8(lookup(_.threatLevel, mavCollisionThreatLevelMap));
      return writer.finish();
    },
  },
  V2_EXTENSION: {
    id: 248,
    crcExtra: 8,
    decode(payload: Uint8Array): V2Extension {
      const reader = createReader(payload, 254);
      const messageType = reader.getUint16();
      const targetNetwork = reader.getUint8();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const payload_ = reader.getUint8Array(249);
      return {
        targetNetwork,
        targetSystem,
        targetComponent,
        messageType,
        payload_,
      };
    },
    encode(_: V2Extension) {
      const writer = createWriter(254);
      writer.setUint16(_.messageType);
      writer.setUint8(_.targetNetwork);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.payload_, 249);
      return writer.finish();
    },
  },
  MEMORY_VECT: {
    id: 249,
    crcExtra: 204,
    decode(payload: Uint8Array): MemoryVect {
      const reader = createReader(payload, 36);
      const address = reader.getUint16();
      const ver = reader.getUint8();
      const type = reader.getUint8();
      const value = [reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8(), reader.getInt8()] as const;
      return {
        address,
        ver,
        type,
        value,
      };
    },
    encode(_: MemoryVect) {
      const writer = createWriter(36);
      writer.setUint16(_.address);
      writer.setUint8(_.ver);
      writer.setUint8(_.type);
      _.value.forEach(writer.setInt8);
      return writer.finish();
    },
  },
  DEBUG_VECT: {
    id: 250,
    crcExtra: 49,
    decode(payload: Uint8Array): DebugVect {
      const reader = createReader(payload, 30);
      const timeUsec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const name = textDecoder.decode(reader.getUint8Array(10)).replace(/\0+$/, "");
      return {
        name,
        timeUsec,
        x,
        y,
        z,
      };
    },
    encode(_: DebugVect) {
      const writer = createWriter(30);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 10);
      return writer.finish();
    },
  },
  NAMED_VALUE_FLOAT: {
    id: 251,
    crcExtra: 170,
    decode(payload: Uint8Array): NamedValueFloat {
      const reader = createReader(payload, 18);
      const timeBootMs = reader.getUint32();
      const value = reader.getFloat32();
      const name = textDecoder.decode(reader.getUint8Array(10)).replace(/\0+$/, "");
      return {
        timeBootMs,
        name,
        value,
      };
    },
    encode(_: NamedValueFloat) {
      const writer = createWriter(18);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.value);
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 10);
      return writer.finish();
    },
  },
  NAMED_VALUE_INT: {
    id: 252,
    crcExtra: 44,
    decode(payload: Uint8Array): NamedValueInt {
      const reader = createReader(payload, 18);
      const timeBootMs = reader.getUint32();
      const value = reader.getInt32();
      const name = textDecoder.decode(reader.getUint8Array(10)).replace(/\0+$/, "");
      return {
        timeBootMs,
        name,
        value,
      };
    },
    encode(_: NamedValueInt) {
      const writer = createWriter(18);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.value);
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 10);
      return writer.finish();
    },
  },
  STATUSTEXT: {
    id: 253,
    crcExtra: 83,
    decode(payload: Uint8Array): Statustext {
      const reader = createReader(payload, 54);
      const severity = reader.getUint8();
      const text = textDecoder.decode(reader.getUint8Array(50)).replace(/\0+$/, "");
      const id = reader.getUint16();
      const chunkSeq = reader.getUint8();
      return {
        severity: mavSeverityInverseMap.get(severity) as MavSeverity,
        text,
        id,
        chunkSeq,
      };
    },
    encode(_: Statustext) {
      const writer = createWriter(54);
      writer.setUint8(lookup(_.severity, mavSeverityMap));
      const textEncoded = textEncoder.encode(_.text);
      writer.setUint8Array(textEncoded, 50);
      writer.setUint16(_.id);
      writer.setUint8(_.chunkSeq);
      return writer.finish();
    },
  },
  DEBUG: {
    id: 254,
    crcExtra: 46,
    decode(payload: Uint8Array): Debug {
      const reader = createReader(payload, 9);
      const timeBootMs = reader.getUint32();
      const value = reader.getFloat32();
      const ind = reader.getUint8();
      return {
        timeBootMs,
        ind,
        value,
      };
    },
    encode(_: Debug) {
      const writer = createWriter(9);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.value);
      writer.setUint8(_.ind);
      return writer.finish();
    },
  },
  SETUP_SIGNING: {
    id: 256,
    crcExtra: 71,
    decode(payload: Uint8Array): SetupSigning {
      const reader = createReader(payload, 42);
      const initialTimestamp = reader.getBigUint64();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const secretKey = reader.getUint8Array(32);
      return {
        targetSystem,
        targetComponent,
        secretKey,
        initialTimestamp,
      };
    },
    encode(_: SetupSigning) {
      const writer = createWriter(42);
      writer.setBigUint64(_.initialTimestamp);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.secretKey, 32);
      return writer.finish();
    },
  },
  BUTTON_CHANGE: {
    id: 257,
    crcExtra: 131,
    decode(payload: Uint8Array): ButtonChange {
      const reader = createReader(payload, 9);
      const timeBootMs = reader.getUint32();
      const lastChangeMs = reader.getUint32();
      const state = reader.getUint8();
      return {
        timeBootMs,
        lastChangeMs,
        state,
      };
    },
    encode(_: ButtonChange) {
      const writer = createWriter(9);
      writer.setUint32(_.timeBootMs);
      writer.setUint32(_.lastChangeMs);
      writer.setUint8(_.state);
      return writer.finish();
    },
  },
  PLAY_TUNE: {
    id: 258,
    crcExtra: 187,
    decode(payload: Uint8Array): PlayTune {
      const reader = createReader(payload, 232);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const tune = textDecoder.decode(reader.getUint8Array(30)).replace(/\0+$/, "");
      const tune2 = textDecoder.decode(reader.getUint8Array(200)).replace(/\0+$/, "");
      return {
        targetSystem,
        targetComponent,
        tune,
        tune2,
      };
    },
    encode(_: PlayTune) {
      const writer = createWriter(232);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      const tuneEncoded = textEncoder.encode(_.tune);
      writer.setUint8Array(tuneEncoded, 30);
      const tune2Encoded = textEncoder.encode(_.tune2);
      writer.setUint8Array(tune2Encoded, 200);
      return writer.finish();
    },
  },
  CAMERA_INFORMATION: {
    id: 259,
    crcExtra: 92,
    decode(payload: Uint8Array): CameraInformation {
      const reader = createReader(payload, 236);
      const timeBootMs = reader.getUint32();
      const firmwareVersion = reader.getUint32();
      const focalLength = reader.getFloat32();
      const sensorSizeH = reader.getFloat32();
      const sensorSizeV = reader.getFloat32();
      const flags = reader.getUint32();
      const resolutionH = reader.getUint16();
      const resolutionV = reader.getUint16();
      const camDefinitionVersion = reader.getUint16();
      const vendorName = reader.getUint8Array(32);
      const modelName = reader.getUint8Array(32);
      const lensId = reader.getUint8();
      const camDefinitionUri = textDecoder.decode(reader.getUint8Array(140)).replace(/\0+$/, "");
      const gimbalDeviceId = reader.getUint8();
      return {
        timeBootMs,
        vendorName,
        modelName,
        firmwareVersion,
        focalLength,
        sensorSizeH,
        sensorSizeV,
        resolutionH,
        resolutionV,
        lensId,
        flags: decodeBitmask(flags, cameraCapFlagsInverseMap),
        camDefinitionVersion,
        camDefinitionUri,
        gimbalDeviceId,
      };
    },
    encode(_: CameraInformation) {
      const writer = createWriter(236);
      writer.setUint32(_.timeBootMs);
      writer.setUint32(_.firmwareVersion);
      writer.setFloat32(_.focalLength);
      writer.setFloat32(_.sensorSizeH);
      writer.setFloat32(_.sensorSizeV);
      writer.setUint32(encodeBitmask(_.flags, cameraCapFlagsMap));
      writer.setUint16(_.resolutionH);
      writer.setUint16(_.resolutionV);
      writer.setUint16(_.camDefinitionVersion);
      writer.setUint8Array(_.vendorName, 32);
      writer.setUint8Array(_.modelName, 32);
      writer.setUint8(_.lensId);
      const camDefinitionUriEncoded = textEncoder.encode(_.camDefinitionUri);
      writer.setUint8Array(camDefinitionUriEncoded, 140);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  CAMERA_SETTINGS: {
    id: 260,
    crcExtra: 146,
    decode(payload: Uint8Array): CameraSettings {
      const reader = createReader(payload, 13);
      const timeBootMs = reader.getUint32();
      const modeId = reader.getUint8();
      const zoomLevel = reader.getFloat32();
      const focusLevel = reader.getFloat32();
      return {
        timeBootMs,
        modeId: cameraModeInverseMap.get(modeId) as CameraMode,
        zoomLevel,
        focusLevel,
      };
    },
    encode(_: CameraSettings) {
      const writer = createWriter(13);
      writer.setUint32(_.timeBootMs);
      writer.setUint8(lookup(_.modeId, cameraModeMap));
      writer.setFloat32(_.zoomLevel);
      writer.setFloat32(_.focusLevel);
      return writer.finish();
    },
  },
  STORAGE_INFORMATION: {
    id: 261,
    crcExtra: 179,
    decode(payload: Uint8Array): StorageInformation {
      const reader = createReader(payload, 60);
      const timeBootMs = reader.getUint32();
      const totalCapacity = reader.getFloat32();
      const usedCapacity = reader.getFloat32();
      const availableCapacity = reader.getFloat32();
      const readSpeed = reader.getFloat32();
      const writeSpeed = reader.getFloat32();
      const storageId = reader.getUint8();
      const storageCount = reader.getUint8();
      const status = reader.getUint8();
      const type = reader.getUint8();
      const name = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      return {
        timeBootMs,
        storageId,
        storageCount,
        status: storageStatusInverseMap.get(status) as StorageStatus,
        totalCapacity,
        usedCapacity,
        availableCapacity,
        readSpeed,
        writeSpeed,
        type: storageTypeInverseMap.get(type) as StorageType,
        name,
      };
    },
    encode(_: StorageInformation) {
      const writer = createWriter(60);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.totalCapacity);
      writer.setFloat32(_.usedCapacity);
      writer.setFloat32(_.availableCapacity);
      writer.setFloat32(_.readSpeed);
      writer.setFloat32(_.writeSpeed);
      writer.setUint8(_.storageId);
      writer.setUint8(_.storageCount);
      writer.setUint8(lookup(_.status, storageStatusMap));
      writer.setUint8(lookup(_.type, storageTypeMap));
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 32);
      return writer.finish();
    },
  },
  CAMERA_CAPTURE_STATUS: {
    id: 262,
    crcExtra: 12,
    decode(payload: Uint8Array): CameraCaptureStatus {
      const reader = createReader(payload, 22);
      const timeBootMs = reader.getUint32();
      const imageInterval = reader.getFloat32();
      const recordingTimeMs = reader.getUint32();
      const availableCapacity = reader.getFloat32();
      const imageStatus = reader.getUint8();
      const videoStatus = reader.getUint8();
      const imageCount = reader.getInt32();
      return {
        timeBootMs,
        imageStatus,
        videoStatus,
        imageInterval,
        recordingTimeMs,
        availableCapacity,
        imageCount,
      };
    },
    encode(_: CameraCaptureStatus) {
      const writer = createWriter(22);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.imageInterval);
      writer.setUint32(_.recordingTimeMs);
      writer.setFloat32(_.availableCapacity);
      writer.setUint8(_.imageStatus);
      writer.setUint8(_.videoStatus);
      writer.setInt32(_.imageCount);
      return writer.finish();
    },
  },
  CAMERA_IMAGE_CAPTURED: {
    id: 263,
    crcExtra: 133,
    decode(payload: Uint8Array): CameraImageCaptured {
      const reader = createReader(payload, 255);
      const timeUtc = reader.getBigUint64();
      const timeBootMs = reader.getUint32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const relativeAlt = reader.getInt32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const imageIndex = reader.getInt32();
      const cameraId = reader.getUint8();
      const captureResult = reader.getInt8();
      const fileUrl = textDecoder.decode(reader.getUint8Array(205)).replace(/\0+$/, "");
      return {
        timeBootMs,
        timeUtc,
        cameraId,
        lat,
        lon,
        alt,
        relativeAlt,
        q,
        imageIndex,
        captureResult,
        fileUrl,
      };
    },
    encode(_: CameraImageCaptured) {
      const writer = createWriter(255);
      writer.setBigUint64(_.timeUtc);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setInt32(_.relativeAlt);
      _.q.forEach(writer.setFloat32);
      writer.setInt32(_.imageIndex);
      writer.setUint8(_.cameraId);
      writer.setInt8(_.captureResult);
      const fileUrlEncoded = textEncoder.encode(_.fileUrl);
      writer.setUint8Array(fileUrlEncoded, 205);
      return writer.finish();
    },
  },
  FLIGHT_INFORMATION: {
    id: 264,
    crcExtra: 49,
    decode(payload: Uint8Array): FlightInformation {
      const reader = createReader(payload, 28);
      const armingTimeUtc = reader.getBigUint64();
      const takeoffTimeUtc = reader.getBigUint64();
      const flightUuid = reader.getBigUint64();
      const timeBootMs = reader.getUint32();
      return {
        timeBootMs,
        armingTimeUtc,
        takeoffTimeUtc,
        flightUuid,
      };
    },
    encode(_: FlightInformation) {
      const writer = createWriter(28);
      writer.setBigUint64(_.armingTimeUtc);
      writer.setBigUint64(_.takeoffTimeUtc);
      writer.setBigUint64(_.flightUuid);
      writer.setUint32(_.timeBootMs);
      return writer.finish();
    },
  },
  MOUNT_ORIENTATION: {
    id: 265,
    crcExtra: 26,
    decode(payload: Uint8Array): MountOrientation {
      const reader = createReader(payload, 20);
      const timeBootMs = reader.getUint32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const yawAbsolute = reader.getFloat32();
      return {
        timeBootMs,
        roll,
        pitch,
        yaw,
        yawAbsolute,
      };
    },
    encode(_: MountOrientation) {
      const writer = createWriter(20);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.yawAbsolute);
      return writer.finish();
    },
  },
  LOGGING_DATA: {
    id: 266,
    crcExtra: 193,
    decode(payload: Uint8Array): LoggingData {
      const reader = createReader(payload, 255);
      const sequence = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const length = reader.getUint8();
      const firstMessageOffset = reader.getUint8();
      const data = reader.getUint8Array(249);
      return {
        targetSystem,
        targetComponent,
        sequence,
        length,
        firstMessageOffset,
        data,
      };
    },
    encode(_: LoggingData) {
      const writer = createWriter(255);
      writer.setUint16(_.sequence);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.length);
      writer.setUint8(_.firstMessageOffset);
      writer.setUint8Array(_.data, 249);
      return writer.finish();
    },
  },
  LOGGING_DATA_ACKED: {
    id: 267,
    crcExtra: 35,
    decode(payload: Uint8Array): LoggingDataAcked {
      const reader = createReader(payload, 255);
      const sequence = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const length = reader.getUint8();
      const firstMessageOffset = reader.getUint8();
      const data = reader.getUint8Array(249);
      return {
        targetSystem,
        targetComponent,
        sequence,
        length,
        firstMessageOffset,
        data,
      };
    },
    encode(_: LoggingDataAcked) {
      const writer = createWriter(255);
      writer.setUint16(_.sequence);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.length);
      writer.setUint8(_.firstMessageOffset);
      writer.setUint8Array(_.data, 249);
      return writer.finish();
    },
  },
  LOGGING_ACK: {
    id: 268,
    crcExtra: 14,
    decode(payload: Uint8Array): LoggingAck {
      const reader = createReader(payload, 4);
      const sequence = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        sequence,
      };
    },
    encode(_: LoggingAck) {
      const writer = createWriter(4);
      writer.setUint16(_.sequence);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  VIDEO_STREAM_INFORMATION: {
    id: 269,
    crcExtra: 109,
    decode(payload: Uint8Array): VideoStreamInformation {
      const reader = createReader(payload, 213);
      const framerate = reader.getFloat32();
      const bitrate = reader.getUint32();
      const flags = reader.getUint16();
      const resolutionH = reader.getUint16();
      const resolutionV = reader.getUint16();
      const rotation = reader.getUint16();
      const hfov = reader.getUint16();
      const streamId = reader.getUint8();
      const count = reader.getUint8();
      const type = reader.getUint8();
      const name = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      const uri = textDecoder.decode(reader.getUint8Array(160)).replace(/\0+$/, "");
      return {
        streamId,
        count,
        type: videoStreamTypeInverseMap.get(type) as VideoStreamType,
        flags: decodeBitmask(flags, videoStreamStatusFlagsInverseMap),
        framerate,
        resolutionH,
        resolutionV,
        bitrate,
        rotation,
        hfov,
        name,
        uri,
      };
    },
    encode(_: VideoStreamInformation) {
      const writer = createWriter(213);
      writer.setFloat32(_.framerate);
      writer.setUint32(_.bitrate);
      writer.setUint16(encodeBitmask(_.flags, videoStreamStatusFlagsMap));
      writer.setUint16(_.resolutionH);
      writer.setUint16(_.resolutionV);
      writer.setUint16(_.rotation);
      writer.setUint16(_.hfov);
      writer.setUint8(_.streamId);
      writer.setUint8(_.count);
      writer.setUint8(lookup(_.type, videoStreamTypeMap));
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 32);
      const uriEncoded = textEncoder.encode(_.uri);
      writer.setUint8Array(uriEncoded, 160);
      return writer.finish();
    },
  },
  VIDEO_STREAM_STATUS: {
    id: 270,
    crcExtra: 59,
    decode(payload: Uint8Array): VideoStreamStatus {
      const reader = createReader(payload, 19);
      const framerate = reader.getFloat32();
      const bitrate = reader.getUint32();
      const flags = reader.getUint16();
      const resolutionH = reader.getUint16();
      const resolutionV = reader.getUint16();
      const rotation = reader.getUint16();
      const hfov = reader.getUint16();
      const streamId = reader.getUint8();
      return {
        streamId,
        flags: decodeBitmask(flags, videoStreamStatusFlagsInverseMap),
        framerate,
        resolutionH,
        resolutionV,
        bitrate,
        rotation,
        hfov,
      };
    },
    encode(_: VideoStreamStatus) {
      const writer = createWriter(19);
      writer.setFloat32(_.framerate);
      writer.setUint32(_.bitrate);
      writer.setUint16(encodeBitmask(_.flags, videoStreamStatusFlagsMap));
      writer.setUint16(_.resolutionH);
      writer.setUint16(_.resolutionV);
      writer.setUint16(_.rotation);
      writer.setUint16(_.hfov);
      writer.setUint8(_.streamId);
      return writer.finish();
    },
  },
  CAMERA_FOV_STATUS: {
    id: 271,
    crcExtra: 22,
    decode(payload: Uint8Array): CameraFovStatus {
      const reader = createReader(payload, 52);
      const timeBootMs = reader.getUint32();
      const latCamera = reader.getInt32();
      const lonCamera = reader.getInt32();
      const altCamera = reader.getInt32();
      const latImage = reader.getInt32();
      const lonImage = reader.getInt32();
      const altImage = reader.getInt32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const hfov = reader.getFloat32();
      const vfov = reader.getFloat32();
      return {
        timeBootMs,
        latCamera,
        lonCamera,
        altCamera,
        latImage,
        lonImage,
        altImage,
        q,
        hfov,
        vfov,
      };
    },
    encode(_: CameraFovStatus) {
      const writer = createWriter(52);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.latCamera);
      writer.setInt32(_.lonCamera);
      writer.setInt32(_.altCamera);
      writer.setInt32(_.latImage);
      writer.setInt32(_.lonImage);
      writer.setInt32(_.altImage);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.hfov);
      writer.setFloat32(_.vfov);
      return writer.finish();
    },
  },
  CAMERA_TRACKING_IMAGE_STATUS: {
    id: 275,
    crcExtra: 126,
    decode(payload: Uint8Array): CameraTrackingImageStatus {
      const reader = createReader(payload, 31);
      const pointX = reader.getFloat32();
      const pointY = reader.getFloat32();
      const radius = reader.getFloat32();
      const recTopX = reader.getFloat32();
      const recTopY = reader.getFloat32();
      const recBottomX = reader.getFloat32();
      const recBottomY = reader.getFloat32();
      const trackingStatus = reader.getUint8();
      const trackingMode = reader.getUint8();
      const targetData = reader.getUint8();
      return {
        trackingStatus: cameraTrackingStatusFlagsInverseMap.get(trackingStatus) as CameraTrackingStatusFlags,
        trackingMode: cameraTrackingModeInverseMap.get(trackingMode) as CameraTrackingMode,
        targetData: decodeBitmask(targetData, cameraTrackingTargetDataInverseMap),
        pointX,
        pointY,
        radius,
        recTopX,
        recTopY,
        recBottomX,
        recBottomY,
      };
    },
    encode(_: CameraTrackingImageStatus) {
      const writer = createWriter(31);
      writer.setFloat32(_.pointX);
      writer.setFloat32(_.pointY);
      writer.setFloat32(_.radius);
      writer.setFloat32(_.recTopX);
      writer.setFloat32(_.recTopY);
      writer.setFloat32(_.recBottomX);
      writer.setFloat32(_.recBottomY);
      writer.setUint8(lookup(_.trackingStatus, cameraTrackingStatusFlagsMap));
      writer.setUint8(lookup(_.trackingMode, cameraTrackingModeMap));
      writer.setUint8(encodeBitmask(_.targetData, cameraTrackingTargetDataMap));
      return writer.finish();
    },
  },
  CAMERA_TRACKING_GEO_STATUS: {
    id: 276,
    crcExtra: 18,
    decode(payload: Uint8Array): CameraTrackingGeoStatus {
      const reader = createReader(payload, 49);
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getFloat32();
      const hAcc = reader.getFloat32();
      const vAcc = reader.getFloat32();
      const velN = reader.getFloat32();
      const velE = reader.getFloat32();
      const velD = reader.getFloat32();
      const velAcc = reader.getFloat32();
      const dist = reader.getFloat32();
      const hdg = reader.getFloat32();
      const hdgAcc = reader.getFloat32();
      const trackingStatus = reader.getUint8();
      return {
        trackingStatus: cameraTrackingStatusFlagsInverseMap.get(trackingStatus) as CameraTrackingStatusFlags,
        lat,
        lon,
        alt,
        hAcc,
        vAcc,
        velN,
        velE,
        velD,
        velAcc,
        dist,
        hdg,
        hdgAcc,
      };
    },
    encode(_: CameraTrackingGeoStatus) {
      const writer = createWriter(49);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.hAcc);
      writer.setFloat32(_.vAcc);
      writer.setFloat32(_.velN);
      writer.setFloat32(_.velE);
      writer.setFloat32(_.velD);
      writer.setFloat32(_.velAcc);
      writer.setFloat32(_.dist);
      writer.setFloat32(_.hdg);
      writer.setFloat32(_.hdgAcc);
      writer.setUint8(lookup(_.trackingStatus, cameraTrackingStatusFlagsMap));
      return writer.finish();
    },
  },
  GIMBAL_MANAGER_INFORMATION: {
    id: 280,
    crcExtra: 70,
    decode(payload: Uint8Array): GimbalManagerInformation {
      const reader = createReader(payload, 33);
      const timeBootMs = reader.getUint32();
      const capFlags = reader.getUint32();
      const rollMin = reader.getFloat32();
      const rollMax = reader.getFloat32();
      const pitchMin = reader.getFloat32();
      const pitchMax = reader.getFloat32();
      const yawMin = reader.getFloat32();
      const yawMax = reader.getFloat32();
      const gimbalDeviceId = reader.getUint8();
      return {
        timeBootMs,
        capFlags: decodeBitmask(capFlags, gimbalManagerCapFlagsInverseMap),
        gimbalDeviceId,
        rollMin,
        rollMax,
        pitchMin,
        pitchMax,
        yawMin,
        yawMax,
      };
    },
    encode(_: GimbalManagerInformation) {
      const writer = createWriter(33);
      writer.setUint32(_.timeBootMs);
      writer.setUint32(encodeBitmask(_.capFlags, gimbalManagerCapFlagsMap));
      writer.setFloat32(_.rollMin);
      writer.setFloat32(_.rollMax);
      writer.setFloat32(_.pitchMin);
      writer.setFloat32(_.pitchMax);
      writer.setFloat32(_.yawMin);
      writer.setFloat32(_.yawMax);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  GIMBAL_MANAGER_STATUS: {
    id: 281,
    crcExtra: 48,
    decode(payload: Uint8Array): GimbalManagerStatus {
      const reader = createReader(payload, 13);
      const timeBootMs = reader.getUint32();
      const flags = reader.getUint32();
      const gimbalDeviceId = reader.getUint8();
      const primaryControlSysid = reader.getUint8();
      const primaryControlCompid = reader.getUint8();
      const secondaryControlSysid = reader.getUint8();
      const secondaryControlCompid = reader.getUint8();
      return {
        timeBootMs,
        flags: decodeBitmask(flags, gimbalManagerFlagsInverseMap),
        gimbalDeviceId,
        primaryControlSysid,
        primaryControlCompid,
        secondaryControlSysid,
        secondaryControlCompid,
      };
    },
    encode(_: GimbalManagerStatus) {
      const writer = createWriter(13);
      writer.setUint32(_.timeBootMs);
      writer.setUint32(encodeBitmask(_.flags, gimbalManagerFlagsMap));
      writer.setUint8(_.gimbalDeviceId);
      writer.setUint8(_.primaryControlSysid);
      writer.setUint8(_.primaryControlCompid);
      writer.setUint8(_.secondaryControlSysid);
      writer.setUint8(_.secondaryControlCompid);
      return writer.finish();
    },
  },
  GIMBAL_MANAGER_SET_ATTITUDE: {
    id: 282,
    crcExtra: 123,
    decode(payload: Uint8Array): GimbalManagerSetAttitude {
      const reader = createReader(payload, 35);
      const flags = reader.getUint32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const angularVelocityX = reader.getFloat32();
      const angularVelocityY = reader.getFloat32();
      const angularVelocityZ = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const gimbalDeviceId = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        flags: decodeBitmask(flags, gimbalManagerFlagsInverseMap),
        gimbalDeviceId,
        q,
        angularVelocityX,
        angularVelocityY,
        angularVelocityZ,
      };
    },
    encode(_: GimbalManagerSetAttitude) {
      const writer = createWriter(35);
      writer.setUint32(encodeBitmask(_.flags, gimbalManagerFlagsMap));
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.angularVelocityX);
      writer.setFloat32(_.angularVelocityY);
      writer.setFloat32(_.angularVelocityZ);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  GIMBAL_DEVICE_INFORMATION: {
    id: 283,
    crcExtra: 74,
    decode(payload: Uint8Array): GimbalDeviceInformation {
      const reader = createReader(payload, 145);
      const uid = reader.getBigUint64();
      const timeBootMs = reader.getUint32();
      const firmwareVersion = reader.getUint32();
      const hardwareVersion = reader.getUint32();
      const rollMin = reader.getFloat32();
      const rollMax = reader.getFloat32();
      const pitchMin = reader.getFloat32();
      const pitchMax = reader.getFloat32();
      const yawMin = reader.getFloat32();
      const yawMax = reader.getFloat32();
      const capFlags = reader.getUint16();
      const customCapFlags = reader.getUint16();
      const vendorName = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      const modelName = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      const customName = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      const gimbalDeviceId = reader.getUint8();
      return {
        timeBootMs,
        vendorName,
        modelName,
        customName,
        firmwareVersion,
        hardwareVersion,
        uid,
        capFlags: decodeBitmask(capFlags, gimbalDeviceCapFlagsInverseMap),
        customCapFlags,
        rollMin,
        rollMax,
        pitchMin,
        pitchMax,
        yawMin,
        yawMax,
        gimbalDeviceId,
      };
    },
    encode(_: GimbalDeviceInformation) {
      const writer = createWriter(145);
      writer.setBigUint64(_.uid);
      writer.setUint32(_.timeBootMs);
      writer.setUint32(_.firmwareVersion);
      writer.setUint32(_.hardwareVersion);
      writer.setFloat32(_.rollMin);
      writer.setFloat32(_.rollMax);
      writer.setFloat32(_.pitchMin);
      writer.setFloat32(_.pitchMax);
      writer.setFloat32(_.yawMin);
      writer.setFloat32(_.yawMax);
      writer.setUint16(encodeBitmask(_.capFlags, gimbalDeviceCapFlagsMap));
      writer.setUint16(_.customCapFlags);
      const vendorNameEncoded = textEncoder.encode(_.vendorName);
      writer.setUint8Array(vendorNameEncoded, 32);
      const modelNameEncoded = textEncoder.encode(_.modelName);
      writer.setUint8Array(modelNameEncoded, 32);
      const customNameEncoded = textEncoder.encode(_.customName);
      writer.setUint8Array(customNameEncoded, 32);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  GIMBAL_DEVICE_SET_ATTITUDE: {
    id: 284,
    crcExtra: 99,
    decode(payload: Uint8Array): GimbalDeviceSetAttitude {
      const reader = createReader(payload, 32);
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const angularVelocityX = reader.getFloat32();
      const angularVelocityY = reader.getFloat32();
      const angularVelocityZ = reader.getFloat32();
      const flags = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        flags: decodeBitmask(flags, gimbalDeviceFlagsInverseMap),
        q,
        angularVelocityX,
        angularVelocityY,
        angularVelocityZ,
      };
    },
    encode(_: GimbalDeviceSetAttitude) {
      const writer = createWriter(32);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.angularVelocityX);
      writer.setFloat32(_.angularVelocityY);
      writer.setFloat32(_.angularVelocityZ);
      writer.setUint16(encodeBitmask(_.flags, gimbalDeviceFlagsMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  GIMBAL_DEVICE_ATTITUDE_STATUS: {
    id: 285,
    crcExtra: 137,
    decode(payload: Uint8Array): GimbalDeviceAttitudeStatus {
      const reader = createReader(payload, 49);
      const timeBootMs = reader.getUint32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const angularVelocityX = reader.getFloat32();
      const angularVelocityY = reader.getFloat32();
      const angularVelocityZ = reader.getFloat32();
      const failureFlags = reader.getUint32();
      const flags = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const deltaYaw = reader.getFloat32();
      const deltaYawVelocity = reader.getFloat32();
      const gimbalDeviceId = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        timeBootMs,
        flags: decodeBitmask(flags, gimbalDeviceFlagsInverseMap),
        q,
        angularVelocityX,
        angularVelocityY,
        angularVelocityZ,
        failureFlags: decodeBitmask(failureFlags, gimbalDeviceErrorFlagsInverseMap),
        deltaYaw,
        deltaYawVelocity,
        gimbalDeviceId,
      };
    },
    encode(_: GimbalDeviceAttitudeStatus) {
      const writer = createWriter(49);
      writer.setUint32(_.timeBootMs);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.angularVelocityX);
      writer.setFloat32(_.angularVelocityY);
      writer.setFloat32(_.angularVelocityZ);
      writer.setUint32(encodeBitmask(_.failureFlags, gimbalDeviceErrorFlagsMap));
      writer.setUint16(encodeBitmask(_.flags, gimbalDeviceFlagsMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setFloat32(_.deltaYaw);
      writer.setFloat32(_.deltaYawVelocity);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  AUTOPILOT_STATE_FOR_GIMBAL_DEVICE: {
    id: 286,
    crcExtra: 210,
    decode(payload: Uint8Array): AutopilotStateForGimbalDevice {
      const reader = createReader(payload, 57);
      const timeBootUs = reader.getBigUint64();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const qEstimatedDelayUs = reader.getUint32();
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const vEstimatedDelayUs = reader.getUint32();
      const feedForwardAngularVelocityZ = reader.getFloat32();
      const estimatorStatus = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const landedState = reader.getUint8();
      const angularVelocityZ = reader.getFloat32();
      return {
        targetSystem,
        targetComponent,
        timeBootUs,
        q,
        qEstimatedDelayUs,
        vx,
        vy,
        vz,
        vEstimatedDelayUs,
        feedForwardAngularVelocityZ,
        estimatorStatus: decodeBitmask(estimatorStatus, estimatorStatusFlagsInverseMap),
        landedState: mavLandedStateInverseMap.get(landedState) as MavLandedState,
        angularVelocityZ,
      };
    },
    encode(_: AutopilotStateForGimbalDevice) {
      const writer = createWriter(57);
      writer.setBigUint64(_.timeBootUs);
      _.q.forEach(writer.setFloat32);
      writer.setUint32(_.qEstimatedDelayUs);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setUint32(_.vEstimatedDelayUs);
      writer.setFloat32(_.feedForwardAngularVelocityZ);
      writer.setUint16(encodeBitmask(_.estimatorStatus, estimatorStatusFlagsMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.landedState, mavLandedStateMap));
      writer.setFloat32(_.angularVelocityZ);
      return writer.finish();
    },
  },
  GIMBAL_MANAGER_SET_PITCHYAW: {
    id: 287,
    crcExtra: 1,
    decode(payload: Uint8Array): GimbalManagerSetPitchyaw {
      const reader = createReader(payload, 23);
      const flags = reader.getUint32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const pitchRate = reader.getFloat32();
      const yawRate = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const gimbalDeviceId = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        flags: decodeBitmask(flags, gimbalManagerFlagsInverseMap),
        gimbalDeviceId,
        pitch,
        yaw,
        pitchRate,
        yawRate,
      };
    },
    encode(_: GimbalManagerSetPitchyaw) {
      const writer = createWriter(23);
      writer.setUint32(encodeBitmask(_.flags, gimbalManagerFlagsMap));
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.pitchRate);
      writer.setFloat32(_.yawRate);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  GIMBAL_MANAGER_SET_MANUAL_CONTROL: {
    id: 288,
    crcExtra: 20,
    decode(payload: Uint8Array): GimbalManagerSetManualControl {
      const reader = createReader(payload, 23);
      const flags = reader.getUint32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const pitchRate = reader.getFloat32();
      const yawRate = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const gimbalDeviceId = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        flags: decodeBitmask(flags, gimbalManagerFlagsInverseMap),
        gimbalDeviceId,
        pitch,
        yaw,
        pitchRate,
        yawRate,
      };
    },
    encode(_: GimbalManagerSetManualControl) {
      const writer = createWriter(23);
      writer.setUint32(encodeBitmask(_.flags, gimbalManagerFlagsMap));
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.pitchRate);
      writer.setFloat32(_.yawRate);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.gimbalDeviceId);
      return writer.finish();
    },
  },
  WIFI_CONFIG_AP: {
    id: 299,
    crcExtra: 19,
    decode(payload: Uint8Array): WifiConfigAp {
      const reader = createReader(payload, 96);
      const ssid = textDecoder.decode(reader.getUint8Array(32)).replace(/\0+$/, "");
      const password = textDecoder.decode(reader.getUint8Array(64)).replace(/\0+$/, "");
      return {
        ssid,
        password,
      };
    },
    encode(_: WifiConfigAp) {
      const writer = createWriter(96);
      const ssidEncoded = textEncoder.encode(_.ssid);
      writer.setUint8Array(ssidEncoded, 32);
      const passwordEncoded = textEncoder.encode(_.password);
      writer.setUint8Array(passwordEncoded, 64);
      return writer.finish();
    },
  },
  AIS_VESSEL: {
    id: 301,
    crcExtra: 243,
    decode(payload: Uint8Array): AisVessel {
      const reader = createReader(payload, 58);
      const mmsi = reader.getUint32();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const cog = reader.getUint16();
      const heading = reader.getUint16();
      const velocity = reader.getUint16();
      const dimensionBow = reader.getUint16();
      const dimensionStern = reader.getUint16();
      const tslc = reader.getUint16();
      const flags = reader.getUint16();
      const turnRate = reader.getInt8();
      const navigationalStatus = reader.getUint8();
      const type = reader.getUint8();
      const dimensionPort = reader.getUint8();
      const dimensionStarboard = reader.getUint8();
      const callsign = textDecoder.decode(reader.getUint8Array(7)).replace(/\0+$/, "");
      const name = textDecoder.decode(reader.getUint8Array(20)).replace(/\0+$/, "");
      return {
        mmsi,
        lat,
        lon,
        cog,
        heading,
        velocity,
        turnRate,
        navigationalStatus: aisNavStatusInverseMap.get(navigationalStatus) as AisNavStatus,
        type: aisTypeInverseMap.get(type) as AisType,
        dimensionBow,
        dimensionStern,
        dimensionPort,
        dimensionStarboard,
        callsign,
        name,
        tslc,
        flags: decodeBitmask(flags, aisFlagsInverseMap),
      };
    },
    encode(_: AisVessel) {
      const writer = createWriter(58);
      writer.setUint32(_.mmsi);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setUint16(_.cog);
      writer.setUint16(_.heading);
      writer.setUint16(_.velocity);
      writer.setUint16(_.dimensionBow);
      writer.setUint16(_.dimensionStern);
      writer.setUint16(_.tslc);
      writer.setUint16(encodeBitmask(_.flags, aisFlagsMap));
      writer.setInt8(_.turnRate);
      writer.setUint8(lookup(_.navigationalStatus, aisNavStatusMap));
      writer.setUint8(lookup(_.type, aisTypeMap));
      writer.setUint8(_.dimensionPort);
      writer.setUint8(_.dimensionStarboard);
      const callsignEncoded = textEncoder.encode(_.callsign);
      writer.setUint8Array(callsignEncoded, 7);
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 20);
      return writer.finish();
    },
  },
  UAVCAN_NODE_STATUS: {
    id: 310,
    crcExtra: 28,
    decode(payload: Uint8Array): UavcanNodeStatus {
      const reader = createReader(payload, 17);
      const timeUsec = reader.getBigUint64();
      const uptimeSec = reader.getUint32();
      const vendorSpecificStatusCode = reader.getUint16();
      const health = reader.getUint8();
      const mode = reader.getUint8();
      const subMode = reader.getUint8();
      return {
        timeUsec,
        uptimeSec,
        health: uavcanNodeHealthInverseMap.get(health) as UavcanNodeHealth,
        mode: uavcanNodeModeInverseMap.get(mode) as UavcanNodeMode,
        subMode,
        vendorSpecificStatusCode,
      };
    },
    encode(_: UavcanNodeStatus) {
      const writer = createWriter(17);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.uptimeSec);
      writer.setUint16(_.vendorSpecificStatusCode);
      writer.setUint8(lookup(_.health, uavcanNodeHealthMap));
      writer.setUint8(lookup(_.mode, uavcanNodeModeMap));
      writer.setUint8(_.subMode);
      return writer.finish();
    },
  },
  UAVCAN_NODE_INFO: {
    id: 311,
    crcExtra: 95,
    decode(payload: Uint8Array): UavcanNodeInfo {
      const reader = createReader(payload, 116);
      const timeUsec = reader.getBigUint64();
      const uptimeSec = reader.getUint32();
      const swVcsCommit = reader.getUint32();
      const name = textDecoder.decode(reader.getUint8Array(80)).replace(/\0+$/, "");
      const hwVersionMajor = reader.getUint8();
      const hwVersionMinor = reader.getUint8();
      const hwUniqueId = reader.getUint8Array(16);
      const swVersionMajor = reader.getUint8();
      const swVersionMinor = reader.getUint8();
      return {
        timeUsec,
        uptimeSec,
        name,
        hwVersionMajor,
        hwVersionMinor,
        hwUniqueId,
        swVersionMajor,
        swVersionMinor,
        swVcsCommit,
      };
    },
    encode(_: UavcanNodeInfo) {
      const writer = createWriter(116);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.uptimeSec);
      writer.setUint32(_.swVcsCommit);
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 80);
      writer.setUint8(_.hwVersionMajor);
      writer.setUint8(_.hwVersionMinor);
      writer.setUint8Array(_.hwUniqueId, 16);
      writer.setUint8(_.swVersionMajor);
      writer.setUint8(_.swVersionMinor);
      return writer.finish();
    },
  },
  PARAM_EXT_REQUEST_READ: {
    id: 320,
    crcExtra: 243,
    decode(payload: Uint8Array): ParamExtRequestRead {
      const reader = createReader(payload, 20);
      const paramIndex = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      return {
        targetSystem,
        targetComponent,
        paramId,
        paramIndex,
      };
    },
    encode(_: ParamExtRequestRead) {
      const writer = createWriter(20);
      writer.setInt16(_.paramIndex);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      return writer.finish();
    },
  },
  PARAM_EXT_REQUEST_LIST: {
    id: 321,
    crcExtra: 88,
    decode(payload: Uint8Array): ParamExtRequestList {
      const reader = createReader(payload, 2);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
      };
    },
    encode(_: ParamExtRequestList) {
      const writer = createWriter(2);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  PARAM_EXT_VALUE: {
    id: 322,
    crcExtra: 243,
    decode(payload: Uint8Array): ParamExtValue {
      const reader = createReader(payload, 149);
      const paramCount = reader.getUint16();
      const paramIndex = reader.getUint16();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const paramValue = textDecoder.decode(reader.getUint8Array(128)).replace(/\0+$/, "");
      const paramType = reader.getUint8();
      return {
        paramId,
        paramValue,
        paramType: mavParamExtTypeInverseMap.get(paramType) as MavParamExtType,
        paramCount,
        paramIndex,
      };
    },
    encode(_: ParamExtValue) {
      const writer = createWriter(149);
      writer.setUint16(_.paramCount);
      writer.setUint16(_.paramIndex);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      const paramValueEncoded = textEncoder.encode(_.paramValue);
      writer.setUint8Array(paramValueEncoded, 128);
      writer.setUint8(lookup(_.paramType, mavParamExtTypeMap));
      return writer.finish();
    },
  },
  PARAM_EXT_SET: {
    id: 323,
    crcExtra: 78,
    decode(payload: Uint8Array): ParamExtSet {
      const reader = createReader(payload, 147);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const paramValue = textDecoder.decode(reader.getUint8Array(128)).replace(/\0+$/, "");
      const paramType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        paramId,
        paramValue,
        paramType: mavParamExtTypeInverseMap.get(paramType) as MavParamExtType,
      };
    },
    encode(_: ParamExtSet) {
      const writer = createWriter(147);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      const paramValueEncoded = textEncoder.encode(_.paramValue);
      writer.setUint8Array(paramValueEncoded, 128);
      writer.setUint8(lookup(_.paramType, mavParamExtTypeMap));
      return writer.finish();
    },
  },
  PARAM_EXT_ACK: {
    id: 324,
    crcExtra: 132,
    decode(payload: Uint8Array): ParamExtAck {
      const reader = createReader(payload, 146);
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const paramValue = textDecoder.decode(reader.getUint8Array(128)).replace(/\0+$/, "");
      const paramType = reader.getUint8();
      const paramResult = reader.getUint8();
      return {
        paramId,
        paramValue,
        paramType: mavParamExtTypeInverseMap.get(paramType) as MavParamExtType,
        paramResult: paramAckInverseMap.get(paramResult) as ParamAck,
      };
    },
    encode(_: ParamExtAck) {
      const writer = createWriter(146);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      const paramValueEncoded = textEncoder.encode(_.paramValue);
      writer.setUint8Array(paramValueEncoded, 128);
      writer.setUint8(lookup(_.paramType, mavParamExtTypeMap));
      writer.setUint8(lookup(_.paramResult, paramAckMap));
      return writer.finish();
    },
  },
  OBSTACLE_DISTANCE: {
    id: 330,
    crcExtra: 23,
    decode(payload: Uint8Array): ObstacleDistance {
      const reader = createReader(payload, 167);
      const timeUsec = reader.getBigUint64();
      const distances = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const minDistance = reader.getUint16();
      const maxDistance = reader.getUint16();
      const sensorType = reader.getUint8();
      const increment = reader.getUint8();
      const incrementF = reader.getFloat32();
      const angleOffset = reader.getFloat32();
      const frame = reader.getUint8();
      return {
        timeUsec,
        sensorType: mavDistanceSensorInverseMap.get(sensorType) as MavDistanceSensor,
        distances,
        increment,
        minDistance,
        maxDistance,
        incrementF,
        angleOffset,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
      };
    },
    encode(_: ObstacleDistance) {
      const writer = createWriter(167);
      writer.setBigUint64(_.timeUsec);
      _.distances.forEach(writer.setUint16);
      writer.setUint16(_.minDistance);
      writer.setUint16(_.maxDistance);
      writer.setUint8(lookup(_.sensorType, mavDistanceSensorMap));
      writer.setUint8(_.increment);
      writer.setFloat32(_.incrementF);
      writer.setFloat32(_.angleOffset);
      writer.setUint8(lookup(_.frame, mavFrameMap));
      return writer.finish();
    },
  },
  ODOMETRY: {
    id: 331,
    crcExtra: 91,
    decode(payload: Uint8Array): Odometry {
      const reader = createReader(payload, 233);
      const timeUsec = reader.getBigUint64();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const q = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const rollspeed = reader.getFloat32();
      const pitchspeed = reader.getFloat32();
      const yawspeed = reader.getFloat32();
      const poseCovariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const velocityCovariance = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const frameId = reader.getUint8();
      const childFrameId = reader.getUint8();
      const resetCounter = reader.getUint8();
      const estimatorType = reader.getUint8();
      const quality = reader.getInt8();
      return {
        timeUsec,
        frameId: mavFrameInverseMap.get(frameId) as MavFrame,
        childFrameId: mavFrameInverseMap.get(childFrameId) as MavFrame,
        x,
        y,
        z,
        q,
        vx,
        vy,
        vz,
        rollspeed,
        pitchspeed,
        yawspeed,
        poseCovariance,
        velocityCovariance,
        resetCounter,
        estimatorType: mavEstimatorTypeInverseMap.get(estimatorType) as MavEstimatorType,
        quality,
      };
    },
    encode(_: Odometry) {
      const writer = createWriter(233);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      _.q.forEach(writer.setFloat32);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.rollspeed);
      writer.setFloat32(_.pitchspeed);
      writer.setFloat32(_.yawspeed);
      _.poseCovariance.forEach(writer.setFloat32);
      _.velocityCovariance.forEach(writer.setFloat32);
      writer.setUint8(lookup(_.frameId, mavFrameMap));
      writer.setUint8(lookup(_.childFrameId, mavFrameMap));
      writer.setUint8(_.resetCounter);
      writer.setUint8(lookup(_.estimatorType, mavEstimatorTypeMap));
      writer.setInt8(_.quality);
      return writer.finish();
    },
  },
  ISBD_LINK_STATUS: {
    id: 335,
    crcExtra: 225,
    decode(payload: Uint8Array): IsbdLinkStatus {
      const reader = createReader(payload, 24);
      const timestamp = reader.getBigUint64();
      const lastHeartbeat = reader.getBigUint64();
      const failedSessions = reader.getUint16();
      const successfulSessions = reader.getUint16();
      const signalQuality = reader.getUint8();
      const ringPending = reader.getUint8();
      const txSessionPending = reader.getUint8();
      const rxSessionPending = reader.getUint8();
      return {
        timestamp,
        lastHeartbeat,
        failedSessions,
        successfulSessions,
        signalQuality,
        ringPending,
        txSessionPending,
        rxSessionPending,
      };
    },
    encode(_: IsbdLinkStatus) {
      const writer = createWriter(24);
      writer.setBigUint64(_.timestamp);
      writer.setBigUint64(_.lastHeartbeat);
      writer.setUint16(_.failedSessions);
      writer.setUint16(_.successfulSessions);
      writer.setUint8(_.signalQuality);
      writer.setUint8(_.ringPending);
      writer.setUint8(_.txSessionPending);
      writer.setUint8(_.rxSessionPending);
      return writer.finish();
    },
  },
  RAW_RPM: {
    id: 339,
    crcExtra: 199,
    decode(payload: Uint8Array): RawRpm {
      const reader = createReader(payload, 5);
      const frequency = reader.getFloat32();
      const index = reader.getUint8();
      return {
        index,
        frequency,
      };
    },
    encode(_: RawRpm) {
      const writer = createWriter(5);
      writer.setFloat32(_.frequency);
      writer.setUint8(_.index);
      return writer.finish();
    },
  },
  UTM_GLOBAL_POSITION: {
    id: 340,
    crcExtra: 99,
    decode(payload: Uint8Array): UtmGlobalPosition {
      const reader = createReader(payload, 70);
      const time = reader.getBigUint64();
      const lat = reader.getInt32();
      const lon = reader.getInt32();
      const alt = reader.getInt32();
      const relativeAlt = reader.getInt32();
      const nextLat = reader.getInt32();
      const nextLon = reader.getInt32();
      const nextAlt = reader.getInt32();
      const vx = reader.getInt16();
      const vy = reader.getInt16();
      const vz = reader.getInt16();
      const hAcc = reader.getUint16();
      const vAcc = reader.getUint16();
      const velAcc = reader.getUint16();
      const updateRate = reader.getUint16();
      const uasId = reader.getUint8Array(18);
      const flightState = reader.getUint8();
      const flags = reader.getUint8();
      return {
        time,
        uasId,
        lat,
        lon,
        alt,
        relativeAlt,
        vx,
        vy,
        vz,
        hAcc,
        vAcc,
        velAcc,
        nextLat,
        nextLon,
        nextAlt,
        updateRate,
        flightState: utmFlightStateInverseMap.get(flightState) as UtmFlightState,
        flags: decodeBitmask(flags, utmDataAvailFlagsInverseMap),
      };
    },
    encode(_: UtmGlobalPosition) {
      const writer = createWriter(70);
      writer.setBigUint64(_.time);
      writer.setInt32(_.lat);
      writer.setInt32(_.lon);
      writer.setInt32(_.alt);
      writer.setInt32(_.relativeAlt);
      writer.setInt32(_.nextLat);
      writer.setInt32(_.nextLon);
      writer.setInt32(_.nextAlt);
      writer.setInt16(_.vx);
      writer.setInt16(_.vy);
      writer.setInt16(_.vz);
      writer.setUint16(_.hAcc);
      writer.setUint16(_.vAcc);
      writer.setUint16(_.velAcc);
      writer.setUint16(_.updateRate);
      writer.setUint8Array(_.uasId, 18);
      writer.setUint8(lookup(_.flightState, utmFlightStateMap));
      writer.setUint8(encodeBitmask(_.flags, utmDataAvailFlagsMap));
      return writer.finish();
    },
  },
  DEBUG_FLOAT_ARRAY: {
    id: 350,
    crcExtra: 232,
    decode(payload: Uint8Array): DebugFloatArray {
      const reader = createReader(payload, 252);
      const timeUsec = reader.getBigUint64();
      const arrayId = reader.getUint16();
      const name = textDecoder.decode(reader.getUint8Array(10)).replace(/\0+$/, "");
      const data = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      return {
        timeUsec,
        name,
        arrayId,
        data,
      };
    },
    encode(_: DebugFloatArray) {
      const writer = createWriter(252);
      writer.setBigUint64(_.timeUsec);
      writer.setUint16(_.arrayId);
      const nameEncoded = textEncoder.encode(_.name);
      writer.setUint8Array(nameEncoded, 10);
      _.data.forEach(writer.setFloat32);
      return writer.finish();
    },
  },
  SMART_BATTERY_INFO: {
    id: 370,
    crcExtra: 75,
    decode(payload: Uint8Array): SmartBatteryInfo {
      const reader = createReader(payload, 109);
      const capacityFullSpecification = reader.getInt32();
      const capacityFull = reader.getInt32();
      const cycleCount = reader.getUint16();
      const weight = reader.getUint16();
      const dischargeMinimumVoltage = reader.getUint16();
      const chargingMinimumVoltage = reader.getUint16();
      const restingMinimumVoltage = reader.getUint16();
      const id = reader.getUint8();
      const batteryFunction = reader.getUint8();
      const type = reader.getUint8();
      const serialNumber = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const deviceName = textDecoder.decode(reader.getUint8Array(50)).replace(/\0+$/, "");
      const chargingMaximumVoltage = reader.getUint16();
      const cellsInSeries = reader.getUint8();
      const dischargeMaximumCurrent = reader.getUint32();
      const dischargeMaximumBurstCurrent = reader.getUint32();
      const manufactureDate = textDecoder.decode(reader.getUint8Array(11)).replace(/\0+$/, "");
      return {
        id,
        batteryFunction: mavBatteryFunctionInverseMap.get(batteryFunction) as MavBatteryFunction,
        type: mavBatteryTypeInverseMap.get(type) as MavBatteryType,
        capacityFullSpecification,
        capacityFull,
        cycleCount,
        serialNumber,
        deviceName,
        weight,
        dischargeMinimumVoltage,
        chargingMinimumVoltage,
        restingMinimumVoltage,
        chargingMaximumVoltage,
        cellsInSeries,
        dischargeMaximumCurrent,
        dischargeMaximumBurstCurrent,
        manufactureDate,
      };
    },
    encode(_: SmartBatteryInfo) {
      const writer = createWriter(109);
      writer.setInt32(_.capacityFullSpecification);
      writer.setInt32(_.capacityFull);
      writer.setUint16(_.cycleCount);
      writer.setUint16(_.weight);
      writer.setUint16(_.dischargeMinimumVoltage);
      writer.setUint16(_.chargingMinimumVoltage);
      writer.setUint16(_.restingMinimumVoltage);
      writer.setUint8(_.id);
      writer.setUint8(lookup(_.batteryFunction, mavBatteryFunctionMap));
      writer.setUint8(lookup(_.type, mavBatteryTypeMap));
      const serialNumberEncoded = textEncoder.encode(_.serialNumber);
      writer.setUint8Array(serialNumberEncoded, 16);
      const deviceNameEncoded = textEncoder.encode(_.deviceName);
      writer.setUint8Array(deviceNameEncoded, 50);
      writer.setUint16(_.chargingMaximumVoltage);
      writer.setUint8(_.cellsInSeries);
      writer.setUint32(_.dischargeMaximumCurrent);
      writer.setUint32(_.dischargeMaximumBurstCurrent);
      const manufactureDateEncoded = textEncoder.encode(_.manufactureDate);
      writer.setUint8Array(manufactureDateEncoded, 11);
      return writer.finish();
    },
  },
  GENERATOR_STATUS: {
    id: 373,
    crcExtra: 117,
    decode(payload: Uint8Array): GeneratorStatus {
      const reader = createReader(payload, 42);
      const status = reader.getBigUint64();
      const batteryCurrent = reader.getFloat32();
      const loadCurrent = reader.getFloat32();
      const powerGenerated = reader.getFloat32();
      const busVoltage = reader.getFloat32();
      const batCurrentSetpoint = reader.getFloat32();
      const runtime = reader.getUint32();
      const timeUntilMaintenance = reader.getInt32();
      const generatorSpeed = reader.getUint16();
      const rectifierTemperature = reader.getInt16();
      const generatorTemperature = reader.getInt16();
      return {
        status: decodeBitmask(status, mavGeneratorStatusFlagInverseMap),
        generatorSpeed,
        batteryCurrent,
        loadCurrent,
        powerGenerated,
        busVoltage,
        rectifierTemperature,
        batCurrentSetpoint,
        generatorTemperature,
        runtime,
        timeUntilMaintenance,
      };
    },
    encode(_: GeneratorStatus) {
      const writer = createWriter(42);
      writer.setBigUint64(encodeBitmask(_.status, mavGeneratorStatusFlagMap));
      writer.setFloat32(_.batteryCurrent);
      writer.setFloat32(_.loadCurrent);
      writer.setFloat32(_.powerGenerated);
      writer.setFloat32(_.busVoltage);
      writer.setFloat32(_.batCurrentSetpoint);
      writer.setUint32(_.runtime);
      writer.setInt32(_.timeUntilMaintenance);
      writer.setUint16(_.generatorSpeed);
      writer.setInt16(_.rectifierTemperature);
      writer.setInt16(_.generatorTemperature);
      return writer.finish();
    },
  },
  ACTUATOR_OUTPUT_STATUS: {
    id: 375,
    crcExtra: 251,
    decode(payload: Uint8Array): ActuatorOutputStatus {
      const reader = createReader(payload, 140);
      const timeUsec = reader.getBigUint64();
      const active = reader.getUint32();
      const actuator = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      return {
        timeUsec,
        active,
        actuator,
      };
    },
    encode(_: ActuatorOutputStatus) {
      const writer = createWriter(140);
      writer.setBigUint64(_.timeUsec);
      writer.setUint32(_.active);
      _.actuator.forEach(writer.setFloat32);
      return writer.finish();
    },
  },
  RELAY_STATUS: {
    id: 376,
    crcExtra: 199,
    decode(payload: Uint8Array): RelayStatus {
      const reader = createReader(payload, 8);
      const timeBootMs = reader.getUint32();
      const on = reader.getUint16();
      const present = reader.getUint16();
      return {
        timeBootMs,
        on,
        present,
      };
    },
    encode(_: RelayStatus) {
      const writer = createWriter(8);
      writer.setUint32(_.timeBootMs);
      writer.setUint16(_.on);
      writer.setUint16(_.present);
      return writer.finish();
    },
  },
  TUNNEL: {
    id: 385,
    crcExtra: 147,
    decode(payload: Uint8Array): Tunnel {
      const reader = createReader(payload, 133);
      const payloadType = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const payloadLength = reader.getUint8();
      const payload_ = reader.getUint8Array(128);
      return {
        targetSystem,
        targetComponent,
        payloadType: mavTunnelPayloadTypeInverseMap.get(payloadType) as MavTunnelPayloadType,
        payloadLength,
        payload_,
      };
    },
    encode(_: Tunnel) {
      const writer = createWriter(133);
      writer.setUint16(lookup(_.payloadType, mavTunnelPayloadTypeMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.payloadLength);
      writer.setUint8Array(_.payload_, 128);
      return writer.finish();
    },
  },
  CAN_FRAME: {
    id: 386,
    crcExtra: 132,
    decode(payload: Uint8Array): CanFrame {
      const reader = createReader(payload, 16);
      const id = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const bus = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(8);
      return {
        targetSystem,
        targetComponent,
        bus,
        len,
        id,
        data,
      };
    },
    encode(_: CanFrame) {
      const writer = createWriter(16);
      writer.setUint32(_.id);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.bus);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 8);
      return writer.finish();
    },
  },
  CANFD_FRAME: {
    id: 387,
    crcExtra: 4,
    decode(payload: Uint8Array): CanfdFrame {
      const reader = createReader(payload, 72);
      const id = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const bus = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(64);
      return {
        targetSystem,
        targetComponent,
        bus,
        len,
        id,
        data,
      };
    },
    encode(_: CanfdFrame) {
      const writer = createWriter(72);
      writer.setUint32(_.id);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.bus);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 64);
      return writer.finish();
    },
  },
  CAN_FILTER_MODIFY: {
    id: 388,
    crcExtra: 8,
    decode(payload: Uint8Array): CanFilterModify {
      const reader = createReader(payload, 37);
      const ids = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const bus = reader.getUint8();
      const operation = reader.getUint8();
      const numIds = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        bus,
        operation: canFilterOpInverseMap.get(operation) as CanFilterOp,
        numIds,
        ids,
      };
    },
    encode(_: CanFilterModify) {
      const writer = createWriter(37);
      _.ids.forEach(writer.setUint16);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.bus);
      writer.setUint8(lookup(_.operation, canFilterOpMap));
      writer.setUint8(_.numIds);
      return writer.finish();
    },
  },
  WHEEL_DISTANCE: {
    id: 9000,
    crcExtra: 113,
    decode(payload: Uint8Array): WheelDistance {
      const reader = createReader(payload, 137);
      const timeUsec = reader.getBigUint64();
      const distance = [reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64(), reader.getFloat64()] as const;
      const count = reader.getUint8();
      return {
        timeUsec,
        count,
        distance,
      };
    },
    encode(_: WheelDistance) {
      const writer = createWriter(137);
      writer.setBigUint64(_.timeUsec);
      _.distance.forEach(writer.setFloat64);
      writer.setUint8(_.count);
      return writer.finish();
    },
  },
  WINCH_STATUS: {
    id: 9005,
    crcExtra: 117,
    decode(payload: Uint8Array): WinchStatus {
      const reader = createReader(payload, 34);
      const timeUsec = reader.getBigUint64();
      const lineLength = reader.getFloat32();
      const speed = reader.getFloat32();
      const tension = reader.getFloat32();
      const voltage = reader.getFloat32();
      const current = reader.getFloat32();
      const status = reader.getUint32();
      const temperature = reader.getInt16();
      return {
        timeUsec,
        lineLength,
        speed,
        tension,
        voltage,
        current,
        temperature,
        status: decodeBitmask(status, mavWinchStatusFlagInverseMap),
      };
    },
    encode(_: WinchStatus) {
      const writer = createWriter(34);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.lineLength);
      writer.setFloat32(_.speed);
      writer.setFloat32(_.tension);
      writer.setFloat32(_.voltage);
      writer.setFloat32(_.current);
      writer.setUint32(encodeBitmask(_.status, mavWinchStatusFlagMap));
      writer.setInt16(_.temperature);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_BASIC_ID: {
    id: 12900,
    crcExtra: 114,
    decode(payload: Uint8Array): OpenDroneIdBasicId {
      const reader = createReader(payload, 44);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const idType = reader.getUint8();
      const uaType = reader.getUint8();
      const uasId = reader.getUint8Array(20);
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        idType: mavOdidIdTypeInverseMap.get(idType) as MavOdidIdType,
        uaType: mavOdidUaTypeInverseMap.get(uaType) as MavOdidUaType,
        uasId,
      };
    },
    encode(_: OpenDroneIdBasicId) {
      const writer = createWriter(44);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(lookup(_.idType, mavOdidIdTypeMap));
      writer.setUint8(lookup(_.uaType, mavOdidUaTypeMap));
      writer.setUint8Array(_.uasId, 20);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_LOCATION: {
    id: 12901,
    crcExtra: 254,
    decode(payload: Uint8Array): OpenDroneIdLocation {
      const reader = createReader(payload, 59);
      const latitude = reader.getInt32();
      const longitude = reader.getInt32();
      const altitudeBarometric = reader.getFloat32();
      const altitudeGeodetic = reader.getFloat32();
      const height = reader.getFloat32();
      const timestamp = reader.getFloat32();
      const direction = reader.getUint16();
      const speedHorizontal = reader.getUint16();
      const speedVertical = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const status = reader.getUint8();
      const heightReference = reader.getUint8();
      const horizontalAccuracy = reader.getUint8();
      const verticalAccuracy = reader.getUint8();
      const barometerAccuracy = reader.getUint8();
      const speedAccuracy = reader.getUint8();
      const timestampAccuracy = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        status: mavOdidStatusInverseMap.get(status) as MavOdidStatus,
        direction,
        speedHorizontal,
        speedVertical,
        latitude,
        longitude,
        altitudeBarometric,
        altitudeGeodetic,
        heightReference: mavOdidHeightRefInverseMap.get(heightReference) as MavOdidHeightRef,
        height,
        horizontalAccuracy: mavOdidHorAccInverseMap.get(horizontalAccuracy) as MavOdidHorAcc,
        verticalAccuracy: mavOdidVerAccInverseMap.get(verticalAccuracy) as MavOdidVerAcc,
        barometerAccuracy: mavOdidVerAccInverseMap.get(barometerAccuracy) as MavOdidVerAcc,
        speedAccuracy: mavOdidSpeedAccInverseMap.get(speedAccuracy) as MavOdidSpeedAcc,
        timestamp,
        timestampAccuracy: mavOdidTimeAccInverseMap.get(timestampAccuracy) as MavOdidTimeAcc,
      };
    },
    encode(_: OpenDroneIdLocation) {
      const writer = createWriter(59);
      writer.setInt32(_.latitude);
      writer.setInt32(_.longitude);
      writer.setFloat32(_.altitudeBarometric);
      writer.setFloat32(_.altitudeGeodetic);
      writer.setFloat32(_.height);
      writer.setFloat32(_.timestamp);
      writer.setUint16(_.direction);
      writer.setUint16(_.speedHorizontal);
      writer.setInt16(_.speedVertical);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(lookup(_.status, mavOdidStatusMap));
      writer.setUint8(lookup(_.heightReference, mavOdidHeightRefMap));
      writer.setUint8(lookup(_.horizontalAccuracy, mavOdidHorAccMap));
      writer.setUint8(lookup(_.verticalAccuracy, mavOdidVerAccMap));
      writer.setUint8(lookup(_.barometerAccuracy, mavOdidVerAccMap));
      writer.setUint8(lookup(_.speedAccuracy, mavOdidSpeedAccMap));
      writer.setUint8(lookup(_.timestampAccuracy, mavOdidTimeAccMap));
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_AUTHENTICATION: {
    id: 12902,
    crcExtra: 140,
    decode(payload: Uint8Array): OpenDroneIdAuthentication {
      const reader = createReader(payload, 53);
      const timestamp = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const authenticationType = reader.getUint8();
      const dataPage = reader.getUint8();
      const lastPageIndex = reader.getUint8();
      const length = reader.getUint8();
      const authenticationData = reader.getUint8Array(23);
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        authenticationType: mavOdidAuthTypeInverseMap.get(authenticationType) as MavOdidAuthType,
        dataPage,
        lastPageIndex,
        length,
        timestamp,
        authenticationData,
      };
    },
    encode(_: OpenDroneIdAuthentication) {
      const writer = createWriter(53);
      writer.setUint32(_.timestamp);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(lookup(_.authenticationType, mavOdidAuthTypeMap));
      writer.setUint8(_.dataPage);
      writer.setUint8(_.lastPageIndex);
      writer.setUint8(_.length);
      writer.setUint8Array(_.authenticationData, 23);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_SELF_ID: {
    id: 12903,
    crcExtra: 249,
    decode(payload: Uint8Array): OpenDroneIdSelfId {
      const reader = createReader(payload, 46);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const descriptionType = reader.getUint8();
      const description = textDecoder.decode(reader.getUint8Array(23)).replace(/\0+$/, "");
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        descriptionType: mavOdidDescTypeInverseMap.get(descriptionType) as MavOdidDescType,
        description,
      };
    },
    encode(_: OpenDroneIdSelfId) {
      const writer = createWriter(46);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(lookup(_.descriptionType, mavOdidDescTypeMap));
      const descriptionEncoded = textEncoder.encode(_.description);
      writer.setUint8Array(descriptionEncoded, 23);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_SYSTEM: {
    id: 12904,
    crcExtra: 77,
    decode(payload: Uint8Array): OpenDroneIdSystem {
      const reader = createReader(payload, 54);
      const operatorLatitude = reader.getInt32();
      const operatorLongitude = reader.getInt32();
      const areaCeiling = reader.getFloat32();
      const areaFloor = reader.getFloat32();
      const operatorAltitudeGeo = reader.getFloat32();
      const timestamp = reader.getUint32();
      const areaCount = reader.getUint16();
      const areaRadius = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const operatorLocationType = reader.getUint8();
      const classificationType = reader.getUint8();
      const categoryEu = reader.getUint8();
      const classEu = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        operatorLocationType: mavOdidOperatorLocationTypeInverseMap.get(operatorLocationType) as MavOdidOperatorLocationType,
        classificationType: mavOdidClassificationTypeInverseMap.get(classificationType) as MavOdidClassificationType,
        operatorLatitude,
        operatorLongitude,
        areaCount,
        areaRadius,
        areaCeiling,
        areaFloor,
        categoryEu: mavOdidCategoryEuInverseMap.get(categoryEu) as MavOdidCategoryEu,
        classEu: mavOdidClassEuInverseMap.get(classEu) as MavOdidClassEu,
        operatorAltitudeGeo,
        timestamp,
      };
    },
    encode(_: OpenDroneIdSystem) {
      const writer = createWriter(54);
      writer.setInt32(_.operatorLatitude);
      writer.setInt32(_.operatorLongitude);
      writer.setFloat32(_.areaCeiling);
      writer.setFloat32(_.areaFloor);
      writer.setFloat32(_.operatorAltitudeGeo);
      writer.setUint32(_.timestamp);
      writer.setUint16(_.areaCount);
      writer.setUint16(_.areaRadius);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(lookup(_.operatorLocationType, mavOdidOperatorLocationTypeMap));
      writer.setUint8(lookup(_.classificationType, mavOdidClassificationTypeMap));
      writer.setUint8(lookup(_.categoryEu, mavOdidCategoryEuMap));
      writer.setUint8(lookup(_.classEu, mavOdidClassEuMap));
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_OPERATOR_ID: {
    id: 12905,
    crcExtra: 49,
    decode(payload: Uint8Array): OpenDroneIdOperatorId {
      const reader = createReader(payload, 43);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const operatorIdType = reader.getUint8();
      const operatorId = textDecoder.decode(reader.getUint8Array(20)).replace(/\0+$/, "");
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        operatorIdType: mavOdidOperatorIdTypeInverseMap.get(operatorIdType) as MavOdidOperatorIdType,
        operatorId,
      };
    },
    encode(_: OpenDroneIdOperatorId) {
      const writer = createWriter(43);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(lookup(_.operatorIdType, mavOdidOperatorIdTypeMap));
      const operatorIdEncoded = textEncoder.encode(_.operatorId);
      writer.setUint8Array(operatorIdEncoded, 20);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_ARM_STATUS: {
    id: 12918,
    crcExtra: 139,
    decode(payload: Uint8Array): OpenDroneIdArmStatus {
      const reader = createReader(payload, 51);
      const status = reader.getUint8();
      const error = textDecoder.decode(reader.getUint8Array(50)).replace(/\0+$/, "");
      return {
        status: mavOdidArmStatusInverseMap.get(status) as MavOdidArmStatus,
        error,
      };
    },
    encode(_: OpenDroneIdArmStatus) {
      const writer = createWriter(51);
      writer.setUint8(lookup(_.status, mavOdidArmStatusMap));
      const errorEncoded = textEncoder.encode(_.error);
      writer.setUint8Array(errorEncoded, 50);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_MESSAGE_PACK: {
    id: 12915,
    crcExtra: 94,
    decode(payload: Uint8Array): OpenDroneIdMessagePack {
      const reader = createReader(payload, 249);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idOrMac = reader.getUint8Array(20);
      const singleMessageSize = reader.getUint8();
      const msgPackSize = reader.getUint8();
      const messages = reader.getUint8Array(225);
      return {
        targetSystem,
        targetComponent,
        idOrMac,
        singleMessageSize,
        msgPackSize,
        messages,
      };
    },
    encode(_: OpenDroneIdMessagePack) {
      const writer = createWriter(249);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.idOrMac, 20);
      writer.setUint8(_.singleMessageSize);
      writer.setUint8(_.msgPackSize);
      writer.setUint8Array(_.messages, 225);
      return writer.finish();
    },
  },
  OPEN_DRONE_ID_SYSTEM_UPDATE: {
    id: 12919,
    crcExtra: 7,
    decode(payload: Uint8Array): OpenDroneIdSystemUpdate {
      const reader = createReader(payload, 18);
      const operatorLatitude = reader.getInt32();
      const operatorLongitude = reader.getInt32();
      const operatorAltitudeGeo = reader.getFloat32();
      const timestamp = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        operatorLatitude,
        operatorLongitude,
        operatorAltitudeGeo,
        timestamp,
      };
    },
    encode(_: OpenDroneIdSystemUpdate) {
      const writer = createWriter(18);
      writer.setInt32(_.operatorLatitude);
      writer.setInt32(_.operatorLongitude);
      writer.setFloat32(_.operatorAltitudeGeo);
      writer.setUint32(_.timestamp);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  HYGROMETER_SENSOR: {
    id: 12920,
    crcExtra: 20,
    decode(payload: Uint8Array): HygrometerSensor {
      const reader = createReader(payload, 5);
      const temperature = reader.getInt16();
      const humidity = reader.getUint16();
      const id = reader.getUint8();
      return {
        id,
        temperature,
        humidity,
      };
    },
    encode(_: HygrometerSensor) {
      const writer = createWriter(5);
      writer.setInt16(_.temperature);
      writer.setUint16(_.humidity);
      writer.setUint8(_.id);
      return writer.finish();
    },
  },
  SENSOR_OFFSETS: {
    id: 150,
    crcExtra: 134,
    decode(payload: Uint8Array): SensorOffsets {
      const reader = createReader(payload, 42);
      const magDeclination = reader.getFloat32();
      const rawPress = reader.getInt32();
      const rawTemp = reader.getInt32();
      const gyroCalX = reader.getFloat32();
      const gyroCalY = reader.getFloat32();
      const gyroCalZ = reader.getFloat32();
      const accelCalX = reader.getFloat32();
      const accelCalY = reader.getFloat32();
      const accelCalZ = reader.getFloat32();
      const magOfsX = reader.getInt16();
      const magOfsY = reader.getInt16();
      const magOfsZ = reader.getInt16();
      return {
        magOfsX,
        magOfsY,
        magOfsZ,
        magDeclination,
        rawPress,
        rawTemp,
        gyroCalX,
        gyroCalY,
        gyroCalZ,
        accelCalX,
        accelCalY,
        accelCalZ,
      };
    },
    encode(_: SensorOffsets) {
      const writer = createWriter(42);
      writer.setFloat32(_.magDeclination);
      writer.setInt32(_.rawPress);
      writer.setInt32(_.rawTemp);
      writer.setFloat32(_.gyroCalX);
      writer.setFloat32(_.gyroCalY);
      writer.setFloat32(_.gyroCalZ);
      writer.setFloat32(_.accelCalX);
      writer.setFloat32(_.accelCalY);
      writer.setFloat32(_.accelCalZ);
      writer.setInt16(_.magOfsX);
      writer.setInt16(_.magOfsY);
      writer.setInt16(_.magOfsZ);
      return writer.finish();
    },
  },
  SET_MAG_OFFSETS: {
    id: 151,
    crcExtra: 219,
    decode(payload: Uint8Array): SetMagOffsets {
      const reader = createReader(payload, 8);
      const magOfsX = reader.getInt16();
      const magOfsY = reader.getInt16();
      const magOfsZ = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        magOfsX,
        magOfsY,
        magOfsZ,
      };
    },
    encode(_: SetMagOffsets) {
      const writer = createWriter(8);
      writer.setInt16(_.magOfsX);
      writer.setInt16(_.magOfsY);
      writer.setInt16(_.magOfsZ);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  MEMINFO: {
    id: 152,
    crcExtra: 208,
    decode(payload: Uint8Array): Meminfo {
      const reader = createReader(payload, 8);
      const brkval = reader.getUint16();
      const freemem = reader.getUint16();
      const freemem32 = reader.getUint32();
      return {
        brkval,
        freemem,
        freemem32,
      };
    },
    encode(_: Meminfo) {
      const writer = createWriter(8);
      writer.setUint16(_.brkval);
      writer.setUint16(_.freemem);
      writer.setUint32(_.freemem32);
      return writer.finish();
    },
  },
  AP_ADC: {
    id: 153,
    crcExtra: 188,
    decode(payload: Uint8Array): ApAdc {
      const reader = createReader(payload, 12);
      const adc1 = reader.getUint16();
      const adc2 = reader.getUint16();
      const adc3 = reader.getUint16();
      const adc4 = reader.getUint16();
      const adc5 = reader.getUint16();
      const adc6 = reader.getUint16();
      return {
        adc1,
        adc2,
        adc3,
        adc4,
        adc5,
        adc6,
      };
    },
    encode(_: ApAdc) {
      const writer = createWriter(12);
      writer.setUint16(_.adc1);
      writer.setUint16(_.adc2);
      writer.setUint16(_.adc3);
      writer.setUint16(_.adc4);
      writer.setUint16(_.adc5);
      writer.setUint16(_.adc6);
      return writer.finish();
    },
  },
  DIGICAM_CONFIGURE: {
    id: 154,
    crcExtra: 84,
    decode(payload: Uint8Array): DigicamConfigure {
      const reader = createReader(payload, 15);
      const extraValue = reader.getFloat32();
      const shutterSpeed = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const mode = reader.getUint8();
      const aperture = reader.getUint8();
      const iso = reader.getUint8();
      const exposureType = reader.getUint8();
      const commandId = reader.getUint8();
      const engineCutOff = reader.getUint8();
      const extraParam = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        mode,
        shutterSpeed,
        aperture,
        iso,
        exposureType,
        commandId,
        engineCutOff,
        extraParam,
        extraValue,
      };
    },
    encode(_: DigicamConfigure) {
      const writer = createWriter(15);
      writer.setFloat32(_.extraValue);
      writer.setUint16(_.shutterSpeed);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.mode);
      writer.setUint8(_.aperture);
      writer.setUint8(_.iso);
      writer.setUint8(_.exposureType);
      writer.setUint8(_.commandId);
      writer.setUint8(_.engineCutOff);
      writer.setUint8(_.extraParam);
      return writer.finish();
    },
  },
  DIGICAM_CONTROL: {
    id: 155,
    crcExtra: 22,
    decode(payload: Uint8Array): DigicamControl {
      const reader = createReader(payload, 13);
      const extraValue = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const session = reader.getUint8();
      const zoomPos = reader.getUint8();
      const zoomStep = reader.getInt8();
      const focusLock = reader.getUint8();
      const shot = reader.getUint8();
      const commandId = reader.getUint8();
      const extraParam = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        session,
        zoomPos,
        zoomStep,
        focusLock,
        shot,
        commandId,
        extraParam,
        extraValue,
      };
    },
    encode(_: DigicamControl) {
      const writer = createWriter(13);
      writer.setFloat32(_.extraValue);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.session);
      writer.setUint8(_.zoomPos);
      writer.setInt8(_.zoomStep);
      writer.setUint8(_.focusLock);
      writer.setUint8(_.shot);
      writer.setUint8(_.commandId);
      writer.setUint8(_.extraParam);
      return writer.finish();
    },
  },
  MOUNT_CONFIGURE: {
    id: 156,
    crcExtra: 19,
    decode(payload: Uint8Array): MountConfigure {
      const reader = createReader(payload, 6);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const mountMode = reader.getUint8();
      const stabRoll = reader.getUint8();
      const stabPitch = reader.getUint8();
      const stabYaw = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        mountMode: mavMountModeInverseMap.get(mountMode) as MavMountMode,
        stabRoll,
        stabPitch,
        stabYaw,
      };
    },
    encode(_: MountConfigure) {
      const writer = createWriter(6);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.mountMode, mavMountModeMap));
      writer.setUint8(_.stabRoll);
      writer.setUint8(_.stabPitch);
      writer.setUint8(_.stabYaw);
      return writer.finish();
    },
  },
  MOUNT_CONTROL: {
    id: 157,
    crcExtra: 21,
    decode(payload: Uint8Array): MountControl {
      const reader = createReader(payload, 15);
      const inputA = reader.getInt32();
      const inputB = reader.getInt32();
      const inputC = reader.getInt32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const savePosition = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        inputA,
        inputB,
        inputC,
        savePosition,
      };
    },
    encode(_: MountControl) {
      const writer = createWriter(15);
      writer.setInt32(_.inputA);
      writer.setInt32(_.inputB);
      writer.setInt32(_.inputC);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.savePosition);
      return writer.finish();
    },
  },
  MOUNT_STATUS: {
    id: 158,
    crcExtra: 134,
    decode(payload: Uint8Array): MountStatus {
      const reader = createReader(payload, 15);
      const pointingA = reader.getInt32();
      const pointingB = reader.getInt32();
      const pointingC = reader.getInt32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const mountMode = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        pointingA,
        pointingB,
        pointingC,
        mountMode: mavMountModeInverseMap.get(mountMode) as MavMountMode,
      };
    },
    encode(_: MountStatus) {
      const writer = createWriter(15);
      writer.setInt32(_.pointingA);
      writer.setInt32(_.pointingB);
      writer.setInt32(_.pointingC);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.mountMode, mavMountModeMap));
      return writer.finish();
    },
  },
  FENCE_POINT: {
    id: 160,
    crcExtra: 78,
    decode(payload: Uint8Array): FencePoint {
      const reader = createReader(payload, 12);
      const lat = reader.getFloat32();
      const lng = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idx = reader.getUint8();
      const count = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        idx,
        count,
        lat,
        lng,
      };
    },
    encode(_: FencePoint) {
      const writer = createWriter(12);
      writer.setFloat32(_.lat);
      writer.setFloat32(_.lng);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.idx);
      writer.setUint8(_.count);
      return writer.finish();
    },
  },
  FENCE_FETCH_POINT: {
    id: 161,
    crcExtra: 68,
    decode(payload: Uint8Array): FenceFetchPoint {
      const reader = createReader(payload, 3);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idx = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        idx,
      };
    },
    encode(_: FenceFetchPoint) {
      const writer = createWriter(3);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.idx);
      return writer.finish();
    },
  },
  AHRS: {
    id: 163,
    crcExtra: 127,
    decode(payload: Uint8Array): Ahrs {
      const reader = createReader(payload, 28);
      const omegaIx = reader.getFloat32();
      const omegaIy = reader.getFloat32();
      const omegaIz = reader.getFloat32();
      const accelWeight = reader.getFloat32();
      const renormVal = reader.getFloat32();
      const errorRp = reader.getFloat32();
      const errorYaw = reader.getFloat32();
      return {
        omegaIx,
        omegaIy,
        omegaIz,
        accelWeight,
        renormVal,
        errorRp,
        errorYaw,
      };
    },
    encode(_: Ahrs) {
      const writer = createWriter(28);
      writer.setFloat32(_.omegaIx);
      writer.setFloat32(_.omegaIy);
      writer.setFloat32(_.omegaIz);
      writer.setFloat32(_.accelWeight);
      writer.setFloat32(_.renormVal);
      writer.setFloat32(_.errorRp);
      writer.setFloat32(_.errorYaw);
      return writer.finish();
    },
  },
  SIMSTATE: {
    id: 164,
    crcExtra: 154,
    decode(payload: Uint8Array): Simstate {
      const reader = createReader(payload, 44);
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const xacc = reader.getFloat32();
      const yacc = reader.getFloat32();
      const zacc = reader.getFloat32();
      const xgyro = reader.getFloat32();
      const ygyro = reader.getFloat32();
      const zgyro = reader.getFloat32();
      const lat = reader.getInt32();
      const lng = reader.getInt32();
      return {
        roll,
        pitch,
        yaw,
        xacc,
        yacc,
        zacc,
        xgyro,
        ygyro,
        zgyro,
        lat,
        lng,
      };
    },
    encode(_: Simstate) {
      const writer = createWriter(44);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.xacc);
      writer.setFloat32(_.yacc);
      writer.setFloat32(_.zacc);
      writer.setFloat32(_.xgyro);
      writer.setFloat32(_.ygyro);
      writer.setFloat32(_.zgyro);
      writer.setInt32(_.lat);
      writer.setInt32(_.lng);
      return writer.finish();
    },
  },
  HWSTATUS: {
    id: 165,
    crcExtra: 21,
    decode(payload: Uint8Array): Hwstatus {
      const reader = createReader(payload, 3);
      const vcc = reader.getUint16();
      const i2Cerr = reader.getUint8();
      return {
        vcc,
        i2Cerr,
      };
    },
    encode(_: Hwstatus) {
      const writer = createWriter(3);
      writer.setUint16(_.vcc);
      writer.setUint8(_.i2Cerr);
      return writer.finish();
    },
  },
  RADIO: {
    id: 166,
    crcExtra: 21,
    decode(payload: Uint8Array): Radio {
      const reader = createReader(payload, 9);
      const rxerrors = reader.getUint16();
      const fixed = reader.getUint16();
      const rssi = reader.getUint8();
      const remrssi = reader.getUint8();
      const txbuf = reader.getUint8();
      const noise = reader.getUint8();
      const remnoise = reader.getUint8();
      return {
        rssi,
        remrssi,
        txbuf,
        noise,
        remnoise,
        rxerrors,
        fixed,
      };
    },
    encode(_: Radio) {
      const writer = createWriter(9);
      writer.setUint16(_.rxerrors);
      writer.setUint16(_.fixed);
      writer.setUint8(_.rssi);
      writer.setUint8(_.remrssi);
      writer.setUint8(_.txbuf);
      writer.setUint8(_.noise);
      writer.setUint8(_.remnoise);
      return writer.finish();
    },
  },
  LIMITS_STATUS: {
    id: 167,
    crcExtra: 144,
    decode(payload: Uint8Array): LimitsStatus {
      const reader = createReader(payload, 22);
      const lastTrigger = reader.getUint32();
      const lastAction = reader.getUint32();
      const lastRecovery = reader.getUint32();
      const lastClear = reader.getUint32();
      const breachCount = reader.getUint16();
      const limitsState = reader.getUint8();
      const modsEnabled = reader.getUint8();
      const modsRequired = reader.getUint8();
      const modsTriggered = reader.getUint8();
      return {
        limitsState: limitsStateInverseMap.get(limitsState) as LimitsState,
        lastTrigger,
        lastAction,
        lastRecovery,
        lastClear,
        breachCount,
        modsEnabled: decodeBitmask(modsEnabled, limitModuleInverseMap),
        modsRequired: decodeBitmask(modsRequired, limitModuleInverseMap),
        modsTriggered: decodeBitmask(modsTriggered, limitModuleInverseMap),
      };
    },
    encode(_: LimitsStatus) {
      const writer = createWriter(22);
      writer.setUint32(_.lastTrigger);
      writer.setUint32(_.lastAction);
      writer.setUint32(_.lastRecovery);
      writer.setUint32(_.lastClear);
      writer.setUint16(_.breachCount);
      writer.setUint8(lookup(_.limitsState, limitsStateMap));
      writer.setUint8(encodeBitmask(_.modsEnabled, limitModuleMap));
      writer.setUint8(encodeBitmask(_.modsRequired, limitModuleMap));
      writer.setUint8(encodeBitmask(_.modsTriggered, limitModuleMap));
      return writer.finish();
    },
  },
  WIND: {
    id: 168,
    crcExtra: 1,
    decode(payload: Uint8Array): Wind {
      const reader = createReader(payload, 12);
      const direction = reader.getFloat32();
      const speed = reader.getFloat32();
      const speedZ = reader.getFloat32();
      return {
        direction,
        speed,
        speedZ,
      };
    },
    encode(_: Wind) {
      const writer = createWriter(12);
      writer.setFloat32(_.direction);
      writer.setFloat32(_.speed);
      writer.setFloat32(_.speedZ);
      return writer.finish();
    },
  },
  DATA16: {
    id: 169,
    crcExtra: 234,
    decode(payload: Uint8Array): Data16 {
      const reader = createReader(payload, 18);
      const type = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(16);
      return {
        type,
        len,
        data,
      };
    },
    encode(_: Data16) {
      const writer = createWriter(18);
      writer.setUint8(_.type);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 16);
      return writer.finish();
    },
  },
  DATA32: {
    id: 170,
    crcExtra: 73,
    decode(payload: Uint8Array): Data32 {
      const reader = createReader(payload, 34);
      const type = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(32);
      return {
        type,
        len,
        data,
      };
    },
    encode(_: Data32) {
      const writer = createWriter(34);
      writer.setUint8(_.type);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 32);
      return writer.finish();
    },
  },
  DATA64: {
    id: 171,
    crcExtra: 181,
    decode(payload: Uint8Array): Data64 {
      const reader = createReader(payload, 66);
      const type = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(64);
      return {
        type,
        len,
        data,
      };
    },
    encode(_: Data64) {
      const writer = createWriter(66);
      writer.setUint8(_.type);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 64);
      return writer.finish();
    },
  },
  DATA96: {
    id: 172,
    crcExtra: 22,
    decode(payload: Uint8Array): Data96 {
      const reader = createReader(payload, 98);
      const type = reader.getUint8();
      const len = reader.getUint8();
      const data = reader.getUint8Array(96);
      return {
        type,
        len,
        data,
      };
    },
    encode(_: Data96) {
      const writer = createWriter(98);
      writer.setUint8(_.type);
      writer.setUint8(_.len);
      writer.setUint8Array(_.data, 96);
      return writer.finish();
    },
  },
  RANGEFINDER: {
    id: 173,
    crcExtra: 83,
    decode(payload: Uint8Array): Rangefinder {
      const reader = createReader(payload, 8);
      const distance = reader.getFloat32();
      const voltage = reader.getFloat32();
      return {
        distance,
        voltage,
      };
    },
    encode(_: Rangefinder) {
      const writer = createWriter(8);
      writer.setFloat32(_.distance);
      writer.setFloat32(_.voltage);
      return writer.finish();
    },
  },
  AIRSPEED_AUTOCAL: {
    id: 174,
    crcExtra: 167,
    decode(payload: Uint8Array): AirspeedAutocal {
      const reader = createReader(payload, 48);
      const vx = reader.getFloat32();
      const vy = reader.getFloat32();
      const vz = reader.getFloat32();
      const diffPressure = reader.getFloat32();
      const eas2tas = reader.getFloat32();
      const ratio = reader.getFloat32();
      const stateX = reader.getFloat32();
      const stateY = reader.getFloat32();
      const stateZ = reader.getFloat32();
      const pax = reader.getFloat32();
      const pby = reader.getFloat32();
      const pcz = reader.getFloat32();
      return {
        vx,
        vy,
        vz,
        diffPressure,
        eas2tas,
        ratio,
        stateX,
        stateY,
        stateZ,
        pax,
        pby,
        pcz,
      };
    },
    encode(_: AirspeedAutocal) {
      const writer = createWriter(48);
      writer.setFloat32(_.vx);
      writer.setFloat32(_.vy);
      writer.setFloat32(_.vz);
      writer.setFloat32(_.diffPressure);
      writer.setFloat32(_.eas2tas);
      writer.setFloat32(_.ratio);
      writer.setFloat32(_.stateX);
      writer.setFloat32(_.stateY);
      writer.setFloat32(_.stateZ);
      writer.setFloat32(_.pax);
      writer.setFloat32(_.pby);
      writer.setFloat32(_.pcz);
      return writer.finish();
    },
  },
  RALLY_POINT: {
    id: 175,
    crcExtra: 138,
    decode(payload: Uint8Array): RallyPoint {
      const reader = createReader(payload, 19);
      const lat = reader.getInt32();
      const lng = reader.getInt32();
      const alt = reader.getInt16();
      const breakAlt = reader.getInt16();
      const landDir = reader.getUint16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idx = reader.getUint8();
      const count = reader.getUint8();
      const flags = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        idx,
        count,
        lat,
        lng,
        alt,
        breakAlt,
        landDir,
        flags: decodeBitmask(flags, rallyFlagsInverseMap),
      };
    },
    encode(_: RallyPoint) {
      const writer = createWriter(19);
      writer.setInt32(_.lat);
      writer.setInt32(_.lng);
      writer.setInt16(_.alt);
      writer.setInt16(_.breakAlt);
      writer.setUint16(_.landDir);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.idx);
      writer.setUint8(_.count);
      writer.setUint8(encodeBitmask(_.flags, rallyFlagsMap));
      return writer.finish();
    },
  },
  RALLY_FETCH_POINT: {
    id: 176,
    crcExtra: 234,
    decode(payload: Uint8Array): RallyFetchPoint {
      const reader = createReader(payload, 3);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const idx = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        idx,
      };
    },
    encode(_: RallyFetchPoint) {
      const writer = createWriter(3);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.idx);
      return writer.finish();
    },
  },
  COMPASSMOT_STATUS: {
    id: 177,
    crcExtra: 240,
    decode(payload: Uint8Array): CompassmotStatus {
      const reader = createReader(payload, 20);
      const current = reader.getFloat32();
      const compensationX = reader.getFloat32();
      const compensationY = reader.getFloat32();
      const compensationZ = reader.getFloat32();
      const throttle = reader.getUint16();
      const interference = reader.getUint16();
      return {
        throttle,
        current,
        interference,
        compensationX,
        compensationY,
        compensationZ,
      };
    },
    encode(_: CompassmotStatus) {
      const writer = createWriter(20);
      writer.setFloat32(_.current);
      writer.setFloat32(_.compensationX);
      writer.setFloat32(_.compensationY);
      writer.setFloat32(_.compensationZ);
      writer.setUint16(_.throttle);
      writer.setUint16(_.interference);
      return writer.finish();
    },
  },
  AHRS2: {
    id: 178,
    crcExtra: 47,
    decode(payload: Uint8Array): Ahrs2 {
      const reader = createReader(payload, 24);
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const altitude = reader.getFloat32();
      const lat = reader.getInt32();
      const lng = reader.getInt32();
      return {
        roll,
        pitch,
        yaw,
        altitude,
        lat,
        lng,
      };
    },
    encode(_: Ahrs2) {
      const writer = createWriter(24);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.altitude);
      writer.setInt32(_.lat);
      writer.setInt32(_.lng);
      return writer.finish();
    },
  },
  CAMERA_STATUS: {
    id: 179,
    crcExtra: 189,
    decode(payload: Uint8Array): CameraStatus {
      const reader = createReader(payload, 29);
      const timeUsec = reader.getBigUint64();
      const p1 = reader.getFloat32();
      const p2 = reader.getFloat32();
      const p3 = reader.getFloat32();
      const p4 = reader.getFloat32();
      const imgIdx = reader.getUint16();
      const targetSystem = reader.getUint8();
      const camIdx = reader.getUint8();
      const eventId = reader.getUint8();
      return {
        timeUsec,
        targetSystem,
        camIdx,
        imgIdx,
        eventId: cameraStatusTypesInverseMap.get(eventId) as CameraStatusTypes,
        p1,
        p2,
        p3,
        p4,
      };
    },
    encode(_: CameraStatus) {
      const writer = createWriter(29);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.p1);
      writer.setFloat32(_.p2);
      writer.setFloat32(_.p3);
      writer.setFloat32(_.p4);
      writer.setUint16(_.imgIdx);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.camIdx);
      writer.setUint8(lookup(_.eventId, cameraStatusTypesMap));
      return writer.finish();
    },
  },
  CAMERA_FEEDBACK: {
    id: 180,
    crcExtra: 52,
    decode(payload: Uint8Array): CameraFeedback {
      const reader = createReader(payload, 47);
      const timeUsec = reader.getBigUint64();
      const lat = reader.getInt32();
      const lng = reader.getInt32();
      const altMsl = reader.getFloat32();
      const altRel = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const focLen = reader.getFloat32();
      const imgIdx = reader.getUint16();
      const targetSystem = reader.getUint8();
      const camIdx = reader.getUint8();
      const flags = reader.getUint8();
      const completedCaptures = reader.getUint16();
      return {
        timeUsec,
        targetSystem,
        camIdx,
        imgIdx,
        lat,
        lng,
        altMsl,
        altRel,
        roll,
        pitch,
        yaw,
        focLen,
        flags: cameraFeedbackFlagsInverseMap.get(flags) as CameraFeedbackFlags,
        completedCaptures,
      };
    },
    encode(_: CameraFeedback) {
      const writer = createWriter(47);
      writer.setBigUint64(_.timeUsec);
      writer.setInt32(_.lat);
      writer.setInt32(_.lng);
      writer.setFloat32(_.altMsl);
      writer.setFloat32(_.altRel);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.focLen);
      writer.setUint16(_.imgIdx);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.camIdx);
      writer.setUint8(lookup(_.flags, cameraFeedbackFlagsMap));
      writer.setUint16(_.completedCaptures);
      return writer.finish();
    },
  },
  BATTERY2: {
    id: 181,
    crcExtra: 174,
    decode(payload: Uint8Array): Battery2 {
      const reader = createReader(payload, 4);
      const voltage = reader.getUint16();
      const currentBattery = reader.getInt16();
      return {
        voltage,
        currentBattery,
      };
    },
    encode(_: Battery2) {
      const writer = createWriter(4);
      writer.setUint16(_.voltage);
      writer.setInt16(_.currentBattery);
      return writer.finish();
    },
  },
  AHRS3: {
    id: 182,
    crcExtra: 229,
    decode(payload: Uint8Array): Ahrs3 {
      const reader = createReader(payload, 40);
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const altitude = reader.getFloat32();
      const lat = reader.getInt32();
      const lng = reader.getInt32();
      const v1 = reader.getFloat32();
      const v2 = reader.getFloat32();
      const v3 = reader.getFloat32();
      const v4 = reader.getFloat32();
      return {
        roll,
        pitch,
        yaw,
        altitude,
        lat,
        lng,
        v1,
        v2,
        v3,
        v4,
      };
    },
    encode(_: Ahrs3) {
      const writer = createWriter(40);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.altitude);
      writer.setInt32(_.lat);
      writer.setInt32(_.lng);
      writer.setFloat32(_.v1);
      writer.setFloat32(_.v2);
      writer.setFloat32(_.v3);
      writer.setFloat32(_.v4);
      return writer.finish();
    },
  },
  AUTOPILOT_VERSION_REQUEST: {
    id: 183,
    crcExtra: 85,
    decode(payload: Uint8Array): AutopilotVersionRequest {
      const reader = createReader(payload, 2);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
      };
    },
    encode(_: AutopilotVersionRequest) {
      const writer = createWriter(2);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  REMOTE_LOG_DATA_BLOCK: {
    id: 184,
    crcExtra: 159,
    decode(payload: Uint8Array): RemoteLogDataBlock {
      const reader = createReader(payload, 206);
      const seqno = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const data = reader.getUint8Array(200);
      return {
        targetSystem,
        targetComponent,
        seqno: mavRemoteLogDataBlockCommandsInverseMap.get(seqno) as MavRemoteLogDataBlockCommands,
        data,
      };
    },
    encode(_: RemoteLogDataBlock) {
      const writer = createWriter(206);
      writer.setUint32(lookup(_.seqno, mavRemoteLogDataBlockCommandsMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8Array(_.data, 200);
      return writer.finish();
    },
  },
  REMOTE_LOG_BLOCK_STATUS: {
    id: 185,
    crcExtra: 186,
    decode(payload: Uint8Array): RemoteLogBlockStatus {
      const reader = createReader(payload, 7);
      const seqno = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const status = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        seqno,
        status: mavRemoteLogDataBlockStatusesInverseMap.get(status) as MavRemoteLogDataBlockStatuses,
      };
    },
    encode(_: RemoteLogBlockStatus) {
      const writer = createWriter(7);
      writer.setUint32(_.seqno);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.status, mavRemoteLogDataBlockStatusesMap));
      return writer.finish();
    },
  },
  LED_CONTROL: {
    id: 186,
    crcExtra: 72,
    decode(payload: Uint8Array): LedControl {
      const reader = createReader(payload, 29);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const instance = reader.getUint8();
      const pattern = reader.getUint8();
      const customLen = reader.getUint8();
      const customBytes = reader.getUint8Array(24);
      return {
        targetSystem,
        targetComponent,
        instance,
        pattern,
        customLen,
        customBytes,
      };
    },
    encode(_: LedControl) {
      const writer = createWriter(29);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.instance);
      writer.setUint8(_.pattern);
      writer.setUint8(_.customLen);
      writer.setUint8Array(_.customBytes, 24);
      return writer.finish();
    },
  },
  MAG_CAL_PROGRESS: {
    id: 191,
    crcExtra: 92,
    decode(payload: Uint8Array): MagCalProgress {
      const reader = createReader(payload, 27);
      const directionX = reader.getFloat32();
      const directionY = reader.getFloat32();
      const directionZ = reader.getFloat32();
      const compassId = reader.getUint8();
      const calMask = reader.getUint8();
      const calStatus = reader.getUint8();
      const attempt = reader.getUint8();
      const completionPct = reader.getUint8();
      const completionMask = reader.getUint8Array(10);
      return {
        compassId,
        calMask,
        calStatus: magCalStatusInverseMap.get(calStatus) as MagCalStatus,
        attempt,
        completionPct,
        completionMask,
        directionX,
        directionY,
        directionZ,
      };
    },
    encode(_: MagCalProgress) {
      const writer = createWriter(27);
      writer.setFloat32(_.directionX);
      writer.setFloat32(_.directionY);
      writer.setFloat32(_.directionZ);
      writer.setUint8(_.compassId);
      writer.setUint8(_.calMask);
      writer.setUint8(lookup(_.calStatus, magCalStatusMap));
      writer.setUint8(_.attempt);
      writer.setUint8(_.completionPct);
      writer.setUint8Array(_.completionMask, 10);
      return writer.finish();
    },
  },
  EKF_STATUS_REPORT: {
    id: 193,
    crcExtra: 71,
    decode(payload: Uint8Array): EkfStatusReport {
      const reader = createReader(payload, 26);
      const velocityVariance = reader.getFloat32();
      const posHorizVariance = reader.getFloat32();
      const posVertVariance = reader.getFloat32();
      const compassVariance = reader.getFloat32();
      const terrainAltVariance = reader.getFloat32();
      const flags = reader.getUint16();
      const airspeedVariance = reader.getFloat32();
      return {
        flags: decodeBitmask(flags, ekfStatusFlagsInverseMap),
        velocityVariance,
        posHorizVariance,
        posVertVariance,
        compassVariance,
        terrainAltVariance,
        airspeedVariance,
      };
    },
    encode(_: EkfStatusReport) {
      const writer = createWriter(26);
      writer.setFloat32(_.velocityVariance);
      writer.setFloat32(_.posHorizVariance);
      writer.setFloat32(_.posVertVariance);
      writer.setFloat32(_.compassVariance);
      writer.setFloat32(_.terrainAltVariance);
      writer.setUint16(encodeBitmask(_.flags, ekfStatusFlagsMap));
      writer.setFloat32(_.airspeedVariance);
      return writer.finish();
    },
  },
  PID_TUNING: {
    id: 194,
    crcExtra: 98,
    decode(payload: Uint8Array): PidTuning {
      const reader = createReader(payload, 33);
      const desired = reader.getFloat32();
      const achieved = reader.getFloat32();
      const ff = reader.getFloat32();
      const p = reader.getFloat32();
      const i = reader.getFloat32();
      const d = reader.getFloat32();
      const axis = reader.getUint8();
      const sRate = reader.getFloat32();
      const pDmod = reader.getFloat32();
      return {
        axis: pidTuningAxisInverseMap.get(axis) as PidTuningAxis,
        desired,
        achieved,
        ff,
        p,
        i,
        d,
        sRate,
        pDmod,
      };
    },
    encode(_: PidTuning) {
      const writer = createWriter(33);
      writer.setFloat32(_.desired);
      writer.setFloat32(_.achieved);
      writer.setFloat32(_.ff);
      writer.setFloat32(_.p);
      writer.setFloat32(_.i);
      writer.setFloat32(_.d);
      writer.setUint8(lookup(_.axis, pidTuningAxisMap));
      writer.setFloat32(_.sRate);
      writer.setFloat32(_.pDmod);
      return writer.finish();
    },
  },
  DEEPSTALL: {
    id: 195,
    crcExtra: 120,
    decode(payload: Uint8Array): Deepstall {
      const reader = createReader(payload, 37);
      const landingLat = reader.getInt32();
      const landingLon = reader.getInt32();
      const pathLat = reader.getInt32();
      const pathLon = reader.getInt32();
      const arcEntryLat = reader.getInt32();
      const arcEntryLon = reader.getInt32();
      const altitude = reader.getFloat32();
      const expectedTravelDistance = reader.getFloat32();
      const crossTrackError = reader.getFloat32();
      const stage = reader.getUint8();
      return {
        landingLat,
        landingLon,
        pathLat,
        pathLon,
        arcEntryLat,
        arcEntryLon,
        altitude,
        expectedTravelDistance,
        crossTrackError,
        stage: deepstallStageInverseMap.get(stage) as DeepstallStage,
      };
    },
    encode(_: Deepstall) {
      const writer = createWriter(37);
      writer.setInt32(_.landingLat);
      writer.setInt32(_.landingLon);
      writer.setInt32(_.pathLat);
      writer.setInt32(_.pathLon);
      writer.setInt32(_.arcEntryLat);
      writer.setInt32(_.arcEntryLon);
      writer.setFloat32(_.altitude);
      writer.setFloat32(_.expectedTravelDistance);
      writer.setFloat32(_.crossTrackError);
      writer.setUint8(lookup(_.stage, deepstallStageMap));
      return writer.finish();
    },
  },
  GIMBAL_REPORT: {
    id: 200,
    crcExtra: 134,
    decode(payload: Uint8Array): GimbalReport {
      const reader = createReader(payload, 42);
      const deltaTime = reader.getFloat32();
      const deltaAngleX = reader.getFloat32();
      const deltaAngleY = reader.getFloat32();
      const deltaAngleZ = reader.getFloat32();
      const deltaVelocityX = reader.getFloat32();
      const deltaVelocityY = reader.getFloat32();
      const deltaVelocityZ = reader.getFloat32();
      const jointRoll = reader.getFloat32();
      const jointEl = reader.getFloat32();
      const jointAz = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        deltaTime,
        deltaAngleX,
        deltaAngleY,
        deltaAngleZ,
        deltaVelocityX,
        deltaVelocityY,
        deltaVelocityZ,
        jointRoll,
        jointEl,
        jointAz,
      };
    },
    encode(_: GimbalReport) {
      const writer = createWriter(42);
      writer.setFloat32(_.deltaTime);
      writer.setFloat32(_.deltaAngleX);
      writer.setFloat32(_.deltaAngleY);
      writer.setFloat32(_.deltaAngleZ);
      writer.setFloat32(_.deltaVelocityX);
      writer.setFloat32(_.deltaVelocityY);
      writer.setFloat32(_.deltaVelocityZ);
      writer.setFloat32(_.jointRoll);
      writer.setFloat32(_.jointEl);
      writer.setFloat32(_.jointAz);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  GIMBAL_CONTROL: {
    id: 201,
    crcExtra: 205,
    decode(payload: Uint8Array): GimbalControl {
      const reader = createReader(payload, 14);
      const demandedRateX = reader.getFloat32();
      const demandedRateY = reader.getFloat32();
      const demandedRateZ = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        demandedRateX,
        demandedRateY,
        demandedRateZ,
      };
    },
    encode(_: GimbalControl) {
      const writer = createWriter(14);
      writer.setFloat32(_.demandedRateX);
      writer.setFloat32(_.demandedRateY);
      writer.setFloat32(_.demandedRateZ);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  GIMBAL_TORQUE_CMD_REPORT: {
    id: 214,
    crcExtra: 69,
    decode(payload: Uint8Array): GimbalTorqueCmdReport {
      const reader = createReader(payload, 8);
      const rlTorqueCmd = reader.getInt16();
      const elTorqueCmd = reader.getInt16();
      const azTorqueCmd = reader.getInt16();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        rlTorqueCmd,
        elTorqueCmd,
        azTorqueCmd,
      };
    },
    encode(_: GimbalTorqueCmdReport) {
      const writer = createWriter(8);
      writer.setInt16(_.rlTorqueCmd);
      writer.setInt16(_.elTorqueCmd);
      writer.setInt16(_.azTorqueCmd);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      return writer.finish();
    },
  },
  GOPRO_HEARTBEAT: {
    id: 215,
    crcExtra: 101,
    decode(payload: Uint8Array): GoproHeartbeat {
      const reader = createReader(payload, 3);
      const status = reader.getUint8();
      const captureMode = reader.getUint8();
      const flags = reader.getUint8();
      return {
        status: goproHeartbeatStatusInverseMap.get(status) as GoproHeartbeatStatus,
        captureMode: goproCaptureModeInverseMap.get(captureMode) as GoproCaptureMode,
        flags: decodeBitmask(flags, goproHeartbeatFlagsInverseMap),
      };
    },
    encode(_: GoproHeartbeat) {
      const writer = createWriter(3);
      writer.setUint8(lookup(_.status, goproHeartbeatStatusMap));
      writer.setUint8(lookup(_.captureMode, goproCaptureModeMap));
      writer.setUint8(encodeBitmask(_.flags, goproHeartbeatFlagsMap));
      return writer.finish();
    },
  },
  GOPRO_GET_REQUEST: {
    id: 216,
    crcExtra: 50,
    decode(payload: Uint8Array): GoproGetRequest {
      const reader = createReader(payload, 3);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const cmdId = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        cmdId: goproCommandInverseMap.get(cmdId) as GoproCommand,
      };
    },
    encode(_: GoproGetRequest) {
      const writer = createWriter(3);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.cmdId, goproCommandMap));
      return writer.finish();
    },
  },
  GOPRO_GET_RESPONSE: {
    id: 217,
    crcExtra: 202,
    decode(payload: Uint8Array): GoproGetResponse {
      const reader = createReader(payload, 6);
      const cmdId = reader.getUint8();
      const status = reader.getUint8();
      const value = reader.getUint8Array(4);
      return {
        cmdId: goproCommandInverseMap.get(cmdId) as GoproCommand,
        status: goproRequestStatusInverseMap.get(status) as GoproRequestStatus,
        value,
      };
    },
    encode(_: GoproGetResponse) {
      const writer = createWriter(6);
      writer.setUint8(lookup(_.cmdId, goproCommandMap));
      writer.setUint8(lookup(_.status, goproRequestStatusMap));
      writer.setUint8Array(_.value, 4);
      return writer.finish();
    },
  },
  GOPRO_SET_REQUEST: {
    id: 218,
    crcExtra: 17,
    decode(payload: Uint8Array): GoproSetRequest {
      const reader = createReader(payload, 7);
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const cmdId = reader.getUint8();
      const value = reader.getUint8Array(4);
      return {
        targetSystem,
        targetComponent,
        cmdId: goproCommandInverseMap.get(cmdId) as GoproCommand,
        value,
      };
    },
    encode(_: GoproSetRequest) {
      const writer = createWriter(7);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.cmdId, goproCommandMap));
      writer.setUint8Array(_.value, 4);
      return writer.finish();
    },
  },
  GOPRO_SET_RESPONSE: {
    id: 219,
    crcExtra: 162,
    decode(payload: Uint8Array): GoproSetResponse {
      const reader = createReader(payload, 2);
      const cmdId = reader.getUint8();
      const status = reader.getUint8();
      return {
        cmdId: goproCommandInverseMap.get(cmdId) as GoproCommand,
        status: goproRequestStatusInverseMap.get(status) as GoproRequestStatus,
      };
    },
    encode(_: GoproSetResponse) {
      const writer = createWriter(2);
      writer.setUint8(lookup(_.cmdId, goproCommandMap));
      writer.setUint8(lookup(_.status, goproRequestStatusMap));
      return writer.finish();
    },
  },
  RPM: {
    id: 226,
    crcExtra: 207,
    decode(payload: Uint8Array): Rpm {
      const reader = createReader(payload, 8);
      const rpm1 = reader.getFloat32();
      const rpm2 = reader.getFloat32();
      return {
        rpm1,
        rpm2,
      };
    },
    encode(_: Rpm) {
      const writer = createWriter(8);
      writer.setFloat32(_.rpm1);
      writer.setFloat32(_.rpm2);
      return writer.finish();
    },
  },
  DEVICE_OP_READ: {
    id: 11000,
    crcExtra: 134,
    decode(payload: Uint8Array): DeviceOpRead {
      const reader = createReader(payload, 52);
      const requestId = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const bustype = reader.getUint8();
      const bus = reader.getUint8();
      const address = reader.getUint8();
      const busname = textDecoder.decode(reader.getUint8Array(40)).replace(/\0+$/, "");
      const regstart = reader.getUint8();
      const count = reader.getUint8();
      const bank = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        requestId,
        bustype: deviceOpBustypeInverseMap.get(bustype) as DeviceOpBustype,
        bus,
        address,
        busname,
        regstart,
        count,
        bank,
      };
    },
    encode(_: DeviceOpRead) {
      const writer = createWriter(52);
      writer.setUint32(_.requestId);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.bustype, deviceOpBustypeMap));
      writer.setUint8(_.bus);
      writer.setUint8(_.address);
      const busnameEncoded = textEncoder.encode(_.busname);
      writer.setUint8Array(busnameEncoded, 40);
      writer.setUint8(_.regstart);
      writer.setUint8(_.count);
      writer.setUint8(_.bank);
      return writer.finish();
    },
  },
  DEVICE_OP_READ_REPLY: {
    id: 11001,
    crcExtra: 15,
    decode(payload: Uint8Array): DeviceOpReadReply {
      const reader = createReader(payload, 136);
      const requestId = reader.getUint32();
      const result = reader.getUint8();
      const regstart = reader.getUint8();
      const count = reader.getUint8();
      const data = reader.getUint8Array(128);
      const bank = reader.getUint8();
      return {
        requestId,
        result,
        regstart,
        count,
        data,
        bank,
      };
    },
    encode(_: DeviceOpReadReply) {
      const writer = createWriter(136);
      writer.setUint32(_.requestId);
      writer.setUint8(_.result);
      writer.setUint8(_.regstart);
      writer.setUint8(_.count);
      writer.setUint8Array(_.data, 128);
      writer.setUint8(_.bank);
      return writer.finish();
    },
  },
  DEVICE_OP_WRITE: {
    id: 11002,
    crcExtra: 234,
    decode(payload: Uint8Array): DeviceOpWrite {
      const reader = createReader(payload, 180);
      const requestId = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const bustype = reader.getUint8();
      const bus = reader.getUint8();
      const address = reader.getUint8();
      const busname = textDecoder.decode(reader.getUint8Array(40)).replace(/\0+$/, "");
      const regstart = reader.getUint8();
      const count = reader.getUint8();
      const data = reader.getUint8Array(128);
      const bank = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        requestId,
        bustype: deviceOpBustypeInverseMap.get(bustype) as DeviceOpBustype,
        bus,
        address,
        busname,
        regstart,
        count,
        data,
        bank,
      };
    },
    encode(_: DeviceOpWrite) {
      const writer = createWriter(180);
      writer.setUint32(_.requestId);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(lookup(_.bustype, deviceOpBustypeMap));
      writer.setUint8(_.bus);
      writer.setUint8(_.address);
      const busnameEncoded = textEncoder.encode(_.busname);
      writer.setUint8Array(busnameEncoded, 40);
      writer.setUint8(_.regstart);
      writer.setUint8(_.count);
      writer.setUint8Array(_.data, 128);
      writer.setUint8(_.bank);
      return writer.finish();
    },
  },
  DEVICE_OP_WRITE_REPLY: {
    id: 11003,
    crcExtra: 64,
    decode(payload: Uint8Array): DeviceOpWriteReply {
      const reader = createReader(payload, 5);
      const requestId = reader.getUint32();
      const result = reader.getUint8();
      return {
        requestId,
        result,
      };
    },
    encode(_: DeviceOpWriteReply) {
      const writer = createWriter(5);
      writer.setUint32(_.requestId);
      writer.setUint8(_.result);
      return writer.finish();
    },
  },
  SECURE_COMMAND: {
    id: 11004,
    crcExtra: 11,
    decode(payload: Uint8Array): SecureCommand {
      const reader = createReader(payload, 232);
      const sequence = reader.getUint32();
      const operation = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const dataLength = reader.getUint8();
      const sigLength = reader.getUint8();
      const data = reader.getUint8Array(220);
      return {
        targetSystem,
        targetComponent,
        sequence,
        operation: secureCommandOpInverseMap.get(operation) as SecureCommandOp,
        dataLength,
        sigLength,
        data,
      };
    },
    encode(_: SecureCommand) {
      const writer = createWriter(232);
      writer.setUint32(_.sequence);
      writer.setUint32(lookup(_.operation, secureCommandOpMap));
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.dataLength);
      writer.setUint8(_.sigLength);
      writer.setUint8Array(_.data, 220);
      return writer.finish();
    },
  },
  SECURE_COMMAND_REPLY: {
    id: 11005,
    crcExtra: 93,
    decode(payload: Uint8Array): SecureCommandReply {
      const reader = createReader(payload, 230);
      const sequence = reader.getUint32();
      const operation = reader.getUint32();
      const result = reader.getUint8();
      const dataLength = reader.getUint8();
      const data = reader.getUint8Array(220);
      return {
        sequence,
        operation: secureCommandOpInverseMap.get(operation) as SecureCommandOp,
        result: mavResultInverseMap.get(result) as MavResult,
        dataLength,
        data,
      };
    },
    encode(_: SecureCommandReply) {
      const writer = createWriter(230);
      writer.setUint32(_.sequence);
      writer.setUint32(lookup(_.operation, secureCommandOpMap));
      writer.setUint8(lookup(_.result, mavResultMap));
      writer.setUint8(_.dataLength);
      writer.setUint8Array(_.data, 220);
      return writer.finish();
    },
  },
  ADAP_TUNING: {
    id: 11010,
    crcExtra: 46,
    decode(payload: Uint8Array): AdapTuning {
      const reader = createReader(payload, 49);
      const desired = reader.getFloat32();
      const achieved = reader.getFloat32();
      const error = reader.getFloat32();
      const theta = reader.getFloat32();
      const omega = reader.getFloat32();
      const sigma = reader.getFloat32();
      const thetaDot = reader.getFloat32();
      const omegaDot = reader.getFloat32();
      const sigmaDot = reader.getFloat32();
      const f = reader.getFloat32();
      const fDot = reader.getFloat32();
      const u = reader.getFloat32();
      const axis = reader.getUint8();
      return {
        axis: pidTuningAxisInverseMap.get(axis) as PidTuningAxis,
        desired,
        achieved,
        error,
        theta,
        omega,
        sigma,
        thetaDot,
        omegaDot,
        sigmaDot,
        f,
        fDot,
        u,
      };
    },
    encode(_: AdapTuning) {
      const writer = createWriter(49);
      writer.setFloat32(_.desired);
      writer.setFloat32(_.achieved);
      writer.setFloat32(_.error);
      writer.setFloat32(_.theta);
      writer.setFloat32(_.omega);
      writer.setFloat32(_.sigma);
      writer.setFloat32(_.thetaDot);
      writer.setFloat32(_.omegaDot);
      writer.setFloat32(_.sigmaDot);
      writer.setFloat32(_.f);
      writer.setFloat32(_.fDot);
      writer.setFloat32(_.u);
      writer.setUint8(lookup(_.axis, pidTuningAxisMap));
      return writer.finish();
    },
  },
  VISION_POSITION_DELTA: {
    id: 11011,
    crcExtra: 106,
    decode(payload: Uint8Array): VisionPositionDelta {
      const reader = createReader(payload, 44);
      const timeUsec = reader.getBigUint64();
      const timeDeltaUsec = reader.getBigUint64();
      const angleDelta = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const positionDelta = [reader.getFloat32(), reader.getFloat32(), reader.getFloat32()] as const;
      const confidence = reader.getFloat32();
      return {
        timeUsec,
        timeDeltaUsec,
        angleDelta,
        positionDelta,
        confidence,
      };
    },
    encode(_: VisionPositionDelta) {
      const writer = createWriter(44);
      writer.setBigUint64(_.timeUsec);
      writer.setBigUint64(_.timeDeltaUsec);
      _.angleDelta.forEach(writer.setFloat32);
      _.positionDelta.forEach(writer.setFloat32);
      writer.setFloat32(_.confidence);
      return writer.finish();
    },
  },
  AOA_SSA: {
    id: 11020,
    crcExtra: 205,
    decode(payload: Uint8Array): AoaSsa {
      const reader = createReader(payload, 16);
      const timeUsec = reader.getBigUint64();
      const aoa = reader.getFloat32();
      const ssa = reader.getFloat32();
      return {
        timeUsec,
        aoa,
        ssa,
      };
    },
    encode(_: AoaSsa) {
      const writer = createWriter(16);
      writer.setBigUint64(_.timeUsec);
      writer.setFloat32(_.aoa);
      writer.setFloat32(_.ssa);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_1_TO_4: {
    id: 11030,
    crcExtra: 144,
    decode(payload: Uint8Array): EscTelemetry1To4 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry1To4) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_5_TO_8: {
    id: 11031,
    crcExtra: 133,
    decode(payload: Uint8Array): EscTelemetry5To8 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry5To8) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_9_TO_12: {
    id: 11032,
    crcExtra: 85,
    decode(payload: Uint8Array): EscTelemetry9To12 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry9To12) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  OSD_PARAM_CONFIG: {
    id: 11033,
    crcExtra: 195,
    decode(payload: Uint8Array): OsdParamConfig {
      const reader = createReader(payload, 37);
      const requestId = reader.getUint32();
      const minValue = reader.getFloat32();
      const maxValue = reader.getFloat32();
      const increment = reader.getFloat32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const osdScreen = reader.getUint8();
      const osdIndex = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const configType = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        requestId,
        osdScreen,
        osdIndex,
        paramId,
        configType: osdParamConfigTypeInverseMap.get(configType) as OsdParamConfigType,
        minValue,
        maxValue,
        increment,
      };
    },
    encode(_: OsdParamConfig) {
      const writer = createWriter(37);
      writer.setUint32(_.requestId);
      writer.setFloat32(_.minValue);
      writer.setFloat32(_.maxValue);
      writer.setFloat32(_.increment);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.osdScreen);
      writer.setUint8(_.osdIndex);
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      writer.setUint8(lookup(_.configType, osdParamConfigTypeMap));
      return writer.finish();
    },
  },
  OSD_PARAM_CONFIG_REPLY: {
    id: 11034,
    crcExtra: 79,
    decode(payload: Uint8Array): OsdParamConfigReply {
      const reader = createReader(payload, 5);
      const requestId = reader.getUint32();
      const result = reader.getUint8();
      return {
        requestId,
        result: osdParamConfigErrorInverseMap.get(result) as OsdParamConfigError,
      };
    },
    encode(_: OsdParamConfigReply) {
      const writer = createWriter(5);
      writer.setUint32(_.requestId);
      writer.setUint8(lookup(_.result, osdParamConfigErrorMap));
      return writer.finish();
    },
  },
  OSD_PARAM_SHOW_CONFIG: {
    id: 11035,
    crcExtra: 128,
    decode(payload: Uint8Array): OsdParamShowConfig {
      const reader = createReader(payload, 8);
      const requestId = reader.getUint32();
      const targetSystem = reader.getUint8();
      const targetComponent = reader.getUint8();
      const osdScreen = reader.getUint8();
      const osdIndex = reader.getUint8();
      return {
        targetSystem,
        targetComponent,
        requestId,
        osdScreen,
        osdIndex,
      };
    },
    encode(_: OsdParamShowConfig) {
      const writer = createWriter(8);
      writer.setUint32(_.requestId);
      writer.setUint8(_.targetSystem);
      writer.setUint8(_.targetComponent);
      writer.setUint8(_.osdScreen);
      writer.setUint8(_.osdIndex);
      return writer.finish();
    },
  },
  OSD_PARAM_SHOW_CONFIG_REPLY: {
    id: 11036,
    crcExtra: 177,
    decode(payload: Uint8Array): OsdParamShowConfigReply {
      const reader = createReader(payload, 34);
      const requestId = reader.getUint32();
      const minValue = reader.getFloat32();
      const maxValue = reader.getFloat32();
      const increment = reader.getFloat32();
      const result = reader.getUint8();
      const paramId = textDecoder.decode(reader.getUint8Array(16)).replace(/\0+$/, "");
      const configType = reader.getUint8();
      return {
        requestId,
        result: osdParamConfigErrorInverseMap.get(result) as OsdParamConfigError,
        paramId,
        configType: osdParamConfigTypeInverseMap.get(configType) as OsdParamConfigType,
        minValue,
        maxValue,
        increment,
      };
    },
    encode(_: OsdParamShowConfigReply) {
      const writer = createWriter(34);
      writer.setUint32(_.requestId);
      writer.setFloat32(_.minValue);
      writer.setFloat32(_.maxValue);
      writer.setFloat32(_.increment);
      writer.setUint8(lookup(_.result, osdParamConfigErrorMap));
      const paramIdEncoded = textEncoder.encode(_.paramId);
      writer.setUint8Array(paramIdEncoded, 16);
      writer.setUint8(lookup(_.configType, osdParamConfigTypeMap));
      return writer.finish();
    },
  },
  OBSTACLE_DISTANCE_3D: {
    id: 11037,
    crcExtra: 130,
    decode(payload: Uint8Array): ObstacleDistance3d {
      const reader = createReader(payload, 28);
      const timeBootMs = reader.getUint32();
      const x = reader.getFloat32();
      const y = reader.getFloat32();
      const z = reader.getFloat32();
      const minDistance = reader.getFloat32();
      const maxDistance = reader.getFloat32();
      const obstacleId = reader.getUint16();
      const sensorType = reader.getUint8();
      const frame = reader.getUint8();
      return {
        timeBootMs,
        sensorType: mavDistanceSensorInverseMap.get(sensorType) as MavDistanceSensor,
        frame: mavFrameInverseMap.get(frame) as MavFrame,
        obstacleId,
        x,
        y,
        z,
        minDistance,
        maxDistance,
      };
    },
    encode(_: ObstacleDistance3d) {
      const writer = createWriter(28);
      writer.setUint32(_.timeBootMs);
      writer.setFloat32(_.x);
      writer.setFloat32(_.y);
      writer.setFloat32(_.z);
      writer.setFloat32(_.minDistance);
      writer.setFloat32(_.maxDistance);
      writer.setUint16(_.obstacleId);
      writer.setUint8(lookup(_.sensorType, mavDistanceSensorMap));
      writer.setUint8(lookup(_.frame, mavFrameMap));
      return writer.finish();
    },
  },
  WATER_DEPTH: {
    id: 11038,
    crcExtra: 47,
    decode(payload: Uint8Array): WaterDepth {
      const reader = createReader(payload, 38);
      const timeBootMs = reader.getUint32();
      const lat = reader.getInt32();
      const lng = reader.getInt32();
      const alt = reader.getFloat32();
      const roll = reader.getFloat32();
      const pitch = reader.getFloat32();
      const yaw = reader.getFloat32();
      const distance = reader.getFloat32();
      const temperature = reader.getFloat32();
      const id = reader.getUint8();
      const healthy = reader.getUint8();
      return {
        timeBootMs,
        id,
        healthy,
        lat,
        lng,
        alt,
        roll,
        pitch,
        yaw,
        distance,
        temperature,
      };
    },
    encode(_: WaterDepth) {
      const writer = createWriter(38);
      writer.setUint32(_.timeBootMs);
      writer.setInt32(_.lat);
      writer.setInt32(_.lng);
      writer.setFloat32(_.alt);
      writer.setFloat32(_.roll);
      writer.setFloat32(_.pitch);
      writer.setFloat32(_.yaw);
      writer.setFloat32(_.distance);
      writer.setFloat32(_.temperature);
      writer.setUint8(_.id);
      writer.setUint8(_.healthy);
      return writer.finish();
    },
  },
  MCU_STATUS: {
    id: 11039,
    crcExtra: 142,
    decode(payload: Uint8Array): McuStatus {
      const reader = createReader(payload, 9);
      const mcuTemperature = reader.getInt16();
      const mcuVoltage = reader.getUint16();
      const mcuVoltageMin = reader.getUint16();
      const mcuVoltageMax = reader.getUint16();
      const id = reader.getUint8();
      return {
        id,
        mcuTemperature,
        mcuVoltage,
        mcuVoltageMin,
        mcuVoltageMax,
      };
    },
    encode(_: McuStatus) {
      const writer = createWriter(9);
      writer.setInt16(_.mcuTemperature);
      writer.setUint16(_.mcuVoltage);
      writer.setUint16(_.mcuVoltageMin);
      writer.setUint16(_.mcuVoltageMax);
      writer.setUint8(_.id);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_13_TO_16: {
    id: 11040,
    crcExtra: 132,
    decode(payload: Uint8Array): EscTelemetry13To16 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry13To16) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_17_TO_20: {
    id: 11041,
    crcExtra: 208,
    decode(payload: Uint8Array): EscTelemetry17To20 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry17To20) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_21_TO_24: {
    id: 11042,
    crcExtra: 201,
    decode(payload: Uint8Array): EscTelemetry21To24 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry21To24) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_25_TO_28: {
    id: 11043,
    crcExtra: 193,
    decode(payload: Uint8Array): EscTelemetry25To28 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry25To28) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
  ESC_TELEMETRY_29_TO_32: {
    id: 11044,
    crcExtra: 189,
    decode(payload: Uint8Array): EscTelemetry29To32 {
      const reader = createReader(payload, 44);
      const voltage = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const current = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const totalcurrent = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const rpm = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const count = [reader.getUint16(), reader.getUint16(), reader.getUint16(), reader.getUint16()] as const;
      const temperature = reader.getUint8Array(4);
      return {
        temperature,
        voltage,
        current,
        totalcurrent,
        rpm,
        count,
      };
    },
    encode(_: EscTelemetry29To32) {
      const writer = createWriter(44);
      _.voltage.forEach(writer.setUint16);
      _.current.forEach(writer.setUint16);
      _.totalcurrent.forEach(writer.setUint16);
      _.rpm.forEach(writer.setUint16);
      _.count.forEach(writer.setUint16);
      writer.setUint8Array(_.temperature, 4);
      return writer.finish();
    },
  },
} as const;

export type Schema = typeof schema;
export type Type = keyof Schema;
